import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import {
  Bus, MapPin, Users, Navigation, AlertTriangle, Clock,
  Phone, User, Shield, LogOut, CheckCircle, HelpCircle,
  ShieldAlert, Settings, FileText, Bell, ChevronLeft, ChevronRight,
  Database, Mail, MessageSquare, Key, Calendar, Sliders, BarChart, Server, History,
  File, FileSpreadsheet, FileCode, Image, LayoutGrid, List, Search, Filter, X, RotateCcw,
  PanelLeft, Sun, Monitor, Moon, Info, Globe, Lock, BookOpen, Check, Fuel, Wrench, Gauge,
  Eye, Download, ExternalLink, HeartPulse, Award, Star, ShieldCheck, Plus, Trash2,
  UserCheck, Edit2, Pencil
} from 'lucide-react';
import { DriverEnrollmentModal } from './components/DriverEnrollmentModal';
import ParentPortal from './components/ParentPortal';
import { GeneralSettingsTabs, DEFAULT_LOOKUPS } from './components/GeneralSettingsTabs';
import './Admin.css';
import { Responsive as ResponsiveGridLayout, useContainerWidth } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';



const BACKEND_URL = localStorage.getItem('busbuddy_backend_url') || import.meta.env.VITE_BACKEND_URL || window.location.origin;

// Custom SteeringWheel icon matching Lucide standard style
const SteeringWheel = ({ size = 24, color = 'currentColor', strokeWidth = 2, className = '', style = {} }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    style={style}
  >
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="2" />
    <path d="M12 14v7" />
    <path d="M10 12l-6.75 -1" />
    <path d="M14 12l6.75 -1" />
  </svg>
);

// Helper to determine document icon based on document file type/mime using downloaded official icons
const getDocumentIcon = (doc) => {
  const name = (doc.file_name || '').toLowerCase();
  const mime = (doc.mime_type || '').toLowerCase();

  // Images (.png, .jpg, .jpeg, .webp, .svg, .bmp)
  if (mime.includes('image') || /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(name)) {
    return {
      icon: <img src="/icons/image.svg" alt="Image" style={{ width: '38px', height: '38px', objectFit: 'contain' }} />,
      label: 'Image'
    };
  }
  // PDF (.pdf)
  if (mime.includes('pdf') || /\.pdf$/i.test(name)) {
    return {
      icon: <img src="/icons/pdf.svg" alt="PDF" style={{ width: '38px', height: '38px', objectFit: 'contain' }} />,
      label: 'PDF'
    };
  }
  // Word Document (.doc, .docx)
  if (mime.includes('word') || mime.includes('officedocument.wordprocessingml') || /\.(docx?|rtf|odt)$/i.test(name)) {
    return {
      icon: <img src="/icons/word.svg" alt="Word" style={{ width: '38px', height: '38px', objectFit: 'contain' }} />,
      label: 'Word'
    };
  }
  // Excel Spreadsheet (.xls, .xlsx, .csv, .tsv)
  if (mime.includes('excel') || mime.includes('spreadsheet') || mime.includes('csv') || /\.(xlsx?|csv|tsv)$/i.test(name)) {
    return {
      icon: <img src="/icons/excel.svg" alt="Excel" style={{ width: '38px', height: '38px', objectFit: 'contain' }} />,
      label: 'Excel'
    };
  }
  // PowerPoint Presentation (.ppt, .pptx)
  if (mime.includes('powerpoint') || mime.includes('presentation') || /\.(pptx?|pps)$/i.test(name)) {
    return {
      icon: <img src="/icons/powerpoint.svg" alt="PowerPoint" style={{ width: '38px', height: '38px', objectFit: 'contain' }} />,
      label: 'PowerPoint'
    };
  }
  // Zip / Compressed Archives (.zip, .rar, .7z, .tar, .gz)
  if (mime.includes('zip') || mime.includes('compressed') || /\.(zip|rar|7z|tar|gz)$/i.test(name)) {
    return {
      icon: <img src="/icons/zip.svg" alt="Archive" style={{ width: '38px', height: '38px', objectFit: 'contain' }} />,
      label: 'Archive'
    };
  }
  // Text / Logs / Config (.txt, .json, .xml, .log, .md)
  if (mime.includes('text') || /\.(txt|json|xml|log|md)$/i.test(name)) {
    return {
      icon: <img src="/icons/text.svg" alt="Text" style={{ width: '38px', height: '38px', objectFit: 'contain' }} />,
      label: 'Text'
    };
  }
  // Generic Document
  return {
    icon: <img src="/icons/document.svg" alt="Document" style={{ width: '38px', height: '38px', objectFit: 'contain' }} />,
    label: 'Document'
  };
};

// Helper to determine preview rendering mode
const getDocumentPreviewType = (doc) => {
  if (!doc) return 'unknown';
  const name = (doc.file_name || '').toLowerCase();
  const mime = (doc.mime_type || '').toLowerCase();
  if (mime.includes('image') || /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(name)) return 'image';
  if (mime.includes('pdf') || /\.pdf$/i.test(name)) return 'pdf';
  if (mime.includes('text') || /\.(txt|json|xml|log|md|csv)$/i.test(name)) return 'text';
  if (mime.includes('word') || mime.includes('officedocument.wordprocessingml') || /\.(docx?|rtf|odt)$/i.test(name)) return 'word';
  if (mime.includes('excel') || mime.includes('spreadsheet') || /\.(xlsx?|tsv)$/i.test(name)) return 'excel';
  if (mime.includes('powerpoint') || mime.includes('presentation') || /\.(pptx?|pps)$/i.test(name)) return 'powerpoint';
  return 'other';
};

// Custom Map Helper to pan/zoom map on center changes dynamically
function ChangeMapView({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || map.getZoom());
    }
  }, [center, zoom, map]);
  return null;
}

// Custom Map Helper to handle click pinpoint events on stops planning
function InteractiveMapEvents({ activeStopIndex, newRouteStops, updateStopRow }) {
  useMapEvents({
    click(e) {
      if (activeStopIndex !== null && activeStopIndex < newRouteStops.length) {
        updateStopRow(activeStopIndex, 'lat', e.latlng.lat.toFixed(6));
        updateStopRow(activeStopIndex, 'lng', e.latlng.lng.toFixed(6));
      }
    }
  });
  return null;
}

// Custom Stop Icon for Map Planning
const getPlanningStopIcon = (seq, isActive) => L.divIcon({
  html: `<div style="background-color: ${isActive ? '#FF7B72' : '#1E2238'}; border: 2.5px solid white; border-radius: 50%; width: 26px; height: 26px; display: flex; align-items: center; justify-content: center; font-size: 11px; color: white; font-weight: 750; box-shadow: 0 4px 10px rgba(30,34,56,0.25); transform: scale(${isActive ? 1.25 : 1}); transition: transform 0.2s;">${seq}</div>`,
  className: 'custom-stop-marker',
  iconSize: [26, 26],
  iconAnchor: [13, 13]
});

const studentProfilesData = {
  Mike: {
    fullName: "Mike Suresh Sharma",
    admissionNo: "ADM-2022-8742",
    rollNo: "18",
    dob: "October 14, 2011 (Age 14)",
    gender: "Male",
    bloodGroup: "B+ Positive",
    aadhaar: "8823-1490-5572",
    photo: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&q=80&w=200",
    grade: "Class 9",
    section: "Section B",
    board: "CBSE",
    stream: "General Science (High School)",
    subjects: "Mathematics, Physics, Chemistry, English, Information Technology",
    secondLang: "Sanskrit",
    classTeacher: "Mrs. Ananya Sen (+91 98450 12234)",
    house: "Gandhi House (Green)",
    fatherName: "Suresh Sharma (Software Architect)",
    motherName: "Ritu Sharma (Pediatrician)",
    guardianName: "N/A",
    primaryContact: "+91 90747 70963",
    emergencyContact: "+91 98860 55432 (Mother)",
    email: "suresh.sharma@gmail.com",
    permanentAddress: "Flat 402, Springdale Apts, Banaswadi, Bangalore - 560043",
    currentAddress: "Flat 402, Springdale Apts, Banaswadi, Bangalore - 560043",
    modeOfTransport: "School Bus",
    busNo: "Bus 10A (DL-1CA-1234)",
    assignedStop: "Kalyan Nagar Crossing (Pickup) / Kalyan Nagar Crossing (Drop)",
    driverContact: "Ramesh Kumar (+91 9777777771)",
    allergies: "Peanuts, Dust Allergy",
    medications: "Cetirizine (on-demand)",
    emergencyPerson: "Suresh Sharma (Father)",
    clubs: "Robotics Club, Eco Committee",
    sports: "Basketball (School Under-14 Team)",
    responsibility: "Class Monitor"
  },
  Sara: {
    fullName: "Sara Suresh Sharma",
    admissionNo: "ADM-2024-9104",
    rollNo: "22",
    dob: "April 05, 2014 (Age 12)",
    gender: "Female",
    bloodGroup: "O+ Positive",
    aadhaar: "7432-8819-3304",
    photo: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=200",
    grade: "Class 7",
    section: "Section A",
    board: "CBSE",
    stream: "General Middle School",
    subjects: "Mathematics, General Science, Social Studies, English",
    secondLang: "Hindi",
    classTeacher: "Mr. Rajeev Joseph (+91 94480 99876)",
    house: "Nehru House (Red)",
    fatherName: "Suresh Sharma (Software Architect)",
    motherName: "Ritu Sharma (Pediatrician)",
    guardianName: "N/A",
    primaryContact: "+91 90747 70963",
    emergencyContact: "+91 98860 55432 (Mother)",
    email: "suresh.sharma@gmail.com",
    permanentAddress: "Flat 402, Springdale Apts, Banaswadi, Bangalore - 560043",
    currentAddress: "Flat 402, Springdale Apts, Banaswadi, Bangalore - 560043",
    modeOfTransport: "School Bus",
    busNo: "Bus 10A (DL-1CA-1234)",
    assignedStop: "HRBR Layout Playground (Pickup) / HRBR Layout Playground (Drop)",
    driverContact: "Ramesh Kumar (+91 9777777771)",
    allergies: "N/A",
    medications: "N/A",
    emergencyPerson: "Suresh Sharma (Father)",
    clubs: "Debate Society, School Choir",
    sports: "Gymnastics, Badminton",
    responsibility: "Prefect"
  },
  Tom: {
    fullName: "Tom Meera Patel",
    admissionNo: "ADM-2021-4452",
    rollNo: "05",
    dob: "January 19, 2009 (Age 17)",
    gender: "Male",
    bloodGroup: "A- Negative",
    aadhaar: "1098-3342-9908",
    photo: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=200",
    grade: "Class 11",
    section: "Section S",
    board: "CBSE",
    stream: "Science (PCMC)",
    subjects: "Physics, Chemistry, Mathematics, Computer Science, English",
    secondLang: "French",
    classTeacher: "Mrs. Shobha Murthy (+91 99002 88443)",
    house: "Tagore House (Blue)",
    fatherName: "Rajesh Patel (Businessman)",
    motherName: "Meera Patel (HR Director)",
    guardianName: "N/A",
    primaryContact: "+91 98888 88882",
    emergencyContact: "+91 99450 77661 (Father)",
    email: "meera.patel@yahoo.com",
    permanentAddress: "Villa 12, Sobha Enclave, Kalyan Nagar, Bangalore - 560043",
    currentAddress: "Villa 12, Sobha Enclave, Kalyan Nagar, Bangalore - 560043",
    modeOfTransport: "School Bus",
    busNo: "Bus 12B (DL-1CB-5678)",
    assignedStop: "HRBR Layout Playground (Pickup) / Kalyan Nagar Crossing (Drop)",
    driverContact: "Satish Singh (+91 9666666662)",
    allergies: "Gluten Intolerant, Asthmatic",
    medications: "Inhaler (as needed)",
    emergencyPerson: "Meera Patel (Mother)",
    clubs: "Coding Club (Lead Developer), Quiz Club",
    sports: "Chess (School Captain)",
    responsibility: "House Captain"
  },
  Lisa: {
    fullName: "Lisa Amit Varma",
    admissionNo: "ADM-2023-7412",
    rollNo: "14",
    dob: "August 20, 2012 (Age 14)",
    gender: "Female",
    bloodGroup: "O- Negative",
    aadhaar: "5543-9876-1211",
    photo: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=200",
    grade: "Class 8",
    section: "Section C",
    board: "ICSE",
    stream: "Middle School General",
    subjects: "English, History, Civics, Geography, Physics, Chemistry, Biology",
    secondLang: "Hindi",
    classTeacher: "Ms. Preeti Malhotra (+91 98765 43210)",
    house: "Gandhi House (Green)",
    fatherName: "Amit Varma (Financial Analyst)",
    motherName: "Pooja Varma (Graphic Designer)",
    guardianName: "N/A",
    primaryContact: "+91 91111 11113",
    emergencyContact: "+91 91111 11113 (Father)",
    email: "amit.varma@varma.co",
    permanentAddress: "No. 45, 2nd Cross, Banaswadi, Bangalore - 560043",
    currentAddress: "No. 45, 2nd Cross, Banaswadi, Bangalore - 560043",
    modeOfTransport: "School Bus",
    busNo: "Bus 14C (KA-03-CC-9999)",
    assignedStop: "Kalyan Nagar Crossing (Pickup) / Kalyan Nagar Crossing (Drop)",
    driverContact: "Vijay Singh (+91 9444444443)",
    allergies: "None",
    medications: "None",
    emergencyPerson: "Amit Varma (Father)",
    clubs: "Drama Club, Art & Crafts Club",
    sports: "Table Tennis",
    responsibility: "Eco Monitor"
  },
  John: {
    fullName: "John Kiran Rao",
    admissionNo: "ADM-2022-3029",
    rollNo: "09",
    dob: "December 01, 2010 (Age 15)",
    gender: "Male",
    bloodGroup: "A+ Positive",
    aadhaar: "9876-5432-1098",
    photo: "https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?auto=format&fit=crop&q=80&w=200",
    grade: "Class 10",
    section: "Section A",
    board: "CBSE",
    stream: "High School",
    subjects: "Mathematics, Science, Social Sciences, English, Kannada",
    secondLang: "Kannada",
    classTeacher: "Mr. Somnath Bose (+91 95432 10987)",
    house: "Bhabha House (Yellow)",
    fatherName: "Kiran Rao (Attorney)",
    motherName: "Sonia Rao (NGO Director)",
    guardianName: "N/A",
    primaryContact: "+91 92222 22224",
    emergencyContact: "+91 92222 22224 (Father)",
    email: "kiran.rao@raoassociates.in",
    permanentAddress: "Plot 302, HRBR Layout 1st Block, Bangalore - 560043",
    currentAddress: "Plot 302, HRBR Layout 1st Block, Bangalore - 560043",
    modeOfTransport: "School Bus",
    busNo: "Bus 16D (KA-03-DD-8888)",
    assignedStop: "HRBR Layout Playground (Pickup) / Banaswadi Apartments (Drop)",
    driverContact: "Anil Murthy (+91 9555555554)",
    allergies: "Dust, Shellfish",
    medications: "Aller-g pill (seasonal)",
    emergencyPerson: "Sonia Rao (Mother)",
    clubs: "Model United Nations (MUN), Science Club",
    sports: "Football (School Team Midfielder)",
    responsibility: "Prefect, House Vice-Captain"
  },
  Amy: {
    fullName: "Amy Kiran Rao",
    admissionNo: "ADM-2025-4491",
    rollNo: "04",
    dob: "June 18, 2015 (Age 11)",
    gender: "Female",
    bloodGroup: "A+ Positive",
    aadhaar: "9876-5432-1099",
    photo: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80&w=200",
    grade: "Class 6",
    section: "Section D",
    board: "CBSE",
    stream: "Primary School",
    subjects: "Mathematics, General Science, Social Studies, English, French",
    secondLang: "French",
    classTeacher: "Mrs. Clara D'Souza (+91 91234 56789)",
    house: "Bhabha House (Yellow)",
    fatherName: "Kiran Rao (Attorney)",
    motherName: "Sonia Rao (NGO Director)",
    guardianName: "N/A",
    primaryContact: "+91 92222 22224",
    emergencyContact: "+91 92222 22224 (Father)",
    email: "kiran.rao@raoassociates.in",
    permanentAddress: "Plot 302, HRBR Layout 1st Block, Bangalore - 560043",
    currentAddress: "Plot 302, HRBR Layout 1st Block, Bangalore - 560043",
    modeOfTransport: "School Bus",
    busNo: "Bus 16D (KA-03-DD-8888)",
    assignedStop: "HRBR Layout Playground (Pickup) / Banaswadi Apartments (Drop)",
    driverContact: "Anil Murthy (+91 9555555554)",
    allergies: "None",
    medications: "None",
    emergencyPerson: "Kiran Rao (Father)",
    clubs: "Nature Club, Photography Society",
    sports: "Swimming",
    responsibility: "Class Monitor"
  },
  David: {
    fullName: "David Deepa Nair",
    admissionNo: "ADM-2020-1102",
    rollNo: "08",
    dob: "November 23, 2008 (Age 17)",
    gender: "Male",
    bloodGroup: "O+ Positive",
    aadhaar: "1234-5678-9012",
    photo: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=200",
    grade: "Class 12",
    section: "Section A",
    board: "State Board",
    stream: "Science (PCMB)",
    subjects: "Physics, Chemistry, Mathematics, Biology, English",
    secondLang: "Malayalam",
    classTeacher: "Mr. Raghavan Pillai (+91 98980 98980)",
    house: "Tagore House (Blue)",
    fatherName: "Unnikrishnan Nair (Professor)",
    motherName: "Deepa Nair (Bank Manager)",
    guardianName: "N/A",
    primaryContact: "+91 93333 33335",
    emergencyContact: "+91 93333 33335 (Mother)",
    email: "deepa.nair@sbi.co.in",
    permanentAddress: "No. 18, Coconut Grove Lane, Banaswadi, Bangalore - 560043",
    currentAddress: "No. 18, Coconut Grove Lane, Banaswadi, Bangalore - 560043",
    modeOfTransport: "School Bus",
    busNo: "Bus 18E (KA-03-EE-7777)",
    assignedStop: "Banaswadi Apartments (Pickup) / HRBR Layout Playground (Drop)",
    driverContact: "Manjunath Gowda (+91 9666666665)",
    allergies: "Dust",
    medications: "N/A",
    emergencyPerson: "Deepa Nair (Mother)",
    clubs: "Debate Club (President), Model United Nations",
    sports: "Athletics (100m Sprinter)",
    responsibility: "School Prefect, Sports Secretary"
  },
  Ryan: {
    fullName: "Ryan Deepa Nair",
    admissionNo: "ADM-2023-5591",
    rollNo: "21",
    dob: "March 11, 2013 (Age 13)",
    gender: "Male",
    bloodGroup: "O+ Positive",
    aadhaar: "1234-5678-9013",
    photo: "https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?auto=format&fit=crop&q=80&w=200",
    grade: "Class 7",
    section: "Section B",
    board: "State Board",
    stream: "General Middle School",
    subjects: "Mathematics, General Science, Social Studies, English",
    secondLang: "Malayalam",
    classTeacher: "Mrs. Latha Verghese (+91 94440 94440)",
    house: "Tagore House (Blue)",
    fatherName: "Unnikrishnan Nair (Professor)",
    motherName: "Deepa Nair (Bank Manager)",
    guardianName: "N/A",
    primaryContact: "+91 93333 33335",
    emergencyContact: "+91 93333 33335 (Mother)",
    email: "deepa.nair@sbi.co.in",
    permanentAddress: "No. 18, Coconut Grove Lane, Banaswadi, Bangalore - 560043",
    currentAddress: "No. 18, Coconut Grove Lane, Banaswadi, Bangalore - 560043",
    modeOfTransport: "School Bus",
    busNo: "Bus 18E (KA-03-EE-7777)",
    assignedStop: "Banaswadi Apartments (Pickup) / HRBR Layout Playground (Drop)",
    driverContact: "Manjunath Gowda (+91 9666666665)",
    allergies: "Lactose Intolerant",
    medications: "None",
    emergencyPerson: "Deepa Nair (Mother)",
    clubs: "Arts Club, Drama Club",
    sports: "Football",
    responsibility: "Class Monitor"
  }
};

const getStudentAvatar = (student) => {
  if (!student) return 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&q=80&w=150';
  if (student.photo) return student.photo;
  if (student.avatar && (student.avatar.startsWith('http://') || student.avatar.startsWith('https://') || student.avatar.startsWith('/'))) {
    return student.avatar;
  }
  const firstName = (student.first_name || '').trim();
  const matchedProfile = studentProfilesData[firstName] || 
                         studentProfilesData[firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()] ||
                         Object.entries(studentProfilesData).find(([k]) => k.toLowerCase() === firstName.toLowerCase())?.[1];
  if (matchedProfile && matchedProfile.photo) {
    return matchedProfile.photo;
  }
  const fullName = `${student.first_name || ''} ${student.last_name || ''}`.trim() || 'Student';
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName)}&background=E2E8F0&color=475569&bold=true`;
};

const driverProfilesData = {
  "Ramesh Kumar": {
    avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=200",
    driverId: "DVR-004",
    name: "Ramesh Kumar",
    phone: "+91 9777777771",
    address: "No. 12, 4th Cross, Kalyan Nagar, Bangalore - 560043",
    licensingStatus: "VALID LICENSE",
    licenseNumber: "DL-0420180098231",
    licenseExpiry: "Oct 15, 2028",
    medicalStatus: "Certified Fit",
    medicalExpiry: "Aug 2027",
    policeVerification: "Cleared & Verified",
    bloodGroup: "O+ Positive",
    experienceYears: "9 Years",
    safetyScore: "99.4%",
    onTimeScore: "98.8%",
    assignedBusNumber: "Bus 10A",
    assignedBusReg: "KA-03-AA-1234",
    assignedRoute: "Route A - Morning Pickup / Route B - Afternoon Drop",
    status: "ACTIVE",
    documents: [
      { name: "Commercial Driver License (Heavy Passenger)", meta: "PDF • 1.2 MB • Expires Oct 2028" },
      { name: "Annual Medical Fitness Certificate", meta: "PDF • 850 KB • Issued Aug 2026" },
      { name: "Police Verification Clearance", meta: "PDF • 620 KB • Verified 2026" }
    ]
  },
  "Satish Singh": {
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=200",
    driverId: "DVR-005",
    name: "Satish Singh",
    phone: "+91 9666666662",
    address: "Flat 201, Maple Heights, Banaswadi, Bangalore - 560043",
    licensingStatus: "VALID LICENSE",
    licenseNumber: "DL-0420190054321",
    licenseExpiry: "May 20, 2029",
    medicalStatus: "Certified Fit",
    medicalExpiry: "May 2027",
    policeVerification: "Cleared & Verified",
    bloodGroup: "B+ Positive",
    experienceYears: "7 Years",
    safetyScore: "99.1%",
    onTimeScore: "97.9%",
    assignedBusNumber: "Bus 12B",
    assignedBusReg: "KA-03-BB-5678",
    assignedRoute: "Route B - Afternoon Drop",
    status: "ACTIVE",
    documents: [
      { name: "Commercial Driver License", meta: "PDF • 1.4 MB • Expires May 2029" },
      { name: "Medical Fitness Certificate", meta: "PDF • 780 KB • Issued May 2026" }
    ]
  },
  "Vijay Singh": {
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=200",
    driverId: "DVR-009",
    name: "Vijay Singh",
    phone: "+91 9444444443",
    address: "No. 45, 2nd Cross, Banaswadi, Bangalore - 560043",
    licensingStatus: "VALID LICENSE",
    licenseNumber: "DL-0420200087654",
    licenseExpiry: "Jun 12, 2030",
    medicalStatus: "Certified Fit",
    medicalExpiry: "Jun 2027",
    policeVerification: "Cleared & Verified",
    bloodGroup: "A+ Positive",
    experienceYears: "6 Years",
    safetyScore: "98.9%",
    onTimeScore: "98.2%",
    assignedBusNumber: "Bus 14C",
    assignedBusReg: "KA-03-CC-9999",
    assignedRoute: "Route A - Morning Pickup",
    status: "ACTIVE",
    documents: [
      { name: "Commercial Driver License", meta: "PDF • 1.1 MB • Expires Jun 2030" },
      { name: "Medical Fitness Certificate", meta: "PDF • 690 KB • Issued Jun 2026" }
    ]
  },
  "Anil Murthy": {
    avatar: "https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?auto=format&fit=crop&q=80&w=200",
    driverId: "DVR-010",
    name: "Anil Murthy",
    phone: "+91 9555555554",
    address: "No. 89, 7th Main, HRBR Layout, Bangalore - 560043",
    licensingStatus: "VALID LICENSE",
    licenseNumber: "DL-0420170065432",
    licenseExpiry: "Nov 02, 2027",
    medicalStatus: "Certified Fit",
    medicalExpiry: "Nov 2026",
    policeVerification: "Cleared & Verified",
    bloodGroup: "AB+ Positive",
    experienceYears: "11 Years",
    safetyScore: "99.8%",
    onTimeScore: "99.1%",
    assignedBusNumber: "Bus 16D",
    assignedBusReg: "KA-03-DD-8888",
    assignedRoute: "Route C - North Express",
    status: "ACTIVE",
    documents: [
      { name: "Commercial Driver License", meta: "PDF • 1.3 MB • Expires Nov 2027" },
      { name: "Medical Fitness Certificate", meta: "PDF • 910 KB • Issued Nov 2025" }
    ]
  },
  "Manjunath Gowda": {
    avatar: "https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?auto=format&fit=crop&q=80&w=200",
    driverId: "DVR-011",
    name: "Manjunath Gowda",
    phone: "+91 9666666665",
    address: "No. 204, Outer Ring Road, Kalyan Nagar, Bangalore - 560043",
    licensingStatus: "VALID LICENSE",
    licenseNumber: "DL-0420190043210",
    licenseExpiry: "Jan 18, 2029",
    medicalStatus: "Certified Fit",
    medicalExpiry: "Jan 2027",
    policeVerification: "Cleared & Verified",
    bloodGroup: "O+ Positive",
    experienceYears: "8 Years",
    safetyScore: "99.2%",
    onTimeScore: "98.5%",
    assignedBusNumber: "Bus 18E",
    assignedBusReg: "KA-03-EE-7777",
    assignedRoute: "Route D - South Feeder",
    status: "ACTIVE",
    documents: [
      { name: "Commercial Driver License", meta: "PDF • 1.5 MB • Expires Jan 2029" },
      { name: "Medical Fitness Certificate", meta: "PDF • 820 KB • Issued Jan 2026" }
    ]
  }
};

const parentProfilesData = {
  "Suresh Sharma": {
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=200",
    parentId: "PRT-002",
    name: "Suresh Sharma",
    occupation: "Principal Software Architect",
    relationship: "Father",
    spouseName: "Dr. Ritu Sharma (Pediatrician)",
    phone: "+91 90747 70963",
    altPhone: "+91 98860 55432",
    email: "suresh.sharma@gmail.com",
    address: "Flat 402, Springdale Apts, Banaswadi, Bangalore - 560043",
    preferredLanguage: "English, Hindi",
    emergencyContact: "+91 98860 55432 (Dr. Ritu Sharma)",
    pickupAuth: "Authorized for self & spouse",
    notificationStatus: "ACTIVE",
    smsEnabled: true,
    pushEnabled: true,
    emailAlerts: true,
    etaAlertSetting: "5 mins before arrival"
  },
  "Meera Patel": {
    avatar: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=200",
    parentId: "PRT-003",
    name: "Meera Patel",
    occupation: "VP of Human Resources",
    relationship: "Mother",
    spouseName: "Rajesh Patel (Managing Director)",
    phone: "+91 98888 88882",
    altPhone: "+91 99450 77661",
    email: "meera.patel@yahoo.com",
    address: "Villa 12, Sobha Enclave, Kalyan Nagar, Bangalore - 560043",
    preferredLanguage: "English, Gujarati, Hindi",
    emergencyContact: "+91 99450 77661 (Rajesh Patel)",
    pickupAuth: "Authorized for self & Rajesh Patel",
    notificationStatus: "ACTIVE",
    smsEnabled: true,
    pushEnabled: true,
    emailAlerts: true,
    etaAlertSetting: "10 mins before arrival"
  },
  "Amit Varma": {
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=200",
    parentId: "PRT-006",
    name: "Amit Varma",
    occupation: "Senior Financial Portfolio Analyst",
    relationship: "Father",
    spouseName: "Pooja Varma (Creative Lead)",
    phone: "+91 91111 11113",
    altPhone: "+91 91111 11113",
    email: "amit.varma@varma.co",
    address: "No. 45, 2nd Cross, Banaswadi, Bangalore - 560043",
    preferredLanguage: "English, Hindi",
    emergencyContact: "+91 91111 11113 (Amit Varma)",
    pickupAuth: "Authorized for self & Pooja Varma",
    notificationStatus: "ACTIVE",
    smsEnabled: true,
    pushEnabled: true,
    emailAlerts: true,
    etaAlertSetting: "5 mins before arrival"
  },
  "Kiran Rao": {
    avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=200",
    parentId: "PRT-007",
    name: "Kiran Rao",
    occupation: "Senior Corporate Attorney",
    relationship: "Father",
    spouseName: "Sonia Rao (Executive NGO Director)",
    phone: "+91 92222 22224",
    altPhone: "+91 92222 22224",
    email: "kiran.rao@raoassociates.in",
    address: "Plot 302, HRBR Layout 1st Block, Bangalore - 560043",
    preferredLanguage: "English, Kannada, Hindi",
    emergencyContact: "+91 92222 22224 (Sonia Rao)",
    pickupAuth: "Authorized for self & spouse",
    notificationStatus: "ACTIVE",
    smsEnabled: true,
    pushEnabled: true,
    emailAlerts: true,
    etaAlertSetting: "5 mins before arrival"
  },
  "Deepa Nair": {
    avatar: "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&q=80&w=200",
    parentId: "PRT-008",
    name: "Deepa Nair",
    occupation: "Senior Branch Manager (State Bank)",
    relationship: "Mother",
    spouseName: "Prof. Unnikrishnan Nair (Dean of Sciences)",
    phone: "+91 93333 33335",
    altPhone: "+91 93333 33335",
    email: "deepa.nair@sbi.co.in",
    address: "No. 18, Coconut Grove Lane, Banaswadi, Bangalore - 560043",
    preferredLanguage: "English, Malayalam, Hindi",
    emergencyContact: "+91 93333 33335 (Deepa Nair)",
    pickupAuth: "Authorized for self & Prof. Unnikrishnan Nair",
    notificationStatus: "ACTIVE",
    smsEnabled: true,
    pushEnabled: true,
    emailAlerts: true,
    etaAlertSetting: "5 mins before arrival"
  }
};

const getParentAvatar = (parent) => {
  if (!parent) return 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&q=80&w=150';
  if (parent.avatar && (parent.avatar.startsWith('http://') || parent.avatar.startsWith('https://') || parent.avatar.startsWith('/'))) {
    return parent.avatar;
  }
  const name = (parent.display_name || parent.name || '').trim();
  const matched = parentProfilesData[name] ||
                  Object.entries(parentProfilesData).find(([k]) => k.toLowerCase() === name.toLowerCase())?.[1];
  if (matched && matched.avatar) return matched.avatar;
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'Parent')}&background=E0E7FF&color=4338CA&bold=true`;
};

const getDriverAvatar = (driver) => {
  if (!driver) return 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&q=80&w=150';
  if (driver.avatar && (driver.avatar.startsWith('http://') || driver.avatar.startsWith('https://') || driver.avatar.startsWith('/'))) {
    return driver.avatar;
  }
  const name = (driver.display_name || driver.name || '').trim();
  const matched = driverProfilesData[name] ||
                  Object.entries(driverProfilesData).find(([k]) => k.toLowerCase() === name.toLowerCase())?.[1];
  if (matched && matched.avatar) return matched.avatar;
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'Driver')}&background=0F172A&color=FFFFFF&bold=true`;
};

const busProfilesData = {
  "Bus 10A": {
    busNumber: "Bus 10A",
    registration: "KA-03-AA-1234",
    makeModel: "Tata Starbus Ultra 40L",
    modelYear: "2023",
    capacity: 40,
    fuelType: "Diesel (BS-VI)",
    engineNo: "TAT-497-TCIC-9021",
    chassisNo: "MAT-426-031-NA-88421",
    status: "active",
    odometer: "28,450 km",
    fuelMileage: "5.8 km/L",
    assignedRoute: "Route 1 - Kalyan Nagar Express",
    routeDirection: "Morning Pickup & Afternoon Drop",
    assignedDriver: "Ramesh Kumar",
    driverPhone: "+91 9777777771",
    driverId: "DVR-004",
    gpsImei: "862291040331091",
    gpsDeviceModel: "Teltonika FMB920 Dual SIM",
    gpsSimNumber: "+91 98450 11223 (Airtel IoT)",
    gpsStatus: "Online (Signal: Strong 4G)",
    speedGovernor: "Active (Speed Ceiling: 40 km/h)",
    cctvSystem: "4-Channel HD DVR (Cabin, Entry, Front, Rear)",
    safetyFeatures: ["Emergency Exit Window", "SOS Panic Push Button", "Fire Extinguisher (5kg ABC)", "Certified First Aid Kit"],
    insuranceProvider: "National Insurance Co. Ltd.",
    insurancePolicyNo: "NIC-SCH-884920-2026",
    insuranceExpiry: "Nov 14, 2027",
    fitnessExpiry: "Sep 20, 2027",
    pucExpiry: "Oct 15, 2027",
    roadPermitExpiry: "Dec 31, 2028",
    roadTaxStatus: "Lifetime Paid (Karnataka RTO)",
    lastServiceDate: "Aug 10, 2026",
    nextServiceDue: "Nov 10, 2026 (or at 35,000 km)",
    serviceHistory: [
      { date: "Aug 10, 2026", odometer: "28,000 km", type: "Periodic Service", notes: "Engine oil change, air filter replacement, brake pads inspection." },
      { date: "May 04, 2026", odometer: "22,500 km", type: "Safety Inspection", notes: "Speed governor recalibration and CCTV hard drive diagnostic." },
      { date: "Jan 18, 2026", odometer: "16,000 km", type: "Full Overhaul", notes: "Suspension bushes and tire rotation completed." }
    ]
  },
  "Bus 12B": {
    busNumber: "Bus 12B",
    registration: "KA-03-BB-5678",
    makeModel: "Ashok Leyland Sunshine 35",
    modelYear: "2022",
    capacity: 35,
    fuelType: "Diesel (BS-VI)",
    engineNo: "AL-H6ETIC-4402",
    chassisNo: "MAL-312-045-PA-19203",
    status: "active",
    odometer: "34,120 km",
    fuelMileage: "6.2 km/L",
    assignedRoute: "Route 2 - Banaswadi Metro Feeder",
    routeDirection: "Morning Pickup & Afternoon Drop",
    assignedDriver: "Satish Singh",
    driverPhone: "+91 9666666662",
    driverId: "DVR-005",
    gpsImei: "862291040332092",
    gpsDeviceModel: "Teltonika FMB920 Dual SIM",
    gpsSimNumber: "+91 98450 22334 (Jio IoT)",
    gpsStatus: "Online (Signal: Excellent 4G)",
    speedGovernor: "Active (Speed Ceiling: 40 km/h)",
    cctvSystem: "4-Channel HD DVR (Cabin, Entry, Front, Rear)",
    safetyFeatures: ["Emergency Exit Door", "SOS Panic Push Button", "Fire Extinguisher (5kg ABC)", "First Aid Kit"],
    insuranceProvider: "ICICI Lombard General Insurance",
    insurancePolicyNo: "ICICI-BUS-990142-2026",
    insuranceExpiry: "May 20, 2027",
    fitnessExpiry: "Apr 12, 2027",
    pucExpiry: "Jun 18, 2027",
    roadPermitExpiry: "Jul 15, 2028",
    roadTaxStatus: "Lifetime Paid (Karnataka RTO)",
    lastServiceDate: "Jul 22, 2026",
    nextServiceDue: "Oct 22, 2026 (or at 40,000 km)",
    serviceHistory: [
      { date: "Jul 22, 2026", odometer: "33,500 km", type: "Periodic Service", notes: "Engine oil change, coolant flush, AC filter replacement." },
      { date: "Mar 15, 2026", odometer: "27,000 km", type: "Brake Overhaul", notes: "Front and rear brake liners replaced." }
    ]
  },
  "Bus 14C": {
    busNumber: "Bus 14C",
    registration: "KA-03-CC-9999",
    makeModel: "BharatBenz School Bus 42S",
    modelYear: "2024",
    capacity: 42,
    fuelType: "Diesel (BS-VI)",
    engineNo: "BB-4D34i-8812",
    chassisNo: "MBB-914-082-QA-77301",
    status: "active",
    odometer: "12,800 km",
    fuelMileage: "5.5 km/L",
    assignedRoute: "Route 3 - HRBR Layout Ring Route",
    routeDirection: "Morning Pickup & Afternoon Drop",
    assignedDriver: "Vijay Singh",
    driverPhone: "+91 9444444443",
    driverId: "DVR-009",
    gpsImei: "862291040333093",
    gpsDeviceModel: "Teltonika FMB920 Dual SIM",
    gpsSimNumber: "+91 98450 33445 (Airtel IoT)",
    gpsStatus: "Online (Signal: Strong 4G)",
    speedGovernor: "Active (Speed Ceiling: 40 km/h)",
    cctvSystem: "4-Channel HD DVR + AI Driver Drowsiness Sensor",
    safetyFeatures: ["Emergency Exit Window", "SOS Panic Push Button", "Fire Extinguisher (5kg ABC)", "Certified First Aid Kit"],
    insuranceProvider: "HDFC ERGO General Insurance",
    insurancePolicyNo: "HDFC-FLEET-339012-2026",
    insuranceExpiry: "Jan 10, 2028",
    fitnessExpiry: "Feb 15, 2028",
    pucExpiry: "Mar 30, 2028",
    roadPermitExpiry: "Jan 20, 2029",
    roadTaxStatus: "Lifetime Paid (Karnataka RTO)",
    lastServiceDate: "Aug 02, 2026",
    nextServiceDue: "Nov 02, 2026 (or at 20,000 km)",
    serviceHistory: [
      { date: "Aug 02, 2026", odometer: "12,000 km", type: "10k Periodic Check", notes: "10,000 km routine factory inspection and fluid top-up." }
    ]
  },
  "Bus 16D": {
    busNumber: "Bus 16D",
    registration: "KA-03-DD-8888",
    makeModel: "Force Traveller Monobus 38",
    modelYear: "2023",
    capacity: 38,
    fuelType: "CNG Eco-Drive",
    engineNo: "FM-2.6CR-7721",
    chassisNo: "MFT-518-099-RA-55210",
    status: "active",
    odometer: "21,950 km",
    fuelMileage: "8.2 km/kg (CNG)",
    assignedRoute: "Route 4 - Kammanahalli Shuttle",
    routeDirection: "Morning Pickup & Afternoon Drop",
    assignedDriver: "Anil Murthy",
    driverPhone: "+91 9555555554",
    driverId: "DVR-010",
    gpsImei: "862291040334094",
    gpsDeviceModel: "Teltonika FMB920 Dual SIM",
    gpsSimNumber: "+91 98450 44556 (Vodafone IoT)",
    gpsStatus: "Online (Signal: Good 4G)",
    speedGovernor: "Active (Speed Ceiling: 40 km/h)",
    cctvSystem: "4-Channel HD DVR (Cabin, Entry, Front, Rear)",
    safetyFeatures: ["CNG Auto Gas Leak Detector", "Emergency Exit Roof Hatch", "SOS Panic Button", "Fire Extinguisher", "First Aid Kit"],
    insuranceProvider: "The Oriental Insurance Co.",
    insurancePolicyNo: "OIC-SCH-119842-2026",
    insuranceExpiry: "Aug 28, 2027",
    fitnessExpiry: "Jul 10, 2027",
    pucExpiry: "Sep 05, 2027",
    roadPermitExpiry: "Aug 15, 2028",
    roadTaxStatus: "Lifetime Paid (Karnataka RTO)",
    lastServiceDate: "Jun 14, 2026",
    nextServiceDue: "Sep 14, 2026 (or at 28,000 km)",
    serviceHistory: [
      { date: "Jun 14, 2026", odometer: "21,000 km", type: "CNG System Testing", notes: "CNG cylinder hydrostatic inspection and leak test passed." },
      { date: "Feb 10, 2026", odometer: "15,000 km", type: "General Service", notes: "Oil change and spark plugs replacement." }
    ]
  },
  "Bus 18E": {
    busNumber: "Bus 18E",
    registration: "KA-03-EE-7777",
    makeModel: "Eicher Starline 45",
    modelYear: "2024",
    capacity: 45,
    fuelType: "Diesel (BS-VI)",
    engineNo: "E494-4CYL-3310",
    chassisNo: "MEI-664-112-SA-99014",
    status: "active",
    odometer: "9,640 km",
    fuelMileage: "5.2 km/L",
    assignedRoute: "Route 5 - Outer Ring Road Connector",
    routeDirection: "Morning Pickup & Afternoon Drop",
    assignedDriver: "Manjunath Gowda",
    driverPhone: "+91 9666666665",
    driverId: "DVR-011",
    gpsImei: "862291040335095",
    gpsDeviceModel: "Teltonika FMB920 Dual SIM",
    gpsSimNumber: "+91 98450 55667 (Jio IoT)",
    gpsStatus: "Online (Signal: Strong 4G)",
    speedGovernor: "Active (Speed Ceiling: 40 km/h)",
    cctvSystem: "4-Channel HD DVR with 1TB Storage",
    safetyFeatures: ["Emergency Exit Rear Door", "SOS Panic Push Button", "Dual Fire Extinguishers (2x 5kg)", "First Aid Kit"],
    insuranceProvider: "Tata AIG General Insurance",
    insurancePolicyNo: "TATA-BUS-550921-2026",
    insuranceExpiry: "Mar 19, 2028",
    fitnessExpiry: "Apr 01, 2028",
    pucExpiry: "May 10, 2028",
    roadPermitExpiry: "Mar 15, 2029",
    roadTaxStatus: "Lifetime Paid (Karnataka RTO)",
    lastServiceDate: "Jul 05, 2026",
    nextServiceDue: "Oct 05, 2026 (or at 15,000 km)",
    serviceHistory: [
      { date: "Jul 05, 2026", odometer: "8,500 km", type: "First Inspection", notes: "Initial post-delivery break-in inspection and software update." }
    ]
  }
};

const WidgetEditModal = ({ visibleWidgets, widgetOrder, onClose, onSave }) => {
  const [order, setOrder] = React.useState([...widgetOrder]);
  const [visible, setVisible] = React.useState({ ...visibleWidgets });

  const WIDGET_LABELS = {
    tripsBreakdown: 'Trips Breakdown',
    averageDelay: 'Average Delay',
    fleetActive: 'Fleet Active',
    activeStudents: 'Active Students',
    systemAlerts: 'System Alerts',
    onTimeRating: 'On-Time Rating',
    weeklyPerformance: 'Weekly Performance',
    recentLogs: 'Recent System Logs',
    busHealth: 'Fleet Maintenance'
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('index', index);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
  };
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('index'), 10);
    if (dragIndex === dropIndex) return;
    const newOrder = [...order];
    const [draggedItem] = newOrder.splice(dragIndex, 1);
    newOrder.splice(dropIndex, 0, draggedItem);
    setOrder(newOrder);
  };

  const moveUp = (index) => {
    if (index === 0) return;
    const newOrder = [...order];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    setOrder(newOrder);
  };

  const moveDown = (index) => {
    if (index === order.length - 1) return;
    const newOrder = [...order];
    [newOrder[index + 1], newOrder[index]] = [newOrder[index], newOrder[index + 1]];
    setOrder(newOrder);
  };

  const toggleVisibility = (id) => {
    setVisible(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="modal-container" style={{ backgroundColor: 'var(--surface-color, white)', borderRadius: '16px', padding: '16px', width: '420px', maxWidth: '90%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 12px 36px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid var(--border-color, #E2E8F0)', paddingBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '8px', backgroundColor: 'var(--bg-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-primary)' }}>
              <LayoutGrid size={18} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: 'var(--text-primary)' }}>Customize Widgets</h3>
              <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-secondary)' }}>Select and drag to reorder</p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: 'var(--text-secondary)', padding: '0 4px' }}>&times;</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '420px', overflowY: 'auto', paddingRight: '4px' }}>
          {order.map((id, index) => (
            <div 
              key={id} 
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                background: 'var(--surface-color, white)', 
                border: '1px solid var(--border-color, #e2e8f0)',
                borderRadius: '8px', 
                padding: '8px 12px', 
                gap: '12px', 
                cursor: 'grab',
                transition: 'background 0.2s'
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--surface-color, white)'}
            >
              {/* Drag Handle */}
              <div style={{ display: 'flex', gap: '2px', color: 'var(--text-secondary)', opacity: 0.4 }}>
                 <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                   <div style={{ width: '3px', height: '3px', borderRadius: '50%', background: 'currentColor' }}/>
                   <div style={{ width: '3px', height: '3px', borderRadius: '50%', background: 'currentColor' }}/>
                   <div style={{ width: '3px', height: '3px', borderRadius: '50%', background: 'currentColor' }}/>
                 </div>
                 <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                   <div style={{ width: '3px', height: '3px', borderRadius: '50%', background: 'currentColor' }}/>
                   <div style={{ width: '3px', height: '3px', borderRadius: '50%', background: 'currentColor' }}/>
                   <div style={{ width: '3px', height: '3px', borderRadius: '50%', background: 'currentColor' }}/>
                 </div>
              </div>

              {/* Checkbox */}
              <input 
                type="checkbox"
                checked={!!visible[id]}
                onChange={() => toggleVisibility(id)}
                style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#10B981' }}
              />

              <span style={{ flex: 1, fontSize: '13px', fontWeight: '500', color: 'var(--text-primary)' }}>{WIDGET_LABELS[id]}</span>
              
              {/* Up/Down buttons */}
              <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--border-color, #e2e8f0)', borderRadius: '6px', overflow: 'hidden', backgroundColor: 'var(--bg-alt)' }}>
                <button 
                  onClick={() => moveUp(index)} 
                  disabled={index === 0}
                  style={{ 
                    padding: '4px 6px', background: 'transparent', border: 'none', 
                    cursor: index === 0 ? 'not-allowed' : 'pointer', 
                    opacity: index === 0 ? 0.3 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRight: '1px solid var(--border-color, #e2e8f0)',
                    color: 'var(--text-secondary)'
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5"/><path d="m5 12 7-7 7 7"/></svg>
                </button>
                <button 
                  onClick={() => moveDown(index)} 
                  disabled={index === order.length - 1}
                  style={{ 
                    padding: '4px 6px', background: 'transparent', border: 'none', 
                    cursor: index === order.length - 1 ? 'not-allowed' : 'pointer', 
                    opacity: index === order.length - 1 ? 0.3 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'var(--text-secondary)'
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14"/><path d="m19 12-7 7-7-7"/></svg>
                </button>
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color, #e2e8f0)' }}>
          <button style={{ padding: '7px 12px', fontSize: '12px', fontWeight: '600', backgroundColor: 'var(--bg-hover)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '8px', cursor: 'pointer' }} onClick={onClose} className="btn-secondary">Cancel</button>
          <button style={{ padding: '7px 14px', fontSize: '12px', fontWeight: '700', backgroundColor: '#10B981', color: '#FFFFFF', border: 'none', borderRadius: '8px', cursor: 'pointer' }} onClick={() => onSave(order, visible)}>Save Changes</button>
        </div>
      </div>
    </div>
  );
};
export default function App() {
  const [gridWidth, setGridWidth] = useState(1200);
  const [gridMounted, setGridMounted] = useState(false);
  const observerRef = useRef(null);
  const gridContainerRef = React.useCallback(node => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    if (node !== null) {
      observerRef.current = new ResizeObserver(entries => {
        const newWidth = entries[0].contentRect.width;
        if (newWidth > 0) {
          setGridWidth(newWidth);
          setGridMounted(true);
        }
      });
      observerRef.current.observe(node);
    }
  }, []);
  
  const [user, setUser] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [showServerSettings, setShowServerSettings] = useState(false);
  const [serverUrlInput, setServerUrlInput] = useState(() => localStorage.getItem('busbuddy_backend_url') || import.meta.env.VITE_BACKEND_URL || window.location.origin);

  const handleSaveServerUrl = () => {
    if (serverUrlInput.trim()) {
      localStorage.setItem('busbuddy_backend_url', serverUrlInput.trim());
    } else {
      localStorage.removeItem('busbuddy_backend_url');
    }
    alert('Server Connection URL saved! Reloading...');
    window.location.reload();
  };

  
  
  const [activeTab, setActiveTab] = useState('monitor'); // 'monitor', 'routes', 'fleet', 'users'
  const [visibleWidgets, setVisibleWidgets] = useState(() => {
    try {
      const saved = localStorage.getItem('adminDashboardWidgets');
      return saved ? JSON.parse(saved) : { tripsBreakdown: true, averageDelay: true, fleetActive: true, onTimeRating: true, weeklyPerformance: true, activeStudents: true, systemAlerts: true };
    } catch {
      return { tripsBreakdown: true, averageDelay: true, fleetActive: true, onTimeRating: true, weeklyPerformance: true, activeStudents: true, systemAlerts: true };
    }
  });
  
  const defaultLayout = [
    { i: 'tripsBreakdown', x: 0, y: 0, w: 4, h: 2, minW: 2, minH: 2 },
    { i: 'averageDelay', x: 4, y: 0, w: 4, h: 2, minW: 2, minH: 2 },
    { i: 'fleetActive', x: 8, y: 0, w: 4, h: 2, minW: 2, minH: 2 },
    { i: 'activeStudents', x: 0, y: 2, w: 4, h: 2, minW: 2, minH: 2 },
    { i: 'systemAlerts', x: 4, y: 2, w: 4, h: 2, minW: 2, minH: 2 },
    { i: 'onTimeRating', x: 8, y: 2, w: 4, h: 2, minW: 2, minH: 2 },
    { i: 'recentLogs', x: 0, y: 4, w: 6, h: 3, minW: 3, minH: 3 },
    { i: 'busHealth', x: 6, y: 4, w: 6, h: 3, minW: 3, minH: 2 },
    { i: 'weeklyPerformance', x: 0, y: 7, w: 12, h: 4, minW: 4, minH: 3 }
  ];
  const [dashboardLayout, setDashboardLayout] = useState(() => {
    const saved = localStorage.getItem('dashboardLayout');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const weeklyItem = parsed.find(i => i.i === 'weeklyPerformance');
        if (weeklyItem && weeklyItem.w < 8) {
          return defaultLayout;
        }
        // Merge missing widgets
        const merged = [...parsed];
        defaultLayout.forEach(defItem => {
          if (!merged.find(i => i.i === defItem.i)) merged.push(defItem);
        });
        return merged.map(item => ({ ...item, maxW: 12 }));
      } catch (e) {}
    }
    return defaultLayout;
  });

  const [widgetOrder, setWidgetOrder] = useState(() => {
    try {
      const saved = localStorage.getItem('adminDashboardWidgetOrder');
      return saved ? JSON.parse(saved) : ['tripsBreakdown', 'averageDelay', 'fleetActive', 'activeStudents', 'systemAlerts', 'onTimeRating', 'weeklyPerformance'];
    } catch {
      return ['tripsBreakdown', 'averageDelay', 'fleetActive', 'activeStudents', 'systemAlerts', 'onTimeRating', 'weeklyPerformance'];
    }
  });
  const [showWidgetEditModal, setShowWidgetEditModal] = useState(false);

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [trips, setTrips] = useState([]);
  const [allTrips, setAllTrips] = useState([]);
  const [deviceStats, setDeviceStats] = useState({ active_devices: 0, total_devices: 0 });
  const [filterRouteId, setFilterRouteId] = useState('');
  const [filterBusId, setFilterBusId] = useState('');
  const [filterDriverId, setFilterDriverId] = useState('');
  const [filterDate, setFilterDate] = useState(() => { const d = new Date(); return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); });
  const [filterStatus, setFilterStatus] = useState('SCHEDULED');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const selectedTripRef = useRef(null);
  const [selectedTripStops, setSelectedTripStops] = useState([]);
  const [selectedTripRoster, setSelectedTripRoster] = useState([]);

  // Logs Dashboard States
  const [logType, setLogType] = useState('audit'); // 'audit' | 'system'
  const [logSearch, setLogSearch] = useState('');
  const [logLevelFilter, setLogLevelFilter] = useState(''); // for system: '', '30', '40', '50'
  const [auditActionFilter, setAuditActionFilter] = useState(''); // for audit: '', 'USER_LOGIN', 'FAILED_LOGIN_ATTEMPT'
  const [logStartDate, setLogStartDate] = useState('');
  const [logEndDate, setLogEndDate] = useState('');
  const [activeLogTooltip, setActiveLogTooltip] = useState(null); // { x, y, data }
  const [logsList, setLogsList] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const fetchLogsData = async (
    type = logType, 
    search = logSearch, 
    level = logLevelFilter, 
    action = auditActionFilter,
    startDate = logStartDate,
    endDate = logEndDate
  ) => {
    setLoadingLogs(true);
    try {
      let url = '';
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      if (type === 'audit') {
        if (action) params.append('action', action);
        url = `${BACKEND_URL}/api/logs/audit?${params.toString()}`;
      } else {
        if (level) params.append('level', level);
        url = `${BACKEND_URL}/api/logs/system?${params.toString()}`;
      }
      const res = await fetch(url);
      const data = await res.json();
      setLogsList(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching logs:', err);
      setLogsList([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  const exportLogsToCSV = () => {
    if (logsList.length === 0) return;
    
    let csvContent = '';
    if (logType === 'audit') {
      // Headers
      csvContent += 'ID,Timestamp,Username,DisplayName,UserID,Action,Details,IPAddress\n';
      // Rows
      logsList.forEach(log => {
        const id = log.id || '';
        const ts = log.timestamp ? new Date(log.timestamp).toLocaleString() : '';
        const username = log.username || '';
        const displayName = log.display_name || '';
        const userId = log.user_id || '';
        const action = log.action || '';
        // Escape double quotes in details
        const details = log.details ? `"${log.details.replace(/"/g, '""')}"` : '';
        const ip = log.ip_address || '';
        
        csvContent += `${id},"${ts}","${username}","${displayName}","${userId}","${action}",${details},"${ip}"\n`;
      });
    } else {
      // Headers
      csvContent += 'Level,Timestamp,CorrelationID,Message,Meta\n';
      // Rows
      logsList.forEach(log => {
        const level = log.level || '';
        const ts = log.time ? new Date(log.time).toLocaleString() : '';
        const corrId = log.correlationId || '';
        const msg = log.msg ? `"${log.msg.replace(/"/g, '""')}"` : '';
        const { level: l, time: t, correlationId: c, pid, hostname, msg: m, ...meta } = log;
        const metaStr = Object.keys(meta).length > 0 ? `"${JSON.stringify(meta).replace(/"/g, '""')}"` : '';
        
        csvContent += `${level},"${ts}","${corrId}",${msg},${metaStr}\n`;
      });
    }
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${logType}_logs_${new Date().toISOString().slice(0,10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderJsonAsTooltipTable = (jsonVal) => {
    let obj = null;
    if (typeof jsonVal === 'object' && jsonVal !== null) {
      obj = jsonVal;
    } else if (typeof jsonVal === 'string') {
      try {
        obj = JSON.parse(jsonVal);
      } catch (e) {
        // Not JSON
      }
    }

    if (!obj || typeof obj !== 'object' || Object.keys(obj).length === 0) {
      return (
        <div style={{ color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: '11px', padding: '4px' }}>
          {typeof jsonVal === 'string' ? jsonVal : 'No details payload'}
        </div>
      );
    }

    return (
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', textAlign: 'left' }}>
        <thead>
          <tr style={{ background: '#F8FAFC' }}>
            <th style={{ padding: '4px 8px', fontWeight: 'bold', color: '#475569', borderBottom: '1px solid var(--border-color)' }}>Key</th>
            <th style={{ padding: '4px 8px', fontWeight: 'bold', color: '#475569', borderBottom: '1px solid var(--border-color)' }}>Value</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(obj).map(([key, val]) => {
            let valDisplay = '';
            if (typeof val === 'object' && val !== null) {
              valDisplay = JSON.stringify(val);
            } else {
              valDisplay = String(val);
            }
            return (
              <tr key={key} style={{ borderBottom: '1px solid #F1F5F9' }}>
                <td style={{ padding: '4px 8px', fontWeight: '600', color: '#475569', verticalAlign: 'top', wordBreak: 'break-all' }}>{key}</td>
                <td style={{ padding: '4px 8px', color: 'var(--text-primary)', verticalAlign: 'top', wordBreak: 'break-all' }}>{valDisplay}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  // Live real-time states
  const [busPositions, setBusPositions] = useState({}); // { [tripId]: { lat, lng, speed, bearing } }
  const [sosAlert, setSosAlert] = useState(null);

  // Static Data Management Lists
  const [allRoutes, setAllRoutes] = useState([]);
  const [allBuses, setAllBuses] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [selectedStudentProfile, setSelectedStudentProfile] = useState(null);
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [columnMapping, setColumnMapping] = useState({
    first_name: '',
    last_name: '',
    parent_name: '',
    parent_phone: '',
    rfid_tag_id: '',
    pickup_stop_name: '',
    drop_stop_name: ''
  });
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Top Header and Context Menu states
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [themeMode, setThemeMode] = useState(() => localStorage.getItem('themeMode') || 'light'); // 'light' | 'system' | 'dark'

  useEffect(() => {
    localStorage.setItem('themeMode', themeMode);
    const applyTheme = () => {
      let activeTheme = themeMode;
      if (themeMode === 'system') {
        activeTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      }
      if (activeTheme === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    };
    applyTheme();
    if (themeMode === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const listener = () => applyTheme();
      mediaQuery.addEventListener('change', listener);
      return () => mediaQuery.removeEventListener('change', listener);
    }
  }, [themeMode]);
  const [headerSearchQuery, setHeaderSearchQuery] = useState('');
  const [showNotificationsDropdown, setShowNotificationsDropdown] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPasswordInput, setCurrentPasswordInput] = useState('');
  const [newPasswordInput, setNewPasswordInput] = useState('');
  const [confirmPasswordInput, setConfirmPasswordInput] = useState('');
  const [passwordStatusMsg, setPasswordStatusMsg] = useState(null);
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [guideActiveSection, setGuideActiveSection] = useState('monitor');
  const [showHelpCenterModal, setShowHelpCenterModal] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);

  const userMenuRef = useRef(null);
  const notifMenuRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const menuSearchItems = [
    { id: 'monitor', title: 'Live Monitor', category: 'Operations', icon: Navigation, desc: 'Real-time GPS bus tracking, route telemetry & live map' },
    { id: 'trips', title: "Today's Trips", category: 'Operations', icon: Clock, desc: 'Scheduled, active, and completed route trips' },
    { id: 'reports', title: 'Reports & Alerts', category: 'Operations', icon: AlertTriangle, desc: 'SOS emergency alerts, speed warnings, delay logs' },
    { id: 'students', title: 'Students Directory', category: 'Directory', icon: Users, desc: 'Student transit profiles, RFID cards, guardian contacts' },
    { id: 'parents', title: 'Parents & Guardians', category: 'Directory', icon: User, desc: 'Parent contact directory, linked wards, phone numbers' },
    { id: 'drivers', title: 'Bus Drivers', category: 'Directory', icon: SteeringWheel, desc: 'Driver profiles, licenses, phone contacts, status' },
    { id: 'buses', title: 'School Buses & Fleet', category: 'Directory', icon: Bus, desc: 'Vehicle numbers, registration, passenger capacity' },
    { id: 'documents', title: 'Documents Vault', category: 'Directory', icon: FileText, desc: 'Vehicle RC, fitness, insurance, and driver licenses' },
    { id: 'announcements', title: 'Announcements', category: 'Communications', icon: MessageSquare, desc: 'Broadcast notices, emergency SMS alerts' },
    { id: 'tickets', title: 'Support Desk', category: 'Communications', icon: HelpCircle, desc: 'Technical helpdesk, driver inquiries, ticketing' },
    { id: 'general_settings', title: 'General Settings', category: 'Setup & Config', icon: Settings, desc: 'System configuration, school timing, operational limits' },
    { id: 'connections', title: 'Service Connections', category: 'Setup & Config', icon: Server, desc: 'Cloud storage (S3/GCS), database backup settings' },
    { id: 'access_control', title: 'Access Control', category: 'Setup & Config', icon: Key, desc: 'User permissions, administrative role management' },
    { id: 'database_ops', title: 'Database Cleanup', category: 'Setup & Config', icon: Database, desc: 'SQLite table vacuuming, reindexing, database maintenance' },
    { id: 'system_logs', title: 'Audit & System Logs', category: 'Setup & Config', icon: FileText, desc: 'Structured Pino logs, correlation tracing, audit trails' },
    { id: 'changelog', title: 'Changelog & History', category: 'Setup & Config', icon: History, desc: 'Platform release notes, update history, roadmap' },
    { id: 'action_about', title: 'About Bus Buddy', category: 'Quick Action', icon: Info, action: () => setShowAboutModal(true), desc: 'Platform version, build details, runtime architecture' },
    { id: 'action_password', title: 'Change Password', category: 'Quick Action', icon: Lock, action: () => setShowPasswordModal(true), desc: 'Update account password and security credentials' },
    { id: 'action_language', title: 'Language Preferences', category: 'Quick Action', icon: Globe, action: () => setShowLanguageModal(true), desc: 'Switch interface language (English, Spanish, Hindi, etc.)' },
    { id: 'action_guide', title: 'User Walkthrough Guide', category: 'Quick Action', icon: BookOpen, action: () => setShowGuideModal(true), desc: 'Platform user manual and quick interactive tour' }
  ];

  const searchResults = React.useMemo(() => {
    const q = headerSearchQuery.trim().toLowerCase();
    if (!q) return { menus: [], students: [], parents: [], drivers: [], buses: [], routes: [], trips: [] };

    const menus = menuSearchItems.filter(item => 
      item.title.toLowerCase().includes(q) || 
      item.category.toLowerCase().includes(q) || 
      item.desc.toLowerCase().includes(q)
    );

    const students = allStudents.filter(s => 
      `${s.first_name || ''} ${s.last_name || ''}`.toLowerCase().includes(q) ||
      (s.roll_no && String(s.roll_no).toLowerCase().includes(q)) ||
      (s.rfid_tag_id && String(s.rfid_tag_id).toLowerCase().includes(q)) ||
      (s.pickup_stop_name && s.pickup_stop_name.toLowerCase().includes(q)) ||
      (s.drop_stop_name && s.drop_stop_name.toLowerCase().includes(q)) ||
      (s.grade && s.grade.toLowerCase().includes(q))
    ).slice(0, 5);

    const parents = allUsers.filter(u => (u.role === 'parent' || u.role === 'PARENT') && (
      (u.display_name && u.display_name.toLowerCase().includes(q)) ||
      (u.username && u.username.toLowerCase().includes(q)) ||
      (u.phone && u.phone.toLowerCase().includes(q)) ||
      allStudents.some(s => s.parent_id === u.id && `${s.first_name || ''} ${s.last_name || ''}`.toLowerCase().includes(q))
    )).slice(0, 5);

    const drivers = allUsers.filter(u => (u.role === 'DRIVER' || u.role === 'driver') && (
      (u.display_name && u.display_name.toLowerCase().includes(q)) ||
      (u.username && u.username.toLowerCase().includes(q)) ||
      (u.phone && u.phone.toLowerCase().includes(q))
    )).slice(0, 5);

    const buses = allBuses.filter(b => 
      (b.bus_number && String(b.bus_number).toLowerCase().includes(q)) ||
      (b.registration_no && b.registration_no.toLowerCase().includes(q)) ||
      (b.model && b.model.toLowerCase().includes(q))
    ).slice(0, 5);

    const routes = allRoutes.filter(r => 
      (r.name && r.name.toLowerCase().includes(q)) ||
      (r.description && r.description.toLowerCase().includes(q))
    ).slice(0, 4);

    const matchingTrips = (trips || []).filter(t => 
      (t.route_name && t.route_name.toLowerCase().includes(q)) ||
      (t.bus_number && String(t.bus_number).toLowerCase().includes(q)) ||
      (t.driver_name && t.driver_name.toLowerCase().includes(q))
    ).slice(0, 4);

    return { menus, students, parents, drivers, buses, routes, trips: matchingTrips };
  }, [headerSearchQuery, allStudents, allUsers, allBuses, allRoutes, trips]);

  const totalSearchCount = 
    searchResults.menus.length + 
    searchResults.students.length + 
    searchResults.parents.length + 
    searchResults.drivers.length + 
    searchResults.buses.length + 
    searchResults.routes.length + 
    searchResults.trips.length;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
      if (notifMenuRef.current && !notifMenuRef.current.contains(event.target)) {
        setShowNotificationsDropdown(false);
      }
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handlePasswordUpdate = (e) => {
    e.preventDefault();
    if (!currentPasswordInput) {
      setPasswordStatusMsg({ type: 'error', text: 'Please enter your current password.' });
      return;
    }
    if (newPasswordInput.length < 6) {
      setPasswordStatusMsg({ type: 'error', text: 'New password must be at least 6 characters.' });
      return;
    }
    if (newPasswordInput !== confirmPasswordInput) {
      setPasswordStatusMsg({ type: 'error', text: 'New passwords do not match.' });
      return;
    }
    setPasswordStatusMsg({ type: 'success', text: 'Password successfully updated!' });
    setTimeout(() => {
      setShowPasswordModal(false);
      setCurrentPasswordInput('');
      setNewPasswordInput('');
      setConfirmPasswordInput('');
      setPasswordStatusMsg(null);
      showToast('Account password updated successfully');
    }, 1000);
  };

  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      
      const parseCSV = (str) => {
        const lines = [];
        let row = [];
        let entry = '';
        let inQuotes = false;
        for (let i = 0; i < str.length; i++) {
          const char = str[i];
          const nextChar = str[i + 1];
          if (char === '"') {
            if (inQuotes && nextChar === '"') {
              entry += '"';
              i++;
            } else {
              inQuotes = !inQuotes;
            }
          } else if (char === ',' && !inQuotes) {
            row.push(entry.trim());
            entry = '';
          } else if ((char === '\r' || char === '\n') && !inQuotes) {
            if (char === '\r' && nextChar === '\n') i++;
            row.push(entry.trim());
            if (row.length > 1 || row[0] !== '') {
              lines.push(row);
            }
            row = [];
            entry = '';
          } else {
            entry += char;
          }
        }
        if (entry !== '' || row.length > 0) {
          row.push(entry.trim());
          lines.push(row);
        }
        return lines;
      };

      const parsedLines = parseCSV(text);
      if (parsedLines.length < 2) {
        alert("The selected CSV file is empty or does not contain data lines.");
        return;
      }

      const headers = parsedLines[0];
      const dataRows = parsedLines.slice(1);

      setCsvHeaders(headers);
      setCsvRows(dataRows);

      const defaultMapping = {
        first_name: '',
        last_name: '',
        parent_name: '',
        parent_phone: '',
        rfid_tag_id: '',
        pickup_stop_name: '',
        drop_stop_name: ''
      };

      headers.forEach(h => {
        const lowerH = h.toLowerCase().replace(/[^a-z0-9]/g, '');
        if (lowerH.includes('firstname') || lowerH.includes('first')) defaultMapping.first_name = h;
        else if (lowerH.includes('lastname') || lowerH.includes('last')) defaultMapping.last_name = h;
        else if (lowerH.includes('parentname') || lowerH.includes('guardianname') || (lowerH.includes('parent') && !lowerH.includes('phone'))) defaultMapping.parent_name = h;
        else if (lowerH.includes('phone') || lowerH.includes('mobile') || lowerH.includes('contact')) defaultMapping.parent_phone = h;
        else if (lowerH.includes('rfid') || lowerH.includes('tag')) defaultMapping.rfid_tag_id = h;
        else if (lowerH.includes('pickup') || lowerH.includes('stop1') || lowerH.includes('fromstop')) defaultMapping.pickup_stop_name = h;
        else if (lowerH.includes('drop') || lowerH.includes('stop2') || lowerH.includes('tostop')) defaultMapping.drop_stop_name = h;
      });

      setColumnMapping(defaultMapping);
      setImportResult(null);
    };
    reader.readAsText(file);
  };

  const submitCSVImport = async () => {
    if (!columnMapping.first_name || !columnMapping.last_name) {
      alert("First Name and Last Name mappings are required.");
      return;
    }
    if (!columnMapping.parent_phone) {
      alert("Parent Phone mapping is required.");
      return;
    }

    setImporting(true);
    setImportResult(null);

    const studentsToImport = csvRows.map(row => {
      const getVal = (field) => {
        const header = columnMapping[field];
        if (!header) return '';
        const idx = csvHeaders.indexOf(header);
        return idx !== -1 ? row[idx] : '';
      };

      return {
        first_name: getVal('first_name'),
        last_name: getVal('last_name'),
        parent_name: getVal('parent_name'),
        parent_phone: getVal('parent_phone'),
        rfid_tag_id: getVal('rfid_tag_id'),
        pickup_stop_name: getVal('pickup_stop_name'),
        drop_stop_name: getVal('drop_stop_name')
      };
    });

    try {
      const res = await fetch(`${BACKEND_URL}/api/students/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ students: studentsToImport })
      });
      const data = await res.json();
      setImportResult(data);
      if (data.success || data.imported_count > 0) {
        fetchManagementData();
      }
    } catch (err) {
      setImportResult({ success: false, total_processed: csvRows.length, imported_count: 0, error_count: csvRows.length, errors: [{ row: 'All', error: 'Network error or backend is not running.' }] });
    } finally {
      setImporting(false);
    }
  };

  const [profileActiveTab, setProfileActiveTab] = useState('academic'); // 'academic', 'address', 'medical', 'achievements'
  const [tripsSubTab, setTripsSubTab] = useState('schedules'); // 'schedules' | 'assign' | 'roster'
  const [expandedTripId, setExpandedTripId] = useState(null);
  const [tripStudentsData, setTripStudentsData] = useState({});
  const [loadingTripStudents, setLoadingTripStudents] = useState({});
  const [rosterSelectedRouteId, setRosterSelectedRouteId] = useState('');
  const [rosterSearchQuery, setRosterSearchQuery] = useState('');
  const [rosterTransportFilter, setRosterTransportFilter] = useState('all'); // 'all' | 'bus' | 'no_bus'

  const toggleExpandTrip = async (tripId) => {
    if (expandedTripId === tripId) {
      setExpandedTripId(null);
      return;
    }
    setExpandedTripId(tripId);
    if (!tripStudentsData[tripId]) {
      setLoadingTripStudents(prev => ({ ...prev, [tripId]: true }));
      try {
        const res = await fetch(`${BACKEND_URL}/api/trips/${tripId}/students`);
        const data = await res.json();
        setTripStudentsData(prev => ({ ...prev, [tripId]: Array.isArray(data) ? data : [] }));
      } catch (err) {
        console.error('Error fetching students for trip:', err);
        setTripStudentsData(prev => ({ ...prev, [tripId]: [] }));
      } finally {
        setLoadingTripStudents(prev => ({ ...prev, [tripId]: false }));
      }
    }
  };

  const [reportsSubTab, setReportsSubTab] = useState('attendance'); // 'attendance' | 'schedule'
  const [docFilter, setDocFilter] = useState('all'); // 'all' | 'driver' | 'bus' | 'student'
  const [docViewMode, setDocViewMode] = useState('grid'); // 'grid' | 'detail'
  const [docSearchQuery, setDocSearchQuery] = useState('');
  const [docStatusFilter, setDocStatusFilter] = useState('all'); // 'all' | 'active' | 'expiring_soon' | 'expired'
  const [docFormatFilter, setDocFormatFilter] = useState('all'); // 'all' | 'pdf' | 'word' | 'excel' | 'image' | 'text'
  const [docSortBy, setDocSortBy] = useState('newest'); // 'newest' | 'oldest' | 'expiry_soon' | 'name_asc'
  const [docCurrentPage, setDocCurrentPage] = useState(1);
  const [docPageSize, setDocPageSize] = useState(10); // default page size 10

  // Students Directory Search, Filter, Sort & Pagination States
  const [studentViewMode, setStudentViewMode] = useState('detail'); // 'detail' | 'grid' (default 'detail')
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [studentCategoryFilter, setStudentCategoryFilter] = useState('all'); // 'all' | 'rfid_assigned' | 'no_rfid' | 'high_school' | 'middle_school'
  const [studentGradeFilter, setStudentGradeFilter] = useState('all');
  const [studentStopFilter, setStudentStopFilter] = useState('all');
  const [studentRfidFilter, setStudentRfidFilter] = useState('all'); // 'all' | 'assigned' | 'unassigned'
  const [studentSortBy, setStudentSortBy] = useState('name_asc'); // 'name_asc' | 'name_desc' | 'admission_asc' | 'admission_desc' | 'grade_asc' | 'stop_asc'
  const [studentCurrentPage, setStudentCurrentPage] = useState(1);
  const [studentPageSize, setStudentPageSize] = useState(10);

  // Parents Directory Search, Filter, Sort & Pagination States
  const [parentViewMode, setParentViewMode] = useState('detail'); // 'detail' | 'grid'
  const [parentSearchQuery, setParentSearchQuery] = useState('');
  const [parentCategoryFilter, setParentCategoryFilter] = useState('all'); // 'all' | 'multiple_wards' | 'single_ward' | 'active_alerts' | 'high_school' | 'middle_school'
  const [parentGradeFilter, setParentGradeFilter] = useState('all');
  const [parentStopFilter, setParentStopFilter] = useState('all');
  const [parentWardsFilter, setParentWardsFilter] = useState('all'); // 'all' | 'single' | 'multiple'
  const [parentSortBy, setParentSortBy] = useState('name_asc'); // 'name_asc' | 'name_desc' | 'id_asc' | 'id_desc' | 'wards_desc' | 'phone_asc'
  const [parentCurrentPage, setParentCurrentPage] = useState(1);
  const [parentPageSize, setParentPageSize] = useState(10);
  const [selectedParentProfile, setSelectedParentProfile] = useState(null);
  const [parentProfileActiveTab, setParentProfileActiveTab] = useState('wards'); // 'wards' | 'contact' | 'notifications' | 'activity'
  
  // Drivers Directory Search, Filter, Sort & Pagination States
  const [driverViewMode, setDriverViewMode] = useState('detail'); // 'detail' | 'grid'
  const [driverSearchQuery, setDriverSearchQuery] = useState('');
  const [driverCategoryFilter, setDriverCategoryFilter] = useState('all'); // 'all' | 'active' | 'valid_license' | 'assigned_bus' | 'standby'
  const [driverStatusFilter, setDriverStatusFilter] = useState('all'); // 'all' | 'ACTIVE' | 'ON LEAVE' | 'INACTIVE'
  const [driverLicenseFilter, setDriverLicenseFilter] = useState('all'); // 'all' | 'VALID LICENSE' | 'EXPIRED LICENSE' | 'SUSPENDED'
  const [driverBusFilter, setDriverBusFilter] = useState('all');
  const [driverSortBy, setDriverSortBy] = useState('name_asc'); // 'name_asc' | 'name_desc' | 'id_asc' | 'id_desc' | 'expiry_asc' | 'bus_asc' | 'status_asc'
  const [driverCurrentPage, setDriverCurrentPage] = useState(1);
  const [driverPageSize, setDriverPageSize] = useState(10);

  // School Buses Fleet Directory Search, Filter, Sort & Pagination States
  const [busViewMode, setBusViewMode] = useState('grid'); // 'grid' | 'detail'
  const [busSearchQuery, setBusSearchQuery] = useState('');
  const [busCategoryFilter, setBusCategoryFilter] = useState('all'); // 'all' | 'active' | 'maintenance' | 'large' | 'medium'
  const [busCapacityFilter, setBusCapacityFilter] = useState('all'); // 'all' | 'mini' | 'medium' | 'large'
  const [busStatusFilter, setBusStatusFilter] = useState('all'); // 'all' | 'active' | 'maintenance'
  const [busRouteFilter, setBusRouteFilter] = useState('all');
  const [busDriverFilter, setBusDriverFilter] = useState('all');
  const [busSortBy, setBusSortBy] = useState('number_asc'); // 'number_asc' | 'number_desc' | 'id_asc' | 'id_desc' | 'capacity_desc' | 'capacity_asc' | 'registration_asc'
  const [busCurrentPage, setBusCurrentPage] = useState(1);
  const [busPageSize, setBusPageSize] = useState(10);
  const [selectedBusProfile, setSelectedBusProfile] = useState(null);
  const [busProfileActiveTab, setBusProfileActiveTab] = useState('overview'); // 'overview' | 'assignment' | 'documents' | 'telemetry' | 'maintenance'
  const [showAddBusModal, setShowAddBusModal] = useState(false);
  const [newBusData, setNewBusData] = useState({
    bus_number: '',
    registration: '',
    capacity: 40,
    status: 'active',
    makeModel: '',
    fuelType: 'Diesel (BS-VI)',
    ownershipType: 'School Owned',
    roadTaxStatus: 'Active / Valid',
    assignedDriver: '',
    assignedRoute: ''
  });
  const [savingBus, setSavingBus] = useState(false);

  // Bus Profile Edit States
  const [isEditingBusProfile, setIsEditingBusProfile] = useState(false);
  const [editBusMakeModel, setEditBusMakeModel] = useState('');
  const [editBusModelYear, setEditBusModelYear] = useState('');
  const [editBusCapacity, setEditBusCapacity] = useState(40);
  const [editBusFuelType, setEditBusFuelType] = useState('Diesel (BS-VI)');
  const [editBusOwnershipType, setEditBusOwnershipType] = useState('School Owned');
  const [editBusStatus, setEditBusStatus] = useState('active');
  const [editBusEngineNo, setEditBusEngineNo] = useState('');
  const [editBusChassisNo, setEditBusChassisNo] = useState('');
  const [editBusRegistration, setEditBusRegistration] = useState('');
  const [editBusInsuranceProvider, setEditBusInsuranceProvider] = useState('');
  const [editBusInsurancePolicyNo, setEditBusInsurancePolicyNo] = useState('');
  const [editBusInsuranceExpiry, setEditBusInsuranceExpiry] = useState('');
  const [editBusFitnessExpiry, setEditBusFitnessExpiry] = useState('');
  const [editBusPucExpiry, setEditBusPucExpiry] = useState('');
  const [editBusRoadPermitExpiry, setEditBusRoadPermitExpiry] = useState('');
  const [editBusRoadTaxStatus, setEditBusRoadTaxStatus] = useState('Active / Valid');
  const [savingBusProfile, setSavingBusProfile] = useState(false);

  const startEditingBusProfile = (busProfile) => {
    setEditBusMakeModel(busProfile.makeModel || '');
    setEditBusModelYear(busProfile.modelYear || '');
    setEditBusCapacity(busProfile.capacity || 40);
    setEditBusFuelType(busProfile.fuelType || 'Diesel (BS-VI)');
    setEditBusOwnershipType(busProfile.ownershipType || 'School Owned');
    setEditBusStatus(busProfile.status || 'active');
    setEditBusEngineNo(busProfile.engineNo || '');
    setEditBusChassisNo(busProfile.chassisNo || '');
    setEditBusRegistration(busProfile.registration || '');
    setEditBusInsuranceProvider(busProfile.insuranceProvider || '');
    setEditBusInsurancePolicyNo(busProfile.insurancePolicyNo || '');
    setEditBusInsuranceExpiry(busProfile.insuranceExpiry || '');
    setEditBusFitnessExpiry(busProfile.fitnessExpiry || '');
    setEditBusPucExpiry(busProfile.pucExpiry || '');
    setEditBusRoadPermitExpiry(busProfile.roadPermitExpiry || '');
    setEditBusRoadTaxStatus(busProfile.roadTaxStatus || 'Active / Valid');
    setIsEditingBusProfile(true);
  };

  const handleSaveBusProfile = async () => {
    if (!selectedBusProfile) return;
    setSavingBusProfile(true);
    try {
      await fetch(`${BACKEND_URL}/api/buses/${selectedBusProfile.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bus_number: selectedBusProfile.busNumber,
          registration: editBusRegistration.trim(),
          capacity: parseInt(editBusCapacity) || 40,
          status: editBusStatus
        })
      });

      const updated = {
        ...selectedBusProfile,
        makeModel: editBusMakeModel,
        modelYear: editBusModelYear,
        capacity: parseInt(editBusCapacity) || 40,
        fuelType: editBusFuelType,
        ownershipType: editBusOwnershipType,
        status: editBusStatus,
        engineNo: editBusEngineNo,
        chassisNo: editBusChassisNo,
        registration: editBusRegistration,
        insuranceProvider: editBusInsuranceProvider,
        insurancePolicyNo: editBusInsurancePolicyNo,
        insuranceExpiry: editBusInsuranceExpiry,
        fitnessExpiry: editBusFitnessExpiry,
        pucExpiry: editBusPucExpiry,
        roadPermitExpiry: editBusRoadPermitExpiry,
        roadTaxStatus: editBusRoadTaxStatus
      };

      setSelectedBusProfile(updated);
      setIsEditingBusProfile(false);
      triggerAlert('Success', 'Vehicle profile and regulatory details updated successfully!');
      fetchManagementData();
    } catch (err) {
      triggerAlert('Error', 'Failed to update vehicle details: ' + err.message, 'error');
    } finally {
      setSavingBusProfile(false);
    }
  };


  // DMS States
  const [documents, setDocuments] = useState([]);
  const [storageProvider, setStorageProvider] = useState('local');
  const [s3Region, setS3Region] = useState('');
  const [s3BucketName, setS3BucketName] = useState('');
  const [s3AccessKeyId, setS3AccessKeyId] = useState('');
  const [s3SecretAccessKey, setS3SecretAccessKey] = useState('');
  const [gcsProjectId, setGcsProjectId] = useState('');
  const [gcsBucketName, setGcsBucketName] = useState('');
  const [gcsKeyJson, setGcsKeyJson] = useState('');
  const [azureConnectionString, setAzureConnectionString] = useState('');
  const [azureContainerName, setAzureContainerName] = useState('');
  
  // Upload DMS Form States
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [uploadEntityType, setUploadEntityType] = useState('vehicle'); // 'vehicle', 'driver', 'student'
  const [uploadEntityId, setUploadEntityId] = useState('');
  const [uploadDocType, setUploadDocType] = useState('RC');
  const [uploadExpiryDate, setUploadExpiryDate] = useState('');
  const [uploadFileObj, setUploadFileObj] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  const fetchDocuments = async (filter = 'all') => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/documents?entity_type=${filter}`);
      const data = await res.json();
      setDocuments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching documents:', err);
    }
  };

  const fetchStorageSettings = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/settings/storage`);
      const data = await res.json();
      if (data) {
        setStorageProvider(data.storage_provider || 'local');
        setS3Region(data.s3_region || '');
        setS3BucketName(data.s3_bucket_name || '');
        setS3AccessKeyId(data.s3_access_key_id || '');
        setS3SecretAccessKey(data.s3_secret_access_key || '');
        setGcsProjectId(data.gcs_project_id || '');
        setGcsBucketName(data.gcs_bucket_name || '');
        setGcsKeyJson(data.gcs_key_json || '');
        setAzureConnectionString(data.azure_connection_string || '');
        setAzureContainerName(data.azure_container_name || '');
      }
    } catch (err) {
      console.error('Error fetching storage settings:', err);
    }
  };

  const handleDeleteDoc = async (id) => {
    if (!window.confirm('Are you sure you want to delete this document? This action is irreversible.')) {
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/documents/${id}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.success) {
        alert('Document deleted successfully!');
        fetchDocuments(docFilter);
      } else {
        alert('Failed to delete document: ' + data.message);
      }
    } catch (err) {
      alert('Error deleting document: ' + err.message);
    }
  };

  const isTabRestricted = (tab) => {
    const roleKey = user?.role?.toLowerCase();
    if (!roleKey || roleKey === 'admin') return false;

    const matchedRole = roles.find(r => r.role_key?.toLowerCase() === roleKey);
    if (!matchedRole) {
      if (roleKey === 'viewer_guard' || roleKey === 'guard' || roleKey === 'viewer') {
        return !['monitor', 'trips', 'reports'].includes(tab);
      }
      if (roleKey === 'route_manager') {
        return ['access_control', 'database_ops', 'documents', 'connections', 'general_settings', 'system_logs'].includes(tab);
      }
      return false;
    }

    if (['monitor', 'trips', 'reports'].includes(tab)) {
      return matchedRole.view_gps === 0;
    }
    if (['routes'].includes(tab)) {
      return matchedRole.edit_routes === 0;
    }
    if (['buses', 'drivers'].includes(tab)) {
      return matchedRole.manage_fleet === 0;
    }
    if (['students', 'parents', 'access_control'].includes(tab)) {
      return matchedRole.manage_users === 0;
    }
    if (['documents'].includes(tab)) {
      return matchedRole.access_documents === 0;
    }
    if (['database_ops', 'system_logs', 'connections', 'general_settings'].includes(tab)) {
      return matchedRole.prune_logs === 0;
    }
    return false;
  };

  const navigateTab = (tab) => {
    if (isTabRestricted(tab)) {
      triggerAlert('Access Restricted', "Your assigned role does not have permission to access this console module according to the ACL Matrix.", 'warning');
      return;
    }

    setActiveTab(tab);
    setSelectedStudentProfile(null);
    setSelectedParentProfile(null);
    setSelectedDriverProfile(null);
    setSelectedBusProfile(null);
    fetchManagementData();
  };
  
  // Settings & Configuration States
  const [timeFormat, setTimeFormat] = useState(localStorage.getItem('time_format') || '12hr');
  const [dateFormat, setDateFormat] = useState(localStorage.getItem('date_format') || 'YYYY-MM-DD');
  const [systemTimezone, setSystemTimezone] = useState(localStorage.getItem('system_timezone') || 'Asia/Kolkata');
  const [studentDocTypesSetting, setStudentDocTypesSetting] = useState(localStorage.getItem('student_doc_types') || 'Transit Pass');
  const [driverDocTypesSetting, setDriverDocTypesSetting] = useState(localStorage.getItem('driver_doc_types') || 'Driving License,Police Verification Certificate');
  
  const [masterLookups, setMasterLookups] = useState(() => {
    const loaded = { ...DEFAULT_LOOKUPS };
    Object.keys(DEFAULT_LOOKUPS).forEach(key => {
      const saved = localStorage.getItem(`master_${key}`);
      if (saved !== null) {
        try { loaded[key] = JSON.parse(saved); } catch { loaded[key] = saved; }
      }
    });
    return loaded;
  });

  const handleLookupsUpdate = (updatedSettings) => {
    setMasterLookups(updatedSettings);
    if (Array.isArray(updatedSettings.student_doc_types)) {
      setStudentDocTypesSetting(updatedSettings.student_doc_types.join(','));
    }
    if (Array.isArray(updatedSettings.driver_doc_types)) {
      setDriverDocTypesSetting(updatedSettings.driver_doc_types.join(','));
    }
  };
  
  // API Integration keys (persisted in localStorage)
  const [mapProvider, setMapProvider] = useState('osm');
  const [mapApiKey, setMapApiKey] = useState('');
  
  useEffect(() => {
    fetch('http://localhost:3000/api/settings/map')
      .then(res => res.json())
      .then(data => {
        if (data && data.success && data.settings) {
          setMapProvider(data.settings.map_provider || 'osm');
          
          if (data.settings.map_provider === 'ola' && data.settings.ola_api_key) {
            setMapApiKey(data.settings.ola_api_key);
          } else if (data.settings.map_provider === 'google' && data.settings.google_api_key) {
            setMapApiKey(data.settings.google_api_key);
          }
        }
      })
      .catch(err => console.error("Failed to load map settings", err));
  }, []);
  const [smsGatewayKey, setSmsGatewayKey] = useState(localStorage.getItem('sms_gateway_key') || 'tw-auth-token-884291');
  const [smsSenderNumber, setSmsSenderNumber] = useState(localStorage.getItem('sms_sender') || '+1888402920');
  const [smtpHost, setSmtpHost] = useState(localStorage.getItem('smtp_host') || 'smtp.schoolmail.com');
  const [smtpPort, setSmtpPort] = useState(localStorage.getItem('smtp_port') || '587');
  const [smtpUser, setSmtpUser] = useState(localStorage.getItem('smtp_user') || 'alerts@schoolbus.edu');
  const [smtpPass, setSmtpPass] = useState(localStorage.getItem('smtp_pass') || '••••••••••••');
  
  // Notification Channel configurations
  const [chanPush, setChanPush] = useState(localStorage.getItem('chan_push') !== 'false');
  const [chanSMS, setChanSMS] = useState(localStorage.getItem('chan_sms') !== 'false');
  const [chanEmail, setChanEmail] = useState(localStorage.getItem('chan_email') !== 'false');

  // Filtered deployments computation logic
  const filteredTrips = allTrips.filter(trip => {
    if (filterRouteId && trip.route_id !== parseInt(filterRouteId)) {
      return false;
    }
    if (filterBusId && trip.bus_id !== parseInt(filterBusId)) {
      return false;
    }
    if (filterDriverId && trip.driver_id !== parseInt(filterDriverId)) {
      return false;
    }
    if (filterDate && trip.scheduled_date !== filterDate) {
      return false;
    }
    if (filterStatus && trip.status !== filterStatus) {
      return false;
    }
    return true;
  });

  // Reset currentPage to 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterRouteId, filterBusId, filterDriverId, filterDate, filterStatus]);

  useEffect(() => {
    if (activeTab === 'system_logs') {
      fetchLogsData(logType, logSearch, logLevelFilter, auditActionFilter, logStartDate, logEndDate);
    }
  }, [activeTab, logType, logLevelFilter, auditActionFilter, logStartDate, logEndDate]);

  const pageSize = 10;
  const totalPages = Math.ceil(filteredTrips.length / pageSize);
  const paginatedTrips = filteredTrips.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  
  // Database maintenance status logs
  const [dbLogs, setDbLogs] = useState([
    '[' + new Date().toLocaleTimeString() + '] [SYSTEM INFO] SQLite engine online.',
    '[' + new Date().toLocaleTimeString() + '] [SYSTEM INFO] Database file: school_bus_tracker.db',
    '[' + new Date().toLocaleTimeString() + '] [SYSTEM INFO] Total indexes: 8',
    '[' + new Date().toLocaleTimeString() + '] [SYSTEM INFO] Integrity check: OK'
  ]);
  const [pruneInterval, setPruneInterval] = useState(localStorage.getItem('prune_interval') || '90');


  // Announcement inputs
  const [announceTitle, setAnnounceTitle] = useState('');
  const [announceBody, setAnnounceBody] = useState('');
  const [announceTarget, setAnnounceTarget] = useState('all');
  const [announcementsList, setAnnouncementsList] = useState([
    { id: 1, title: 'Heavy Rainfall Delay Alert', body: 'All morning pickup routes are running 10-15 minutes late due to heavy downpour.', target: 'all', timestamp: new Date(Date.now() - 3600000).toLocaleString() },
    { id: 2, title: 'Bus 12B Driver Assigned Update', body: 'Please note driver Satish Singh will be operating Route B drop today.', target: 'parents', timestamp: new Date(Date.now() - 7200000).toLocaleString() }
  ]);

  // Support Tickets inputs/states
  const [ticketsList, setTicketsList] = useState([
    { id: 101, parent: 'Suresh Sharma', phone: '+91 90747 70963', issue: 'RFID check-in did not notify me today for child Mike.', status: 'OPEN', date: 'Today, 08:45 AM' },
    { id: 102, parent: 'Meera Patel', phone: '+91 98888 88882', issue: 'Bus 12B driver Satish was running too fast near stop 3.', status: 'OPEN', date: 'Today, 09:12 AM' },
    { id: 103, parent: 'Deepa Nair', phone: '+91 93333 33335', issue: 'Can I change my drop-off stop to Banaswadi Apartments permanently?', status: 'RESOLVED', date: 'Yesterday, 04:30 PM' }
  ]);

  // Scheduled Alerts inputs
  const [alertConfigEmail, setAlertConfigEmail] = useState(localStorage.getItem('alert_email') || 'admin@schoolbus.edu');
  const [alertConfigFreq, setAlertConfigFreq] = useState(localStorage.getItem('alert_freq') || 'daily');
  const [alertConfigType, setAlertConfigType] = useState(localStorage.getItem('alert_type') || 'attendance');

  // Route Scheduling form states
  const [showAddRouteForm, setShowAddRouteForm] = useState(false);
  const [newRouteName, setNewRouteName] = useState('');
  const [newRouteDirection, setNewRouteDirection] = useState('PICKUP');
  const [newRouteStops, setNewRouteStops] = useState([
    { stopName: '', lat: '', lng: '', scheduledTime: '', sequenceNumber: 1 }
  ]);
  const [activeStopIndex, setActiveStopIndex] = useState(0);
  const [mapSearchQuery, setMapSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [mapCenter, setMapCenter] = useState([12.9913, 77.6483]);
  const [editingRouteId, setEditingRouteId] = useState(null);

  const [showAddDriverForm, setShowAddDriverForm] = useState(false);
  const [newDriverUsername, setNewDriverUsername] = useState('');
  const [newDriverPassword, setNewDriverPassword] = useState('');
  const [newDriverDisplayName, setNewDriverDisplayName] = useState('');
  const [newDriverPhone, setNewDriverPhone] = useState('');
  const [driverSubmitError, setDriverSubmitError] = useState('');

  // Selected driver profile details
  const [showEnrollmentModalDriver, setShowEnrollmentModalDriver] = useState(null);
  const [selectedDriverProfile, setSelectedDriverProfile] = useState(null);
  const [driverProfiles, setDriverProfiles] = useState(driverProfilesData);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editDriverId, setEditDriverId] = useState('');
  const [editDriverPhone, setEditDriverPhone] = useState('');
  const [editDriverAddress, setEditDriverAddress] = useState('');
  const [editDriverLicenseExpiry, setEditDriverLicenseExpiry] = useState('');
  const [editDriverLicensingStatus, setEditDriverLicensingStatus] = useState('');
  const [editDriverStatus, setEditDriverStatus] = useState('');
  const [editDriverBloodGroup, setEditDriverBloodGroup] = useState('O+ Positive');
  const [editDriverLicenseNumber, setEditDriverLicenseNumber] = useState('');
  const [editDriverMedicalStatus, setEditDriverMedicalStatus] = useState('Certified Fit');
  const [editDriverMedicalExpiry, setEditDriverMedicalExpiry] = useState('');
  const [editDriverPoliceVerification, setEditDriverPoliceVerification] = useState('Cleared & Verified');
  const [editDriverExperienceYears, setEditDriverExperienceYears] = useState('');
  const [editDriverAssignedBus, setEditDriverAssignedBus] = useState('');
  const [editDriverAssignedRoute, setEditDriverAssignedRoute] = useState('');
  const [editDriverError, setEditDriverError] = useState('');
  const [editDriverUsername, setEditDriverUsername] = useState('');
  const [editDriverPassword, setEditDriverPassword] = useState('');
  const [editDriverDisplayName, setEditDriverDisplayName] = useState('');

  // Selected student profile details & edit states
  const [isEditingStudent, setIsEditingStudent] = useState(false);
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  
  // Student core fields
  const [editStudentFirstName, setEditStudentFirstName] = useState('');
  const [editStudentLastName, setEditStudentLastName] = useState('');
  const [editStudentAdmissionNo, setEditStudentAdmissionNo] = useState('');
  const [editStudentRfidTagId, setEditStudentRfidTagId] = useState('');
  const [editStudentParentId, setEditStudentParentId] = useState('');
  const [editStudentPickupStopId, setEditStudentPickupStopId] = useState('');
  const [editStudentDropStopId, setEditStudentDropStopId] = useState('');
  const [editStudentAvatar, setEditStudentAvatar] = useState('');

  // Student profile_data secondary fields
  const [editStudentRollNo, setEditStudentRollNo] = useState('');
  const [editStudentDob, setEditStudentDob] = useState('');
  const [editStudentGender, setEditStudentGender] = useState('');
  const [editStudentBloodGroup, setEditStudentBloodGroup] = useState('');
  const [editStudentAadhaar, setEditStudentAadhaar] = useState('');
  const [editStudentGrade, setEditStudentGrade] = useState('');
  const [editStudentSection, setEditStudentSection] = useState('');
  const [editStudentBoard, setEditStudentBoard] = useState('');
  const [editStudentStream, setEditStudentStream] = useState('');
  const [editStudentSubjects, setEditStudentSubjects] = useState('');
  const [editStudentSecondLang, setEditStudentSecondLang] = useState('');
  const [editStudentClassTeacher, setEditStudentClassTeacher] = useState('');
  const [editStudentHouse, setEditStudentHouse] = useState('');
  const [editStudentPermanentAddress, setEditStudentPermanentAddress] = useState('');
  const [editStudentCurrentAddress, setEditStudentCurrentAddress] = useState('');
  const [editStudentModeOfTransport, setEditStudentModeOfTransport] = useState('School Bus');
  const [editStudentAllergies, setEditStudentAllergies] = useState('');
  const [editStudentMedications, setEditStudentMedications] = useState('');
  const [editStudentEmergencyContact, setEditStudentEmergencyContact] = useState('');
  const [editStudentEmergencyPerson, setEditStudentEmergencyPerson] = useState('');
  const [editStudentClubs, setEditStudentClubs] = useState('');
  const [editStudentSports, setEditStudentSports] = useState('');
  const [editStudentResponsibility, setEditStudentResponsibility] = useState('');

  const [editStudentError, setEditStudentError] = useState('');
  const [savingStudent, setSavingStudent] = useState(false);

  // Parent autocomplete & quick add states
  const [parentSearchText, setParentSearchText] = useState('');
  const [showParentDropdown, setShowParentDropdown] = useState(false);
  const [showQuickAddParentModal, setShowQuickAddParentModal] = useState(false);
  const [quickAddParentName, setQuickAddParentName] = useState('');
  const [quickAddParentPhone, setQuickAddParentPhone] = useState('');
  const [quickAddParentUsername, setQuickAddParentUsername] = useState('');
  const [quickAddParentPassword, setQuickAddParentPassword] = useState('parent123');
  const [quickAddParentError, setQuickAddParentError] = useState('');

  // Parent profile editing states
  const [isEditingParent, setIsEditingParent] = useState(false);
  const [savingParent, setSavingParent] = useState(false);
  const [editParentError, setEditParentError] = useState('');
  const [showAddParentModal, setShowAddParentModal] = useState(false);

  // Console User Management states (Route Manager, Viewer/Guard, Admin)
  const [showAddConsoleUserModal, setShowAddConsoleUserModal] = useState(false);
  const [newConsoleUserRole, setNewConsoleUserRole] = useState('route_manager');
  const [newConsoleUserDisplayName, setNewConsoleUserDisplayName] = useState('');
  const [newConsoleUserUsername, setNewConsoleUserUsername] = useState('');
  const [newConsoleUserPassword, setNewConsoleUserPassword] = useState('');
  const [newConsoleUserPhone, setNewConsoleUserPhone] = useState('');
  const [addConsoleUserError, setAddConsoleUserError] = useState('');

  // Roles & Permissions state
  const [roles, setRoles] = useState([
    {
      id: 1,
      role_key: 'admin',
      role_name: 'System Admin',
      description: 'Full unrestricted system administration privileges',
      edit_routes: 1, view_gps: 1, prune_logs: 1, manage_fleet: 1, manage_users: 1, access_documents: 1, is_system: 1
    },
    {
      id: 2,
      role_key: 'route_manager',
      role_name: 'Route Manager',
      description: 'Manages bus routes, pickup stops, drivers, and fleet vehicles',
      edit_routes: 1, view_gps: 1, prune_logs: 0, manage_fleet: 1, manage_users: 0, access_documents: 0, is_system: 0
    },
    {
      id: 3,
      role_key: 'viewer_guard',
      role_name: 'Viewer / Guard',
      description: 'Security guards and gate monitors with view-only GPS map access',
      edit_routes: 0, view_gps: 1, prune_logs: 0, manage_fleet: 0, manage_users: 0, access_documents: 0, is_system: 0
    }
  ]);
  const [showAddRoleModal, setShowAddRoleModal] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleKey, setNewRoleKey] = useState('');
  const [newRoleDesc, setNewRoleDesc] = useState('');
  const [newRolePermissions, setNewRolePermissions] = useState({
    edit_routes: false,
    view_gps: true,
    prune_logs: false,
    manage_fleet: false,
    manage_users: false,
    access_documents: false
  });
  const [addRoleError, setAddRoleError] = useState('');

  // Edit Role modal state
  const [showEditRoleModal, setShowEditRoleModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [editRoleName, setEditRoleName] = useState('');
  const [editRoleDesc, setEditRoleDesc] = useState('');
  const [editRolePermissions, setEditRolePermissions] = useState({
    edit_routes: false,
    view_gps: true,
    prune_logs: false,
    manage_fleet: false,
    manage_users: false,
    access_documents: false
  });
  const [editRoleError, setEditRoleError] = useState('');

  // Edit Console User modal state
  const [showEditConsoleUserModal, setShowEditConsoleUserModal] = useState(false);
  const [editingConsoleUser, setEditingConsoleUser] = useState(null);
  const [editConsoleUserDisplayName, setEditConsoleUserDisplayName] = useState('');
  const [editConsoleUserUsername, setEditConsoleUserUsername] = useState('');
  const [editConsoleUserRole, setEditConsoleUserRole] = useState('route_manager');
  const [editConsoleUserPhone, setEditConsoleUserPhone] = useState('');
  const [editConsoleUserPassword, setEditConsoleUserPassword] = useState('');
  const [editConsoleUserError, setEditConsoleUserError] = useState('');

  // Core fields
  const [editParentDisplayName, setEditParentDisplayName] = useState('');
  const [editParentPhone, setEditParentPhone] = useState('');
  const [editParentUsername, setEditParentUsername] = useState('');
  const [editParentPassword, setEditParentPassword] = useState('');

  // Profile secondary fields (stored in profile_data)
  const [editParentAddress, setEditParentAddress] = useState('');
  const [editParentAltPhone, setEditParentAltPhone] = useState('');
  const [editParentEmail, setEditParentEmail] = useState('');
  const [editParentOccupation, setEditParentOccupation] = useState('');
  const [editParentRelationship, setEditParentRelationship] = useState('Guardian');
  const [editParentSpouseName, setEditParentSpouseName] = useState('');
  const [editParentEmergencyContact, setEditParentEmergencyContact] = useState('');
  const [editParentPreferredLanguage, setEditParentPreferredLanguage] = useState('English');
  const [editParentPickupAuth, setEditParentPickupAuth] = useState('Authorized');
  const [editParentSmsEnabled, setEditParentSmsEnabled] = useState(true);
  const [editParentPushEnabled, setEditParentPushEnabled] = useState(true);
  const [editParentEtaAlertSetting, setEditParentEtaAlertSetting] = useState('5 mins before arrival');
  const [editParentEmailAlerts, setEditParentEmailAlerts] = useState(false);
  const [editParentStudentIds, setEditParentStudentIds] = useState([]);
  const [modalConfig, setModalConfig] = useState(null);

  const addStopRow = () => {
    setNewRouteStops(prev => [
      ...prev,
      { stopName: '', lat: '', lng: '', scheduledTime: '', sequenceNumber: prev.length + 1 }
    ]);
  };

  const removeStopRow = (index) => {
    if (newRouteStops.length === 1) return;
    setNewRouteStops(prev => prev.filter((_, idx) => idx !== index).map((stop, idx) => ({ ...stop, sequenceNumber: idx + 1 })));
  };

  const updateStopRow = (index, field, value) => {
    setNewRouteStops(prev => prev.map((stop, idx) => {
      if (idx === index) {
        return { ...stop, [field]: value };
      }
      return stop;
    }));
  };

  const handleMapSearch = async () => {
    if (!mapSearchQuery.trim()) return;
    setIsSearching(true);
    try {
      let newLat, newLng, displayName;

      if (mapProvider === 'google' && mapApiKey) {
        const res = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(mapSearchQuery)}&key=${mapApiKey}`);
        const data = await res.json();
        if (data.results && data.results.length > 0) {
          const loc = data.results[0].geometry.location;
          newLat = loc.lat;
          newLng = loc.lng;
          displayName = data.results[0].formatted_address;
        }
      } else if (mapProvider === 'ola' && mapApiKey) {
        // Ola Geocoding / Search API
        const res = await fetch(`https://api.olamaps.io/places/v1/autocomplete?input=${encodeURIComponent(mapSearchQuery)}&api_key=${mapApiKey}`);
        const data = await res.json();
        if (data.predictions && data.predictions.length > 0) {
          const loc = data.predictions[0].geometry.location;
          newLat = loc.lat;
          newLng = loc.lng;
          displayName = data.predictions[0].description;
        }
      } else {
        // Default to OSM / Nominatim
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(mapSearchQuery)}&limit=1`);
        const data = await res.json();
        if (data && data.length > 0) {
          newLat = parseFloat(data[0].lat);
          newLng = parseFloat(data[0].lon);
          displayName = data[0].display_name;
        }
      }

      if (newLat && newLng && displayName) {
        updateStopRow(activeStopIndex, 'lat', newLat.toFixed(6));
        updateStopRow(activeStopIndex, 'lng', newLng.toFixed(6));
        updateStopRow(activeStopIndex, 'stopName', displayName.split(',')[0]);
        setMapCenter([newLat, newLng]);
      } else {
        alert('Location not found.');
      }
    } catch (err) {
      console.error(err);
      alert('Error searching for location.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSaveRoute = async (e) => {
    e.preventDefault();
    if (!newRouteName.trim()) return alert('Route name is required.');
    
    // Check stop values
    for (let stop of newRouteStops) {
      if (!stop.stopName.trim() || !stop.lat || !stop.lng || !stop.scheduledTime.trim()) {
        return alert('All stop fields (Name, Lat, Lng, Time) are required.');
      }
    }

    try {
      const isEditing = editingRouteId !== null;
      const url = isEditing 
        ? `${BACKEND_URL}/api/routes/${editingRouteId}`
        : `${BACKEND_URL}/api/routes`;
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          routeName: newRouteName,
          direction: newRouteDirection,
          stops: newRouteStops
        })
      });
      const data = await response.json();
      if (data.success) {
        setShowAddRouteForm(false);
        setNewRouteName('');
        setNewRouteDirection('PICKUP');
        setNewRouteStops([{ stopName: '', lat: '', lng: '', scheduledTime: '', sequenceNumber: 1 }]);
        setEditingRouteId(null);
        fetchManagementData();
        alert(isEditing ? 'Route plan updated successfully!' : 'Route plan created successfully!');
      } else {
        alert(data.message || 'Failed to save route plan.');
      }
    } catch (err) {
      console.error(err);
      alert('Network error while saving route.');
    }
  };

  const handleEditRoute = (route) => {
    setEditingRouteId(route.id);
    setNewRouteName(route.route_name);
    setNewRouteDirection(route.direction);
    
    const stopsFormatted = route.stops.map(s => ({
      stopName: s.stop_name,
      lat: s.lat.toString(),
      lng: s.lng.toString(),
      scheduledTime: s.scheduled_time,
      sequenceNumber: s.sequence_number
    }));
    
    setNewRouteStops(stopsFormatted);
    setActiveStopIndex(0);
    
    if (stopsFormatted.length > 0) {
      const firstLat = parseFloat(stopsFormatted[0].lat);
      const firstLng = parseFloat(stopsFormatted[0].lng);
      if (!isNaN(firstLat) && !isNaN(firstLng)) {
        setMapCenter([firstLat, firstLng]);
      }
    }
    
    setShowAddRouteForm(true);
  };

  const handleDeleteRoute = async (routeId) => {
    if (!confirm('Are you sure you want to delete this route? This will cancel all associated active or scheduled trips.')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/routes/${routeId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        fetchManagementData();
        alert('Route deleted successfully.');
      } else {
        alert(data.message || 'Failed to delete route.');
      }
    } catch (err) {
      console.error(err);
      alert('Network error while deleting route.');
    }
  };

  // Trip Assignment states
  const [selectedRouteId, setSelectedRouteId] = useState('');
  const [selectedBusId, setSelectedBusId] = useState('');
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [scheduledDate, setScheduledDate] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  });

  const handleAssignDriver = async (e) => {
    e.preventDefault();
    if (!selectedRouteId || !selectedBusId || !selectedDriverId) {
      return alert('Please select a Route, Bus, and Driver.');
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/trips`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          routeId: parseInt(selectedRouteId),
          busId: parseInt(selectedBusId),
          driverId: parseInt(selectedDriverId),
          scheduledDate: scheduledDate
        })
      });
      const data = await response.json();
      if (data.success) {
        setSelectedRouteId('');
        setSelectedBusId('');
        setSelectedDriverId('');
        const today = new Date();
        setScheduledDate(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`);
        fetchActiveTrips(); // Refresh the list of active/scheduled trips
        fetchAllTrips();
      fetchDeviceStats();
        alert('Driver and Bus assigned to route successfully!');
      } else {
        alert(data.message || 'Failed to create trip assignment.');
      }
    } catch (err) {
      console.error(err);
      alert('Network error while assigning trip.');
    }
  };

  const handleCancelAssignment = async (tripId) => {
    if (!confirm('Are you sure you want to cancel this route assignment?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/trips/${tripId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        fetchActiveTrips(); // Refresh trips list
        fetchAllTrips();
      fetchDeviceStats();
      } else {
        alert(data.message || 'Failed to cancel assignment.');
      }
    } catch (err) {
      console.error(err);
      alert('Network error while cancelling assignment.');
    }
  };

  const handleAddNewDriverClick = () => {
    const newDvrId = generateNextDriverId();
    const tempProfile = {
      avatar: "",
      driverId: newDvrId,
      name: "New Driver",
      phone: "",
      address: "Bangalore, Karnataka, India",
      bloodGroup: "O+ Positive",
      licenseNumber: "DL-04" + Math.floor(10000000000 + Math.random() * 90000000000),
      licenseExpiry: "Oct 15, 2028",
      licensingStatus: "VALID LICENSE",
      medicalStatus: "Certified Fit",
      medicalExpiry: "Aug 2027",
      policeVerification: "Cleared & Verified",
      experienceYears: "5+ Years",
      safetyScore: "99.0%",
      onTimeScore: "98.5%",
      assignedBusNumber: "Bus 10A",
      assignedBusReg: "KA-03-AA-1234",
      assignedRoute: "Primary School Route",
      status: "ACTIVE",
      documents: [],
      isNew: true
    };
    
    setSelectedDriverProfile(tempProfile);
    setIsEditingProfile(true);
    setEditDriverId(tempProfile.driverId);
    setEditDriverPhone(tempProfile.phone);
    setEditDriverAddress(tempProfile.address);
    setEditDriverLicenseExpiry(tempProfile.licenseExpiry);
    setEditDriverLicensingStatus(tempProfile.licensingStatus);
    setEditDriverStatus(tempProfile.status);
    setEditDriverBloodGroup(tempProfile.bloodGroup);
    setEditDriverLicenseNumber(tempProfile.licenseNumber);
    setEditDriverMedicalStatus(tempProfile.medicalStatus);
    setEditDriverMedicalExpiry(tempProfile.medicalExpiry);
    setEditDriverPoliceVerification(tempProfile.policeVerification);
    setEditDriverExperienceYears(tempProfile.experienceYears);
    setEditDriverAssignedBus(tempProfile.assignedBusNumber);
    setEditDriverAssignedRoute(tempProfile.assignedRoute);
    setEditDriverUsername("");
    setEditDriverPassword("");
    setEditDriverDisplayName("");
    setEditDriverError("");
  };

  const handleUpdateDriverProfile = async (originalName, updatedProfile) => {
    // Check if the edited driverId conflicts with any other driver's profile (excluding the current driver)
    const duplicate = Object.values(driverProfiles).find(
      p => p.name !== originalName && p.driverId === updatedProfile.driverId
    );

    if (duplicate) {
      setEditDriverError(`Duplicate Driver ID "${updatedProfile.driverId}" is not allowed.`);
      return false;
    }

    const profilePayload = {
      address: updatedProfile.address,
      bloodGroup: updatedProfile.bloodGroup,
      licenseNumber: updatedProfile.licenseNumber,
      licenseExpiry: updatedProfile.licenseExpiry,
      licensingStatus: updatedProfile.licensingStatus,
      medicalStatus: updatedProfile.medicalStatus,
      medicalExpiry: updatedProfile.medicalExpiry,
      policeVerification: updatedProfile.policeVerification,
      experienceYears: updatedProfile.experienceYears,
      safetyScore: updatedProfile.safetyScore,
      onTimeScore: updatedProfile.onTimeScore,
      assignedBusNumber: updatedProfile.assignedBusNumber,
      assignedBusReg: updatedProfile.assignedBusReg,
      assignedRoute: updatedProfile.assignedRoute,
      status: updatedProfile.status,
      driverId: updatedProfile.driverId
    };

    if (updatedProfile.isNew) {
      if (!editDriverUsername.trim() || !editDriverPassword || !editDriverDisplayName.trim()) {
        setEditDriverError('Username, Password, and Full Name are required.');
        return false;
      }

      try {
        const response = await fetch(`${BACKEND_URL}/api/users`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: editDriverUsername,
            password: editDriverPassword,
            role: 'driver',
            displayName: editDriverDisplayName,
            phone: editDriverPhone,
            profile_data: JSON.stringify(profilePayload)
          })
        });
        const data = await response.json();
        if (data.success) {
          const finalizedProfile = {
            ...updatedProfile,
            name: editDriverDisplayName,
            phone: editDriverPhone || "N/A"
          };
          delete finalizedProfile.isNew;

          const updatedProfiles = { ...driverProfiles, [editDriverDisplayName]: finalizedProfile };
          setDriverProfiles(updatedProfiles);
          setSelectedDriverProfile(finalizedProfile);
          setIsEditingProfile(false);
          setEditDriverError('');
          fetchManagementData();
          alert(`Driver account created successfully with ID ${finalizedProfile.driverId}!`);
          return true;
        } else {
          setEditDriverError(data.message || 'Failed to create driver account.');
          return false;
        }
      } catch (err) {
        console.error(err);
        setEditDriverError('Network error while saving driver.');
        return false;
      }
    } else {
      if (updatedProfile.id) {
        try {
          await fetch(`${BACKEND_URL}/api/users/${updatedProfile.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              username: updatedProfile.username || updatedProfile.name.toLowerCase().replace(/\s+/g, '_'),
              displayName: updatedProfile.name,
              phone: updatedProfile.phone,
              profile_data: JSON.stringify(profilePayload)
            })
          });
        } catch (err) {
          console.error("Failed to sync profile to server:", err);
        }
      }

      const updatedProfiles = { ...driverProfiles };
      if (originalName !== updatedProfile.name) {
        delete updatedProfiles[originalName];
      }
      updatedProfiles[updatedProfile.name] = updatedProfile;
      setDriverProfiles(updatedProfiles);
      setSelectedDriverProfile(updatedProfile);
      setIsEditingProfile(false);
      setEditDriverError('');
      fetchManagementData();
      return true;
    }
  };

  const socketRef = useRef(null);

  // Check storage on load
  useEffect(() => {
    const savedUser = localStorage.getItem('busbuddy_admin');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
  }, []);

  // Socket Connection setup
  useEffect(() => {
    if (!user) return;

    socketRef.current = io(BACKEND_URL);
    socketRef.current.emit('join_admin');

    socketRef.current.on('location_update', (data) => {
      setBusPositions(prev => ({
        ...prev,
        [data.tripId]: data
      }));
    });

    socketRef.current.on('trip_status_change', ({ tripId, status }) => {
      // Reload trips list to reflect status update
      fetchActiveTrips();
      fetchAllTrips();
      fetchDeviceStats();
      const currentSelected = selectedTripRef.current;
      if (currentSelected && currentSelected.id === parseInt(tripId)) {
        setSelectedTrip(prev => prev ? { ...prev, status } : null);
      }
    });

    
    socketRef.current.on('student_absence_update', (data) => {
      setSelectedTripRoster(prev => {
        if (!prev) return prev;
        return prev.map(s => {
          if (s.id === parseInt(data.studentId)) {
            return { ...s, absence_reason: data.isAbsent ? data.reason : null };
          }
          return s;
        });
      });
    });

    socketRef.current.on('student_attendance_update', (data) => {
      const currentSelected = selectedTripRef.current;
      if (currentSelected && currentSelected.id === parseInt(data.tripId)) {
        // Refresh roster attendance status
        fetchTripRoster(currentSelected.id);
      }
    });

    socketRef.current.on('delay_update', ({ tripId, delayMinutes }) => {
      setTrips(prev => prev.map(t => t.id === parseInt(tripId) ? { ...t, delay_minutes: delayMinutes } : t));
      setAllTrips(prev => prev.map(t => t.id === parseInt(tripId) ? { ...t, delay_minutes: delayMinutes } : t));
      const currentSelected = selectedTripRef.current;
      if (currentSelected && currentSelected.id === parseInt(tripId)) {
        setSelectedTrip(prev => prev ? { ...prev, delay_minutes: delayMinutes } : null);
      }
    });

    socketRef.current.on('sos_alert', (data) => {
      setSosAlert(data);
    });

    // Load initial listings
    fetchActiveTrips();
    fetchAllTrips();
      fetchDeviceStats();
    fetchManagementData();

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [user]);

  // Load details when selectedTrip changes
  useEffect(() => {
    selectedTripRef.current = selectedTrip;
    if (selectedTrip) {
      fetchTripStops(selectedTrip.id);
      fetchTripRoster(selectedTrip.id);
    } else {
      setSelectedTripStops([]);
      setSelectedTripRoster([]);
    }
  }, [selectedTrip]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const response = await fetch(`${BACKEND_URL}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      const data = await response.json();
      if (data.success) {
        const allowedConsoleRoles = ['admin', 'route_manager', 'viewer_guard', 'guard', 'viewer', 'parent'];
        if (!allowedConsoleRoles.includes(data.user.role?.toLowerCase())) {
          setLoginError('Access denied. Administrator / Console / Parent privileges required.');
          return;
        }
        setUser(data.user);
        localStorage.setItem('busbuddy_admin', JSON.stringify(data.user));
        if (['viewer_guard', 'guard', 'viewer'].includes(data.user.role?.toLowerCase())) {
          setActiveTab('monitor');
        }
      } else {
        setLoginError(data.message || 'Login failed');
      }
    } catch (err) {
      setLoginError('Server connection failed. Ensure server is running.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('busbuddy_admin');
    setUser(null);
  };

  const fetchActiveTrips = async () => {
    try {
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const res = await fetch(`${BACKEND_URL}/api/trips/active?date=${todayStr}`);
      const data = await res.json();
      const activeOrTodayScheduled = Array.isArray(data) ? data.filter(t => 
        t.status === 'EN_ROUTE' || (t.status === 'SCHEDULED' && (!t.scheduled_date || t.scheduled_date === todayStr))
      ) : [];
      setTrips(activeOrTodayScheduled);
      setSelectedTrip(prev => {
        if (!prev) return null;
        return activeOrTodayScheduled.find(t => t.id === prev.id) || null;
      });
    } catch (err) {
      console.error('Error fetching trips:', err);
    }
  };

  const fetchDeviceStats = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/devices/stats`);
      const data = await res.json();
      setDeviceStats(data);
    } catch (err) {
      console.error('Error fetching device stats:', err);
    }
  };

  const fetchAllTrips = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/trips`);
      const data = await res.json();
      setAllTrips(data);
    } catch (err) {
      console.error('Error fetching all trips:', err);
    }
  };

  const fetchTripStops = async (tripId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/trips/${tripId}/stops`);
      const data = await res.json();
      setSelectedTripStops(data);
    } catch (err) {
      console.error('Error fetching stops:', err);
    }
  };

  const fetchTripRoster = async (tripId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/trips/${tripId}/students`);
      const data = await res.json();
      setSelectedTripRoster(data);
    } catch (err) {
      console.error('Error fetching roster:', err);
    }
  };

  const fetchManagementData = async () => {
    try {
      const routesRes = await fetch(`${BACKEND_URL}/api/routes`);
      const routesData = await routesRes.json();
      setAllRoutes(routesData);

      const busesRes = await fetch(`${BACKEND_URL}/api/buses`);
      const busesData = await busesRes.json();
      setAllBuses(busesData);

      const usersRes = await fetch(`${BACKEND_URL}/api/users`);
      const usersData = await usersRes.json();
      setAllUsers(usersData);

      const studentsRes = await fetch(`${BACKEND_URL}/api/students`);
      const studentsData = await studentsRes.json();
      setAllStudents(studentsData);

      try {
        const rolesRes = await fetch(`${BACKEND_URL}/api/roles`);
        if (rolesRes.ok) {
          const rolesData = await rolesRes.json();
          if (Array.isArray(rolesData) && rolesData.length > 0) {
            setRoles(rolesData);
          }
        }
      } catch (rolesErr) {
        console.error('Error fetching roles in mgmt data:', rolesErr);
      }

      return { usersData, studentsData };
    } catch (err) {
      console.error('Error fetching mgmt data:', err);
      return {};
    }
  };


  const getLinkedParentForStudent = (student) => {
    if (!student) return null;
    const firstName = (student.first_name || '').trim();
    const profile = studentProfilesData[firstName] || 
                    studentProfilesData[firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()] || 
                    Object.entries(studentProfilesData).find(([k]) => k.toLowerCase() === firstName.toLowerCase())?.[1] || {};
    
    const parentId = student.parent_id;
    const parentName = (student.parent_name || profile.fatherName || profile.guardianName || '').trim();
    const parentPhone = (student.parent_phone || profile.primaryContact || '').replace(/\D/g, '');

    const matchedParent = allUsers.find(u => {
      if (u.role !== 'parent' && u.role !== 'PARENT') return false;
      if (parentId && u.id === parentId) return true;
      const uName = (u.display_name || u.name || '').trim().toLowerCase();
      if (parentName && uName === parentName.toLowerCase()) return true;
      const uPhone = (u.phone || '').replace(/\D/g, '');
      if (parentPhone && uPhone && uPhone === parentPhone) return true;
      return false;
    });

    if (matchedParent) return matchedParent;

    if (parentName) {
      return {
        id: parentId || 999,
        display_name: parentName,
        name: parentName,
        phone: student.parent_phone || profile.primaryContact || 'N/A',
        role: 'parent'
      };
    }
    return null;
  };

  const handleStudentClick = (student) => {
    let parsedProfile = {};
    if (student.profile_data) {
      try {
        parsedProfile = JSON.parse(student.profile_data);
      } catch (e) {
        console.error("Error parsing profile_data JSON:", e);
      }
    }

    const firstName = (student.first_name || '').trim();
    const matchedProfile = studentProfilesData[firstName] || 
                           studentProfilesData[firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()] ||
                           Object.entries(studentProfilesData).find(([k]) => k.toLowerCase() === firstName.toLowerCase())?.[1] || {};
    
    const photo = student.avatar || parsedProfile.photo || getStudentAvatar(student);
    const linkedParent = getLinkedParentForStudent(student);

    const mergedProfile = {
      // 1. Defaults
      admissionNo: student.admission_no || `ADM-${student.rfid_tag_id || ('00' + student.id)}`,
      rollNo: "N/A",
      dob: "N/A",
      gender: "N/A",
      bloodGroup: "N/A",
      aadhaar: "N/A",
      grade: student.grade || "N/A",
      section: student.section || "N/A",
      board: "N/A",
      stream: "N/A",
      subjects: "N/A",
      secondLang: "N/A",
      classTeacher: "N/A",
      house: "N/A",
      fatherName: student.parent_name || "N/A",
      motherName: "N/A",
      guardianName: "N/A",
      primaryContact: student.parent_phone || "N/A",
      emergencyContact: student.parent_phone || "N/A",
      email: "N/A",
      permanentAddress: "N/A",
      currentAddress: "N/A",
      modeOfTransport: student.transport_mode || (parsedProfile && parsedProfile.modeOfTransport) || "School Bus",
      busNo: student.bus_number || "N/A",
      assignedStop: (student.transport_mode === 'No Transport' || parsedProfile?.modeOfTransport === 'No Transport')
        ? 'None (No School Transport)'
        : `${student.pickup_stop_name || 'N/A'} (Pickup) / ${student.dropoff_stop_name || 'N/A'} (Drop)`,
      driverContact: "N/A",
      allergies: "None",
      medications: "None",
      emergencyPerson: student.parent_name || "N/A",
      clubs: "None",
      sports: "None",
      responsibility: "None",

      // 2. Static matched
      ...matchedProfile,

      // 3. Dynamic from DB
      ...parsedProfile,

      // 4. Forces
      fullName: parsedProfile.fullName || matchedProfile.fullName || `${student.first_name} ${student.last_name}`,
      photo: photo,
      linkedParent: linkedParent,
      studentData: student
    };

    setSelectedStudentProfile(mergedProfile);

    // Initialize editing states
    setEditStudentFirstName(student.first_name || '');
    setEditStudentLastName(student.last_name || '');
    setEditStudentAdmissionNo(student.admission_no || '');
    setEditStudentRfidTagId(student.rfid_tag_id || '');
    setEditStudentParentId(student.parent_id || '');
    setParentSearchText(student.parent_name ? `${student.parent_name} (${student.parent_phone || ''})` : '');
    setEditStudentPickupStopId(student.pickup_stop_id || '');
    setEditStudentDropStopId(student.drop_stop_id || '');
    setEditStudentAvatar(photo || '');

    setEditStudentRollNo(mergedProfile.rollNo !== "N/A" ? mergedProfile.rollNo : '');
    setEditStudentDob(mergedProfile.dob !== "N/A" ? mergedProfile.dob : '');
    setEditStudentGender(mergedProfile.gender !== "N/A" ? mergedProfile.gender : '');
    setEditStudentBloodGroup(mergedProfile.bloodGroup !== "N/A" ? mergedProfile.bloodGroup : '');
    setEditStudentAadhaar(mergedProfile.aadhaar !== "N/A" ? mergedProfile.aadhaar : '');
    setEditStudentGrade(mergedProfile.grade !== "N/A" ? mergedProfile.grade : '');
    setEditStudentSection(mergedProfile.section !== "N/A" ? mergedProfile.section : '');
    setEditStudentBoard(mergedProfile.board !== "N/A" ? mergedProfile.board : '');
    setEditStudentStream(mergedProfile.stream !== "N/A" ? mergedProfile.stream : '');
    setEditStudentSubjects(mergedProfile.subjects !== "N/A" ? mergedProfile.subjects : '');
    setEditStudentSecondLang(mergedProfile.secondLang !== "N/A" ? mergedProfile.secondLang : '');
    setEditStudentClassTeacher(mergedProfile.classTeacher !== "N/A" ? mergedProfile.classTeacher : '');
    setEditStudentHouse(mergedProfile.house !== "N/A" ? mergedProfile.house : '');
    setEditStudentPermanentAddress(mergedProfile.permanentAddress !== "N/A" ? mergedProfile.permanentAddress : '');
    setEditStudentCurrentAddress(mergedProfile.currentAddress !== "N/A" ? mergedProfile.currentAddress : '');
    setEditStudentModeOfTransport(student.transport_mode || mergedProfile.modeOfTransport || 'School Bus');
    setEditStudentAllergies(mergedProfile.allergies !== "None" ? mergedProfile.allergies : '');
    setEditStudentMedications(mergedProfile.medications !== "None" ? mergedProfile.medications : '');
    setEditStudentEmergencyContact(mergedProfile.emergencyContact !== "N/A" ? mergedProfile.emergencyContact : '');
    setEditStudentEmergencyPerson(mergedProfile.emergencyPerson !== "N/A" ? mergedProfile.emergencyPerson : '');
    setEditStudentClubs(mergedProfile.clubs !== "None" ? mergedProfile.clubs : '');
    setEditStudentSports(mergedProfile.sports !== "None" ? mergedProfile.sports : '');
    setEditStudentResponsibility(mergedProfile.responsibility !== "None" ? mergedProfile.responsibility : '');

    setIsEditingStudent(false);
    setEditStudentError('');
  };

  const handleUpdateStudentProfile = async () => {
    if (!editStudentFirstName.trim() || !editStudentLastName.trim()) {
      setEditStudentError('First name and last name are required.');
      return;
    }
    if (!editStudentParentId) {
      setEditStudentError('Parent is required.');
      return;
    }

    setSavingStudent(true);
    setEditStudentError('');

    // Prepare profile_data JSON object
    const profile_data = {
      rollNo: editStudentRollNo.trim() || 'N/A',
      dob: editStudentDob.trim() || 'N/A',
      gender: editStudentGender || 'N/A',
      bloodGroup: editStudentBloodGroup || 'N/A',
      aadhaar: editStudentAadhaar.trim() || 'N/A',
      grade: editStudentGrade.trim() || 'N/A',
      section: editStudentSection.trim() || 'N/A',
      board: editStudentBoard.trim() || 'N/A',
      stream: editStudentStream.trim() || 'N/A',
      subjects: editStudentSubjects.trim() || 'N/A',
      secondLang: editStudentSecondLang.trim() || 'N/A',
      classTeacher: editStudentClassTeacher.trim() || 'N/A',
      house: editStudentHouse.trim() || 'N/A',
      permanentAddress: editStudentPermanentAddress.trim() || 'N/A',
      currentAddress: editStudentCurrentAddress.trim() || 'N/A',
      modeOfTransport: editStudentModeOfTransport || 'School Bus',
      allergies: editStudentAllergies.trim() || 'None',
      medications: editStudentMedications.trim() || 'None',
      emergencyContact: editStudentEmergencyContact.trim() || 'N/A',
      emergencyPerson: editStudentEmergencyPerson.trim() || 'N/A',
      clubs: editStudentClubs.trim() || 'None',
      sports: editStudentSports.trim() || 'None',
      responsibility: editStudentResponsibility.trim() || 'None'
    };

    try {
      const res = await fetch(`${BACKEND_URL}/api/students/${selectedStudentProfile.studentData.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          parent_id: parseInt(editStudentParentId),
          first_name: editStudentFirstName.trim(),
          last_name: editStudentLastName.trim(),
          admission_no: editStudentAdmissionNo.trim() || null,
          pickup_stop_id: editStudentModeOfTransport === 'School Bus' && editStudentPickupStopId ? parseInt(editStudentPickupStopId) : null,
          drop_stop_id: editStudentModeOfTransport === 'School Bus' && editStudentDropStopId ? parseInt(editStudentDropStopId) : null,
          rfid_tag_id: editStudentRfidTagId.trim() || null,
          avatar: editStudentAvatar || null,
          profile_data,
          transport_mode: editStudentModeOfTransport === 'School Bus' ? 'School Bus' : 'No Transport'
        })
      });

      const data = await res.json();
      if (res.ok) {
        triggerAlert('Success', 'Student profile updated successfully!');
        setIsEditingStudent(false);
        // Refresh management data
        await fetchManagementData();
        // Update local selectedStudentProfile state
        const updatedStudent = {
          id: selectedStudentProfile.studentData.id,
          parent_id: parseInt(editStudentParentId),
          first_name: editStudentFirstName.trim(),
          last_name: editStudentLastName.trim(),
          admission_no: editStudentAdmissionNo.trim() || null,
          pickup_stop_id: editStudentModeOfTransport === 'School Bus' && editStudentPickupStopId ? parseInt(editStudentPickupStopId) : null,
          drop_stop_id: editStudentModeOfTransport === 'School Bus' && editStudentDropStopId ? parseInt(editStudentDropStopId) : null,
          rfid_tag_id: editStudentRfidTagId.trim() || null,
          avatar: editStudentAvatar || null,
          profile_data: JSON.stringify(profile_data),
          transport_mode: editStudentModeOfTransport === 'School Bus' ? 'School Bus' : 'No Transport'
        };
        // Re-enrich with details
        handleStudentClick(updatedStudent);
      } else {
        setEditStudentError(data.message || 'Failed to update student profile.');
      }
    } catch (err) {
      console.error(err);
      setEditStudentError('Network error while updating student profile.');
    } finally {
      setSavingStudent(false);
    }
  };

  const handleDeleteStudent = (id) => {
    triggerConfirm(
      'Confirm Delete',
      'Are you sure you want to delete this student profile permanently?',
      async () => {
        try {
          const res = await fetch(`${BACKEND_URL}/api/students/${id}`, {
            method: 'DELETE'
          });
          if (res.ok) {
            triggerAlert('Success', 'Student profile deleted successfully.');
            setSelectedStudentProfile(null);
            setIsEditingStudent(false);
            fetchManagementData();
          } else {
            triggerAlert('Error', 'Failed to delete student.', 'error');
          }
        } catch (err) {
          console.error(err);
          triggerAlert('Error', 'Error connecting to backend.', 'error');
        }
      },
      () => {},
      true
    );
  };

  const handleCreateStudent = async (e) => {
    e?.preventDefault();
    if (!editStudentFirstName.trim() || !editStudentLastName.trim()) {
      triggerAlert('Validation Error', 'First Name and Last Name are required.', 'warning');
      return;
    }
    if (!editStudentParentId) {
      triggerAlert('Validation Error', 'Please select or register a parent account first.', 'warning');
      return;
    }

    setSavingStudent(true);
    setEditStudentError('');

    // Prepare profile_data JSON object
    const profile_data = {
      rollNo: editStudentRollNo.trim() || 'N/A',
      dob: editStudentDob.trim() || 'N/A',
      gender: editStudentGender || 'N/A',
      bloodGroup: editStudentBloodGroup || 'N/A',
      aadhaar: editStudentAadhaar.trim() || 'N/A',
      grade: editStudentGrade.trim() || 'N/A',
      section: editStudentSection.trim() || 'N/A',
      board: editStudentBoard.trim() || 'N/A',
      stream: editStudentStream.trim() || 'N/A',
      subjects: editStudentSubjects.trim() || 'N/A',
      secondLang: editStudentSecondLang.trim() || 'N/A',
      classTeacher: editStudentClassTeacher.trim() || 'N/A',
      house: editStudentHouse.trim() || 'N/A',
      permanentAddress: editStudentPermanentAddress.trim() || 'N/A',
      currentAddress: editStudentCurrentAddress.trim() || 'N/A',
      modeOfTransport: editStudentModeOfTransport || 'School Bus',
      allergies: editStudentAllergies.trim() || 'None',
      medications: editStudentMedications.trim() || 'None',
      emergencyContact: editStudentEmergencyContact.trim() || 'N/A',
      emergencyPerson: editStudentEmergencyPerson.trim() || 'N/A',
      clubs: editStudentClubs.trim() || 'None',
      sports: editStudentSports.trim() || 'None',
      responsibility: editStudentResponsibility.trim() || 'None'
    };

    try {
      const res = await fetch(`${BACKEND_URL}/api/students`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          parent_id: parseInt(editStudentParentId),
          first_name: editStudentFirstName.trim(),
          last_name: editStudentLastName.trim(),
          admission_no: editStudentAdmissionNo.trim() || null,
          pickup_stop_id: editStudentModeOfTransport === 'School Bus' && editStudentPickupStopId ? parseInt(editStudentPickupStopId) : null,
          drop_stop_id: editStudentModeOfTransport === 'School Bus' && editStudentDropStopId ? parseInt(editStudentDropStopId) : null,
          rfid_tag_id: editStudentRfidTagId.trim() || null,
          avatar: editStudentAvatar || null,
          profile_data,
          transport_mode: editStudentModeOfTransport === 'School Bus' ? 'School Bus' : 'No Transport'
        })
      });

      const data = await res.json();
      if (res.ok) {
        triggerAlert('Success', 'Student profile created successfully!');
        setShowAddStudentModal(false);
        // Clear forms
        clearStudentFormFields();
        fetchManagementData();
      } else {
        triggerAlert('Error', 'Failed to create student: ' + (data.message || 'Unknown error'), 'error');
      }
    } catch (err) {
      console.error(err);
      triggerAlert('Error', 'Error creating student profile.', 'error');
    } finally {
      setSavingStudent(false);
    }
  };

  const clearStudentFormFields = () => {
    setEditStudentFirstName('');
    setEditStudentLastName('');
    setEditStudentAdmissionNo('');
    setEditStudentRfidTagId('');
    setEditStudentParentId('');
    setParentSearchText('');
    setShowParentDropdown(false);
    setEditStudentPickupStopId('');
    setEditStudentDropStopId('');
    setEditStudentAvatar('');
    setEditStudentRollNo('');
    setEditStudentDob('');
    setEditStudentGender('Male');
    setEditStudentBloodGroup('');
    setEditStudentAadhaar('');
    setEditStudentGrade('');
    setEditStudentSection('');
    setEditStudentBoard('');
    setEditStudentStream('');
    setEditStudentSubjects('');
    setEditStudentSecondLang('');
    setEditStudentClassTeacher('');
    setEditStudentHouse('');
    setEditStudentPermanentAddress('');
    setEditStudentCurrentAddress('');
    setEditStudentModeOfTransport('School Bus');
    setEditStudentAllergies('');
    setEditStudentMedications('');
    setEditStudentEmergencyContact('');
    setEditStudentEmergencyPerson('');
    setEditStudentClubs('');
    setEditStudentSports('');
    setEditStudentResponsibility('');
    setEditStudentError('');
  };

  const handleQuickAddParent = async (e) => {
    e?.preventDefault();
    if (!quickAddParentName.trim()) {
      setQuickAddParentError('Parent Name is required.');
      return;
    }
    const usernameVal = quickAddParentUsername.trim() || `parent_${Date.now().toString().slice(-6)}`;
    const passwordVal = quickAddParentPassword.trim() || 'parent123';
    
    setQuickAddParentError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: usernameVal,
          password: passwordVal,
          role: 'parent',
          displayName: quickAddParentName.trim(),
          phone: quickAddParentPhone.trim() || null
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        const newParent = {
          id: data.userId,
          username: usernameVal,
          role: 'parent',
          display_name: quickAddParentName.trim(),
          phone: quickAddParentPhone.trim() || 'N/A'
        };
        // Update local state list of users
        setAllUsers(prev => [...prev, newParent]);
        // Auto select this parent
        setEditStudentParentId(data.userId.toString());
        setParentSearchText(`${newParent.display_name} (${newParent.phone})`);
        
        // Reset states and close modal
        setShowQuickAddParentModal(false);
        setQuickAddParentName('');
        setQuickAddParentPhone('');
        setQuickAddParentUsername('');
        setQuickAddParentPassword('parent123');
        triggerAlert('Success', 'Parent registered and linked successfully.');
      } else {
        setQuickAddParentError(data.message || 'Failed to register parent.');
      }
    } catch (err) {
      console.error(err);
      setQuickAddParentError('Connection error registering parent.');
    }
  };

  const triggerAlert = (title, message, type = 'success') => {
    setModalConfig({
      type,
      title,
      message,
      onConfirm: () => setModalConfig(null)
    });
  };

  const triggerConfirm = (title, message, onConfirm, onCancel = () => {}, isDestructive = false) => {
    setModalConfig({
      type: 'confirm',
      title,
      message,
      isDestructive,
      onConfirm: () => {
        setModalConfig(null);
        onConfirm();
      },
      onCancel: () => {
        setModalConfig(null);
        onCancel();
      }
    });
  };

  const handleUpdateParentProfile = async () => {
    if (!editParentDisplayName.trim() || !editParentUsername.trim()) {
      setEditParentError('Name and Username are required.');
      return;
    }
    setSavingParent(true);
    setEditParentError('');

    const profile_data = {
      parentId: selectedParentProfile.parentId,
      occupation: editParentOccupation.trim() || 'Parent / Guardian',
      relationship: editParentRelationship || 'Guardian',
      spouseName: editParentSpouseName.trim() || 'N/A',
      altPhone: editParentAltPhone.trim() || editParentPhone.trim(),
      email: editParentEmail.trim() || '',
      address: editParentAddress.trim() || 'N/A',
      preferredLanguage: editParentPreferredLanguage || 'English',
      emergencyContact: editParentEmergencyContact.trim() || editParentPhone.trim(),
      pickupAuth: editParentPickupAuth || 'Authorized',
      smsEnabled: editParentSmsEnabled,
      pushEnabled: editParentPushEnabled,
      emailAlerts: editParentEmailAlerts,
      etaAlertSetting: editParentEtaAlertSetting
    };

    try {
      const payload = {
        username: editParentUsername.trim(),
        displayName: editParentDisplayName.trim(),
        phone: editParentPhone.trim() || null,
        profile_data,
        studentIds: editParentStudentIds
      };
      if (editParentPassword.trim()) {
        payload.password = editParentPassword.trim();
      }

      const res = await fetch(`${BACKEND_URL}/api/users/${selectedParentProfile.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok) {
        triggerAlert('Success', 'Parent profile updated successfully!');
        setIsEditingParent(false);
        const { studentsData } = await fetchManagementData();
        const updatedParent = {
          id: selectedParentProfile.id,
          username: editParentUsername.trim(),
          display_name: editParentDisplayName.trim(),
          phone: editParentPhone.trim(),
          profile_data
        };
        handleParentClick(updatedParent, studentsData);
      } else {
        setEditParentError(data.message || 'Failed to update parent profile.');
      }
    } catch (err) {
      console.error(err);
      setEditParentError('Network error updating parent profile.');
    } finally {
      setSavingParent(false);
    }
  };

  const handleDeleteParent = (id) => {
    triggerConfirm(
      'Confirm Delete',
      'Are you sure you want to permanently delete this parent profile? Linked student parent associations will be set to unassigned.',
      async () => {
        try {
          const res = await fetch(`${BACKEND_URL}/api/users/${id}`, {
            method: 'DELETE'
          });
          if (res.ok) {
            triggerAlert('Success', 'Parent profile deleted successfully.');
            setSelectedParentProfile(null);
            setIsEditingParent(false);
            fetchManagementData();
          } else {
            triggerAlert('Error', 'Failed to delete parent.', 'error');
          }
        } catch (err) {
          console.error(err);
          triggerAlert('Error', 'Error connecting to backend.', 'error');
        }
      },
      () => {},
      true
    );
  };

  const handleCreateParent = async (e) => {
    e?.preventDefault();
    if (!editParentDisplayName.trim() || !editParentUsername.trim() || !editParentPassword.trim()) {
      triggerAlert('Validation Error', 'Name, Username, and Password are required.', 'warning');
      return;
    }
    setSavingParent(true);
    
    const profile_data = {
      parentId: `#PRT-00${Date.now().toString().slice(-4)}`,
      occupation: editParentOccupation.trim() || 'Parent / Guardian',
      relationship: editParentRelationship || 'Guardian',
      spouseName: editParentSpouseName.trim() || 'N/A',
      altPhone: editParentAltPhone.trim() || editParentPhone.trim(),
      email: editParentEmail.trim() || '',
      address: editParentAddress.trim() || 'N/A',
      preferredLanguage: editParentPreferredLanguage || 'English',
      emergencyContact: editParentEmergencyContact.trim() || editParentPhone.trim(),
      pickupAuth: editParentPickupAuth || 'Authorized',
      smsEnabled: editParentSmsEnabled,
      pushEnabled: editParentPushEnabled,
      emailAlerts: editParentEmailAlerts,
      etaAlertSetting: editParentEtaAlertSetting
    };

    try {
      const res = await fetch(`${BACKEND_URL}/api/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: editParentUsername.trim(),
          password: editParentPassword.trim(),
          role: 'parent',
          displayName: editParentDisplayName.trim(),
          phone: editParentPhone.trim() || null,
          profile_data
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        triggerAlert('Success', 'Parent profile registered successfully!');
        setShowAddParentModal(false);
        clearParentFormFields();
        fetchManagementData();
      } else {
        triggerAlert('Error', 'Failed to register parent: ' + (data.message || 'Unknown error'), 'error');
      }
    } catch (err) {
      console.error(err);
      triggerAlert('Error', 'Error connecting to backend.', 'error');
    } finally {
      setSavingParent(false);
    }
  };

  const clearParentFormFields = () => {
    setEditParentDisplayName('');
    setEditParentPhone('');
    setEditParentUsername('');
    setEditParentPassword('');
    setEditParentAddress('');
    setEditParentAltPhone('');
    setEditParentEmail('');
    setEditParentOccupation('');
    setEditParentRelationship('Guardian');
    setEditParentSpouseName('');
    setEditParentEmergencyContact('');
    setEditParentPreferredLanguage('English');
    setEditParentPickupAuth('Authorized');
    setEditParentSmsEnabled(true);
    setEditParentPushEnabled(true);
    setEditParentEmailAlerts(false);
    setEditParentEtaAlertSetting('5 mins before arrival');
    setEditParentError('');
  };

  const handleParentClick = (parent, freshStudentsList = null) => {
    const parentName = (parent.display_name || parent.name || '').trim();
    const matchedProfile = parentProfilesData[parentName] ||
                           Object.entries(parentProfilesData).find(([k]) => k.toLowerCase() === parentName.toLowerCase())?.[1];
    const avatar = getParentAvatar(parent);
    
    // Find all linked students for this parent
    const studentsSource = freshStudentsList || allStudents;
    const linkedStudents = studentsSource.filter(s => 
      s.parent_id === parent.id || 
      (s.parent_name && s.parent_name.toLowerCase() === parentName.toLowerCase()) ||
      (s.parent_phone && parent.phone && s.parent_phone.replace(/\D/g, '') === parent.phone.replace(/\D/g, ''))
    );

    const email = parent.email || (matchedProfile && matchedProfile.email) || `${parent.username || parentName.toLowerCase().replace(/\s+/g, '.')}@school.com`;
    const phone = parent.phone || (matchedProfile && matchedProfile.phone) || 'N/A';

    let parsedProfile = {};
    if (parent.profile_data) {
      try {
        parsedProfile = typeof parent.profile_data === 'string' ? JSON.parse(parent.profile_data) : parent.profile_data;
      } catch (err) {
        console.error('Error parsing parent profile_data:', err);
      }
    }

    const mergedParentProfile = {
      id: parent.id,
      parentId: parsedProfile.parentId || (matchedProfile && matchedProfile.parentId) || `#PRT-00${parent.id}`,
      name: parent.display_name || parentName || 'Parent / Guardian',
      avatar: avatar,
      occupation: parsedProfile.occupation || (matchedProfile && matchedProfile.occupation) || 'Parent / Guardian',
      relationship: parsedProfile.relationship || (matchedProfile && matchedProfile.relationship) || 'Guardian',
      spouseName: parsedProfile.spouseName || (matchedProfile && matchedProfile.spouseName) || 'N/A',
      phone: parent.phone || phone,
      altPhone: parsedProfile.altPhone || (matchedProfile && matchedProfile.altPhone) || phone,
      email: parsedProfile.email || email,
      address: parsedProfile.address || (matchedProfile && matchedProfile.address) || 'Banaswadi, Bangalore - 560043',
      preferredLanguage: parsedProfile.preferredLanguage || (matchedProfile && matchedProfile.preferredLanguage) || 'English',
      emergencyContact: parsedProfile.emergencyContact || (matchedProfile && matchedProfile.emergencyContact) || phone,
      pickupAuth: parsedProfile.pickupAuth || (matchedProfile && matchedProfile.pickupAuth) || 'Authorized for registered guardians',
      notificationStatus: parsedProfile.notificationStatus || (matchedProfile && matchedProfile.notificationStatus) || 'ACTIVE',
      smsEnabled: parsedProfile.smsEnabled !== undefined ? parsedProfile.smsEnabled : (matchedProfile ? matchedProfile.smsEnabled !== false : true),
      pushEnabled: parsedProfile.pushEnabled !== undefined ? parsedProfile.pushEnabled : (matchedProfile ? matchedProfile.pushEnabled !== false : true),
      emailAlerts: parsedProfile.emailAlerts !== undefined ? parsedProfile.emailAlerts : (matchedProfile ? matchedProfile.emailAlerts !== false : true),
      etaAlertSetting: parsedProfile.etaAlertSetting || (matchedProfile && matchedProfile.etaAlertSetting) || '5 mins before arrival',
      linkedStudents: linkedStudents,
      userData: parent,
      documents: [
        { name: `Parent ID Proof - ${parentName}`, meta: "PDF • 1.1 MB • Verified" },
        { name: "Child Pickup Authorization Form", meta: "PDF • 480 KB • Active" }
      ]
    };

    setSelectedParentProfile(mergedParentProfile);

    // Initialize edit parent states
    setEditParentDisplayName(parent.display_name || '');
    setEditParentPhone(parent.phone || '');
    setEditParentUsername(parent.username || '');
    setEditParentPassword('');

    setEditParentAddress(mergedParentProfile.address || '');
    setEditParentAltPhone(mergedParentProfile.altPhone || '');
    setEditParentEmail(mergedParentProfile.email || '');
    setEditParentOccupation(mergedParentProfile.occupation || '');
    setEditParentRelationship(mergedParentProfile.relationship || 'Guardian');
    setEditParentSpouseName(mergedParentProfile.spouseName || '');
    setEditParentEmergencyContact(mergedParentProfile.emergencyContact || '');
    setEditParentPreferredLanguage(mergedParentProfile.preferredLanguage || 'English');
    setEditParentPickupAuth(mergedParentProfile.pickupAuth || 'Authorized');
    setEditParentSmsEnabled(mergedParentProfile.smsEnabled);
    setEditParentPushEnabled(mergedParentProfile.pushEnabled);
    setEditParentEmailAlerts(mergedParentProfile.emailAlerts);
    setEditParentEtaAlertSetting(mergedParentProfile.etaAlertSetting);
    setEditParentStudentIds(linkedStudents.map(s => s.id));

    setIsEditingParent(false);
    setEditParentError('');
  };

  const generateNextDriverId = () => {
    let maxNum = 0;
    Object.values(driverProfiles).forEach(p => {
      if (p.driverId) {
        const match = p.driverId.match(/DVR-0*(\d+)/);
        if (match) {
          const num = parseInt(match[1]);
          if (num > maxNum) maxNum = num;
        }
      }
    });
    const nextNum = maxNum + 1;
    return `DVR-${String(nextNum).padStart(3, '0')}`;
  };

  const handleDriverClick = (driver) => {
    let dbProfileData = {};
    if (driver.profile_data) {
      try {
        dbProfileData = typeof driver.profile_data === 'string' ? JSON.parse(driver.profile_data) : driver.profile_data;
      } catch (e) {
        console.error('Failed to parse driver.profile_data', e);
      }
    }

    const defaultProfile = driverProfiles[driver.display_name] ||
      driverProfilesData[driver.display_name] ||
      Object.entries(driverProfilesData).find(([k]) => k.toLowerCase() === (driver.display_name || '').toLowerCase())?.[1] ||
      {};

    const profile = {
      avatar: defaultProfile.avatar || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&q=80&w=200",
      driverId: dbProfileData.driverId || defaultProfile.driverId || `DVR-00${driver.id}`,
      name: driver.display_name,
      phone: driver.phone || dbProfileData.phone || defaultProfile.phone || "N/A",
      address: dbProfileData.address || defaultProfile.address || "Bangalore, Karnataka, India",
      licensingStatus: dbProfileData.licensingStatus || defaultProfile.licensingStatus || "VALID LICENSE",
      licenseNumber: dbProfileData.licenseNumber || defaultProfile.licenseNumber || "DL-0420180098231",
      licenseExpiry: dbProfileData.licenseExpiry || defaultProfile.licenseExpiry || "Oct 15, 2028",
      medicalStatus: dbProfileData.medicalStatus || defaultProfile.medicalStatus || "Certified Fit",
      medicalExpiry: dbProfileData.medicalExpiry || defaultProfile.medicalExpiry || "Aug 2027",
      policeVerification: dbProfileData.policeVerification || defaultProfile.policeVerification || "Cleared & Verified",
      bloodGroup: dbProfileData.bloodGroup || defaultProfile.bloodGroup || "O+ Positive",
      experienceYears: dbProfileData.experienceYears || defaultProfile.experienceYears || "8+ Years",
      safetyScore: dbProfileData.safetyScore || defaultProfile.safetyScore || "99.4%",
      onTimeScore: dbProfileData.onTimeScore || defaultProfile.onTimeScore || "98.8%",
      assignedBusNumber: dbProfileData.assignedBusNumber || defaultProfile.assignedBusNumber || "Bus 10A",
      assignedBusReg: dbProfileData.assignedBusReg || defaultProfile.assignedBusReg || "KA-03-AA-1234",
      assignedRoute: dbProfileData.assignedRoute || defaultProfile.assignedRoute || "Primary School Route",
      status: dbProfileData.status || defaultProfile.status || "ACTIVE",
      documents: dbProfileData.documents || defaultProfile.documents || []
    };
    profile.id = driver.id;
    profile.username = driver.username;
    setSelectedDriverProfile(profile);
  };

  const handleUploadDriverDocument = (driverName, fileName, fileSize, docType = 'Driving License') => {
    const updatedProfiles = { ...driverProfiles };
    if (!updatedProfiles[driverName]) {
      updatedProfiles[driverName] = {
        avatar: "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&q=80&w=200",
        driverId: "DVR-NEW",
        name: driverName,
        phone: "N/A",
        address: "Not Provided",
        licensingStatus: "VALID LICENSE",
        licenseExpiry: "Oct 15, 2028",
        status: "ACTIVE",
        documents: []
      };
    }
    
    const docMeta = `${docType} • PDF • ${fileSize} • Uploaded Just Now`;
    const newDoc = { name: `${docType} (${fileName})`, meta: docMeta };
    
    updatedProfiles[driverName] = {
      ...updatedProfiles[driverName],
      documents: [...(updatedProfiles[driverName].documents || []), newDoc]
    };
    
    setDriverProfiles(updatedProfiles);
    setSelectedDriverProfile(updatedProfiles[driverName]);
  };

  const updateDelay = async (tripId, delayVal) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/trips/${tripId}/delay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ delayMinutes: parseInt(delayVal) })
      });
      const data = await res.json();
      if (data.success) {
        alert('Delay configuration updated and broadcasted.');
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleBusClick = (bus) => {
    const busNum = (bus.bus_number || '').trim();
    const matched = busProfilesData[busNum] || 
                    Object.entries(busProfilesData).find(([k]) => k.toLowerCase() === busNum.toLowerCase())?.[1] || {};

    const assignedTrip = allTrips.find(t => t.bus_id === bus.id || t.bus_number === bus.bus_number);
    const assignedDriverObj = allUsers.find(u => (u.role === 'driver' || u.role === 'DRIVER') && (u.id === assignedTrip?.driver_id || u.display_name === assignedTrip?.driver_name));
    const assignedRouteObj = allRoutes.find(r => r.id === assignedTrip?.route_id || r.route_name === assignedTrip?.route_name);

    const enrichedProfile = {
      id: bus.id,
      busNumber: bus.bus_number || `Bus ${bus.id}`,
      registration: bus.registration || matched.registration || `KA-03-XX-${1000 + bus.id}`,
      makeModel: matched.makeModel || `${bus.capacity > 40 ? 'Tata Starbus Ultra' : 'Ashok Leyland Sunshine'} ${bus.capacity || 40}L`,
      modelYear: matched.modelYear || "2023",
      capacity: bus.capacity || matched.capacity || 40,
      fuelType: matched.fuelType || "Diesel (BS-VI)",
      ownershipType: bus.ownershipType || matched.ownershipType || "School Owned",
      engineNo: matched.engineNo || `ENG-${8000 + bus.id}-TCIC`,
      chassisNo: matched.chassisNo || `MAT-426-031-NA-${88420 + bus.id}`,
      status: bus.status || matched.status || 'active',
      odometer: matched.odometer || `${(18000 + bus.id * 3200).toLocaleString()} km`,
      fuelMileage: matched.fuelMileage || "5.8 km/L",
      assignedRoute: assignedTrip?.route_name || assignedRouteObj?.route_name || matched.assignedRoute || "Route 1 - Kalyan Nagar Express",
      routeDirection: assignedTrip?.direction || matched.routeDirection || "Morning Pickup & Afternoon Drop",
      assignedDriver: assignedTrip?.driver_name || assignedDriverObj?.display_name || matched.assignedDriver || "Ramesh Kumar",
      driverPhone: assignedDriverObj?.phone || matched.driverPhone || "+91 9777777771",
      driverId: matched.driverId || `DVR-00${assignedDriverObj?.id || 4}`,
      gpsImei: matched.gpsImei || `86229104033${bus.id}91`,
      gpsDeviceModel: matched.gpsDeviceModel || "Teltonika FMB920 Dual SIM",
      gpsSimNumber: matched.gpsSimNumber || `+91 98450 ${10000 + bus.id} (Airtel IoT)`,
      gpsStatus: matched.gpsStatus || "Online (Signal: Strong 4G)",
      speedGovernor: matched.speedGovernor || "Active (Speed Ceiling: 40 km/h)",
      cctvSystem: matched.cctvSystem || "4-Channel HD DVR (Cabin, Entry, Front, Rear)",
      safetyFeatures: matched.safetyFeatures || ["Emergency Exit Window", "SOS Panic Push Button", "Fire Extinguisher (5kg ABC)", "Certified First Aid Kit"],
      insuranceProvider: matched.insuranceProvider || "National Insurance Co. Ltd.",
      insurancePolicyNo: matched.insurancePolicyNo || `NIC-SCH-${880000 + bus.id}-2026`,
      insuranceExpiry: matched.insuranceExpiry || "Nov 14, 2027",
      fitnessExpiry: matched.fitnessExpiry || "Sep 20, 2027",
      pucExpiry: matched.pucExpiry || "Oct 15, 2027",
      roadPermitExpiry: matched.roadPermitExpiry || "Dec 31, 2028",
      roadTaxStatus: matched.roadTaxStatus || "Lifetime Paid (Karnataka RTO)",
      lastServiceDate: matched.lastServiceDate || "Aug 10, 2026",
      nextServiceDue: matched.nextServiceDue || "Nov 10, 2026 (or at 35,000 km)",
      serviceHistory: matched.serviceHistory || [
        { date: "Aug 10, 2026", odometer: "28,000 km", type: "Periodic Service", notes: "Engine oil change, air filter replacement, brake pads inspection." },
        { date: "May 04, 2026", odometer: "22,500 km", type: "Safety Inspection", notes: "Speed governor recalibration and CCTV hard drive diagnostic." }
      ],
      documents: [
        { name: `Registration Certificate (RC) - ${bus.bus_number}`, type: "RC", format: "PDF", size: "1.4 MB", expiry: matched.fitnessExpiry || "Sep 2027", status: "Active" },
        { name: `Commercial Vehicle Insurance - ${bus.bus_number}`, type: "Insurance", format: "PDF", size: "2.1 MB", expiry: matched.insuranceExpiry || "Nov 2027", status: "Active" },
        { name: `RTO Vehicle Fitness Certificate`, type: "Fitness", format: "PDF", size: "950 KB", expiry: matched.fitnessExpiry || "Sep 2027", status: "Active" },
        { name: `Pollution Under Control (PUC)`, type: "PUC", format: "PDF", size: "620 KB", expiry: matched.pucExpiry || "Oct 2027", status: "Active" },
        { name: `All-State School Bus Road Permit`, type: "Permit", format: "PDF", size: "1.1 MB", expiry: matched.roadPermitExpiry || "Dec 2028", status: "Active" }
      ]
    };

    setSelectedBusProfile(enrichedProfile);
    setBusProfileActiveTab('overview');
  };

  const handleCreateConsoleUser = async (e) => {
    e?.preventDefault();
    setAddConsoleUserError('');
    if (!newConsoleUserUsername.trim() || !newConsoleUserPassword || !newConsoleUserDisplayName.trim()) {
      setAddConsoleUserError('Full Name, Username, and Password are required.');
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: newConsoleUserUsername.trim(),
          password: newConsoleUserPassword,
          role: newConsoleUserRole,
          displayName: newConsoleUserDisplayName.trim(),
          phone: newConsoleUserPhone.trim() || null
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        const created = {
          id: data.userId,
          username: newConsoleUserUsername.trim(),
          role: newConsoleUserRole,
          display_name: newConsoleUserDisplayName.trim(),
          phone: newConsoleUserPhone.trim() || null
        };
        setAllUsers(prev => [...prev, created]);
        setShowAddConsoleUserModal(false);
        setNewConsoleUserUsername('');
        setNewConsoleUserPassword('');
        setNewConsoleUserDisplayName('');
        setNewConsoleUserPhone('');
        triggerAlert('User Created', `Console user "${created.display_name}" created with role ${newConsoleUserRole === 'route_manager' ? 'Route Manager' : newConsoleUserRole === 'viewer_guard' ? 'Viewer / Guard' : 'System Admin'}!`);
      } else {
        setAddConsoleUserError(data.message || 'Failed to create console user.');
      }
    } catch (err) {
      setAddConsoleUserError('Server error: ' + err.message);
    }
  };

  const handleDeleteConsoleUser = (userId, displayName) => {
    triggerConfirm(
      'Delete Console User',
      `Are you sure you want to delete administrative user "${displayName}"? This action cannot be undone.`,
      async () => {
        try {
          const res = await fetch(`${BACKEND_URL}/api/users/${userId}`, { method: 'DELETE' });
          const data = await res.json();
          if (res.ok && data.success) {
            setAllUsers(prev => prev.filter(u => u.id !== userId));
            triggerAlert('User Deleted', `Console user "${displayName}" deleted successfully.`);
          } else {
            triggerAlert('Error', data.message || 'Failed to delete user.', 'error');
          }
        } catch (err) {
          triggerAlert('Error', 'Server error deleting user: ' + err.message, 'error');
        }
      },
      () => {},
      true
    );
  };

  const handleCreateRole = async (e) => {
    e?.preventDefault();
    setAddRoleError('');
    if (!newRoleName.trim()) {
      setAddRoleError('Role Name is required.');
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/roles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role_name: newRoleName.trim(),
          role_key: newRoleKey.trim() || undefined,
          description: newRoleDesc.trim(),
          ...newRolePermissions
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setRoles(prev => [...prev, data.role]);
        setShowAddRoleModal(false);
        setNewRoleName('');
        setNewRoleKey('');
        setNewRoleDesc('');
        setNewRolePermissions({
          edit_routes: false,
          view_gps: true,
          prune_logs: false,
          manage_fleet: false,
          manage_users: false,
          access_documents: false
        });
        triggerAlert('Role Created', `Role "${data.role.role_name}" has been created with custom ACL permissions!`);
      } else {
        setAddRoleError(data.message || 'Failed to create role.');
      }
    } catch (err) {
      setAddRoleError('Server error creating role: ' + err.message);
    }
  };

  const handleOpenEditRole = (roleItem) => {
    setEditingRole(roleItem);
    setEditRoleName(roleItem.role_name);
    setEditRoleDesc(roleItem.description || '');
    setEditRolePermissions({
      edit_routes: Boolean(roleItem.edit_routes),
      view_gps: Boolean(roleItem.view_gps),
      prune_logs: Boolean(roleItem.prune_logs),
      manage_fleet: Boolean(roleItem.manage_fleet),
      manage_users: Boolean(roleItem.manage_users),
      access_documents: Boolean(roleItem.access_documents)
    });
    setEditRoleError('');
    setShowEditRoleModal(true);
  };

  const handleSaveEditRole = async (e) => {
    e?.preventDefault();
    if (!editingRole) return;
    setEditRoleError('');
    if (!editRoleName.trim()) {
      setEditRoleError('Role Name is required.');
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/roles/${editingRole.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role_name: editRoleName.trim(),
          description: editRoleDesc.trim(),
          ...editRolePermissions
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setRoles(prev => prev.map(r => r.id === editingRole.id ? data.role : r));
        setShowEditRoleModal(false);
        setEditingRole(null);
        triggerAlert('Role Updated', `Permissions for role "${data.role.role_name}" have been updated successfully!`);
      } else {
        setEditRoleError(data.message || 'Failed to update role.');
      }
    } catch (err) {
      setEditRoleError('Server error updating role: ' + err.message);
    }
  };

  const handleDeleteRole = (roleItem) => {
    if (roleItem.is_system || roleItem.role_key === 'admin') {
      triggerAlert('Protected Role', 'Default system roles cannot be deleted.', 'warning');
      return;
    }
    triggerConfirm(
      'Delete Role',
      `Are you sure you want to delete role "${roleItem.role_name}"? This action cannot be undone.`,
      async () => {
        try {
          const res = await fetch(`${BACKEND_URL}/api/roles/${roleItem.id}`, { method: 'DELETE' });
          const data = await res.json();
          if (res.ok && data.success) {
            setRoles(prev => prev.filter(r => r.id !== roleItem.id));
            triggerAlert('Role Deleted', `Role "${roleItem.role_name}" was deleted successfully.`);
          } else {
            triggerAlert('Error', data.message || 'Failed to delete role.', 'error');
          }
        } catch (err) {
          triggerAlert('Error', 'Server error deleting role: ' + err.message, 'error');
        }
      },
      () => {},
      true
    );
  };

  const handleParentPasswordReset = async (u) => {
    const newPassword = window.prompt(`Enter new password for ${u.display_name} (leave blank to cancel):`);
    if (!newPassword || !newPassword.trim()) return;
    
    try {
      const payload = {
        displayName: u.display_name,
        username: u.username,
        role: u.role,
        phone: u.phone || null,
        password: newPassword.trim(),
        profile_data: u.profile_data
      };

      const res = await fetch(`${BACKEND_URL}/api/users/${u.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        triggerAlert('Success', `Password reset successfully for ${u.display_name}`);
      } else {
        triggerAlert('Error', data.message || 'Failed to reset password', 'error');
      }
    } catch (err) {
      triggerAlert('Error', 'Server error: ' + err.message, 'error');
    }
  };

  const handleOpenEditConsoleUser = (u) => {
    setEditingConsoleUser(u);
    setEditConsoleUserDisplayName(u.display_name || '');
    setEditConsoleUserUsername(u.username || '');
    setEditConsoleUserRole(u.role || 'route_manager');
    setEditConsoleUserPhone(u.phone || '');
    setEditConsoleUserPassword('');
    setEditConsoleUserError('');
    setShowEditConsoleUserModal(true);
  };

  const handleSaveEditConsoleUser = async (e) => {
    e?.preventDefault();
    if (!editingConsoleUser) return;
    setEditConsoleUserError('');
    if (!editConsoleUserDisplayName.trim() || !editConsoleUserUsername.trim()) {
      setEditConsoleUserError('Full Name and Username are required.');
      return;
    }
    try {
      const payload = {
        displayName: editConsoleUserDisplayName.trim(),
        username: editConsoleUserUsername.trim(),
        role: editConsoleUserRole,
        phone: editConsoleUserPhone.trim() || null
      };
      if (editConsoleUserPassword.trim()) {
        payload.password = editConsoleUserPassword.trim();
      }
      const res = await fetch(`${BACKEND_URL}/api/users/${editingConsoleUser.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setAllUsers(prev => prev.map(u => u.id === editingConsoleUser.id ? {
          ...u,
          display_name: editConsoleUserDisplayName.trim(),
          username: editConsoleUserUsername.trim(),
          role: editConsoleUserRole,
          phone: editConsoleUserPhone.trim() || null
        } : u));
        setShowEditConsoleUserModal(false);
        setEditingConsoleUser(null);
        triggerAlert('User Updated', `Console user "${editConsoleUserDisplayName}" updated successfully!`);
      } else {
        setEditConsoleUserError(data.message || 'Failed to update user.');
      }
    } catch (err) {
      setEditConsoleUserError('Server error updating user: ' + err.message);
    }
  };

  const handleCreateBus = async (e) => {
    e?.preventDefault();
    if (!newBusData.bus_number || !newBusData.registration) {
      alert('Please provide Bus Number and Registration Certificate plate.');
      return;
    }
    setSavingBus(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/buses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bus_number: newBusData.bus_number.trim(),
          registration: newBusData.registration.trim().toUpperCase(),
          capacity: parseInt(newBusData.capacity) || 40,
          status: newBusData.status || 'active'
        })
      });
      const data = await res.json();
      if (res.ok) {
        alert('Bus added to fleet successfully!');
        setShowAddBusModal(false);
        setNewBusData({ bus_number: '', registration: '', capacity: 40, status: 'active', makeModel: '', assignedDriver: '', assignedRoute: '' });
        fetchManagementData();
      } else {
        alert('Failed to create bus: ' + (data.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error creating bus:', err);
      // Fallback update local state
      const mockId = allBuses.length + 1;
      const createdBus = {
        id: mockId,
        bus_number: newBusData.bus_number.trim(),
        registration: newBusData.registration.trim().toUpperCase(),
        capacity: parseInt(newBusData.capacity) || 40,
        status: newBusData.status || 'active'
      };
      setAllBuses([...allBuses, createdBus]);
      setShowAddBusModal(false);
      setNewBusData({ bus_number: '', registration: '', capacity: 40, status: 'active', makeModel: '', assignedDriver: '', assignedRoute: '' });
      alert('Bus added to fleet successfully!');
    } finally {
      setSavingBus(false);
    }
  };

  const handleDeleteBus = async (id) => {
    if (!window.confirm('Are you sure you want to remove this bus from the fleet?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/buses/${id}`, { method: 'DELETE' });
      if (res.ok) {
        alert('Bus removed from fleet.');
        fetchManagementData();
        if (selectedBusProfile && selectedBusProfile.id === id) {
          setSelectedBusProfile(null);
        }
      }
    } catch (err) {
      setAllBuses(allBuses.filter(b => b.id !== id));
      alert('Bus removed from fleet.');
      if (selectedBusProfile && selectedBusProfile.id === id) {
        setSelectedBusProfile(null);
      }
    }
  };

  // Custom DivIcons for Leaflet Map
  const getStopIcon = (seq) => L.divIcon({
    html: `<div style="background-color: #1E2238; border: 2.5px solid white; border-radius: 50%; width: 26px; height: 26px; display: flex; align-items: center; justify-content: center; font-size: 11px; color: white; font-weight: 750; box-shadow: 0 4px 10px rgba(30,34,56,0.25);">${seq}</div>`,
    className: 'custom-stop-marker',
    iconSize: [26, 26],
    iconAnchor: [13, 13]
  });

  const getSchoolIcon = () => L.divIcon({
    html: `<div style="background-color: #FF7B72; border: 2.5px solid white; border-radius: 50%; width: 34px; height: 34px; display: flex; align-items: center; justify-content: center; font-size: 16px; box-shadow: 0 4px 12px rgba(255,123,114,0.35);">🏫</div>`,
    className: 'custom-school-marker',
    iconSize: [34, 34],
    iconAnchor: [17, 17]
  });

  const getBusIcon = () => L.divIcon({
    html: `<div style="background-color: #2EAEA0; border: 2.5px solid white; border-radius: 50%; width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; font-size: 20px; box-shadow: 0 6px 16px rgba(46,174,160,0.4); animation: pulse-red 2.5s infinite;">🚌</div>`,
    className: 'custom-bus-marker',
    iconSize: [36, 36],
    iconAnchor: [18, 18]
  });

  // Calculate default map parameters
  const getMapCenter = () => {
    if (selectedTrip && busPositions[selectedTrip.id]) {
      const pos = busPositions[selectedTrip.id];
      return [pos.lat, pos.lng];
    }
    if (selectedTripStops.length > 0) {
      // Find average of stop locations
      const lats = selectedTripStops.map(s => s.lat);
      const lngs = selectedTripStops.map(s => s.lng);
      const avgLat = lats.reduce((a, b) => a + b, 0) / lats.length;
      const avgLng = lngs.reduce((a, b) => a + b, 0) / lngs.length;
      return [avgLat, avgLng];
    }
    return [13.01, 77.64]; // Default Bangalore center
  };

  const getRoutePolyline = () => {
    return selectedTripStops.map(s => [s.lat, s.lng]);
  };

  if (user && user.role === 'parent') {
    return <ParentPortal user={user} onLogout={handleLogout} BACKEND_URL={BACKEND_URL} />;
  }

  if (!user) {
    return (
      <div className="login-container">
        <form className="login-card" onSubmit={handleLogin}>
          <div className="brand-header">
            <div className="brand-logo"><Bus size={32} /> BusBuddy</div>
            <div className="brand-subtitle">Control Room Admin Dashboard</div>
          </div>
          {loginError && <div style={{ color: 'var(--coral-red)', fontSize: '13px', marginBottom: '8px', textAlign: 'center', fontWeight: '500' }}>{loginError}</div>}
          <div className="form-group">
            <label>Username</label>
            <input type="text" value={username} onChange={e => setUsername(e.target.value)} required placeholder="Enter admin username" />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required placeholder="Enter password" />
          </div>
          <button type="submit" className="login-btn">Sign In to Dashboard</button>
          
          <div className="server-settings-toggle" style={{ marginTop: '16px', textAlign: 'center', fontSize: '12px' }}>
            <span 
              onClick={() => setShowServerSettings(!showServerSettings)} 
              style={{ color: '#2563EB', cursor: 'pointer', textDecoration: 'underline' }}
            >
              {showServerSettings ? 'Hide Server Settings' : 'Configure Server Connection'}
            </span>
          </div>
          {showServerSettings && (
            <div style={{ marginTop: '12px', borderTop: '1px solid #E5E7EB', paddingTop: '12px', textAlign: 'left' }}>
              <div className="form-group">
                <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#4B5563' }}>Backend API URL</label>
                <input 
                  type="text" 
                  value={serverUrlInput} 
                  onChange={e => setServerUrlInput(e.target.value)} 
                  placeholder="e.g. https://your-backend.onrender.com" 
                  style={{ width: '100%', padding: '6px', fontSize: '12px', border: '1px solid #D1D5DB', borderRadius: '4px', marginTop: '4px' }}
                />
              </div>
              <button 
                type="button" 
                onClick={handleSaveServerUrl} 
                style={{ width: '100%', padding: '8px', fontSize: '12px', backgroundColor: '#4B5563', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginTop: '6px', fontWeight: 'bold' }}
              >
                Save & Reload Connection
              </button>
            </div>
          )}
        </form>
      </div>
    );
  }

  return (
    <div className="admin-container">
      {/* SOS EMERGENCY DIALOG MODAL */}
      {sosAlert && (
        <div className="sos-overlay">
          <div className="sos-modal">
            <ShieldAlert size={64} color="var(--coral-red)" style={{ margin: '0 auto' }} />
            <div className="sos-title">SOS PANIC ALARM</div>
            <div className="sos-msg">
              <strong>Bus:</strong> {sosAlert.busNumber} <br />
              <strong>Driver:</strong> {sosAlert.driverName} <br />
              <strong>Message:</strong> {sosAlert.message} <br />
              <strong>Reported At:</strong> {new Date(sosAlert.timestamp).toLocaleTimeString()}
            </div>
            <button className="sos-dismiss-btn" onClick={() => setSosAlert(null)}>Acknowledge & Dismiss</button>
          </div>
        </div>
      )}

      {/* CSV IMPORT DIALOG MODAL */}
      {showCSVModal && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="modal-container" style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', width: '600px', maxWidth: '90%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,0.12)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', borderBottom: '1px solid var(--border-color)', paddingBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--text-primary)', textAlign: 'left' }}>📥 Import Students via CSV</h3>
              <button onClick={() => { setShowCSVModal(false); setCsvRows([]); setCsvHeaders([]); setImportResult(null); }} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: 'var(--text-secondary)' }}>&times;</button>
            </div>

            {csvRows.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', border: '2px dashed #CBD5E1', borderRadius: '8px', backgroundColor: 'var(--bg-alt)' }}>
                <div style={{ fontSize: '32px', marginBottom: '6px' }}>📊</div>
                <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#475569' }}>Select a CSV file containing your student and parent records.</p>
                <div style={{ marginBottom: '20px' }}>
                  <a 
                    href="/student_import_template.csv" 
                    download="student_import_template.csv" 
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#2563EB', textDecoration: 'none', fontWeight: '500', padding: '6px 12px', borderRadius: '6px', backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE' }}
                  >
                    <span>📋</span> Download CSV Template
                  </a>
                </div>
                <input 
                  type="file" 
                  accept=".csv" 
                  onChange={handleCSVUpload} 
                  id="csv-file-input" 
                  style={{ display: 'none' }}
                />
                <label 
                  htmlFor="csv-file-input" 
                  style={{ backgroundcolor: 'var(--text-primary)', color: 'white', padding: '10px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', display: 'inline-block' }}
                >
                  Choose CSV File
                </label>
              </div>
            ) : (
              <div>
                {!importResult ? (
                  <div>
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: 'var(--text-secondary)', textAlign: 'left' }}>Map CSV Columns to Fields</h4>
                    <p style={{ margin: '0 0 16px 0', fontSize: '12px', color: 'var(--text-secondary)', textAlign: 'left' }}>Specify which columns in your CSV match the required student details.</p>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: '12px', marginBottom: '20px', background: '#F8FAFC', padding: '16px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                      {[
                        { field: 'first_name', label: 'First Name *' },
                        { field: 'last_name', label: 'Last Name *' },
                        { field: 'parent_phone', label: 'Parent Phone *' },
                        { field: 'parent_name', label: 'Parent Full Name' },
                        { field: 'rfid_tag_id', label: 'RFID Card Tag ID' },
                        { field: 'pickup_stop_name', label: 'Pickup Stop Name' },
                        { field: 'drop_stop_name', label: 'Drop-off Stop Name' }
                      ].map(f => (
                        <React.Fragment key={f.field}>
                          <label style={{ fontSize: '13px', fontWeight: '500', color: '#475569', display: 'flex', alignItems: 'center', textAlign: 'left' }}>{f.label}</label>
                          <select 
                            value={columnMapping[f.field] || ''} 
                            onChange={(e) => setColumnMapping({ ...columnMapping, [f.field]: e.target.value })}
                            style={{ padding: '8px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '13px', backgroundColor: 'white' }}
                          >
                            <option value="">-- Skip / Unmapped --</option>
                            {csvHeaders.map(h => (
                              <option key={h} value={h}>{h}</option>
                            ))}
                          </select>
                        </React.Fragment>
                      ))}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Parsed {csvRows.length} data rows from file.</span>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button 
                          onClick={() => { setCsvRows([]); setCsvHeaders([]); }}
                          style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #CBD5E1', backgroundColor: 'var(--bg-hover)', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}
                        >
                          Reset File
                        </button>
                        <button 
                          onClick={submitCSVImport}
                          disabled={importing}
                          style={{ padding: '8px 20px', borderRadius: '6px', border: 'none', backgroundcolor: 'var(--text-primary)', color: 'white', fontSize: '12px', fontWeight: 'bold', cursor: importing ? 'not-allowed' : 'pointer' }}
                        >
                          {importing ? 'Importing...' : 'Start Import'}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ padding: '16px', borderRadius: '8px', backgroundColor: importResult.success ? '#F0FDF4' : '#FEF2F2', border: importResult.success ? '1px solid #BBF7D0' : '1px solid #FCA5A5', marginBottom: '20px', textAlign: 'left' }}>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '15px', color: importResult.success ? '#15803D' : '#B91C1C' }}>
                        {importResult.success ? '🎉 Import Completed Successfully!' : '⚠️ Import Completed with Errors'}
                      </h4>
                      <p style={{ margin: 0, fontSize: '13px', color: '#475569' }}>
                        Processed <strong>{importResult.total_processed}</strong> rows: <strong>{importResult.imported_count}</strong> students imported/updated, <strong>{importResult.error_count}</strong> errors.
                      </p>
                    </div>

                    {importResult.errors && importResult.errors.length > 0 && (
                      <div style={{ marginBottom: '20px', textAlign: 'left' }}>
                        <h5 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#991B1B' }}>Error Log:</h5>
                        <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #FCA5A5', borderRadius: '6px', padding: '10px', backgroundColor: '#FEF2F2' }}>
                          {importResult.errors.map((e, idx) => (
                            <div key={idx} style={{ fontSize: '12px', color: '#991B1B', marginBottom: '6px', borderBottom: '1px solid #FEE2E2', paddingBottom: '4px' }}>
                              <strong>Row {e.row}</strong> {e.student ? `(${e.student})` : ''}: {e.error}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button 
                        onClick={() => { setShowCSVModal(false); setCsvRows([]); setCsvHeaders([]); setImportResult(null); }}
                        style={{ padding: '8px 20px', borderRadius: '6px', border: 'none', backgroundcolor: 'var(--text-primary)', color: 'white', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ADD CONSOLE USER MODAL (ROUTE MANAGER, VIEWER/GUARD, ADMIN) */}
      {showAddConsoleUserModal && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="modal-container" style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '520px', maxWidth: '90%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 12px 36px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', borderBottom: '1.5px solid #E2E8F0', paddingBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '10px', backgroundColor: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
                  🔑
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: 'var(--text-primary)' }}>Create Console User</h3>
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)' }}>Add Route Manager, Viewer/Guard, or Administrator</p>
                </div>
              </div>
              <button 
                onClick={() => setShowAddConsoleUserModal(false)} 
                style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#94A3B8' }}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleCreateConsoleUser}>
              {addConsoleUserError && (
                <div style={{ padding: '8px 12px', background: '#FEE2E2', color: '#DC2626', borderRadius: '6px', fontSize: '12px', marginBottom: '14px', textAlign: 'left' }}>
                  {addConsoleUserError}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', textAlign: 'left' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                    Role Assignment *
                  </label>
                  <select 
                    value={newConsoleUserRole} 
                    onChange={(e) => setNewConsoleUserRole(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', backgroundColor: '#F8FAFC', fontWeight: '600' }}
                  >
                    {roles.map(r => (
                      <option key={r.id || r.role_key} value={r.role_key}>
                        {r.role_name} {r.is_system ? '(System)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Rajesh Sharma"
                    required
                    value={newConsoleUserDisplayName}
                    onChange={(e) => setNewConsoleUserDisplayName(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Username *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. manager1"
                      required
                      value={newConsoleUserUsername}
                      onChange={(e) => setNewConsoleUserUsername(e.target.value)}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Password *
                    </label>
                    <input
                      type="password"
                      placeholder="••••••••"
                      required
                      value={newConsoleUserPassword}
                      onChange={(e) => setNewConsoleUserPassword(e.target.value)}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    placeholder="+91 9876543210"
                    value={newConsoleUserPhone}
                    onChange={(e) => setNewConsoleUserPhone(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '22px' }}>
                <button 
                  type="button" 
                  onClick={() => setShowAddConsoleUserModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                >
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT CONSOLE USER MODAL */}
      {showEditConsoleUserModal && editingConsoleUser && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="modal-container" style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '520px', maxWidth: '90%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 12px 36px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', borderBottom: '1.5px solid #E2E8F0', paddingBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '10px', backgroundColor: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
                  ✏️
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: 'var(--text-primary)' }}>Edit Console User</h3>
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)' }}>Update profile details and change assigned console role</p>
                </div>
              </div>
              <button 
                onClick={() => { setShowEditConsoleUserModal(false); setEditingConsoleUser(null); }} 
                style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#94A3B8' }}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSaveEditConsoleUser}>
              {editConsoleUserError && (
                <div style={{ padding: '8px 12px', background: '#FEE2E2', color: '#DC2626', borderRadius: '6px', fontSize: '12px', marginBottom: '14px', textAlign: 'left' }}>
                  {editConsoleUserError}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', textAlign: 'left' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                    Assigned Role *
                  </label>
                  <select 
                    value={editConsoleUserRole} 
                    onChange={(e) => setEditConsoleUserRole(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', backgroundColor: '#F8FAFC', fontWeight: '600' }}
                  >
                    {roles.map(r => (
                      <option key={r.id || r.role_key} value={r.role_key}>
                        {r.role_name} {r.is_system ? '(System)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={editConsoleUserDisplayName}
                    onChange={(e) => setEditConsoleUserDisplayName(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Username *
                    </label>
                    <input
                      type="text"
                      required
                      value={editConsoleUserUsername}
                      onChange={(e) => setEditConsoleUserUsername(e.target.value)}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Password (Optional)
                    </label>
                    <input
                      type="password"
                      placeholder="Leave blank to keep current"
                      value={editConsoleUserPassword}
                      onChange={(e) => setEditConsoleUserPassword(e.target.value)}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={editConsoleUserPhone}
                    onChange={(e) => setEditConsoleUserPhone(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '22px' }}>
                <button 
                  type="button" 
                  onClick={() => { setShowEditConsoleUserModal(false); setEditingConsoleUser(null); }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ADD ROLE MODAL */}
      {showAddRoleModal && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="modal-container" style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '580px', maxWidth: '90%', maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 12px 36px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', borderBottom: '1.5px solid #E2E8F0', paddingBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '10px', backgroundColor: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', color: '#16A34A' }}>
                  🛡️
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: 'var(--text-primary)' }}>Define New Role</h3>
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)' }}>Create a customized role with fine-grained ACL permissions</p>
                </div>
              </div>
              <button 
                onClick={() => setShowAddRoleModal(false)} 
                style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#94A3B8' }}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleCreateRole}>
              {addRoleError && (
                <div style={{ padding: '8px 12px', background: '#FEE2E2', color: '#DC2626', borderRadius: '6px', fontSize: '12px', marginBottom: '14px', textAlign: 'left' }}>
                  {addRoleError}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', textAlign: 'left' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Role Display Name *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Transport Coordinator"
                      required
                      value={newRoleName}
                      onChange={(e) => {
                        setNewRoleName(e.target.value);
                        if (!newRoleKey || newRoleKey === newRoleName.toLowerCase().replace(/[^a-z0-9_]/g, '_')) {
                          setNewRoleKey(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, ''));
                        }
                      }}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Role Key Identifier *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. transport_coordinator"
                      required
                      value={newRoleKey}
                      onChange={(e) => setNewRoleKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none', fontFamily: 'monospace' }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                    Description
                  </label>
                  <input
                    type="text"
                    placeholder="Brief description of role responsibilities..."
                    value={newRoleDesc}
                    onChange={(e) => setNewRoleDesc(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '800', color: 'var(--text-primary)', marginBottom: '8px', borderBottom: '1px solid #F1F5F9', paddingBottom: '4px' }}>
                    Console Access Permissions
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '8px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', backgroundColor: '#F8FAFC', cursor: 'pointer' }}>
                      <input 
                        type="checkbox" 
                        checked={newRolePermissions.edit_routes} 
                        onChange={(e) => setNewRolePermissions(prev => ({ ...prev, edit_routes: e.target.checked }))} 
                        style={{ marginTop: '2px', cursor: 'pointer' }} 
                      />
                      <div>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', display: 'block' }}>Edit Routes & Stops</span>
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Create & modify bus routes, sequences, and timings</span>
                      </div>
                    </label>

                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '8px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', backgroundColor: '#F8FAFC', cursor: 'pointer' }}>
                      <input 
                        type="checkbox" 
                        checked={newRolePermissions.view_gps} 
                        onChange={(e) => setNewRolePermissions(prev => ({ ...prev, view_gps: e.target.checked }))} 
                        style={{ marginTop: '2px', cursor: 'pointer' }} 
                      />
                      <div>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', display: 'block' }}>View GPS Live Map</span>
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Monitor real-time bus locations & active trip feeds</span>
                      </div>
                    </label>

                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '8px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', backgroundColor: '#F8FAFC', cursor: 'pointer' }}>
                      <input 
                        type="checkbox" 
                        checked={newRolePermissions.manage_fleet} 
                        onChange={(e) => setNewRolePermissions(prev => ({ ...prev, manage_fleet: e.target.checked }))} 
                        style={{ marginTop: '2px', cursor: 'pointer' }} 
                      />
                      <div>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', display: 'block' }}>Manage Fleet</span>
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Register buses, assign drivers, view vehicle health</span>
                      </div>
                    </label>

                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '8px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', backgroundColor: '#F8FAFC', cursor: 'pointer' }}>
                      <input 
                        type="checkbox" 
                        checked={newRolePermissions.manage_users} 
                        onChange={(e) => setNewRolePermissions(prev => ({ ...prev, manage_users: e.target.checked }))} 
                        style={{ marginTop: '2px', cursor: 'pointer' }} 
                      />
                      <div>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', display: 'block' }}>Manage Users</span>
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Students, parents, and administrative logins</span>
                      </div>
                    </label>

                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '8px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', backgroundColor: '#F8FAFC', cursor: 'pointer' }}>
                      <input 
                        type="checkbox" 
                        checked={newRolePermissions.access_documents} 
                        onChange={(e) => setNewRolePermissions(prev => ({ ...prev, access_documents: e.target.checked }))} 
                        style={{ marginTop: '2px', cursor: 'pointer' }} 
                      />
                      <div>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', display: 'block' }}>Access Documents</span>
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Document storage vault, student passes, driver licenses</span>
                      </div>
                    </label>

                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '8px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', backgroundColor: '#F8FAFC', cursor: 'pointer' }}>
                      <input 
                        type="checkbox" 
                        checked={newRolePermissions.prune_logs} 
                        onChange={(e) => setNewRolePermissions(prev => ({ ...prev, prune_logs: e.target.checked }))} 
                        style={{ marginTop: '2px', cursor: 'pointer' }} 
                      />
                      <div>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', display: 'block' }}>Prune Logs & Maintenance</span>
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Database cleanup, audit logs, and maintenance actions</span>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '22px' }}>
                <button 
                  type="button" 
                  onClick={() => setShowAddRoleModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                >
                  Create Role
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT ROLE MODAL */}
      {showEditRoleModal && editingRole && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="modal-container" style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '580px', maxWidth: '90%', maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 12px 36px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', borderBottom: '1.5px solid #E2E8F0', paddingBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '10px', backgroundColor: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', color: '#3B82F6' }}>
                  🛡️
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: 'var(--text-primary)' }}>Edit Role: {editingRole.role_name}</h3>
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)' }}>Modify role name and adjust console permission boundaries</p>
                </div>
              </div>
              <button 
                onClick={() => { setShowEditRoleModal(false); setEditingRole(null); }} 
                style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#94A3B8' }}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSaveEditRole}>
              {editRoleError && (
                <div style={{ padding: '8px 12px', background: '#FEE2E2', color: '#DC2626', borderRadius: '6px', fontSize: '12px', marginBottom: '14px', textAlign: 'left' }}>
                  {editRoleError}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', textAlign: 'left' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Role Display Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={editRoleName}
                      onChange={(e) => setEditRoleName(e.target.value)}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Role Key
                    </label>
                    <input
                      type="text"
                      disabled
                      value={editingRole.role_key}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '13px', backgroundColor: '#F1F5F9', color: '#64748B', fontFamily: 'monospace' }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                    Description
                  </label>
                  <input
                    type="text"
                    placeholder="Brief description of role responsibilities..."
                    value={editRoleDesc}
                    onChange={(e) => setEditRoleDesc(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '800', color: 'var(--text-primary)', marginBottom: '8px', borderBottom: '1px solid #F1F5F9', paddingBottom: '4px' }}>
                    Console Access Permissions
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '8px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', backgroundColor: '#F8FAFC', cursor: editingRole.is_system && editingRole.role_key === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input 
                        type="checkbox" 
                        disabled={Boolean(editingRole.is_system && editingRole.role_key === 'admin')}
                        checked={editRolePermissions.edit_routes} 
                        onChange={(e) => setEditRolePermissions(prev => ({ ...prev, edit_routes: e.target.checked }))} 
                        style={{ marginTop: '2px', cursor: editingRole.is_system && editingRole.role_key === 'admin' ? 'not-allowed' : 'pointer' }} 
                      />
                      <div>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', display: 'block' }}>Edit Routes & Stops</span>
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Create & modify bus routes, sequences, and timings</span>
                      </div>
                    </label>

                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '8px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', backgroundColor: '#F8FAFC', cursor: editingRole.is_system && editingRole.role_key === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input 
                        type="checkbox" 
                        disabled={Boolean(editingRole.is_system && editingRole.role_key === 'admin')}
                        checked={editRolePermissions.view_gps} 
                        onChange={(e) => setEditRolePermissions(prev => ({ ...prev, view_gps: e.target.checked }))} 
                        style={{ marginTop: '2px', cursor: editingRole.is_system && editingRole.role_key === 'admin' ? 'not-allowed' : 'pointer' }} 
                      />
                      <div>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', display: 'block' }}>View GPS Live Map</span>
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Monitor real-time bus locations & active trip feeds</span>
                      </div>
                    </label>

                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '8px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', backgroundColor: '#F8FAFC', cursor: editingRole.is_system && editingRole.role_key === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input 
                        type="checkbox" 
                        disabled={Boolean(editingRole.is_system && editingRole.role_key === 'admin')}
                        checked={editRolePermissions.manage_fleet} 
                        onChange={(e) => setEditRolePermissions(prev => ({ ...prev, manage_fleet: e.target.checked }))} 
                        style={{ marginTop: '2px', cursor: editingRole.is_system && editingRole.role_key === 'admin' ? 'not-allowed' : 'pointer' }} 
                      />
                      <div>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', display: 'block' }}>Manage Fleet</span>
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Register buses, assign drivers, view vehicle health</span>
                      </div>
                    </label>

                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '8px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', backgroundColor: '#F8FAFC', cursor: editingRole.is_system && editingRole.role_key === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input 
                        type="checkbox" 
                        disabled={Boolean(editingRole.is_system && editingRole.role_key === 'admin')}
                        checked={editRolePermissions.manage_users} 
                        onChange={(e) => setEditRolePermissions(prev => ({ ...prev, manage_users: e.target.checked }))} 
                        style={{ marginTop: '2px', cursor: editingRole.is_system && editingRole.role_key === 'admin' ? 'not-allowed' : 'pointer' }} 
                      />
                      <div>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', display: 'block' }}>Manage Users</span>
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Students, parents, and administrative logins</span>
                      </div>
                    </label>

                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '8px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', backgroundColor: '#F8FAFC', cursor: editingRole.is_system && editingRole.role_key === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input 
                        type="checkbox" 
                        disabled={Boolean(editingRole.is_system && editingRole.role_key === 'admin')}
                        checked={editRolePermissions.access_documents} 
                        onChange={(e) => setEditRolePermissions(prev => ({ ...prev, access_documents: e.target.checked }))} 
                        style={{ marginTop: '2px', cursor: editingRole.is_system && editingRole.role_key === 'admin' ? 'not-allowed' : 'pointer' }} 
                      />
                      <div>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', display: 'block' }}>Access Documents</span>
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Document storage vault, student passes, driver licenses</span>
                      </div>
                    </label>

                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '8px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', backgroundColor: '#F8FAFC', cursor: editingRole.is_system && editingRole.role_key === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input 
                        type="checkbox" 
                        disabled={Boolean(editingRole.is_system && editingRole.role_key === 'admin')}
                        checked={editRolePermissions.prune_logs} 
                        onChange={(e) => setEditRolePermissions(prev => ({ ...prev, prune_logs: e.target.checked }))} 
                        style={{ marginTop: '2px', cursor: editingRole.is_system && editingRole.role_key === 'admin' ? 'not-allowed' : 'pointer' }} 
                      />
                      <div>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', display: 'block' }}>Prune Logs & Maintenance</span>
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Database cleanup, audit logs, and maintenance actions</span>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '22px' }}>
                <button 
                  type="button" 
                  onClick={() => { setShowEditRoleModal(false); setEditingRole(null); }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ADD VEHICLE / BUS MODAL */}
      {showAddBusModal && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="modal-container" style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '520px', maxWidth: '90%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 12px 36px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', borderBottom: '1.5px solid #E2E8F0', paddingBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '44px',
                                  height: '44px', borderRadius: '10px', backgroundColor: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
                  🚌
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: 'var(--text-primary)' }}>Register Fleet Vehicle</h3>
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)' }}>Add a new school bus to the tracking inventory</p>
                </div>
              </div>
              <button 
                onClick={() => setShowAddBusModal(false)} 
                style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#94A3B8' }}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleCreateBus}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Bus Number *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Bus 20F"
                      required
                      value={newBusData.bus_number}
                      onChange={(e) => setNewBusData({ ...newBusData, bus_number: e.target.value })}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Registration Plate (RC) *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. KA-03-FF-1122"
                      required
                      value={newBusData.registration}
                      onChange={(e) => setNewBusData({ ...newBusData, registration: e.target.value })}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Seating Capacity
                    </label>
                    <select
                      value={newBusData.capacity}
                      onChange={(e) => setNewBusData({ ...newBusData, capacity: parseInt(e.target.value) || 40 })}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', backgroundColor: 'var(--bg-alt)', outline: 'none' }}
                    >
                      <option value={30}>30 Seats (Mini Bus)</option>
                      <option value={35}>35 Seats (Medium)</option>
                      <option value={40}>40 Seats (Standard)</option>
                      <option value={42}>42 Seats (Large)</option>
                      <option value={45}>45 Seats (Large)</option>
                      <option value={50}>50 Seats (Extra Large)</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Operational Status
                    </label>
                    <select
                      value={newBusData.status}
                      onChange={(e) => setNewBusData({ ...newBusData, status: e.target.value })}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', backgroundColor: 'var(--bg-alt)', outline: 'none' }}
                    >
                      <option value="active">Active (On Duty)</option>
                      <option value="maintenance">In Maintenance</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                    Make & Model
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Tata Starbus Ultra 40L / Ashok Leyland Sunshine"
                    value={newBusData.makeModel}
                    onChange={(e) => setNewBusData({ ...newBusData, makeModel: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Fuel & Powertrain Type
                    </label>
                    <input
                      type="text"
                      list="master_bus_fuel_types_list"
                      placeholder="e.g. Diesel (BS-VI)"
                      value={newBusData.fuelType}
                      onChange={(e) => setNewBusData({ ...newBusData, fuelType: e.target.value })}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Bus Ownership Classification
                    </label>
                    <input
                      type="text"
                      list="master_bus_ownership_types_list"
                      placeholder="e.g. School Owned"
                      value={newBusData.ownershipType}
                      onChange={(e) => setNewBusData({ ...newBusData, ownershipType: e.target.value })}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                    Initial Compliance / Document Status
                  </label>
                  <input
                    type="text"
                    list="master_bus_doc_statuses_list"
                    placeholder="e.g. Active / Valid"
                    value={newBusData.roadTaxStatus}
                    onChange={(e) => setNewBusData({ ...newBusData, roadTaxStatus: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Assign Primary Route
                    </label>
                    <select
                      value={newBusData.assignedRoute}
                      onChange={(e) => setNewBusData({ ...newBusData, assignedRoute: e.target.value })}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', backgroundColor: 'var(--bg-alt)', outline: 'none' }}
                    >
                      <option value="">Standby / Unassigned</option>
                      {allRoutes.map(r => (
                        <option key={r.id} value={r.route_name}>{r.route_name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Assign Primary Driver
                    </label>
                    <select
                      value={newBusData.assignedDriver}
                      onChange={(e) => setNewBusData({ ...newBusData, assignedDriver: e.target.value })}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', backgroundColor: 'var(--bg-alt)', outline: 'none' }}
                    >
                      <option value="">Standby / Unassigned</option>
                      {allUsers.filter(u => u.role === 'driver' || u.role === 'DRIVER').map(d => (
                        <option key={d.id} value={d.display_name}>{d.display_name} ({d.phone || 'No phone'})</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '10px' }}>
                  <button
                    type="button"
                    onClick={() => setShowAddBusModal(false)}
                    style={{
                      padding: '9px 18px',
                      borderRadius: '8px',
                      border: '1px solid var(--border-color)',
                      backgroundColor: 'var(--surface-color)',
                      color: 'var(--text-secondary)',
                      fontSize: '13px',
                      fontWeight: '700',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={savingBus}
                    style={{
                      padding: '9px 20px',
                      borderRadius: '8px',
                      border: 'none',
                      backgroundcolor: 'var(--text-primary)',
                      color: '#FFFFFF',
                      fontSize: '13px',
                      fontWeight: '700',
                      cursor: savingBus ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {savingBus ? 'Saving...' : 'Add Vehicle'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ADD STUDENT MODAL */}
      {showAddStudentModal && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="modal-container" style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '600px', maxWidth: '90%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 12px 36px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', borderBottom: '1.5px solid #E2E8F0', paddingBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '10px', backgroundColor: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
                  🎓
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: 'var(--text-primary)' }}>Register New Student</h3>
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)' }}>Add student and assign parent, stops, and profile details</p>
                </div>
              </div>
              <button 
                onClick={() => { setShowAddStudentModal(false); clearStudentFormFields(); }} 
                style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#94A3B8' }}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleCreateStudent}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                
                <h4 style={{ margin: '0 0 -4px 0', fontSize: '13px', fontWeight: '800', color: 'var(--text-primary)', borderBottom: '1px solid #F1F3F5', paddingBottom: '4px' }}>👤 Basic Information</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>First Name *</label>
                    <input
                      type="text"
                      required
                      value={editStudentFirstName}
                      onChange={(e) => setEditStudentFirstName(e.target.value)}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Last Name *</label>
                    <input
                      type="text"
                      required
                      value={editStudentLastName}
                      onChange={(e) => setEditStudentLastName(e.target.value)}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Admission Number</label>
                    <input
                      type="text"
                      value={editStudentAdmissionNo}
                      onChange={(e) => setEditStudentAdmissionNo(e.target.value)}
                      placeholder="e.g. ADM-2026-1122"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>RFID Tag ID</label>
                    <input
                      type="text"
                      value={editStudentRfidTagId}
                      onChange={(e) => setEditStudentRfidTagId(e.target.value)}
                      placeholder="e.g. RFID_NEW_1122"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                </div>

                <h4 style={{ margin: '8px 0 -4px 0', fontSize: '13px', fontWeight: '800', color: 'var(--text-primary)', borderBottom: '1px solid #F1F3F5', paddingBottom: '4px' }}>🏫 Academic & House Details</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Grade / Standard</label>
                    <input
                      type="text"
                      list="master_student_grades_list"
                      value={editStudentGrade}
                      onChange={(e) => setEditStudentGrade(e.target.value)}
                      placeholder="e.g. Class 9"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Section / Division</label>
                    <input
                      type="text"
                      list="master_student_sections_list"
                      value={editStudentSection}
                      onChange={(e) => setEditStudentSection(e.target.value)}
                      placeholder="e.g. A"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Board of Education</label>
                    <input
                      type="text"
                      list="master_student_boards_list"
                      value={editStudentBoard}
                      onChange={(e) => setEditStudentBoard(e.target.value)}
                      placeholder="e.g. CBSE"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Academic Stream</label>
                    <input
                      type="text"
                      list="master_student_streams_list"
                      value={editStudentStream}
                      onChange={(e) => setEditStudentStream(e.target.value)}
                      placeholder="e.g. Science (PCM)"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Optional Language</label>
                    <input
                      type="text"
                      list="master_student_languages_list"
                      value={editStudentSecondLang}
                      onChange={(e) => setEditStudentSecondLang(e.target.value)}
                      placeholder="e.g. Hindi"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>House Name</label>
                    <input
                      type="text"
                      list="master_student_houses_list"
                      value={editStudentHouse}
                      onChange={(e) => setEditStudentHouse(e.target.value)}
                      placeholder="e.g. Red Tigers (Ruby)"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                </div>

                <h4 style={{ margin: '8px 0 -4px 0', fontSize: '13px', fontWeight: '800', color: 'var(--text-primary)', borderBottom: '1px solid #F1F3F5', paddingBottom: '4px' }}>👨‍👩‍👧 Linked Parent & Stops</h4>
                <div style={{ position: 'relative' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Parent Account *</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                      <input
                        type="text"
                        value={parentSearchText}
                        onChange={(e) => {
                          setParentSearchText(e.target.value);
                          setShowParentDropdown(true);
                          if (!e.target.value) setEditStudentParentId('');
                        }}
                        onFocus={() => setShowParentDropdown(true)}
                        placeholder="Search parent by name or phone..."
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                        required
                      />
                      {editStudentParentId && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditStudentParentId('');
                            setParentSearchText('');
                          }}
                          style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#94A3B8', cursor: 'pointer', fontSize: '14px' }}
                        >
                          &times;
                        </button>
                      )}
                      {showParentDropdown && (
                        <>
                          <div 
                            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9 }} 
                            onClick={() => setShowParentDropdown(false)} 
                          />
                          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #CBD5E1', borderRadius: '8px', zIndex: 10, maxHeight: '180px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', marginTop: '4px' }}>
                            {allUsers
                              .filter(u => u.role === 'parent')
                              .filter(u => {
                                if (!parentSearchText) return true;
                                const name = (u.display_name || '').toLowerCase();
                                const phone = (u.phone || '').toLowerCase();
                                const query = parentSearchText.toLowerCase();
                                return name.includes(query) || phone.includes(query);
                              })
                              .slice(0, 15)
                              .map(p => (
                                <div
                                  key={p.id}
                                  onClick={() => {
                                    setEditStudentParentId(p.id.toString());
                                    setParentSearchText(`${p.display_name} (${p.phone || 'No phone'})`);
                                    setShowParentDropdown(false);
                                  }}
                                  style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '12px', borderBottom: '1px solid #F1F5F9', textAlign: 'left' }}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F8FAFC'}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                  <strong style={{ color: 'var(--text-primary)' }}>{p.display_name}</strong>
                                  <span style={{ color: 'var(--text-secondary)', marginLeft: '6px', fontSize: '11px' }}>({p.phone || 'No phone'})</span>
                                </div>
                              ))}
                            {allUsers.filter(u => u.role === 'parent').filter(u => {
                              if (!parentSearchText) return true;
                              const name = (u.display_name || '').toLowerCase();
                              const phone = (u.phone || '').toLowerCase();
                              const query = parentSearchText.toLowerCase();
                              return name.includes(query) || phone.includes(query);
                            }).length === 0 && (
                              <div style={{ padding: '8px 12px', fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                                No parents found. Click "Quick Add" to create one.
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setQuickAddParentUsername(`parent_${Date.now().toString().slice(-6)}`);
                        setShowQuickAddParentModal(true);
                      }}
                      style={{
                        padding: '8px 14px',
                        backgroundColor: '#10B981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: '700',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      ➕ Quick Add
                    </button>
                  </div>
                </div>

                {/* School Transport Option */}
                <div style={{ background: '#F8FAFC', border: '1.5px solid #E2E8F0', borderRadius: '10px', padding: '12px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: 'var(--text-primary)', marginBottom: '8px' }}>
                    🚌 School Bus Transport Selection *
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: editStudentModeOfTransport === 'School Bus' ? '2px solid #2563EB' : '1px solid #CBD5E1',
                      backgroundColor: editStudentModeOfTransport === 'School Bus' ? '#EFF6FF' : '#FFFFFF',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '700',
                      color: editStudentModeOfTransport === 'School Bus' ? '#1D4ED8' : 'var(--text-secondary)'
                    }}>
                      <input
                        type="radio"
                        name="newStudentTransportOption"
                        checked={editStudentModeOfTransport === 'School Bus'}
                        onChange={() => setEditStudentModeOfTransport('School Bus')}
                        style={{ accentColor: '#2563EB' }}
                      />
                      <span>🚌 Opt for School Bus</span>
                    </label>

                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: editStudentModeOfTransport !== 'School Bus' ? '2px solid #DC2626' : '1px solid #CBD5E1',
                      backgroundColor: editStudentModeOfTransport !== 'School Bus' ? '#FEF2F2' : '#FFFFFF',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '700',
                      color: editStudentModeOfTransport !== 'School Bus' ? '#B91C1C' : 'var(--text-secondary)'
                    }}>
                      <input
                        type="radio"
                        name="newStudentTransportOption"
                        checked={editStudentModeOfTransport !== 'School Bus'}
                        onChange={() => {
                          setEditStudentModeOfTransport('No Transport');
                          setEditStudentPickupStopId('');
                          setEditStudentDropStopId('');
                        }}
                        style={{ accentColor: '#DC2626' }}
                      />
                      <span>🚫 No School Transport</span>
                    </label>
                  </div>
                  {editStudentModeOfTransport !== 'School Bus' && (
                    <div style={{ marginTop: '8px', fontSize: '11px', color: '#B91C1C', fontWeight: '600' }}>
                      ℹ️ Student does not use school transport (private / self-transit). No bus stops will be assigned.
                    </div>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', opacity: editStudentModeOfTransport !== 'School Bus' ? 0.5 : 1 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Pickup Stop {editStudentModeOfTransport !== 'School Bus' ? '(Not Applicable)' : ''}
                    </label>
                    <select
                      value={editStudentPickupStopId}
                      disabled={editStudentModeOfTransport !== 'School Bus'}
                      onChange={(e) => setEditStudentPickupStopId(e.target.value)}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', backgroundColor: editStudentModeOfTransport !== 'School Bus' ? '#F1F5F9' : 'var(--bg-alt)', outline: 'none', cursor: editStudentModeOfTransport !== 'School Bus' ? 'not-allowed' : 'pointer' }}
                    >
                      <option value="">{editStudentModeOfTransport !== 'School Bus' ? 'None (No School Transport)' : 'Select Stop'}</option>
                      {editStudentModeOfTransport === 'School Bus' && allRoutes.flatMap(route => (route.stops || []).map(s => (
                        <option key={s.id} value={s.id}>{s.stop_name} ({route.route_name})</option>
                      )))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Drop-off Stop {editStudentModeOfTransport !== 'School Bus' ? '(Not Applicable)' : ''}
                    </label>
                    <select
                      value={editStudentDropStopId}
                      disabled={editStudentModeOfTransport !== 'School Bus'}
                      onChange={(e) => setEditStudentDropStopId(e.target.value)}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', backgroundColor: editStudentModeOfTransport !== 'School Bus' ? '#F1F5F9' : 'var(--bg-alt)', outline: 'none', cursor: editStudentModeOfTransport !== 'School Bus' ? 'not-allowed' : 'pointer' }}
                    >
                      <option value="">{editStudentModeOfTransport !== 'School Bus' ? 'None (No School Transport)' : 'Select Stop'}</option>
                      {editStudentModeOfTransport === 'School Bus' && allRoutes.flatMap(route => (route.stops || []).map(s => (
                        <option key={s.id} value={s.id}>{s.stop_name} ({route.route_name})</option>
                      )))}
                    </select>
                  </div>
                </div>

                <h4 style={{ margin: '8px 0 -4px 0', fontSize: '13px', fontWeight: '800', color: 'var(--text-primary)', borderBottom: '1px solid #F1F3F5', paddingBottom: '4px' }}>ℹ️ Secondary Details</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Date of Birth</label>
                    <input
                      type="text"
                      value={editStudentDob}
                      onChange={(e) => setEditStudentDob(e.target.value)}
                      placeholder="e.g. October 14, 2011"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Blood Group</label>
                    <input
                      type="text"
                      value={editStudentBloodGroup}
                      onChange={(e) => setEditStudentBloodGroup(e.target.value)}
                      placeholder="e.g. B+ Positive"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Gender</label>
                    <select
                      value={editStudentGender}
                      onChange={(e) => setEditStudentGender(e.target.value)}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', backgroundColor: 'var(--bg-alt)', outline: 'none' }}
                    >
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Aadhaar ID</label>
                    <input
                      type="text"
                      value={editStudentAadhaar}
                      onChange={(e) => setEditStudentAadhaar(e.target.value)}
                      placeholder="e.g. 8823-1490-5572"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '14px' }}>
                  <button
                    type="button"
                    onClick={() => { setShowAddStudentModal(false); clearStudentFormFields(); }}
                    style={{
                      padding: '9px 18px',
                      borderRadius: '8px',
                      border: '1px solid var(--border-color)',
                      backgroundColor: 'var(--surface-color)',
                      color: 'var(--text-secondary)',
                      fontSize: '13px',
                      fontWeight: '700',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={savingStudent}
                    style={{
                      padding: '9px 20px',
                      borderRadius: '8px',
                      border: 'none',
                      backgroundColor: 'var(--text-primary)',
                      color: '#FFFFFF',
                      fontSize: '13px',
                      fontWeight: '700',
                      cursor: savingStudent ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {savingStudent ? 'Saving...' : 'Add Student'}
                  </button>
                </div>

              </div>
            </form>
          </div>
        </div>
      )}

      {/* QUICK ADD PARENT MODAL */}
      {showQuickAddParentModal && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div className="modal-container" style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '420px', maxWidth: '90%', boxShadow: '0 12px 36px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', borderBottom: '1.5px solid #E2E8F0', paddingBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '8px', backgroundColor: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', color: '#10B981' }}>
                  👨‍👩‍👧
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: 'var(--text-primary)' }}>Quick Add Parent</h3>
                  <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-secondary)' }}>Create parent account instantly</p>
                </div>
              </div>
              <button 
                type="button"
                onClick={() => {
                  setShowQuickAddParentModal(false);
                  setQuickAddParentError('');
                }} 
                style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#94A3B8' }}
              >
                &times;
              </button>
            </div>

            {quickAddParentError && (
              <div style={{ color: '#EF4444', backgroundColor: '#FEF2F2', padding: '8px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '600', marginBottom: '12px' }}>
                ⚠️ {quickAddParentError}
              </div>
            )}

            <form onSubmit={handleQuickAddParent}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Full Name *</label>
                  <input
                    type="text"
                    required
                    value={quickAddParentName}
                    onChange={(e) => setQuickAddParentName(e.target.value)}
                    placeholder="e.g. Ramesh Kumar"
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Mobile Phone</label>
                  <input
                    type="text"
                    value={quickAddParentPhone}
                    onChange={(e) => setQuickAddParentPhone(e.target.value)}
                    placeholder="e.g. +91 98765 43210"
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Username (Auto-generated if empty)</label>
                  <input
                    type="text"
                    value={quickAddParentUsername}
                    onChange={(e) => setQuickAddParentUsername(e.target.value)}
                    placeholder="e.g. ramesh_kumar"
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Password *</label>
                  <input
                    type="text"
                    required
                    value={quickAddParentPassword}
                    onChange={(e) => setQuickAddParentPassword(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '10px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowQuickAddParentModal(false);
                      setQuickAddParentError('');
                    }}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: '1px solid var(--border-color)',
                      backgroundColor: 'var(--surface-color)',
                      color: 'var(--text-secondary)',
                      fontSize: '12px',
                      fontWeight: '700',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      padding: '8px 18px',
                      borderRadius: '8px',
                      border: 'none',
                      backgroundColor: '#10B981',
                      color: '#FFFFFF',
                      fontSize: '12px',
                      fontWeight: '700',
                      cursor: 'pointer'
                    }}
                  >
                    Register Parent
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ADD PARENT MODAL */}
      {showAddParentModal && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 }}>
          <div className="modal-container" style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '560px', maxWidth: '90%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 12px 36px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', borderBottom: '1.5px solid #E2E8F0', paddingBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '38px', height: '38px', borderRadius: '8px', backgroundColor: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', color: '#10B981' }}>
                  👨‍👩‍👧
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: 'var(--text-primary)' }}>Add New Parent Profile</h3>
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)' }}>Register a new parent account and configure notification settings</p>
                </div>
              </div>
              <button 
                type="button"
                onClick={() => setShowAddParentModal(false)} 
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#94A3B8' }}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleCreateParent}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                
                <h4 style={{ margin: '0 0 -4px 0', fontSize: '13px', fontWeight: '800', color: 'var(--text-primary)', borderBottom: '1px solid #F1F3F5', paddingBottom: '4px' }}>👤 Account Details</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Full Name *</label>
                    <input
                      type="text"
                      required
                      value={editParentDisplayName}
                      onChange={(e) => setEditParentDisplayName(e.target.value)}
                      placeholder="e.g. Ramesh Kumar"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Occupation</label>
                    <input
                      type="text"
                      value={editParentOccupation}
                      onChange={(e) => setEditParentOccupation(e.target.value)}
                      placeholder="e.g. Software Engineer"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Username *</label>
                    <input
                      type="text"
                      required
                      value={editParentUsername}
                      onChange={(e) => setEditParentUsername(e.target.value)}
                      placeholder="e.g. ramesh_kumar"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Password *</label>
                    <input
                      type="text"
                      required
                      value={editParentPassword}
                      onChange={(e) => setEditParentPassword(e.target.value)}
                      placeholder="e.g. securePass123"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                </div>

                <h4 style={{ margin: '8px 0 -4px 0', fontSize: '13px', fontWeight: '800', color: 'var(--text-primary)', borderBottom: '1px solid #F1F3F5', paddingBottom: '4px' }}>📍 Contact & Address</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Primary Phone *</label>
                    <input
                      type="text"
                      required
                      value={editParentPhone}
                      onChange={(e) => setEditParentPhone(e.target.value)}
                      placeholder="e.g. +91 98765 43210"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Alternative Phone</label>
                    <input
                      type="text"
                      value={editParentAltPhone}
                      onChange={(e) => setEditParentAltPhone(e.target.value)}
                      placeholder="e.g. +91 98765 00000"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Email Address</label>
                    <input
                      type="email"
                      value={editParentEmail}
                      onChange={(e) => setEditParentEmail(e.target.value)}
                      placeholder="e.g. ramesh@gmail.com"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Residential Address</label>
                    <textarea
                      value={editParentAddress}
                      onChange={(e) => setEditParentAddress(e.target.value)}
                      placeholder="e.g. Banaswadi, Bangalore - 560043"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', minHeight: '50px', outline: 'none' }}
                    />
                  </div>
                </div>

                <h4 style={{ margin: '8px 0 -4px 0', fontSize: '13px', fontWeight: '800', color: 'var(--text-primary)', borderBottom: '1px solid #F1F3F5', paddingBottom: '4px' }}>🚨 Personal & Emergency</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Relationship</label>
                    <select
                      value={editParentRelationship}
                      onChange={(e) => setEditParentRelationship(e.target.value)}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', backgroundColor: 'var(--bg-alt)', outline: 'none' }}
                    >
                      <option value="Father">Father</option>
                      <option value="Mother">Mother</option>
                      <option value="Guardian">Guardian</option>
                      <option value="Spouse">Spouse</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Spouse Name</label>
                    <input
                      type="text"
                      value={editParentSpouseName}
                      onChange={(e) => setEditParentSpouseName(e.target.value)}
                      placeholder="e.g. Priya Kumar"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Emergency Contact Phone</label>
                    <input
                      type="text"
                      value={editParentEmergencyContact}
                      onChange={(e) => setEditParentEmergencyContact(e.target.value)}
                      placeholder="e.g. +91 99999 88888"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Preferred Language</label>
                    <select
                      value={editParentPreferredLanguage}
                      onChange={(e) => setEditParentPreferredLanguage(e.target.value)}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', backgroundColor: 'var(--bg-alt)', outline: 'none' }}
                    >
                      <option value="English">English</option>
                      <option value="Hindi">Hindi</option>
                      <option value="Kannada">Kannada</option>
                      <option value="Tamil">Tamil</option>
                      <option value="Telugu">Telugu</option>
                      <option value="Spanish">Spanish</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Pickup Authorization Status</label>
                    <select
                      value={editParentPickupAuth}
                      onChange={(e) => setEditParentPickupAuth(e.target.value)}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', backgroundColor: 'var(--bg-alt)', outline: 'none' }}
                    >
                      <option value="Authorized">Authorized</option>
                      <option value="Authorized for registered guardians">Authorized for registered guardians</option>
                      <option value="Restricted">Restricted / Suspended</option>
                      <option value="Pending Verification">Pending Verification</option>
                    </select>
                  </div>
                </div>

                <h4 style={{ margin: '8px 0 -4px 0', fontSize: '13px', fontWeight: '800', color: 'var(--text-primary)', borderBottom: '1px solid #F1F3F5', paddingBottom: '4px' }}>🔔 Dispatch Alert Preferences</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      id="createParentSms"
                      checked={editParentSmsEnabled}
                      onChange={(e) => setEditParentSmsEnabled(e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <label htmlFor="createParentSms" style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', cursor: 'pointer' }}>Enable SMS Alerts</label>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      id="createParentPush"
                      checked={editParentPushEnabled}
                      onChange={(e) => setEditParentPushEnabled(e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <label htmlFor="createParentPush" style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', cursor: 'pointer' }}>Enable App Push Alerts</label>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '4px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      id="createParentEmailAlerts"
                      checked={editParentEmailAlerts}
                      onChange={(e) => setEditParentEmailAlerts(e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <label htmlFor="createParentEmailAlerts" style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', cursor: 'pointer' }}>Daily Attendance Digest</label>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>ETA Proximity Trigger</label>
                    <select
                      value={editParentEtaAlertSetting}
                      onChange={(e) => setEditParentEtaAlertSetting(e.target.value)}
                      style={{ width: '100%', padding: '6px 10px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '12px', backgroundColor: 'var(--bg-alt)', outline: 'none' }}
                    >
                      <option value="2 mins before arrival">2 mins before arrival</option>
                      <option value="5 mins before arrival">5 mins before arrival</option>
                      <option value="10 mins before arrival">10 mins before arrival</option>
                      <option value="15 mins before arrival">15 mins before arrival</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '14px', borderTop: '1px solid #E2E8F0', paddingTop: '14px' }}>
                  <button
                    type="button"
                    onClick={() => setShowAddParentModal(false)}
                    style={{
                      padding: '9px 18px',
                      borderRadius: '8px',
                      border: '1px solid var(--border-color)',
                      backgroundColor: 'var(--surface-color)',
                      color: 'var(--text-secondary)',
                      fontSize: '13px',
                      fontWeight: '700',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={savingParent}
                    style={{
                      padding: '9px 20px',
                      borderRadius: '8px',
                      border: 'none',
                      backgroundColor: 'var(--text-primary)',
                      color: '#FFFFFF',
                      fontSize: '13px',
                      fontWeight: '700',
                      cursor: savingParent ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {savingParent ? 'Saving...' : 'Add Parent'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* UPLOAD DOCUMENT DIALOG MODAL */}
      {showUploadModal && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="modal-container" style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', width: '500px', maxWidth: '90%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,0.12)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', borderBottom: '1px solid var(--border-color)', paddingBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--text-primary)', textAlign: 'left' }}>📤 Upload Document to DMS</h3>
              <button onClick={() => setShowUploadModal(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: 'var(--text-secondary)' }}>&times;</button>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!uploadFileObj) {
                alert('Please select a file to upload.');
                return;
              }
              if (!uploadEntityId) {
                alert('Please select an entity.');
                return;
              }
              
              setUploadingDoc(true);
              const formData = new FormData();
              formData.append('file', uploadFileObj);
              formData.append('entity_type', uploadEntityType);
              formData.append('entity_id', uploadEntityId);
              formData.append('document_type', uploadDocType);
              formData.append('expiry_date', uploadExpiryDate);

              try {
                const response = await fetch(`${BACKEND_URL}/api/documents`, {
                  method: 'POST',
                  body: formData
                });
                const data = await response.json();
                if (data.success) {
                  alert('Document uploaded successfully!');
                  setShowUploadModal(false);
                  const apiFilter = docFilter === 'bus' ? 'vehicle' : docFilter;
                  fetchDocuments(apiFilter);
                } else {
                  alert('Failed to upload document: ' + data.message);
                }
              } catch (err) {
                alert('Error uploading document: ' + err.message);
              } finally {
                setUploadingDoc(false);
              }
            }}>
              <div className="form-group" style={{ marginBottom: '8px', textAlign: 'left' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '13px' }}>Category (Entity Type)</label>
                <select 
                  className="form-control" 
                  value={uploadEntityType} 
                  onChange={(e) => {
                    const type = e.target.value;
                    setUploadEntityType(type);
                    setUploadEntityId('');
                    if (type === 'bus' || type === 'vehicle') {
                      setUploadDocType('RC');
                      setUploadEntityId(allBuses[0] ? allBuses[0].id : '');
                    } else if (type === 'driver') {
                      setUploadDocType('License');
                      const drivers = allUsers.filter(u => u.role === 'DRIVER' || u.role === 'driver');
                      setUploadEntityId(drivers[0] ? drivers[0].id : '');
                    } else if (type === 'student') {
                      setUploadDocType('Transit Pass');
                      setUploadEntityId(allStudents[0] ? allStudents[0].id : '');
                    }
                  }}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #CBD5E1' }}
                >
                  <option value="bus">School Bus</option>
                  <option value="driver">Driver</option>
                  <option value="student">Student</option>
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: '8px', textAlign: 'left' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '13px' }}>
                  Select {uploadEntityType === 'bus' ? 'Bus' : (uploadEntityType === 'driver' ? 'Driver' : 'Student')}
                </label>
                <select 
                  className="form-control" 
                  value={uploadEntityId} 
                  onChange={(e) => setUploadEntityId(e.target.value)}
                  required
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #CBD5E1' }}
                >
                  <option value="">-- Choose {uploadEntityType} --</option>
                  {uploadEntityType === 'bus' && allBuses.map(b => (
                    <option key={b.id} value={b.id}>Bus #{b.bus_number} ({b.registration_no || 'No Reg'})</option>
                  ))}
                  {uploadEntityType === 'driver' && allUsers.filter(u => u.role === 'DRIVER').map(d => (
                    <option key={d.id} value={d.id}>{d.display_name || d.username} ({d.phone || 'No phone'})</option>
                  ))}
                  {uploadEntityType === 'student' && allStudents.map(s => (
                    <option key={s.id} value={s.id}>{s.first_name} {s.last_name} (Roll: {s.roll_no || s.id})</option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: '8px', textAlign: 'left' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '13px' }}>Document Type</label>
                <select 
                  className="form-control" 
                  value={uploadDocType} 
                  onChange={(e) => setUploadDocType(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #CBD5E1' }}
                >
                  {uploadEntityType === 'bus' && (masterLookups?.bus_doc_types || [
                    'Registration Certificate (RC)',
                    'Commercial Vehicle Insurance Policy',
                    'Fitness Certificate (Form 38)',
                    'Pollution Under Control (PUC)',
                    'State / All-India Route Permit'
                  ]).map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                  {uploadEntityType === 'driver' && ((masterLookups?.driver_doc_types && masterLookups.driver_doc_types.length > 0) ? masterLookups.driver_doc_types : (driverDocTypesSetting || 'Driving License,Police Verification Certificate').split(',')).map(type => {
                    const t = typeof type === 'string' ? type.trim() : '';
                    return t ? <option key={t} value={t}>{t}</option> : null;
                  })}
                  {uploadEntityType === 'student' && ((masterLookups?.student_doc_types && masterLookups.student_doc_types.length > 0) ? masterLookups.student_doc_types : (studentDocTypesSetting || 'Transit Pass').split(',')).map(type => {
                    const t = typeof type === 'string' ? type.trim() : '';
                    return t ? <option key={t} value={t}>{t}</option> : null;
                  })}
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: '8px', textAlign: 'left' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '13px' }}>Select File</label>
                <input 
                  type="file" 
                  onChange={(e) => setUploadFileObj(e.target.files[0])}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #CBD5E1' }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '16px', textAlign: 'left' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '13px' }}>Expiry Date (Optional)</label>
                <input 
                  type="date" 
                  value={uploadExpiryDate} 
                  onChange={(e) => setUploadExpiryDate(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #CBD5E1' }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button 
                  type="button" 
                  onClick={() => setShowUploadModal(false)} 
                  style={{ padding: '8px 16px', borderRadius: '6px', border: '1.5px solid #CBD5E1', backgroundColor: 'white', color: 'var(--text-secondary)', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={uploadingDoc}
                  style={{ padding: '8px 20px', borderRadius: '6px', border: 'none', backgroundColor: '#3B82F6', color: 'white', fontWeight: 'bold', cursor: uploadingDoc ? 'not-allowed' : 'pointer' }}
                >
                  {uploadingDoc ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DOCUMENT PREVIEW MODAL */}
      {previewDoc && (() => {
        const previewType = getDocumentPreviewType(previewDoc);
        const iconData = getDocumentIcon(previewDoc);

        // Expiry status
        let statusColor = '#10B981';
        let statusLabel = 'Active';
        if (previewDoc.status === 'expired') {
          statusColor = '#EF4444';
          statusLabel = 'Expired';
        } else if (previewDoc.expiry_date) {
          const expiryTime = new Date(previewDoc.expiry_date).getTime();
          const todayTime = new Date().setHours(0, 0, 0, 0);
          const diffDays = Math.ceil((expiryTime - todayTime) / (1000 * 60 * 60 * 24));
          if (diffDays <= 30 && diffDays >= 0) {
            statusColor = '#F59E0B';
            statusLabel = `Expiring in ${diffDays}d`;
          }
        }

        const downloadUrl = previewDoc.url 
          ? (previewDoc.url.includes('?') ? `${previewDoc.url}&download=1` : `${previewDoc.url}?download=1`)
          : '#';

        return (
          <div 
            className="modal-overlay" 
            onClick={() => setPreviewDoc(null)}
            style={{ 
              position: 'fixed', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              backgroundColor: 'rgba(15, 23, 42, 0.75)', 
              backdropFilter: 'blur(5px)',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              zIndex: 9999,
              padding: '16px'
            }}
          >
            <div 
              className="modal-container" 
              onClick={(e) => e.stopPropagation()}
              style={{ 
                backgroundColor: 'var(--surface-color)', 
                borderRadius: '16px', 
                width: '950px', 
                maxWidth: '96vw', 
                maxHeight: '92vh', 
                display: 'flex', 
                flexDirection: 'column', 
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
                border: '1px solid var(--border-color)',
                overflow: 'hidden',
                animation: 'fadeIn 0.2s ease-out'
              }}
            >
              {/* Modal Header */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                padding: '16px 20px', 
                borderBottom: '1px solid var(--border-color)',
                backgroundColor: 'var(--surface-color)',
                flexShrink: 0
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                  <div style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {iconData.icon}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: '700', fontSize: '15px', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {previewDoc.entity_name}
                      </span>
                      <span style={{ 
                        fontSize: '11px', 
                        fontWeight: '700', 
                        padding: '2px 8px', 
                        borderRadius: '6px', 
                        backgroundColor: 'var(--bg-hover)', 
                        color: 'var(--text-secondary)',
                        border: '1px solid var(--border-color)'
                      }}>
                        {previewDoc.document_type}
                      </span>
                      {previewDoc.expiry_date && (
                        <span style={{ 
                          fontSize: '11px', 
                          fontWeight: '700', 
                          padding: '2px 8px', 
                          borderRadius: '6px', 
                          backgroundColor: statusColor + '18', 
                          color: statusColor
                        }}>
                          {statusLabel} ({previewDoc.expiry_date})
                        </span>
                      )}
                    </div>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={previewDoc.file_name}>
                      {previewDoc.file_name} • Uploaded {new Date(previewDoc.uploaded_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {/* Header Action Buttons */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, marginLeft: '16px' }}>
                  {previewDoc.url && (
                    <a
                      href={downloadUrl}
                      download={previewDoc.file_name}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Download document to your device"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '7px 14px',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: '700',
                        backgroundColor: '#10B981',
                        color: '#FFFFFF',
                        border: 'none',
                        textDecoration: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      <Download size={15} />
                      Download
                    </a>
                  )}

                  {previewDoc.url && (
                    <a
                      href={previewDoc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open file in separate tab"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '7px 12px',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: 'var(--bg-hover)',
                        color: 'var(--text-primary)',
                        border: '1px solid var(--border-color)',
                        textDecoration: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      <ExternalLink size={14} />
                      Open in Tab
                    </a>
                  )}

                  <button
                    onClick={() => setPreviewDoc(null)}
                    title="Close Preview (Esc)"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      border: 'none',
                      backgroundColor: 'transparent',
                      color: 'var(--text-secondary)',
                      cursor: 'pointer',
                      fontSize: '18px'
                    }}
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>

              {/* Modal Body: Document Preview */}
              <div style={{ 
                flex: 1, 
                padding: previewType === 'pdf' ? '0' : '20px', 
                overflowY: 'auto', 
                backgroundColor: previewType === 'pdf' ? '#525659' : 'var(--bg-alt)',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                minHeight: '400px'
              }}>
                {/* 1. IMAGE PREVIEW */}
                {previewType === 'image' && (
                  <div style={{ textAlign: 'center', maxWidth: '100%', maxHeight: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <img 
                      src={previewDoc.url} 
                      alt={previewDoc.file_name} 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '68vh', 
                        objectFit: 'contain', 
                        borderRadius: '8px', 
                        boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                        border: '1px solid var(--border-color)',
                        backgroundColor: '#FFFFFF'
                      }} 
                    />
                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '8px' }}>
                      Image Format • Click Download above to save the full original image
                    </span>
                  </div>
                )}

                {/* 2. PDF PREVIEW */}
                {previewType === 'pdf' && (
                  <div style={{ width: '100%', height: '70vh', position: 'relative' }}>
                    <iframe
                      src={previewDoc.url}
                      title={previewDoc.file_name}
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        display: 'block'
                      }}
                    />
                  </div>
                )}

                {/* 3. TEXT / CODE / LOG PREVIEW */}
                {previewType === 'text' && (
                  <div style={{ width: '100%', height: '65vh' }}>
                    <iframe
                      src={previewDoc.url}
                      title={previewDoc.file_name}
                      style={{
                        width: '100%',
                        height: '100%',
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        backgroundColor: '#FFFFFF'
                      }}
                    />
                  </div>
                )}

                {/* 4. OFFICE / OTHER FILE (WORD, EXCEL, PPT, ARCHIVE) */}
                {['word', 'excel', 'powerpoint', 'other'].includes(previewType) && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '40px 24px',
                    maxWidth: '480px',
                    backgroundColor: 'var(--surface-color)',
                    borderRadius: '12px',
                    border: '1px solid var(--border-color)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                    textAlign: 'center'
                  }}>
                    <div style={{ width: '64px', height: '64px', marginBottom: '16px' }}>
                      {iconData.icon}
                    </div>
                    <h4 style={{ margin: '0 0 6px 0', fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)' }}>
                      {previewDoc.file_name}
                    </h4>
                    <p style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '500', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                      Preview not available
                    </p>

                    <div style={{ 
                      width: '100%', 
                      padding: '12px', 
                      backgroundColor: 'var(--bg-alt)', 
                      borderRadius: '8px', 
                      marginBottom: '20px',
                      fontSize: '12px',
                      color: 'var(--text-secondary)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      textAlign: 'left'
                    }}>
                      <div><strong>Associated Entity:</strong> {previewDoc.entity_name}</div>
                      <div><strong>Document Category:</strong> {previewDoc.document_type}</div>
                      <div><strong>Uploaded Date:</strong> {new Date(previewDoc.uploaded_at).toLocaleString()}</div>
                      {previewDoc.expiry_date && <div><strong>Expiry Date:</strong> {previewDoc.expiry_date}</div>}
                    </div>

                    <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
                      <a
                        href={downloadUrl}
                        download={previewDoc.file_name}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          flex: 1,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          padding: '10px 16px',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: '700',
                          backgroundColor: '#2563EB',
                          color: '#FFFFFF',
                          border: 'none',
                          textDecoration: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        <Download size={16} />
                        Download File
                      </a>
                      <a
                        href={previewDoc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          padding: '10px 16px',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: '700',
                          backgroundColor: 'var(--bg-hover)',
                          color: 'var(--text-primary)',
                          border: '1px solid var(--border-color)',
                          textDecoration: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        <ExternalLink size={16} />
                        Open
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 20px',
                borderTop: '1px solid var(--border-color)',
                backgroundColor: 'var(--surface-color)',
                fontSize: '12px',
                color: 'var(--text-secondary)'
              }}>
                <span>
                  Tip: Press <kbd style={{ padding: '2px 6px', borderRadius: '4px', backgroundColor: 'var(--bg-hover)', border: '1px solid var(--border-color)' }}>Esc</kbd> or click outside to dismiss preview
                </span>
                <button
                  onClick={() => setPreviewDoc(null)}
                  style={{
                    padding: '6px 14px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '600',
                    backgroundColor: 'var(--bg-hover)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-color)',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      {showAboutModal && (
        <div className="modal-backdrop" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-card" style={{ backgroundColor: 'white', borderRadius: '16px', width: '480px', padding: '28px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', animation: 'fadeIn 0.2s ease-out' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '38px', height: '38px', borderRadius: '10px', backgroundColor: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563EB' }}>
                  <Info size={22} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)' }}>About Bus Buddy</h3>
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)' }}>Enterprise School Fleet Management</p>
                </div>
              </div>
              <button onClick={() => setShowAboutModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#94A3B8' }}>&times;</button>
            </div>

            <div style={{ backgroundColor: 'var(--bg-alt)', borderRadius: '12px', padding: '16px', marginBottom: '18px', border: '1px solid var(--border-color)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '13px' }}>
                <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>System Version</span>
                <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>v1.3.0 (Enterprise)</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '13px' }}>
                <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>Build Release</span>
                <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>August 2026 Stable</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '13px' }}>
                <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>Engine Architecture</span>
                <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Node.js / SQLite / Socket.IO</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '13px' }}>
                <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>Storage Vault</span>
                <span style={{ fontWeight: 600, color: '#10B981' }}>Local / S3 / GCS Multi-Provider</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>System Status</span>
                <span style={{ fontWeight: 700, color: '#10B981', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10B981', display: 'inline-block' }}></span>
                  Operational & Healthy
                </span>
              </div>
            </div>

            <p style={{ fontSize: '12.5px', color: '#475569', lineHeight: 1.6, marginBottom: '20px' }}>
              Bus Buddy provides complete end-to-end telemetry, live GPS vehicle tracking, automated parent ETA alerts, digital document vaults, and comprehensive student transit safety administration.
            </p>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button 
                onClick={() => { setShowAboutModal(false); setActiveTab('changelog'); }}
                style={{ padding: '9px 16px', borderRadius: '8px', border: '1px solid #CBD5E1', backgroundColor: 'var(--surface-color)', color: 'var(--text-secondary)', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
              >
                View Changelog
              </button>
              <button 
                onClick={() => setShowAboutModal(false)} 
                style={{ padding: '9px 20px', borderRadius: '8px', border: 'none', backgroundColor: '#2563EB', color: '#FFFFFF', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LANGUAGE SELECTION MODAL */}
      {showLanguageModal && (
        <div className="modal-backdrop" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-card" style={{ backgroundColor: 'white', borderRadius: '16px', width: '420px', padding: '24px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', animation: 'fadeIn 0.2s ease-out' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '44px',
                                  height: '44px', borderRadius: '10px', backgroundColor: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#16A34A' }}>
                  <Globe size={20} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: 'var(--text-primary)' }}>Language Preferences</h3>
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)' }}>Choose your interface display language</p>
                </div>
              </div>
              <button onClick={() => setShowLanguageModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#94A3B8' }}>&times;</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
              {[
                { code: 'en', name: 'English (United States)', flag: '🇺🇸' },
                { code: 'es', name: 'Español (Spanish)', flag: '🇪🇸' },
                { code: 'fr', name: 'Français (French)', flag: '🇫🇷' },
                { code: 'de', name: 'Deutsch (German)', flag: '🇩🇪' },
                { code: 'hi', name: 'हिंदी (Hindi)', flag: '🇮🇳' },
                { code: 'ar', name: 'العربية (Arabic)', flag: '🇦🇪' }
              ].map(lang => (
                <div 
                  key={lang.code}
                  onClick={() => setSelectedLanguage(lang.code)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: selectedLanguage === lang.code ? '1.5px solid #2563EB' : '1px solid #E2E8F0',
                    backgroundColor: selectedLanguage === lang.code ? '#EFF6FF' : '#FFFFFF',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '18px' }}>{lang.flag}</span>
                    <span style={{ fontSize: '13.5px', fontWeight: selectedLanguage === lang.code ? 600 : 500, color: 'var(--text-primary)' }}>{lang.name}</span>
                  </div>
                  {selectedLanguage === lang.code && <Check size={16} color="#2563EB" strokeWidth={2.5} />}
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button 
                onClick={() => setShowLanguageModal(false)} 
                style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #CBD5E1', backgroundColor: 'var(--surface-color)', color: '#475569', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button 
                onClick={() => {
                  setShowLanguageModal(false);
                  showToast(`Language set to ${selectedLanguage.toUpperCase()}`);
                }}
                style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', backgroundColor: '#2563EB', color: '#FFFFFF', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
              >
                Apply Language
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PASSWORD CHANGE MODAL */}
      {showPasswordModal && (
        <div className="modal-backdrop" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-card" style={{ backgroundColor: 'white', borderRadius: '16px', width: '420px', padding: '24px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', animation: 'fadeIn 0.2s ease-out' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '44px',
                                  height: '44px', borderRadius: '10px', backgroundColor: '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#EF4444' }}>
                  <Lock size={20} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: 'var(--text-primary)' }}>Change Password</h3>
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)' }}>Update admin account security credentials</p>
                </div>
              </div>
              <button onClick={() => { setShowPasswordModal(false); setPasswordStatusMsg(null); }} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#94A3B8' }}>&times;</button>
            </div>

            {passwordStatusMsg && (
              <div style={{
                padding: '10px 14px',
                borderRadius: '8px',
                marginBottom: '8px',
                fontSize: '13px',
                fontWeight: 500,
                backgroundColor: passwordStatusMsg.type === 'success' ? '#F0FDF4' : '#FEF2F2',
                color: passwordStatusMsg.type === 'success' ? '#16A34A' : '#DC2626',
                border: `1px solid ${passwordStatusMsg.type === 'success' ? '#BBF7D0' : '#FECACA'}`
              }}>
                {passwordStatusMsg.text}
              </div>
            )}

            <form onSubmit={handlePasswordUpdate}>
              <div className="form-group" style={{ marginBottom: '8px', textAlign: 'left' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '13px', color: 'var(--text-secondary)' }}>Current Password</label>
                <input 
                  type="password" 
                  value={currentPasswordInput}
                  onChange={(e) => setCurrentPasswordInput(e.target.value)}
                  placeholder="Enter current password"
                  required
                  style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px' }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '8px', textAlign: 'left' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '13px', color: 'var(--text-secondary)' }}>New Password</label>
                <input 
                  type="password" 
                  value={newPasswordInput}
                  onChange={(e) => setNewPasswordInput(e.target.value)}
                  placeholder="Minimum 6 characters"
                  required
                  style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px' }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '22px', textAlign: 'left' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '13px', color: 'var(--text-secondary)' }}>Confirm New Password</label>
                <input 
                  type="password" 
                  value={confirmPasswordInput}
                  onChange={(e) => setConfirmPasswordInput(e.target.value)}
                  placeholder="Re-type new password"
                  required
                  style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px' }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button 
                  type="button"
                  onClick={() => { setShowPasswordModal(false); setPasswordStatusMsg(null); }} 
                  style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #CBD5E1', backgroundColor: 'var(--surface-color)', color: '#475569', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', backgroundColor: '#2563EB', color: '#FFFFFF', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
                >
                  Update Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* USER GUIDE & WALKTHROUGH MODAL */}
      {showGuideModal && (
        <div className="modal-backdrop" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-card" style={{ backgroundColor: 'white', borderRadius: '16px', width: '600px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', padding: '24px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', animation: 'fadeIn 0.2s ease-out' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '44px',
                                  height: '44px', borderRadius: '10px', backgroundColor: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#D97706' }}>
                  <BookOpen size={20} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)' }}>Platform User Guide</h3>
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)' }}>Master the core workflows of Bus Buddy Admin</p>
                </div>
              </div>
              <button onClick={() => setShowGuideModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#94A3B8' }}>&times;</button>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px', flexShrink: 0 }}>
              {[
                { id: 'monitor', label: 'Live Monitor' },
                { id: 'directory', label: 'Directory & RFID' },
                { id: 'vault', label: 'Documents Vault' },
                { id: 'logs', label: 'Logs & Storage' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setGuideActiveSection(tab.id)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontSize: '12.5px',
                    fontWeight: guideActiveSection === tab.id ? 700 : 500,
                    backgroundColor: guideActiveSection === tab.id ? '#2563EB' : 'transparent',
                    color: guideActiveSection === tab.id ? '#FFFFFF' : '#64748B',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div style={{ overflowY: 'auto', flex: 1, paddingRight: '4px', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              {guideActiveSection === 'monitor' && (
                <div>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '15px', color: 'var(--text-primary)' }}>Real-time GPS Fleet Monitoring</h4>
                  <p>The <strong>Live Monitor</strong> displays all buses on interactive Leaflet maps with live telemetry updates received via WebSockets.</p>
                  <ul style={{ paddingLeft: '20px', marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <li>Click on any bus pin or route card to inspect current speed, active heading, driver contact, and passenger manifest.</li>
                    <li>Stops are numbered sequentially; hover over markers to view scheduled ETA and boarding status.</li>
                    <li>Speed alerts and detour notifications pop up in real-time in the notification drawer.</li>
                  </ul>
                </div>
              )}
              {guideActiveSection === 'directory' && (
                <div>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '15px', color: 'var(--text-primary)' }}>Student Directory & RFID Tag Management</h4>
                  <p>Maintain your school transit roster and manage emergency guardian contacts.</p>
                  <ul style={{ paddingLeft: '20px', marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <li>Import student rosters seamlessly in bulk via CSV format using the <strong>Import Students</strong> modal.</li>
                    <li>Map pickup and drop-off stops dynamically to routes.</li>
                    <li>Link RFID tags to students to enable automated tap-on/tap-off notifications for parents.</li>
                  </ul>
                </div>
              )}
              {guideActiveSection === 'vault' && (
                <div>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '15px', color: 'var(--text-primary)' }}>Digital Compliance & Documents Vault</h4>
                  <p>Store vehicle registration (RC), insurance policies, fitness certificates, and driver licenses safely in the cloud.</p>
                  <ul style={{ paddingLeft: '20px', marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <li>Filter documents by vehicle, driver, or expiry date.</li>
                    <li>Download or preview uploaded documents directly from the vault grid.</li>
                    <li>Receive automated warnings 30 days before insurance or fitness certificates expire.</li>
                  </ul>
                </div>
              )}
              {guideActiveSection === 'logs' && (
                <div>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '15px', color: 'var(--text-primary)' }}>Audit Trails & Pluggable Storage</h4>
                  <p>Configure enterprise cloud storage and monitor structured Pino log output.</p>
                  <ul style={{ paddingLeft: '20px', marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <li>Under <strong>Service Connections</strong>, switch between Local filesystem, Amazon S3, or Google Cloud Storage (GCS).</li>
                    <li>Inspect audit events (logins, uploads, deletions) and server log levels under <strong>Audit & System Logs</strong>.</li>
                  </ul>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '18px', paddingTop: '12px', borderTop: '1px solid var(--border-color)', flexShrink: 0 }}>
              <button 
                onClick={() => setShowGuideModal(false)} 
                style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', backgroundColor: '#2563EB', color: '#FFFFFF', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
              >
                Got It
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HELP CENTER MODAL */}
      {showHelpCenterModal && (
        <div className="modal-backdrop" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-card" style={{ backgroundColor: 'white', borderRadius: '16px', width: '480px', padding: '24px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', animation: 'fadeIn 0.2s ease-out' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '44px',
                                  height: '44px', borderRadius: '10px', backgroundColor: '#F3E8FF', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9333EA' }}>
                  <HelpCircle size={20} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)' }}>Help & Support Center</h3>
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)' }}>Assistance and technical operations</p>
                </div>
              </div>
              <button onClick={() => setShowHelpCenterModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#94A3B8' }}>&times;</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
              <div style={{ padding: '12px', borderRadius: '10px', backgroundColor: 'var(--bg-alt)', border: '1px solid var(--border-color)' }}>
                <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text-primary)', marginBottom: '4px' }}>⚡ Urgent Operations Hotline</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>For emergency bus breakdowns or driver SOS: <strong>+1 (800) 555-BUS1</strong></div>
              </div>
              <div style={{ padding: '12px', borderRadius: '10px', backgroundColor: 'var(--bg-alt)', border: '1px solid var(--border-color)' }}>
                <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text-primary)', marginBottom: '4px' }}>✉️ IT & Portal Support</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Email queries & ticket escalation: <strong>support@schoolbusbuddy.internal</strong></div>
              </div>
              <div style={{ padding: '12px', borderRadius: '10px', backgroundColor: 'var(--bg-alt)', border: '1px solid var(--border-color)' }}>
                <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text-primary)', marginBottom: '4px' }}>⌨️ Keyboard Shortcuts</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Press <kbd style={{ padding: '1px 5px', background: '#E2E8F0', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>⌘K</kbd> / <kbd style={{ padding: '1px 5px', background: '#E2E8F0', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>Ctrl+K</kbd> anywhere to focus global search.</div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button 
                onClick={() => {
                  setShowHelpCenterModal(false);
                  setActiveTab('tickets');
                }}
                style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #CBD5E1', backgroundColor: 'var(--surface-color)', color: 'var(--text-secondary)', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
              >
                Go to Support Desk
              </button>
              <button 
                onClick={() => setShowHelpCenterModal(false)} 
                style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', backgroundColor: '#2563EB', color: '#FFFFFF', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FLOATING TOAST NOTIFICATION */}
      {toastMessage && (
        <div className="header-toast-notification">
          <Check size={16} color="#10B981" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* LEFT SIDEBAR */}
      <aside className={`sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
        <div 
          className="sidebar-brand"
          onClick={() => isSidebarCollapsed && setIsSidebarCollapsed(false)}
          title={isSidebarCollapsed ? "Click to expand sidebar" : undefined}
        >
          <div className="logo-icon">
            <Bus size={24} color="#131417" />
          </div>
          {!isSidebarCollapsed && <span className="brand-name">Bus Buddy</span>}
          <button 
            className="sidebar-toggle-btn" 
            onClick={(e) => {
              e.stopPropagation();
              setIsSidebarCollapsed(!isSidebarCollapsed);
            }}
            title={isSidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
          >
            <PanelLeft size={16} color="#3B82F6" />
          </button>
        </div>

        <nav className="sidebar-nav" style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 180px)', paddingRight: '4px' }}>
          {/* GROUP 1: OPERATIONS */}
          <div className="sidebar-group">
            {!isSidebarCollapsed && <div className="sidebar-group-header">Operations Room</div>}
            <button className={`nav-item-sub ${activeTab === 'monitor' ? 'active' : ''}`} onClick={() => navigateTab('monitor')} title="Live Monitor">
              <span className="nav-icon"><Navigation size={16} /></span>
              {!isSidebarCollapsed && <span className="nav-label">Live Monitor</span>}
              {!isSidebarCollapsed && <span className="badge-count count-green">{trips.filter(t => t.status === 'EN_ROUTE').length || 3}</span>}
            </button>
            <button className={`nav-item-sub ${activeTab === 'trips' ? 'active' : ''}`} onClick={() => navigateTab('trips')} title="Today's Trips">
              <span className="nav-icon"><MapPin size={16} /></span>
              {!isSidebarCollapsed && <span className="nav-label">Today's Trips</span>}
            </button>
            <button className={`nav-item-sub ${activeTab === 'reports' ? 'active' : ''}`} onClick={() => navigateTab('reports')} title="Reports & Alerts">
              <span className="nav-icon"><BarChart size={16} /></span>
              {!isSidebarCollapsed && <span className="nav-label">Reports & Alerts</span>}
            </button>
          </div>

          {/* GROUP 2: SCHOOL DIRECTORY */}
          <div className="sidebar-group">
            {!isSidebarCollapsed && <div className="sidebar-group-header">School Directory</div>}
            <button 
              className={`nav-item-sub ${activeTab === 'students' ? 'active' : ''}`} 
              onClick={() => navigateTab('students')} 
              title={isTabRestricted('students') ? "Students (Restricted by Role)" : "Students"}
              style={isTabRestricted('students') ? { opacity: 0.45 } : {}}
            >
              <span className="nav-icon"><Users size={16} /></span>
              {!isSidebarCollapsed && <span className="nav-label">Students</span>}
              {!isSidebarCollapsed && isTabRestricted('students') && <span style={{ fontSize: '10px', color: '#94A3B8' }}>🔒</span>}
            </button>
            <button 
              className={`nav-item-sub ${activeTab === 'parents' ? 'active' : ''}`} 
              onClick={() => navigateTab('parents')} 
              title={isTabRestricted('parents') ? "Parents (Restricted by Role)" : "Parents & Guardians"}
              style={isTabRestricted('parents') ? { opacity: 0.45 } : {}}
            >
              <span className="nav-icon"><User size={16} /></span>
              {!isSidebarCollapsed && <span className="nav-label">Parents</span>}
              {!isSidebarCollapsed && isTabRestricted('parents') && <span style={{ fontSize: '10px', color: '#94A3B8' }}>🔒</span>}
            </button>
            <button 
              className={`nav-item-sub ${activeTab === 'drivers' ? 'active' : ''}`} 
              onClick={() => navigateTab('drivers')} 
              title={isTabRestricted('drivers') ? "Drivers (Restricted by Role)" : "Bus Drivers"}
              style={isTabRestricted('drivers') ? { opacity: 0.45 } : {}}
            >
              <span className="nav-icon"><SteeringWheel size={16} /></span>
              {!isSidebarCollapsed && <span className="nav-label">Drivers</span>}
              {!isSidebarCollapsed && isTabRestricted('drivers') && <span style={{ fontSize: '10px', color: '#94A3B8' }}>🔒</span>}
            </button>
            <button 
              className={`nav-item-sub ${activeTab === 'buses' ? 'active' : ''}`} 
              onClick={() => navigateTab('buses')} 
              title={isTabRestricted('buses') ? "Buses (Restricted by Role)" : "School Buses"}
              style={isTabRestricted('buses') ? { opacity: 0.45 } : {}}
            >
              <span className="nav-icon"><Bus size={16} /></span>
              {!isSidebarCollapsed && <span className="nav-label">School Buses</span>}
              {!isSidebarCollapsed && isTabRestricted('buses') && <span style={{ fontSize: '10px', color: '#94A3B8' }}>🔒</span>}
            </button>
            <button 
              className={`nav-item-sub ${activeTab === 'documents' ? 'active' : ''}`} 
              onClick={() => { navigateTab('documents'); fetchDocuments('all'); }} 
              title={isTabRestricted('documents') ? "Documents (Restricted by Role)" : "Documents Vault"}
              style={isTabRestricted('documents') ? { opacity: 0.45 } : {}}
            >
              <span className="nav-icon"><FileText size={16} /></span>
              {!isSidebarCollapsed && <span className="nav-label">Documents Vault</span>}
              {!isSidebarCollapsed && isTabRestricted('documents') && <span style={{ fontSize: '10px', color: '#94A3B8' }}>🔒</span>}
            </button>
          </div>

          {/* GROUP 3: COMMUNICATIONS */}
          <div className="sidebar-group">
            {!isSidebarCollapsed && <div className="sidebar-group-header">Communications</div>}
            <button 
              className={`nav-item-sub ${activeTab === 'announcements' ? 'active' : ''}`} 
              onClick={() => navigateTab('announcements')} 
              title={isTabRestricted('announcements') ? "Announcements (Restricted by Role)" : "Announcements"}
              style={isTabRestricted('announcements') ? { opacity: 0.45 } : {}}
            >
              <span className="nav-icon"><Bell size={16} /></span>
              {!isSidebarCollapsed && <span className="nav-label">Announcements</span>}
              {!isSidebarCollapsed && isTabRestricted('announcements') && <span style={{ fontSize: '10px', color: '#94A3B8' }}>🔒</span>}
            </button>
            <button 
              className={`nav-item-sub ${activeTab === 'tickets' ? 'active' : ''}`} 
              onClick={() => navigateTab('tickets')} 
              title={isTabRestricted('tickets') ? "Tickets (Restricted by Role)" : "Support Desk"}
              style={isTabRestricted('tickets') ? { opacity: 0.45 } : {}}
            >
              <span className="nav-icon"><MessageSquare size={16} /></span>
              {!isSidebarCollapsed && <span className="nav-label">Support Desk</span>}
              {!isSidebarCollapsed && isTabRestricted('tickets') && <span style={{ fontSize: '10px', color: '#94A3B8' }}>🔒</span>}
            </button>
          </div>

          {/* GROUP 4: CONFIG & CONTROL */}
          <div className="sidebar-group">
            {!isSidebarCollapsed && <div className="sidebar-group-header">Setup & Config</div>}
            <button 
              className={`nav-item-sub ${activeTab === 'general_settings' ? 'active' : ''}`} 
              onClick={() => navigateTab('general_settings')} 
              title={isTabRestricted('general_settings') ? "Settings (Restricted by Role)" : "General Settings"}
              style={isTabRestricted('general_settings') ? { opacity: 0.45 } : {}}
            >
              <span className="nav-icon"><Settings size={16} /></span>
              {!isSidebarCollapsed && <span className="nav-label">General Settings</span>}
              {!isSidebarCollapsed && isTabRestricted('general_settings') && <span style={{ fontSize: '10px', color: '#94A3B8' }}>🔒</span>}
            </button>
            <button 
              className={`nav-item-sub ${activeTab === 'connections' ? 'active' : ''}`} 
              onClick={() => { navigateTab('connections'); fetchStorageSettings(); }} 
              title={isTabRestricted('connections') ? "Connections (Restricted by Role)" : "Service Connections"}
              style={isTabRestricted('connections') ? { opacity: 0.45 } : {}}
            >
              <span className="nav-icon"><Sliders size={16} /></span>
              {!isSidebarCollapsed && <span className="nav-label">Service Connections</span>}
              {!isSidebarCollapsed && isTabRestricted('connections') && <span style={{ fontSize: '10px', color: '#94A3B8' }}>🔒</span>}
            </button>
            <button 
              className={`nav-item-sub ${activeTab === 'access_control' ? 'active' : ''}`} 
              onClick={() => navigateTab('access_control')} 
              title={isTabRestricted('access_control') ? "Access Control (Restricted by Role)" : "Access Control"}
              style={isTabRestricted('access_control') ? { opacity: 0.45 } : {}}
            >
              <span className="nav-icon"><Key size={16} /></span>
              {!isSidebarCollapsed && <span className="nav-label">Access Control</span>}
              {!isSidebarCollapsed && isTabRestricted('access_control') && <span style={{ fontSize: '10px', color: '#94A3B8' }}>🔒</span>}
            </button>
            <button 
              className={`nav-item-sub ${activeTab === 'database_ops' ? 'active' : ''}`} 
              onClick={() => navigateTab('database_ops')} 
              title={isTabRestricted('database_ops') ? "Database (Restricted by Role)" : "Database Cleanup"}
              style={isTabRestricted('database_ops') ? { opacity: 0.45 } : {}}
            >
              <span className="nav-icon"><Database size={16} /></span>
              {!isSidebarCollapsed && <span className="nav-label">Database Cleanup</span>}
              {!isSidebarCollapsed && isTabRestricted('database_ops') && <span style={{ fontSize: '10px', color: '#94A3B8' }}>🔒</span>}
            </button>
            <button 
              className={`nav-item-sub ${activeTab === 'system_logs' ? 'active' : ''}`} 
              onClick={() => navigateTab('system_logs')} 
              title={isTabRestricted('system_logs') ? "Logs (Restricted by Role)" : "Audit & System Logs"}
              style={isTabRestricted('system_logs') ? { opacity: 0.45 } : {}}
            >
              <span className="nav-icon"><FileText size={16} /></span>
              {!isSidebarCollapsed && <span className="nav-label">Audit & System Logs</span>}
              {!isSidebarCollapsed && isTabRestricted('system_logs') && <span style={{ fontSize: '10px', color: '#94A3B8' }}>🔒</span>}
            </button>
          </div>
        </nav>

      </aside>

      {/* MAIN CONTENT AREA */}
      <div className="content-area">
        {/* TOP HEADER */}
        <header className="content-header-modern">
          <div className="header-left-section">
            <div className="header-search-container" ref={searchContainerRef}>
              <div className="header-search-bar" onClick={() => { setIsSearchOpen(true); searchInputRef.current?.focus(); }}>
                <Search size={15} color="#64748B" className="header-search-icon" />
                <input 
                  ref={searchInputRef}
                  type="text" 
                  placeholder="Search menus or entities..." 
                  value={headerSearchQuery}
                  onFocus={() => setIsSearchOpen(true)}
                  onChange={(e) => {
                    setHeaderSearchQuery(e.target.value);
                    setIsSearchOpen(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setIsSearchOpen(false);
                    }
                  }}
                  className="header-search-input" 
                />
                {headerSearchQuery ? (
                  <button 
                    onClick={(e) => { e.stopPropagation(); setHeaderSearchQuery(''); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: '0 2px', display: 'flex', alignItems: 'center' }}
                  >
                    <X size={14} />
                  </button>
                ) : (
                  <kbd className="header-search-kbd">⌘K</kbd>
                )}
              </div>

              {/* SEARCH RESULTS DROPDOWN */}
              {isSearchOpen && headerSearchQuery.trim().length > 0 && (
                <div className="search-results-dropdown" style={{ animation: 'dropdownFade 0.15s ease-out' }}>
                  {totalSearchCount === 0 ? (
                    <div className="search-empty-state">
                      <Search size={22} color="#94A3B8" />
                      <div style={{ fontWeight: 600, color: '#475569', fontSize: '13px', marginTop: '6px' }}>No matches found</div>
                      <div style={{ fontSize: '12px', color: '#94A3B8' }}>No menu items or entities matching "{headerSearchQuery}"</div>
                    </div>
                  ) : (
                    <div className="search-results-scrollable">
                      {/* MENU ITEMS */}
                      {searchResults.menus.length > 0 && (
                        <div className="search-group">
                          <div className="search-group-title">Navigation & Menu ({searchResults.menus.length})</div>
                          {searchResults.menus.map(item => {
                            const ItemIcon = item.icon || LayoutGrid;
                            return (
                              <div 
                                key={item.id} 
                                className="search-result-item"
                                onClick={() => {
                                  if (item.action) {
                                    item.action();
                                  } else {
                                    setActiveTab(item.id);
                                    if (item.id === 'trips' || item.id === 'reports' || item.id === 'students' || item.id === 'parents' || item.id === 'drivers' || item.id === 'buses' || item.id === 'documents' || item.id === 'access_control') {
                                      fetchManagementData();
                                    }
                                  }
                                  setIsSearchOpen(false);
                                  setHeaderSearchQuery('');
                                }}
                              >
                                <div className="search-result-left">
                                  <div className="search-icon-badge menu-badge">
                                    <ItemIcon size={15} />
                                  </div>
                                  <div>
                                    <div className="search-result-title">{item.title}</div>
                                    <div className="search-result-sub">{item.desc}</div>
                                  </div>
                                </div>
                                <span className="search-tag-pill menu-tag">{item.category}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* STUDENTS */}
                      {searchResults.students.length > 0 && (
                        <div className="search-group">
                          <div className="search-group-title">Students ({searchResults.students.length})</div>
                          {searchResults.students.map(student => (
                            <div 
                              key={student.id} 
                              className="search-result-item"
                              onClick={() => {
                                handleStudentClick(student);
                                setIsSearchOpen(false);
                                setHeaderSearchQuery('');
                              }}
                            >
                              <div className="search-result-left">
                                <div className="search-icon-badge student-badge">
                                  <Users size={15} />
                                </div>
                                <div>
                                  <div className="search-result-title">{student.first_name} {student.last_name}</div>
                                  <div className="search-result-sub">
                                    {student.grade ? `${student.grade} • ` : ''}RFID: {student.rfid_tag_id || 'N/A'} • Stop: {student.pickup_stop_name || 'Assigned'}
                                  </div>
                                </div>
                              </div>
                              <span className="search-tag-pill student-tag">Student</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* PARENTS */}
                      {searchResults.parents && searchResults.parents.length > 0 && (
                        <div className="search-group">
                          <div className="search-group-title">Parents & Guardians ({searchResults.parents.length})</div>
                          {searchResults.parents.map(parent => (
                            <div 
                              key={parent.id} 
                              className="search-result-item"
                              onClick={() => {
                                setActiveTab('parents');
                                handleParentClick(parent);
                                setIsSearchOpen(false);
                                setHeaderSearchQuery('');
                              }}
                            >
                              <div className="search-result-left">
                                <div className="search-icon-badge parent-badge" style={{ backgroundColor: '#EEF2FF', color: '#4F46E5' }}>
                                  <User size={15} />
                                </div>
                                <div>
                                  <div className="search-result-title">{parent.display_name}</div>
                                  <div className="search-result-sub">
                                    Phone: {parent.phone || 'N/A'} • {allStudents.filter(s => s.parent_id === parent.id).map(s => s.first_name).join(', ') ? `Wards: ${allStudents.filter(s => s.parent_id === parent.id).map(s => s.first_name).join(', ')}` : 'Parent'}
                                  </div>
                                </div>
                              </div>
                              <span className="search-tag-pill" style={{ backgroundColor: '#EEF2FF', color: '#4F46E5' }}>Parent</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* DRIVERS */}
                      {searchResults.drivers.length > 0 && (
                        <div className="search-group">
                          <div className="search-group-title">Drivers ({searchResults.drivers.length})</div>
                          {searchResults.drivers.map(driver => (
                            <div 
                              key={driver.id} 
                              className="search-result-item"
                              onClick={() => {
                                setActiveTab('drivers');
                                handleDriverClick(driver);
                                setIsSearchOpen(false);
                                setHeaderSearchQuery('');
                              }}
                            >
                              <div className="search-result-left">
                                <div className="search-icon-badge driver-badge">
                                  <Shield size={15} />
                                </div>
                                <div>
                                  <div className="search-result-title">{driver.display_name || driver.username}</div>
                                  <div className="search-result-sub">
                                    Phone: {driver.phone || 'N/A'} • {driver.email || 'Driver Staff'}
                                  </div>
                                </div>
                              </div>
                              <span className="search-tag-pill driver-tag">Driver</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* BUSES */}
                      {searchResults.buses.length > 0 && (
                        <div className="search-group">
                          <div className="search-group-title">School Buses ({searchResults.buses.length})</div>
                          {searchResults.buses.map(bus => (
                            <div 
                              key={bus.id} 
                              className="search-result-item"
                              onClick={() => {
                                setActiveTab('buses');
                                handleBusClick(bus);
                                setIsSearchOpen(false);
                                setHeaderSearchQuery('');
                              }}
                            >
                              <div className="search-result-left">
                                <div className="search-icon-badge bus-badge">
                                  <Bus size={15} />
                                </div>
                                <div>
                                  <div className="search-result-title">Bus #{bus.bus_number}</div>
                                  <div className="search-result-sub">
                                    Reg: {bus.registration_no || 'N/A'} • Capacity: {bus.capacity || 40} Seats
                                  </div>
                                </div>
                              </div>
                              <span className="search-tag-pill bus-tag">Vehicle</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* ROUTES */}
                      {searchResults.routes.length > 0 && (
                        <div className="search-group">
                          <div className="search-group-title">Routes ({searchResults.routes.length})</div>
                          {searchResults.routes.map(route => (
                            <div 
                              key={route.id} 
                              className="search-result-item"
                              onClick={() => {
                                setActiveTab('monitor');
                                setFilterRouteId(String(route.id));
                                setIsSearchOpen(false);
                                setHeaderSearchQuery('');
                              }}
                            >
                              <div className="search-result-left">
                                <div className="search-icon-badge route-badge">
                                  <MapPin size={15} />
                                </div>
                                <div>
                                  <div className="search-result-title">{route.name}</div>
                                  <div className="search-result-sub">
                                    {route.description || 'Assigned Transit Route'}
                                  </div>
                                </div>
                              </div>
                              <span className="search-tag-pill route-tag">Route</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* TRIPS */}
                      {searchResults.trips.length > 0 && (
                        <div className="search-group">
                          <div className="search-group-title">Active Trips ({searchResults.trips.length})</div>
                          {searchResults.trips.map(trip => (
                            <div 
                              key={trip.id} 
                              className="search-result-item"
                              onClick={() => {
                                setActiveTab('monitor');
                                setSelectedTrip(trip);
                                setIsSearchOpen(false);
                                setHeaderSearchQuery('');
                              }}
                            >
                              <div className="search-result-left">
                                <div className="search-icon-badge trip-badge">
                                  <Clock size={15} />
                                </div>
                                <div>
                                  <div className="search-result-title">{trip.route_name || `Trip #${trip.id}`} (Bus #{trip.bus_number})</div>
                                  <div className="search-result-sub">
                                    Driver: {trip.driver_name || 'Assigned'} • Status: {trip.status}
                                  </div>
                                </div>
                              </div>
                              <span className="search-tag-pill trip-tag">Trip</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="header-right-section">
            {/* Notification Bell with Badge 6 */}
            <div className="notif-wrapper" ref={notifMenuRef}>
              <button 
                className="header-icon-btn notif-bell-btn"
                onClick={() => setShowNotificationsDropdown(!showNotificationsDropdown)}
                title="Notifications"
              >
                <Bell size={18} color="#EF4444" />
                <span className="notif-badge-pill">6</span>
              </button>
              {showNotificationsDropdown && (
                <div className="notif-dropdown-menu">
                  <div className="notif-dropdown-header">
                    <span style={{ fontWeight: 700, fontSize: '13px', color: 'var(--text-primary)' }}>System Notifications (6)</span>
                    <span 
                      style={{ fontSize: '11px', color: '#3B82F6', cursor: 'pointer', fontWeight: 600 }}
                      onClick={() => { showToast('All notifications marked as read'); setShowNotificationsDropdown(false); }}
                    >
                      Mark all read
                    </span>
                  </div>
                  <div className="notif-dropdown-list">
                    <div className="notif-dropdown-item" onClick={() => { setActiveTab('reports'); setShowNotificationsDropdown(false); }}>
                      <div className="notif-item-dot" style={{ backgroundColor: '#EF4444' }}></div>
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)' }}>SOS Alert: Bus #102</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Driver reported heavy roadblock on North Route</div>
                        <div style={{ fontSize: '10px', color: '#94A3B8', marginTop: '2px' }}>2 mins ago</div>
                      </div>
                    </div>
                    <div className="notif-dropdown-item" onClick={() => { setActiveTab('monitor'); setShowNotificationsDropdown(false); }}>
                      <div className="notif-item-dot" style={{ backgroundColor: '#F59E0B' }}></div>
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)' }}>Speed Warning: Route #4</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Speed exceeded 45 km/h on Ring Road corridor</div>
                        <div style={{ fontSize: '10px', color: '#94A3B8', marginTop: '2px' }}>14 mins ago</div>
                      </div>
                    </div>
                    <div className="notif-dropdown-item" onClick={() => { setActiveTab('trips'); setShowNotificationsDropdown(false); }}>
                      <div className="notif-item-dot" style={{ backgroundColor: '#10B981' }}></div>
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)' }}>Morning Trips Completed</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>14 of 14 morning routes finished with 100% check-in</div>
                        <div style={{ fontSize: '10px', color: '#94A3B8', marginTop: '2px' }}>35 mins ago</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Theme Toggle Group (Sun, Monitor, Moon) */}
            <div className="theme-toggle-group">
              <button 
                className={`theme-toggle-btn ${themeMode === 'light' ? 'active-sun' : ''}`}
                onClick={() => setThemeMode('light')}
                title="Light Mode"
              >
                <Sun size={15} color={themeMode === 'light' ? '#F59E0B' : '#94A3B8'} />
              </button>
              <button 
                className={`theme-toggle-btn ${themeMode === 'system' ? 'active' : ''}`}
                onClick={() => setThemeMode('system')}
                title="System Mode"
              >
                <Monitor size={15} color={themeMode === 'system' ? '#3B82F6' : '#94A3B8'} />
              </button>
              <button 
                className={`theme-toggle-btn ${themeMode === 'dark' ? 'active' : ''}`}
                onClick={() => setThemeMode('dark')}
                title="Dark Mode"
              >
                <Moon size={15} color={themeMode === 'dark' ? '#6366F1' : '#94A3B8'} />
              </button>
            </div>

            {/* Help Question Button */}
            <button 
              className="header-icon-btn help-btn"
              onClick={() => setShowHelpCenterModal(true)}
              title="Help & Documentation"
            >
              <HelpCircle size={18} color="#8B5CF6" />
            </button>

            {/* User DP & Context Menu */}
            <div className="header-user-menu-container" ref={userMenuRef}>
              <div 
                className="header-user-btn" 
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                title="Account Menu"
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: '1.2' }}>
                  <span className="header-username">{user?.display_name || user?.username || 'admin'}</span>
                  <span style={{ fontSize: '10px', color: 'var(--text-secondary)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {user?.role === 'admin' ? 'System Admin' : user?.role === 'route_manager' ? 'Route Manager' : (user?.role === 'viewer_guard' || user?.role === 'guard' || user?.role === 'viewer') ? 'Viewer / Guard' : (user?.role || 'Admin')}
                  </span>
                </div>
                <div className="header-user-dp">
                  {user?.avatar || user?.photo ? (
                    <img src={user.avatar || user.photo} alt={user?.display_name || 'avatar'} />
                  ) : (
                    <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=150" alt="avatar" />
                  )}
                </div>
              </div>

              {/* Context Menu Dropdown */}
              {isUserMenuOpen && (
                <div className="user-context-menu">
                  <button 
                    className="context-menu-item"
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      setShowAboutModal(true);
                    }}
                  >
                    <Info size={16} className="context-menu-icon" />
                    <span>About</span>
                  </button>
                  <button 
                    className="context-menu-item"
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      setActiveTab('changelog');
                    }}
                  >
                    <FileText size={16} className="context-menu-icon" />
                    <span>Change Log</span>
                  </button>
                  <button 
                    className="context-menu-item"
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      setShowHelpCenterModal(true);
                    }}
                  >
                    <HelpCircle size={16} className="context-menu-icon" />
                    <span>Help</span>
                  </button>
                  <button 
                    className="context-menu-item"
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      setShowLanguageModal(true);
                    }}
                  >
                    <Globe size={16} className="context-menu-icon" />
                    <span>Language</span>
                  </button>
                  <button 
                    className="context-menu-item"
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      setShowPasswordModal(true);
                    }}
                  >
                    <Lock size={16} className="context-menu-icon" />
                    <span>Password</span>
                  </button>
                  <button 
                    className="context-menu-item"
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      setShowGuideModal(true);
                    }}
                  >
                    <BookOpen size={16} className="context-menu-icon" />
                    <span>Guide</span>
                  </button>
                  <button 
                    className="context-menu-item logout"
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      handleLogout();
                    }}
                  >
                    <LogOut size={16} className="context-menu-icon" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* BODY PANEL */}
        <div className="page-body-container">
          {selectedStudentProfile ? (
            <div className="student-profile-fullpage" style={{ animation: 'fadeIn 0.3s ease-out' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <button 
                    onClick={() => { setSelectedStudentProfile(null); setIsEditingStudent(false); }}
                    style={{
                      backgroundColor: 'var(--surface-color)',
                      border: '1.5px solid var(--border-color)',
                      borderRadius: '12px',
                      padding: '6px 12px',
                      fontSize: '11px',
                      fontWeight: '700',
                      color: 'var(--text-secondary)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    ← Back
                  </button>
                  <h1 className="page-title" style={{ fontSize: '24px', margin: 0 }}>Student Profile Directory</h1>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {!isEditingStudent ? (
                    <>
                      <button
                        onClick={() => setIsEditingStudent(true)}
                        className="save-delay-btn"
                        style={{ padding: '8px 16px', fontSize: '13px' }}
                      >
                        Edit Profile
                      </button>
                      <button
                        onClick={() => handleDeleteStudent(selectedStudentProfile.studentData.id)}
                        style={{
                          backgroundColor: '#EF4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '12px',
                          padding: '8px 16px',
                          fontSize: '13px',
                          fontWeight: '700',
                          cursor: 'pointer'
                        }}
                      >
                        Delete Student
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          setIsEditingStudent(false);
                          setEditStudentError('');
                        }}
                        style={{
                          backgroundColor: 'var(--surface-color)',
                          border: '1.5px solid var(--border-color)',
                          borderRadius: '12px',
                          padding: '8px 16px',
                          fontSize: '13px',
                          fontWeight: '700',
                          color: 'var(--text-secondary)',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleUpdateStudentProfile}
                        disabled={savingStudent}
                        className="save-delay-btn"
                        style={{ padding: '8px 16px', fontSize: '13px', opacity: savingStudent ? 0.7 : 1 }}
                      >
                        {savingStudent ? 'Saving...' : 'Save Changes'}
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="profile-layout-grid" style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '28px', alignItems: 'start' }}>
                {/* LEFT COLUMN: Sidebar Card (Avatar, Basic details, Quick Contact details) */}
                <div className="profile-sidebar-card" style={{ background: '#F8FAFC', borderRadius: '24px', border: '1.5px solid var(--border-color)', padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  
                  {/* Big Center Avatar section */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '12px', borderBottom: '1.5px solid #E2E8F0', paddingBottom: '20px' }}>
                    <div style={{ width: '120px', height: '120px', borderRadius: '50%', overflow: 'hidden', border: '4px solid #FFFFFF', boxShadow: '0 8px 24px rgba(0,0,0,0.08)', background: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img 
                        src={selectedStudentProfile.photo} 
                        alt={selectedStudentProfile.fullName} 
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedStudentProfile.fullName || 'Student')}&background=E2E8F0&color=475569&bold=true`;
                        }}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} 
                      />
                    </div>
                    <div>
                      {isEditingStudent ? (
                        <div style={{ display: 'flex', gap: '6px', width: '100%', marginTop: '8px' }}>
                          <input
                            type="text"
                            value={editStudentFirstName}
                            onChange={(e) => setEditStudentFirstName(e.target.value)}
                            placeholder="First Name"
                            style={{ width: '50%', padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', outline: 'none' }}
                          />
                          <input
                            type="text"
                            value={editStudentLastName}
                            onChange={(e) => setEditStudentLastName(e.target.value)}
                            placeholder="Last Name"
                            style={{ width: '50%', padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', outline: 'none' }}
                          />
                        </div>
                      ) : (
                        <div style={{ fontSize: '20px', fontWeight: '800', color: 'var(--text-primary)' }}>{selectedStudentProfile.fullName}</div>
                      )}

                      {isEditingStudent ? (
                        <div style={{ display: 'flex', gap: '6px', width: '100%', marginTop: '4px', justifyContent: 'center' }}>
                          <input
                            type="text"
                            list="master_student_grades_list"
                            value={editStudentGrade}
                            onChange={(e) => setEditStudentGrade(e.target.value)}
                            placeholder="Grade e.g. Class 9"
                            style={{ width: '60%', padding: '4px 8px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border-color)', outline: 'none' }}
                          />
                          <input
                            type="text"
                            list="master_student_sections_list"
                            value={editStudentSection}
                            onChange={(e) => setEditStudentSection(e.target.value)}
                            placeholder="Sec e.g. B"
                            style={{ width: '30%', padding: '4px 8px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border-color)', outline: 'none' }}
                          />
                        </div>
                      ) : (
                        <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-secondary)', marginTop: '4px' }}>Grade {selectedStudentProfile.grade} (Sec {selectedStudentProfile.section})</div>
                      )}
                    </div>
                    
                    {isEditingStudent ? (
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', marginTop: '6px' }}>
                        <input
                          type="text"
                          value={editStudentRollNo}
                          onChange={(e) => setEditStudentRollNo(e.target.value)}
                          placeholder="Roll No"
                          style={{ width: '70px', padding: '4px 8px', fontSize: '11px', borderRadius: '6px', border: '1px solid var(--border-color)', outline: 'none' }}
                        />
                        <input
                          type="text"
                          value={editStudentAdmissionNo}
                          onChange={(e) => setEditStudentAdmissionNo(e.target.value)}
                          placeholder="Admission ID"
                          style={{ width: '110px', padding: '4px 8px', fontSize: '11px', borderRadius: '6px', border: '1px solid var(--border-color)', outline: 'none' }}
                        />
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'center', marginTop: '6px' }}>
                        <span className="meta-pill pill-yellow">Roll No: {selectedStudentProfile.rollNo}</span>
                        <span className="meta-pill pill-purple">ID: {selectedStudentProfile.admissionNo}</span>
                      </div>
                    )}
                  </div>

                  {/* Quick Bio Info */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', borderBottom: '1.5px solid #E2E8F0', paddingBottom: '20px' }}>
                    <h3 style={{ fontSize: '12px', fontWeight: '800', color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0 }}>📋 Fast Facts</h3>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                      <span style={{ color: 'var(--text-secondary)', fontWeight: '600' }}>Date of Birth:</span>
                      {isEditingStudent ? (
                        <input
                          type="text"
                          value={editStudentDob}
                          onChange={(e) => setEditStudentDob(e.target.value)}
                          placeholder="e.g. October 14, 2011"
                          style={{ padding: '4px 6px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '140px', outline: 'none' }}
                        />
                      ) : (
                        <span style={{ color: 'var(--text-primary)', fontWeight: '700' }}>{selectedStudentProfile.dob}</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                      <span style={{ color: 'var(--text-secondary)', fontWeight: '600' }}>Gender:</span>
                      {isEditingStudent ? (
                        <select
                          value={editStudentGender}
                          onChange={(e) => setEditStudentGender(e.target.value)}
                          style={{ padding: '4px 6px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border-color)', outline: 'none' }}
                        >
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </select>
                      ) : (
                        <span style={{ color: 'var(--text-primary)', fontWeight: '700' }}>{selectedStudentProfile.gender}</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                      <span style={{ color: 'var(--text-secondary)', fontWeight: '600' }}>Blood Group:</span>
                      {isEditingStudent ? (
                        <input
                          type="text"
                          value={editStudentBloodGroup}
                          onChange={(e) => setEditStudentBloodGroup(e.target.value)}
                          placeholder="e.g. B+ Positive"
                          style={{ padding: '4px 6px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '100px', outline: 'none' }}
                        />
                      ) : (
                        <span style={{ fontWeight: '700', color: '#FF7B72' }}>{selectedStudentProfile.bloodGroup}</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                      <span style={{ color: 'var(--text-secondary)', fontWeight: '600' }}>Aadhaar ID:</span>
                      {isEditingStudent ? (
                        <input
                          type="text"
                          value={editStudentAadhaar}
                          onChange={(e) => setEditStudentAadhaar(e.target.value)}
                          placeholder="e.g. 8823-1490-5572"
                          style={{ padding: '4px 6px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '140px', outline: 'none' }}
                        />
                      ) : (
                        <span style={{ color: 'var(--text-primary)', fontWeight: '700' }}>{selectedStudentProfile.aadhaar}</span>
                      )}
                    </div>
                  </div>

                  {/* Primary Contacts & Linked Parent */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <h3 style={{ fontSize: '12px', fontWeight: '800', color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0 }}>📞 Linked Parent & Guardian</h3>
                    
                    {editStudentError && <div style={{ color: 'red', fontSize: '12px', fontWeight: 'bold' }}>{editStudentError}</div>}

                    {isEditingStudent ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', textAlign: 'left' }}>
                        <div style={{ position: 'relative' }}>
                          <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Linked User Account *</label>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <div style={{ position: 'relative', flex: 1 }}>
                              <input
                                type="text"
                                value={parentSearchText}
                                onChange={(e) => {
                                  setParentSearchText(e.target.value);
                                  setShowParentDropdown(true);
                                  if (!e.target.value) setEditStudentParentId('');
                                }}
                                onFocus={() => setShowParentDropdown(true)}
                                placeholder="Search parent by name or phone..."
                                style={{ width: '100%', padding: '6px 8px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border-color)', outline: 'none' }}
                                required
                              />
                              {editStudentParentId && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditStudentParentId('');
                                    setParentSearchText('');
                                  }}
                                  style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#94A3B8', cursor: 'pointer', fontSize: '14px' }}
                                >
                                  &times;
                                </button>
                              )}
                              {showParentDropdown && (
                                <>
                                  <div 
                                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9 }} 
                                    onClick={() => setShowParentDropdown(false)} 
                                  />
                                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid var(--border-color)', borderRadius: '6px', zIndex: 10, maxHeight: '180px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', marginTop: '4px' }}>
                                    {allUsers
                                      .filter(u => u.role === 'parent')
                                      .filter(u => {
                                        if (!parentSearchText) return true;
                                        const name = (u.display_name || '').toLowerCase();
                                        const phone = (u.phone || '').toLowerCase();
                                        const query = parentSearchText.toLowerCase();
                                        return name.includes(query) || phone.includes(query);
                                      })
                                      .slice(0, 15)
                                      .map(p => (
                                        <div
                                          key={p.id}
                                          onClick={() => {
                                            setEditStudentParentId(p.id.toString());
                                            setParentSearchText(`${p.display_name} (${p.phone || 'No phone'})`);
                                            setShowParentDropdown(false);
                                          }}
                                          style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '12px', borderBottom: '1px solid #F1F5F9', textAlign: 'left' }}
                                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F8FAFC'}
                                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        >
                                          <strong style={{ color: 'var(--text-primary)' }}>{p.display_name}</strong>
                                          <span style={{ color: 'var(--text-secondary)', marginLeft: '6px', fontSize: '11px' }}>({p.phone || 'No phone'})</span>
                                        </div>
                                      ))}
                                    {allUsers.filter(u => u.role === 'parent').filter(u => {
                                      if (!parentSearchText) return true;
                                      const name = (u.display_name || '').toLowerCase();
                                      const phone = (u.phone || '').toLowerCase();
                                      const query = parentSearchText.toLowerCase();
                                      return name.includes(query) || phone.includes(query);
                                    }).length === 0 && (
                                      <div style={{ padding: '8px 12px', fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                                        No parents found. Click "Quick Add" to create one.
                                      </div>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                setQuickAddParentUsername(`parent_${Date.now().toString().slice(-6)}`);
                                setShowQuickAddParentModal(true);
                              }}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#10B981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '11px',
                                fontWeight: '700',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              ➕ Quick Add
                            </button>
                          </div>
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Emergency Contact Name</label>
                          <input
                            type="text"
                            value={editStudentEmergencyPerson}
                            onChange={(e) => setEditStudentEmergencyPerson(e.target.value)}
                            placeholder="e.g. Suresh Sharma"
                            style={{ width: '100%', padding: '6px 8px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border-color)', outline: 'none' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Emergency Contact Phone</label>
                          <input
                            type="text"
                            value={editStudentEmergencyContact}
                            onChange={(e) => setEditStudentEmergencyContact(e.target.value)}
                            placeholder="e.g. +91 98860 55432"
                            style={{ width: '100%', padding: '6px 8px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border-color)', outline: 'none' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>RFID Tag ID</label>
                          <input
                            type="text"
                            value={editStudentRfidTagId}
                            onChange={(e) => setEditStudentRfidTagId(e.target.value)}
                            placeholder="e.g. RFID_MIKE_123"
                            style={{ width: '100%', padding: '6px 8px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border-color)', outline: 'none' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Avatar Image URL</label>
                          <input
                            type="text"
                            value={editStudentAvatar}
                            onChange={(e) => setEditStudentAvatar(e.target.value)}
                            placeholder="e.g. https://images.unsplash.com/..."
                            style={{ width: '100%', padding: '6px 8px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border-color)', outline: 'none' }}
                          />
                        </div>
                      </div>
                    ) : (
                      <>
                        {selectedStudentProfile.linkedParent ? (() => {
                          const lp = selectedStudentProfile.linkedParent;
                          const lpName = (lp.display_name || lp.name || selectedStudentProfile.fatherName || 'Parent').trim();
                          const lpProfile = parentProfilesData[lpName] || Object.entries(parentProfilesData).find(([k]) => k.toLowerCase() === lpName.toLowerCase())?.[1] || {};
                          const lpAvatar = getParentAvatar(lp);
                          const lpId = lpProfile.parentId || (`#PRT-00${lp.id}`);
                          const lpRelationship = lpProfile.relationship || 'Guardian';
                          const lpPhone = lp.phone || selectedStudentProfile.primaryContact || 'N/A';

                          return (
                            <div 
                              onClick={() => {
                                setSelectedStudentProfile(null);
                                handleParentClick(lp);
                              }}
                              title="Click to view full parent profile"
                              style={{
                                backgroundColor: 'var(--surface-color)',
                                border: '1.5px solid #C7D2FE',
                                borderRadius: '16px',
                                padding: '12px',
                                cursor: 'pointer',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                transition: 'all 0.15s ease',
                                boxShadow: '0 2px 8px rgba(79, 70, 229, 0.08)'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#818CF8';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = '#C7D2FE';
                                e.currentTarget.style.transform = 'translateY(0)';
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ width: '42px', height: '42px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0, border: '2px solid #EEF2FF', background: '#EEF2FF' }}>
                                  <img 
                                    src={lpAvatar} 
                                    alt={lpName} 
                                    onError={(e) => {
                                      e.currentTarget.onerror = null;
                                      e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(lpName)}&background=E0E7FF&color=4338CA&bold=true`;
                                    }}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                                  />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left', flex: 1, minWidth: 0 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px' }}>
                                    <strong style={{ fontSize: '13px', color: '#1E1B4B', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{lpName}</strong>
                                    <span style={{ fontSize: '10px', fontWeight: '700', padding: '1px 6px', borderRadius: '4px', backgroundColor: '#EEF2FF', color: '#4F46E5' }}>{lpRelationship}</span>
                                  </div>
                                  <span style={{ fontSize: '11px', color: '#6366F1', fontWeight: '600' }}>{lpId}</span>
                                </div>
                              </div>

                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', borderTop: '1px solid #F1F5F9', paddingTop: '6px' }}>
                                <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>Mobile:</span>
                                <span style={{ color: '#2563EB', fontWeight: '700' }}>{lpPhone}</span>
                              </div>

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedStudentProfile(null);
                                  handleParentClick(lp);
                                }}
                                style={{
                                  width: '100%',
                                  padding: '6px',
                                  borderRadius: '8px',
                                  border: 'none',
                                  backgroundColor: '#EEF2FF',
                                  color: '#4F46E5',
                                  fontSize: '11px',
                                  fontWeight: '700',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '4px'
                                }}
                              >
                                <span>🔗 View Parent Profile →</span>
                              </button>
                            </div>
                          );
                        })() : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '600' }}>Father's Name & Contact:</span>
                            <span style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: '700' }}>{selectedStudentProfile.fatherName}</span>
                            <span style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: '700' }}>{selectedStudentProfile.primaryContact}</span>
                          </div>
                        )}

                        {selectedStudentProfile.motherName && selectedStudentProfile.motherName !== 'N/A' && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                            <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '600' }}>Mother's Name:</span>
                            <span style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: '700' }}>{selectedStudentProfile.motherName}</span>
                          </div>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                          <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '600' }}>Emergency Phone:</span>
                          <span style={{ fontSize: '13px', color: '#FF7B72', fontWeight: '700' }}>{selectedStudentProfile.emergencyContact}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* RIGHT COLUMN: Tab Navigation and Detailed Information Cards */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  
                  {/* Horizontal Tabs Selection */}
                  <div className="profile-tabs-nav" style={{ display: 'flex', gap: '8px', borderBottom: '2px solid #F1F3F5', paddingBottom: '16px', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    {[
                      { id: 'academic', label: '🏫 Academic Info' },
                      { id: 'address', label: '📍 Address & Route' },
                      { id: 'medical', label: '❤️ Medical Alerts' },
                      { id: 'achievements', label: '🏆 Co-Curricular' },
                      { id: 'documents', label: '📁 Documents' }
                    ].map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => setProfileActiveTab(tab.id)}
                        style={{
                          padding: '10px 18px',
                          borderRadius: '12px',
                          fontSize: '13px',
                          fontWeight: '700',
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          border: 'none',
                          backgroundColor: profileActiveTab === tab.id ? 'var(--text-primary)' : 'var(--bg-hover)',
                          color: profileActiveTab === tab.id ? 'var(--surface-color)' : 'var(--text-secondary)',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Tabs Content */}
                  <div className="profile-tabs-content" style={{ animation: 'fadeIn 0.25s ease-out' }}>
                    
                    {/* 1. ACADEMIC INFORMATION */}
                    {profileActiveTab === 'academic' && (
                      <div className="profile-section-card" style={{ width: '100%' }}>
                        <div className="profile-section-title">🏫 Academic Information</div>
                        <div className="profile-fields-list">
                          <div className="profile-field-row">
                            <span className="field-label">Grade / Standard:</span>
                            {isEditingStudent ? (
                              <input 
                                type="text" 
                                list="master_student_grades_list"
                                value={editStudentGrade} 
                                onChange={(e) => setEditStudentGrade(e.target.value)}
                                style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '200px', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value">{selectedStudentProfile.grade}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Section / Division:</span>
                            {isEditingStudent ? (
                              <input 
                                type="text" 
                                list="master_student_sections_list"
                                value={editStudentSection} 
                                onChange={(e) => setEditStudentSection(e.target.value)}
                                style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '200px', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value">{selectedStudentProfile.section}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Board of Education:</span>
                            {isEditingStudent ? (
                              <input 
                                type="text" 
                                list="master_student_boards_list"
                                value={editStudentBoard} 
                                onChange={(e) => setEditStudentBoard(e.target.value)}
                                style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '200px', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value">{selectedStudentProfile.board}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Academic Stream:</span>
                            {isEditingStudent ? (
                              <input 
                                type="text" 
                                list="master_student_streams_list"
                                value={editStudentStream} 
                                onChange={(e) => setEditStudentStream(e.target.value)}
                                style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '250px', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value">{selectedStudentProfile.stream}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Subjects:</span>
                            {isEditingStudent ? (
                              <textarea 
                                value={editStudentSubjects} 
                                onChange={(e) => setEditStudentSubjects(e.target.value)}
                                style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '100%', minHeight: '60px', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value">{selectedStudentProfile.subjects}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Optional Language:</span>
                            {isEditingStudent ? (
                              <input 
                                type="text" 
                                list="master_student_languages_list"
                                value={editStudentSecondLang} 
                                onChange={(e) => setEditStudentSecondLang(e.target.value)}
                                style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '200px', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value">{selectedStudentProfile.secondLang}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Class Teacher:</span>
                            {isEditingStudent ? (
                              <input 
                                type="text" 
                                value={editStudentClassTeacher} 
                                onChange={(e) => setEditStudentClassTeacher(e.target.value)}
                                style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '250px', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value">{selectedStudentProfile.classTeacher}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">House Name:</span>
                            {isEditingStudent ? (
                              <input 
                                type="text" 
                                list="master_student_houses_list"
                                value={editStudentHouse} 
                                onChange={(e) => setEditStudentHouse(e.target.value)}
                                style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '200px', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value">{selectedStudentProfile.house}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
 
                    {/* 2. ADDRESS & TRANSPORTATION DETAILS */}
                    {profileActiveTab === 'address' && (
                      <div className="profile-section-card" style={{ width: '100%' }}>
                        <div className="profile-section-title">📍 Address & Transportation</div>
                        <div className="profile-fields-list">
                          <div className="profile-field-row">
                            <span className="field-label">Permanent Address:</span>
                            {isEditingStudent ? (
                              <textarea 
                                value={editStudentPermanentAddress} 
                                onChange={(e) => setEditStudentPermanentAddress(e.target.value)}
                                style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '100%', minHeight: '50px', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value">{selectedStudentProfile.permanentAddress}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Residential Address:</span>
                            {isEditingStudent ? (
                              <textarea 
                                value={editStudentCurrentAddress} 
                                onChange={(e) => setEditStudentCurrentAddress(e.target.value)}
                                style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '100%', minHeight: '50px', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value">{selectedStudentProfile.currentAddress}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Mode of Transport:</span>
                            {isEditingStudent ? (
                              <select 
                                value={editStudentModeOfTransport} 
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setEditStudentModeOfTransport(val);
                                  if (val !== 'School Bus') {
                                    setEditStudentPickupStopId('');
                                    setEditStudentDropStopId('');
                                  }
                                }}
                                style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', outline: 'none' }}
                              >
                                <option value="School Bus">🚌 School Bus Transport</option>
                                <option value="No Transport">🚫 No School Transport (Opted Out)</option>
                                <option value="Private Transport">🚗 Private Transport (Self)</option>
                                <option value="Walk">🚶 Walk / Self</option>
                              </select>
                            ) : (
                              selectedStudentProfile.modeOfTransport === 'School Bus' ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: '6px', backgroundColor: '#EFF6FF', color: '#1D4ED8', fontWeight: 'bold', fontSize: '12px' }}>
                                  🚌 School Bus Transport
                                </span>
                              ) : (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: '6px', backgroundColor: '#FEF2F2', color: '#B91C1C', fontWeight: 'bold', fontSize: '12px' }}>
                                  🚫 No School Transport ({selectedStudentProfile.modeOfTransport})
                                </span>
                              )
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Pickup Route Stop:</span>
                            {isEditingStudent ? (
                              <select 
                                value={editStudentPickupStopId} 
                                disabled={editStudentModeOfTransport !== 'School Bus'}
                                onChange={(e) => setEditStudentPickupStopId(e.target.value)}
                                style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '250px', outline: 'none', backgroundColor: editStudentModeOfTransport !== 'School Bus' ? '#F1F5F9' : 'var(--bg-alt)', cursor: editStudentModeOfTransport !== 'School Bus' ? 'not-allowed' : 'pointer' }}
                              >
                                <option value="">{editStudentModeOfTransport !== 'School Bus' ? 'None (No School Transport)' : 'No Stop Assigned'}</option>
                                {editStudentModeOfTransport === 'School Bus' && allRoutes.flatMap(route => (route.stops || []).map(s => (
                                  <option key={s.id} value={s.id}>{s.stop_name} ({route.route_name})</option>
                                )))}
                              </select>
                            ) : (
                              selectedStudentProfile.modeOfTransport !== 'School Bus' ? (
                                <span className="field-value" style={{ color: '#94A3B8', fontStyle: 'italic' }}>Not Applicable (Independent Transit)</span>
                              ) : (
                                <span className="field-value">{selectedStudentProfile.assignedStop.split(' / ')[0]}</span>
                              )
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Drop-off Route Stop:</span>
                            {isEditingStudent ? (
                              <select 
                                value={editStudentDropStopId} 
                                disabled={editStudentModeOfTransport !== 'School Bus'}
                                onChange={(e) => setEditStudentDropStopId(e.target.value)}
                                style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '250px', outline: 'none', backgroundColor: editStudentModeOfTransport !== 'School Bus' ? '#F1F5F9' : 'var(--bg-alt)', cursor: editStudentModeOfTransport !== 'School Bus' ? 'not-allowed' : 'pointer' }}
                              >
                                <option value="">{editStudentModeOfTransport !== 'School Bus' ? 'None (No School Transport)' : 'No Stop Assigned'}</option>
                                {editStudentModeOfTransport === 'School Bus' && allRoutes.flatMap(route => (route.stops || []).map(s => (
                                  <option key={s.id} value={s.id}>{s.stop_name} ({route.route_name})</option>
                                )))}
                              </select>
                            ) : (
                              selectedStudentProfile.modeOfTransport !== 'School Bus' ? (
                                <span className="field-value" style={{ color: '#94A3B8', fontStyle: 'italic' }}>Not Applicable (Independent Transit)</span>
                              ) : (
                                <span className="field-value">{selectedStudentProfile.assignedStop.split(' / ')[1]}</span>
                              )
                            )}
                          </div>
                          {!isEditingStudent && selectedStudentProfile.modeOfTransport === 'School Bus' && (
                            <>
                              <div className="profile-field-row">
                                <span className="field-label">Route / Bus Number:</span>
                                <span className="field-value">{selectedStudentProfile.busNo}</span>
                              </div>
                              <div className="profile-field-row">
                                <span className="field-label">Driver & Attendant:</span>
                                <span className="field-value">{selectedStudentProfile.driverContact}</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    )}
 
                    {/* 3. MEDICAL & EMERGENCY INFO */}
                    {profileActiveTab === 'medical' && (
                      <div className="profile-section-card" style={{ width: '100%' }}>
                        <div className="profile-section-title">❤️ Medical & Emergency Information</div>
                        <div className="profile-fields-list">
                          <div className="profile-field-row">
                            <span className="field-label">Known Allergies:</span>
                            {isEditingStudent ? (
                              <input 
                                type="text" 
                                value={editStudentAllergies} 
                                onChange={(e) => setEditStudentAllergies(e.target.value)}
                                style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '100%', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value" style={{ color: selectedStudentProfile.allergies !== 'N/A' && selectedStudentProfile.allergies !== 'None' ? '#FF7B72' : 'inherit', fontWeight: selectedStudentProfile.allergies !== 'N/A' && selectedStudentProfile.allergies !== 'None' ? 'bold' : 'normal' }}>
                                {selectedStudentProfile.allergies}
                              </span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Regular Medications:</span>
                            {isEditingStudent ? (
                              <input 
                                type="text" 
                                value={editStudentMedications} 
                                onChange={(e) => setEditStudentMedications(e.target.value)}
                                style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '100%', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value">{selectedStudentProfile.medications}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Emergency Contact Person:</span>
                            {isEditingStudent ? (
                              <input 
                                type="text" 
                                value={editStudentEmergencyPerson} 
                                onChange={(e) => setEditStudentEmergencyPerson(e.target.value)}
                                style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '250px', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value">{selectedStudentProfile.emergencyPerson}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
 
                    {/* 4. CO-CURRICULAR & ACHIEVEMENTS */}
                    {profileActiveTab === 'achievements' && (
                      <div className="profile-section-card" style={{ width: '100%' }}>
                        <div className="profile-section-title">🏆 Co-Curricular & Achievements</div>
                        <div className="profile-fields-list">
                          <div className="profile-field-row">
                            <span className="field-label">Clubs & Committees:</span>
                            {isEditingStudent ? (
                              <input 
                                type="text" 
                                value={editStudentClubs} 
                                onChange={(e) => setEditStudentClubs(e.target.value)}
                                style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '100%', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value">{selectedStudentProfile.clubs}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Sports & PE Details:</span>
                            {isEditingStudent ? (
                              <input 
                                type="text" 
                                value={editStudentSports} 
                                onChange={(e) => setEditStudentSports(e.target.value)}
                                style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '100%', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value">{selectedStudentProfile.sports}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Responsibility Roles:</span>
                            {isEditingStudent ? (
                              <input 
                                type="text" 
                                value={editStudentResponsibility} 
                                onChange={(e) => setEditStudentResponsibility(e.target.value)}
                                style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '100%', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value" style={{ fontWeight: 'bold', color: '#B4B0FC' }}>{selectedStudentProfile.responsibility}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {profileActiveTab === 'documents' && (() => {
                      const studentDocs = documents.filter(doc => doc.entity_type === 'student' && doc.entity_id === selectedStudentProfile.studentData.id);
                      
                      return (
                        <div className="profile-section-card" style={{ width: '100%' }}>
                          <div className="profile-section-title">📁 Student Documents Vault</div>

                          {/* Existing Documents List */}
                          <div style={{ marginBottom: '24px' }}>
                            <h4 style={{ fontSize: '13px', fontWeight: '800', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>Current Associated Documents ({studentDocs.length})</h4>
                            
                            {studentDocs.length > 0 ? (
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px' }}>
                                {studentDocs.map(doc => {
                                  return (
                                    <div
                                      key={doc.id}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '12px 16px',
                                        backgroundColor: '#F8FAFC',
                                        border: '1.5px solid #E2E8F0',
                                        borderRadius: '12px',
                                        fontSize: '13px'
                                      }}
                                    >
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <span style={{ fontSize: '20px' }}>📄</span>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                          <strong style={{ color: 'var(--text-primary)' }}>{doc.file_name}</strong>
                                          <span style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                                            Type: {doc.document_type} • Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()} {doc.expiry_date ? `• Expiry: ${doc.expiry_date}` : ''}
                                          </span>
                                        </div>
                                      </div>
                                      
                                      <div style={{ display: 'flex', gap: '8px' }}>
                                        <a
                                          href={`${BACKEND_URL}/api/documents/${doc.id}/file`}
                                          target="_blank"
                                          rel="noreferrer"
                                          style={{
                                            padding: '6px 12px',
                                            backgroundColor: '#3B82F6',
                                            color: '#ffffff',
                                            borderRadius: '6px',
                                            fontWeight: '700',
                                            textDecoration: 'none',
                                            fontSize: '11px'
                                          }}
                                        >
                                          View File
                                        </a>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            triggerConfirm(
                                              'Confirm Unassociation',
                                              `Are you sure you want to remove document "${doc.file_name}" from this student's profile? The document will remain in the DMS vault but will be unlinked.`,
                                              async () => {
                                                try {
                                                  const res = await fetch(`${BACKEND_URL}/api/documents/${doc.id}`, {
                                                    method: 'PUT',
                                                    headers: { 'Content-Type': 'application/json' },
                                                    body: JSON.stringify({ entity_type: 'unassigned', entity_id: null })
                                                  });
                                                  if (res.ok) {
                                                    triggerAlert('Success', 'Document unlinked successfully.');
                                                    fetchDocuments('all');
                                                  } else {
                                                    triggerAlert('Error', 'Failed to unlink document.', 'error');
                                                  }
                                                } catch (err) {
                                                  triggerAlert('Error', 'Error connecting to backend.', 'error');
                                                }
                                              },
                                              () => {},
                                              true
                                            );
                                          }}
                                          style={{
                                            padding: '6px 12px',
                                            backgroundColor: '#FEE2E2',
                                            color: '#EF4444',
                                            border: 'none',
                                            borderRadius: '6px',
                                            fontWeight: '700',
                                            cursor: 'pointer',
                                            fontSize: '11px'
                                          }}
                                        >
                                          Unlink
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div style={{ padding: '20px', backgroundColor: '#F8FAFC', borderRadius: '12px', textAlign: 'center', color: '#94A3B8', fontSize: '13px', border: '1.5px dashed #E2E8F0' }}>
                                No documents currently associated with this student profile.
                              </div>
                            )}
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', borderTop: '1px solid #E2E8F0', paddingTop: '20px' }}>
                            {/* Form to Upload new Doc */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                              <h4 style={{ fontSize: '13px', fontWeight: '800', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0 }}>Upload & Link New Document</h4>
                              
                              <form
                                onSubmit={async (e) => {
                                  e.preventDefault();
                                  const fileInput = document.getElementById('studentDocFileInput');
                                  const docTypeInput = document.getElementById('studentDocTypeInput');
                                  const expiryInput = document.getElementById('studentDocExpiryInput');
                                  
                                  if (!fileInput.files || !fileInput.files[0]) {
                                    triggerAlert('Validation Error', 'Please select a file to upload.', 'warning');
                                    return;
                                  }

                                  const formData = new FormData();
                                  formData.append('file', fileInput.files[0]);
                                  formData.append('entity_type', 'student');
                                  formData.append('entity_id', selectedStudentProfile.studentData.id);
                                  formData.append('document_type', docTypeInput.value || 'Transit Pass');
                                  formData.append('expiry_date', expiryInput.value || '');

                                  try {
                                    const response = await fetch(`${BACKEND_URL}/api/documents`, {
                                      method: 'POST',
                                      body: formData
                                    });
                                    const resData = await response.json();
                                    if (resData.success) {
                                      triggerAlert('Success', 'Document uploaded and linked successfully!');
                                      fileInput.value = '';
                                      expiryInput.value = '';
                                      fetchDocuments('all');
                                    } else {
                                      triggerAlert('Error', 'Failed to upload document: ' + resData.message, 'error');
                                    }
                                  } catch (err) {
                                    triggerAlert('Error', 'Error uploading document: ' + err.message, 'error');
                                  }
                                }}
                                style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
                              >
                                <div>
                                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Select File</label>
                                  <input
                                    id="studentDocFileInput"
                                    type="file"
                                    required
                                    style={{ width: '100%', fontSize: '12px' }}
                                  />
                                </div>
                                
                                <div style={{ display: 'flex', gap: '10px' }}>
                                  <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Document Type</label>
                                    <select
                                      id="studentDocTypeInput"
                                      style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '12px' }}
                                    >
                                      {(masterLookups?.student_doc_types || DEFAULT_LOOKUPS.student_doc_types).map(t => (
                                        <option key={t} value={t}>{t}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Expiry Date</label>
                                    <input
                                      id="studentDocExpiryInput"
                                      type="date"
                                      style={{ width: '100%', padding: '5px 10px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '12px' }}
                                    />
                                  </div>
                                </div>

                                <button
                                  type="submit"
                                  style={{
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    backgroundColor: '#10B981',
                                    color: '#ffffff',
                                    fontSize: '12px',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    marginTop: '6px'
                                  }}
                                >
                                  📤 Upload & Link Document
                                </button>
                              </form>
                            </div>

                            {/* Associate Existing Document from Vault */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                              <h4 style={{ fontSize: '13px', fontWeight: '800', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0 }}>Link Existing Document from Vault</h4>
                              
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <div>
                                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Choose Document</label>
                                  <select
                                    id="linkVaultDocSelector"
                                    defaultValue=""
                                    style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '12px' }}
                                  >
                                    <option value="">-- Select document from vault --</option>
                                    {documents
                                      .filter(doc => doc.entity_type !== 'student' || doc.entity_id !== selectedStudentProfile.studentData.id)
                                      .map(doc => (
                                        <option key={doc.id} value={doc.id}>
                                          {doc.file_name} ({doc.document_type} • Category: {doc.entity_type})
                                        </option>
                                      ))}
                                  </select>
                                </div>

                                <button
                                  type="button"
                                  onClick={async () => {
                                    const selectEl = document.getElementById('linkVaultDocSelector');
                                    const docId = selectEl.value;
                                    if (!docId) {
                                      triggerAlert('Validation Error', 'Please select a document to link.', 'warning');
                                      return;
                                    }
                                    try {
                                      const res = await fetch(`${BACKEND_URL}/api/documents/${docId}`, {
                                        method: 'PUT',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({
                                          entity_type: 'student',
                                          entity_id: selectedStudentProfile.studentData.id
                                        })
                                      });
                                      if (res.ok) {
                                        triggerAlert('Success', 'Document associated successfully!');
                                        selectEl.value = '';
                                        fetchDocuments('all');
                                      } else {
                                        triggerAlert('Error', 'Failed to associate document.', 'error');
                                      }
                                    } catch (err) {
                                      triggerAlert('Error', 'Error connecting to backend.', 'error');
                                    }
                                  }}
                                  style={{
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    backgroundColor: '#3B82F6',
                                    color: '#ffffff',
                                    fontSize: '12px',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    marginTop: '6px'
                                  }}
                                >
                                  🔗 Associate Selected Document
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

              </div>
            </div>
          ) : selectedDriverProfile ? (
            <div className="student-profile-fullpage" style={{ animation: 'fadeIn 0.3s ease-out' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <button 
                    onClick={() => setSelectedDriverProfile(null)}
                    style={{
                      backgroundColor: 'var(--surface-color)',
                      border: '1.5px solid var(--border-color)',
                      borderRadius: '12px',
                      padding: '6px 12px',
                      fontSize: '11px',
                      fontWeight: '700',
                      color: 'var(--text-secondary)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    ← Back
                  </button>
                  <h1 className="page-title" style={{ fontSize: '24px', margin: 0 }}>Driver Profile Directory</h1>
                </div>

                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {!isEditingProfile ? (
                  <>
                  <button
                    onClick={() => setShowEnrollmentModalDriver(selectedDriverProfile)}
                    style={{
                      backgroundColor: '#0F766E',
                      border: 'none',
                      borderRadius: '12px',
                      padding: '8px 16px',
                      fontSize: '13px',
                      fontWeight: '700',
                      color: 'white',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    Generate Enrollment QR
                  </button>
                  <button
                    onClick={() => {
                      setIsEditingProfile(true);
                      setEditDriverId(selectedDriverProfile.driverId || '');
                      setEditDriverPhone(selectedDriverProfile.phone || '');
                      setEditDriverAddress(selectedDriverProfile.address || '');
                      setEditDriverLicenseExpiry(selectedDriverProfile.licenseExpiry || '');
                      setEditDriverLicensingStatus(selectedDriverProfile.licensingStatus || 'VALID LICENSE');
                      setEditDriverStatus(selectedDriverProfile.status || 'ACTIVE');
                      setEditDriverBloodGroup(selectedDriverProfile.bloodGroup || 'O+ Positive');
                      setEditDriverLicenseNumber(selectedDriverProfile.licenseNumber || 'DL-0420180098231');
                      setEditDriverMedicalStatus(selectedDriverProfile.medicalStatus || 'Certified Fit');
                      setEditDriverMedicalExpiry(selectedDriverProfile.medicalExpiry || 'Aug 2027');
                      setEditDriverPoliceVerification(selectedDriverProfile.policeVerification || 'Cleared & Verified');
                      setEditDriverExperienceYears(selectedDriverProfile.experienceYears || '8+ Years');
                      setEditDriverAssignedBus(selectedDriverProfile.assignedBusNumber || 'Bus 10A');
                      setEditDriverAssignedRoute(selectedDriverProfile.assignedRoute || 'Primary School Route');
                      setEditDriverError('');
                    }}
                    className="save-delay-btn"
                    style={{ padding: '8px 16px', fontSize: '13px' }}
                  >
                    Edit Profile
                  </button>
                  </>
                ) : (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => {
                        setIsEditingProfile(false);
                        setEditDriverError('');
                      }}
                      style={{
                        backgroundColor: 'var(--surface-color)',
                        border: '1.5px solid var(--border-color)',
                        borderRadius: '12px',
                        padding: '6px 12px',
                        fontSize: '11px',
                        fontWeight: '700',
                        color: 'var(--text-secondary)',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        if (!editDriverId.trim()) {
                          setEditDriverError('Driver ID is required.');
                          return;
                        }
                        if (!editDriverPhone.trim()) {
                          setEditDriverError('Phone number is required.');
                          return;
                        }
                        const updatedProfileObj = {
                          ...selectedDriverProfile,
                          driverId: editDriverId,
                          phone: editDriverPhone,
                          address: editDriverAddress,
                          licenseExpiry: editDriverLicenseExpiry,
                          licensingStatus: editDriverLicensingStatus,
                          status: editDriverStatus,
                          bloodGroup: editDriverBloodGroup,
                          licenseNumber: editDriverLicenseNumber,
                          medicalStatus: editDriverMedicalStatus,
                          medicalExpiry: editDriverMedicalExpiry,
                          policeVerification: editDriverPoliceVerification,
                          experienceYears: editDriverExperienceYears,
                          assignedBusNumber: editDriverAssignedBus,
                          assignedRoute: editDriverAssignedRoute
                        };
                        const success = await handleUpdateDriverProfile(selectedDriverProfile.name, updatedProfileObj);
                        if (success) {
                          alert('Profile updated successfully!');
                        }
                      }}
                      className="save-delay-btn"
                      style={{ padding: '8px 16px', fontSize: '13px' }}
                    >
                      Save Changes
                    </button>
                  </div>
                )}
                </div>
              </div>

            <div className="profile-layout-grid" style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '28px', alignItems: 'start' }}>
              {/* LEFT COLUMN: Sidebar Card (Avatar, Basic details, Quick Contact details) */}
              <div className="profile-sidebar-card" style={{ background: '#F8FAFC', borderRadius: '24px', border: '1.5px solid var(--border-color)', padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                
                {/* Big Center Avatar section */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '12px', borderBottom: '1.5px solid #E2E8F0', paddingBottom: '20px' }}>
                  <div style={{ width: '120px', height: '120px', borderRadius: '50%', overflow: 'hidden', border: '4px solid #FFFFFF', boxShadow: '0 8px 24px rgba(0,0,0,0.08)', backgroundColor: '#E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {selectedDriverProfile.avatar ? (
                      <img src={selectedDriverProfile.avatar} alt={selectedDriverProfile.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <User size={48} color="#94A3B8" />
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: '20px', fontWeight: '800', color: 'var(--text-primary)' }}>{selectedDriverProfile.name}</div>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-secondary)', marginTop: '4px' }}>School Bus Driver</div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'center', marginTop: '6px' }}>
                    <span className="meta-pill pill-purple">ID: {selectedDriverProfile.driverId}</span>
                    <span className="meta-pill" style={{ backgroundColor: selectedDriverProfile.status === 'ACTIVE' ? '#DCFCE7' : '#FEE2E2', color: selectedDriverProfile.status === 'ACTIVE' ? '#16A34A' : '#DC2626' }}>
                      {selectedDriverProfile.status || 'ACTIVE'}
                    </span>
                  </div>
                </div>

                {/* Fast Facts */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <h3 style={{ fontSize: '12px', fontWeight: '800', color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0 }}>📋 Fast Facts</h3>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                    <span style={{ color: 'var(--text-secondary)', fontWeight: '600' }}>Blood Group:</span>
                    {isEditingProfile ? (
                      <select 
                        value={editDriverBloodGroup} 
                        onChange={(e) => setEditDriverBloodGroup(e.target.value)}
                        style={{ padding: '4px 6px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border-color)' }}
                      >
                        <option value="A+ Positive">A+ Positive</option>
                        <option value="A- Negative">A- Negative</option>
                        <option value="B+ Positive">B+ Positive</option>
                        <option value="B- Negative">B- Negative</option>
                        <option value="O+ Positive">O+ Positive</option>
                        <option value="O- Negative">O- Negative</option>
                        <option value="AB+ Positive">AB+ Positive</option>
                        <option value="AB- Negative">AB- Negative</option>
                      </select>
                    ) : (
                      <span style={{ fontWeight: '800', color: '#DC2626', backgroundColor: '#FEE2E2', padding: '2px 8px', borderRadius: '6px' }}>{selectedDriverProfile.bloodGroup || 'O+ Positive'}</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                    <span style={{ color: 'var(--text-secondary)', fontWeight: '600' }}>Licensing:</span>
                    {isEditingProfile ? (
                      <select 
                        value={editDriverLicensingStatus} 
                        onChange={(e) => setEditDriverLicensingStatus(e.target.value)}
                        style={{ padding: '4px 6px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border-color)' }}
                      >
                        <option value="VALID LICENSE">VALID LICENSE</option>
                        <option value="EXPIRED LICENSE">EXPIRED LICENSE</option>
                        <option value="SUSPENDED">SUSPENDED</option>
                      </select>
                    ) : (
                      <span style={{ fontWeight: '700', color: selectedDriverProfile.licensingStatus === 'VALID LICENSE' ? '#2EAEA0' : '#FF7B72' }}>{selectedDriverProfile.licensingStatus}</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                    <span style={{ color: 'var(--text-secondary)', fontWeight: '600' }}>License Expiry:</span>
                    {isEditingProfile ? (
                      <input 
                        type="text" 
                        value={editDriverLicenseExpiry} 
                        onChange={(e) => setEditDriverLicenseExpiry(e.target.value)}
                        style={{ padding: '4px 6px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '100px' }}
                      />
                    ) : (
                      <span style={{ color: 'var(--text-primary)', fontWeight: '700' }}>{selectedDriverProfile.licenseExpiry}</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                    <span style={{ color: 'var(--text-secondary)', fontWeight: '600' }}>Experience:</span>
                    {isEditingProfile ? (
                      <input 
                        type="text" 
                        value={editDriverExperienceYears} 
                        onChange={(e) => setEditDriverExperienceYears(e.target.value)}
                        style={{ padding: '4px 6px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '100px' }}
                      />
                    ) : (
                      <span style={{ color: 'var(--text-primary)', fontWeight: '700' }}>{selectedDriverProfile.experienceYears || '8+ Years'}</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                    <span style={{ color: 'var(--text-secondary)', fontWeight: '600' }}>Safety Score:</span>
                    <span style={{ color: '#EAB308', fontWeight: '800' }}>★ {selectedDriverProfile.safetyScore || '99.4%'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                    <span style={{ color: 'var(--text-secondary)', fontWeight: '600' }}>On-Time Score:</span>
                    <span style={{ color: '#2563EB', fontWeight: '800' }}>⏱ {selectedDriverProfile.onTimeScore || '98.8%'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                    <span style={{ color: 'var(--text-secondary)', fontWeight: '600' }}>Status:</span>
                    {isEditingProfile ? (
                      <select 
                        value={editDriverStatus} 
                        onChange={(e) => setEditDriverStatus(e.target.value)}
                        style={{ padding: '4px 6px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border-color)' }}
                      >
                        <option value="ACTIVE">ACTIVE</option>
                        <option value="ON LEAVE">ON LEAVE</option>
                        <option value="INACTIVE">INACTIVE</option>
                      </select>
                    ) : (
                      <span style={{ fontWeight: '700', color: selectedDriverProfile.status === 'ACTIVE' ? '#2EAEA0' : '#FF7B72' }}>{selectedDriverProfile.status}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* RIGHT COLUMN: Profile Details */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                
                {/* Assigned Vehicle & Route Card */}
                <div className="profile-section-card" style={{ width: '100%' }}>
                  <div className="profile-section-title">🚌 Assigned Vehicle & Route Details</div>
                  <div className="profile-fields-list">
                    <div className="profile-field-row">
                      <span className="field-label">Assigned Bus:</span>
                      {isEditingProfile ? (
                        <input 
                          type="text" 
                          value={editDriverAssignedBus} 
                          onChange={(e) => setEditDriverAssignedBus(e.target.value)}
                          placeholder="e.g. Bus 10A"
                          style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '200px' }}
                        />
                      ) : (
                        <span className="field-value" style={{ fontWeight: '700', color: 'var(--text-primary)' }}>
                          {selectedDriverProfile.assignedBusNumber || 'Bus 10A'} ({selectedDriverProfile.assignedBusReg || 'KA-03-AA-1234'})
                        </span>
                      )}
                    </div>
                    <div className="profile-field-row">
                      <span className="field-label">Assigned Route:</span>
                      {isEditingProfile ? (
                        <input 
                          type="text" 
                          value={editDriverAssignedRoute} 
                          onChange={(e) => setEditDriverAssignedRoute(e.target.value)}
                          placeholder="e.g. Route A - Morning Pickup"
                          style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '320px' }}
                        />
                      ) : (
                        <span className="field-value">{selectedDriverProfile.assignedRoute || 'Primary School Route'}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Licensing & Compliance Card */}
                <div className="profile-section-card" style={{ width: '100%' }}>
                  <div className="profile-section-title">🛡️ Licensing & Compliance Details</div>
                  <div className="profile-fields-list">
                    <div className="profile-field-row">
                      <span className="field-label">License Number:</span>
                      {isEditingProfile ? (
                        <input 
                          type="text" 
                          value={editDriverLicenseNumber} 
                          onChange={(e) => setEditDriverLicenseNumber(e.target.value)}
                          placeholder="DL-0420180098231"
                          style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '220px' }}
                        />
                      ) : (
                        <span className="field-value" style={{ fontFamily: 'monospace', fontWeight: '700', color: 'var(--text-primary)' }}>
                          {selectedDriverProfile.licenseNumber || 'DL-0420180098231'}
                        </span>
                      )}
                    </div>
                    <div className="profile-field-row">
                      <span className="field-label">License Expiry:</span>
                      {isEditingProfile ? (
                        <input 
                          type="text" 
                          value={editDriverLicenseExpiry} 
                          onChange={(e) => setEditDriverLicenseExpiry(e.target.value)}
                          style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '160px' }}
                        />
                      ) : (
                        <span className="field-value">{selectedDriverProfile.licenseExpiry}</span>
                      )}
                    </div>
                    <div className="profile-field-row">
                      <span className="field-label">Medical Fitness:</span>
                      {isEditingProfile ? (
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <input 
                            type="text" 
                            list="master_driver_medical_statuses_list"
                            value={editDriverMedicalStatus} 
                            onChange={(e) => setEditDriverMedicalStatus(e.target.value)}
                            placeholder="Certified Fit"
                            style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '150px' }}
                          />
                          <input 
                            type="text" 
                            value={editDriverMedicalExpiry} 
                            onChange={(e) => setEditDriverMedicalExpiry(e.target.value)}
                            placeholder="Expiry (Aug 2027)"
                            style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '130px' }}
                          />
                        </div>
                      ) : (
                        <span className="field-value" style={{ color: '#16A34A', fontWeight: '700' }}>
                          {selectedDriverProfile.medicalStatus || 'Certified Fit'} ({selectedDriverProfile.medicalExpiry || 'Aug 2027'})
                        </span>
                      )}
                    </div>
                    <div className="profile-field-row">
                      <span className="field-label">Police Verification:</span>
                      {isEditingProfile ? (
                        <input 
                          type="text" 
                          list="master_driver_police_statuses_list"
                          value={editDriverPoliceVerification} 
                          onChange={(e) => setEditDriverPoliceVerification(e.target.value)}
                          placeholder="Cleared & Verified"
                          style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '220px' }}
                        />
                      ) : (
                        <span className="field-value" style={{ color: '#16A34A', fontWeight: '700' }}>
                          {selectedDriverProfile.policeVerification || 'Cleared & Verified'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contact & Personal Information Card */}
                <div className="profile-section-card" style={{ width: '100%' }}>
                  <div className="profile-section-title">📞 Contact & Address Details</div>
                  {editDriverError && <div style={{ color: 'var(--coral-red)', fontSize: '13px', marginBottom: '8px', fontWeight: '500' }}>{editDriverError}</div>}
                  <div className="profile-fields-list">
                    {selectedDriverProfile.isNew && isEditingProfile ? (
                      <>
                        <div className="profile-field-row">
                          <span className="field-label">Full Name:</span>
                          <input 
                            type="text" 
                            value={editDriverDisplayName} 
                            onChange={(e) => setEditDriverDisplayName(e.target.value)}
                            placeholder="e.g. Ramesh Kumar"
                            style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '200px' }}
                            required
                          />
                        </div>
                        <div className="profile-field-row">
                          <span className="field-label">Username (for Login):</span>
                          <input 
                            type="text" 
                            value={editDriverUsername} 
                            onChange={(e) => setEditDriverUsername(e.target.value)}
                            placeholder="e.g. driver_ramesh"
                            style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '200px' }}
                            required
                          />
                        </div>
                        <div className="profile-field-row">
                          <span className="field-label">Password:</span>
                          <input 
                            type="password" 
                            value={editDriverPassword} 
                            onChange={(e) => setEditDriverPassword(e.target.value)}
                            placeholder="••••••••"
                            style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '200px' }}
                            required
                          />
                        </div>
                      </>
                    ) : (
                      <div className="profile-field-row">
                        <span className="field-label">Full Name:</span>
                        <span className="field-value">{selectedDriverProfile.name}</span>
                      </div>
                    )}
                    <div className="profile-field-row">
                      <span className="field-label">Driver ID:</span>
                      {isEditingProfile ? (
                        <input 
                          type="text" 
                          value={editDriverId} 
                          onChange={(e) => setEditDriverId(e.target.value)}
                          style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '200px' }}
                          required
                        />
                      ) : (
                        <span className="field-value">{selectedDriverProfile.driverId}</span>
                      )}
                    </div>
                    <div className="profile-field-row">
                      <span className="field-label">Mobile Number:</span>
                      {isEditingProfile ? (
                        <input 
                          type="text" 
                          value={editDriverPhone} 
                          onChange={(e) => setEditDriverPhone(e.target.value)}
                          style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '200px' }}
                        />
                      ) : (
                        <span className="field-value">{selectedDriverProfile.phone}</span>
                      )}
                    </div>
                    <div className="profile-field-row">
                      <span className="field-label">Blood Group:</span>
                      {isEditingProfile ? (
                        <select 
                          value={editDriverBloodGroup} 
                          onChange={(e) => setEditDriverBloodGroup(e.target.value)}
                          style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '200px' }}
                        >
                          <option value="A+ Positive">A+ Positive</option>
                          <option value="A- Negative">A- Negative</option>
                          <option value="B+ Positive">B+ Positive</option>
                          <option value="B- Negative">B- Negative</option>
                          <option value="O+ Positive">O+ Positive</option>
                          <option value="O- Negative">O- Negative</option>
                          <option value="AB+ Positive">AB+ Positive</option>
                          <option value="AB- Negative">AB- Negative</option>
                        </select>
                      ) : (
                        <span className="field-value" style={{ color: '#DC2626', fontWeight: '800' }}>
                          {selectedDriverProfile.bloodGroup || 'O+ Positive'}
                        </span>
                      )}
                    </div>
                    <div className="profile-field-row">
                      <span className="field-label">Residential Address:</span>
                      {isEditingProfile ? (
                        <textarea 
                          value={editDriverAddress} 
                          onChange={(e) => setEditDriverAddress(e.target.value)}
                          style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '100%', minHeight: '60px' }}
                        />
                      ) : (
                        <span className="field-value">{selectedDriverProfile.address}</span>
                      )}
                    </div>
                  </div>
                </div>

                  {/* Documents Card with Simulated Upload */}
                  <div className="profile-section-card" style={{ width: '100%' }}>
                    <div className="profile-section-title">📁 Documents Vault & Upload</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      
                      {/* Document Grid List */}
                      <div className="doc-grid" style={{ gridTemplateColumns: '1fr', gap: '12px' }}>
                        {selectedDriverProfile.documents && selectedDriverProfile.documents.length > 0 ? (
                          selectedDriverProfile.documents.map((doc, idx) => (
                            <div key={idx} className="doc-card" style={{ width: '100%', boxSizing: 'border-box' }}>
                              <div className="doc-icon-box"><FileText size={20} /></div>
                              <div className="doc-info">
                                <span className="doc-name">{doc.name}</span>
                                <span className="doc-meta">{doc.meta}</span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div style={{ fontSize: '13px', color: 'var(--text-secondary)', fontStyle: 'italic', padding: '10px 0' }}>No documents uploaded yet.</div>
                        )}
                      </div>

                      {/* File Selector Upload Area */}
                      <div style={{ border: '2px dashed #E2E8F0', borderRadius: '12px', padding: '20px', textAlign: 'center', background: '#F8FAFC' }}>
                        <FileText size={32} color="#7E8794" style={{ margin: '0 auto 10px auto' }} />
                        <div style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--text-primary)', marginBottom: '4px' }}>Upload Driver Documents</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '12px' }}>Support PDF, PNG, JPG files up to 10MB</div>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', maxWidth: '300px', margin: '0 auto' }}>
                          <div style={{ width: '100%', textAlign: 'left' }}>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Document Classification</label>
                            <select
                              id="driverProfileDocTypeSelect"
                              style={{ width: '100%', padding: '7px 10px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '12px', background: '#FFFFFF' }}
                            >
                              {(masterLookups?.driver_doc_types || DEFAULT_LOOKUPS.driver_doc_types).map(t => (
                                <option key={t} value={t}>{t}</option>
                              ))}
                            </select>
                          </div>

                          <label 
                            style={{
                              display: 'inline-block',
                              backgroundColor: 'var(--text-primary)',
                              color: '#FFFFFF',
                              padding: '8px 20px',
                              borderRadius: '8px',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              cursor: 'pointer',
                              transition: 'background 0.2s',
                              width: '100%',
                              boxSizing: 'border-box',
                              textAlign: 'center'
                            }}
                          >
                            Select File & Upload
                            <input 
                              type="file" 
                              style={{ display: 'none' }} 
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  const sizeStr = file.size > 1024 * 1024 
                                    ? `${(file.size / (1024 * 1024)).toFixed(1)} MB` 
                                    : `${(file.size / 1024).toFixed(0)} KB`;
                                  const docTypeSelect = document.getElementById('driverProfileDocTypeSelect');
                                  const chosenType = docTypeSelect ? docTypeSelect.value : 'Driving License';
                                  handleUploadDriverDocument(selectedDriverProfile.name, file.name, sizeStr, chosenType);
                                }
                              }}
                            />
                          </label>
                        </div>
                      </div>

                    </div>
                  </div>

                </div>
              </div>
              {showEnrollmentModalDriver && (() => {
                const resolvedDriverId = showEnrollmentModalDriver.id || 
                  allUsers.find(u => (u.role === 'driver' || u.role === 'DRIVER') && 
                    ((u.display_name && (u.display_name === showEnrollmentModalDriver.name || u.display_name === showEnrollmentModalDriver.driverName || u.display_name === showEnrollmentModalDriver.display_name)) ||
                     (u.username && (u.username === showEnrollmentModalDriver.username || u.username === showEnrollmentModalDriver.driverId)))
                  )?.id || showEnrollmentModalDriver.driverId;

                return (
                  <DriverEnrollmentModal
                    driverId={resolvedDriverId}
                    driverName={showEnrollmentModalDriver.driverName || showEnrollmentModalDriver.display_name || showEnrollmentModalDriver.name}
                    backendUrl={BACKEND_URL}
                    onClose={() => setShowEnrollmentModalDriver(null)}
                  />
                );
              })()}
            </div>
          ) : selectedParentProfile ? (
            <div className="student-profile-fullpage" style={{ animation: 'fadeIn 0.3s ease-out' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <button 
                    onClick={() => { setSelectedParentProfile(null); setIsEditingParent(false); }}
                    style={{
                      backgroundColor: 'var(--surface-color)',
                      border: '1.5px solid var(--border-color)',
                      borderRadius: '12px',
                      padding: '6px 12px',
                      fontSize: '11px',
                      fontWeight: '700',
                      color: 'var(--text-secondary)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    ← Back
                  </button>
                  <h1 className="page-title" style={{ fontSize: '24px', margin: 0 }}>Parent & Guardian Profile</h1>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {!isEditingParent ? (
                    <>
                      <button
                        onClick={() => setIsEditingParent(true)}
                        className="save-delay-btn"
                        style={{ padding: '8px 16px', fontSize: '13px' }}
                      >
                        Edit Profile
                      </button>
                      <button
                        onClick={() => handleDeleteParent(selectedParentProfile.id)}
                        style={{
                          backgroundColor: '#EF4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '12px',
                          padding: '8px 16px',
                          fontSize: '13px',
                          fontWeight: '700',
                          cursor: 'pointer'
                        }}
                      >
                        Delete Parent
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          setIsEditingParent(false);
                          setEditParentError('');
                        }}
                        style={{
                          backgroundColor: 'var(--surface-color)',
                          border: '1.5px solid var(--border-color)',
                          borderRadius: '12px',
                          padding: '8px 16px',
                          fontSize: '13px',
                          fontWeight: '700',
                          color: 'var(--text-secondary)',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleUpdateParentProfile}
                        disabled={savingParent}
                        className="save-delay-btn"
                        style={{ padding: '8px 16px', fontSize: '13px', opacity: savingParent ? 0.7 : 1 }}
                      >
                        {savingParent ? 'Saving...' : 'Save Changes'}
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="profile-layout-grid" style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '28px', alignItems: 'start' }}>
                {/* LEFT COLUMN: Sidebar Card */}
                <div className="profile-sidebar-card" style={{ background: '#F8FAFC', borderRadius: '24px', border: '1.5px solid var(--border-color)', padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  
                  {/* Big Center Avatar section */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '12px', borderBottom: '1.5px solid #E2E8F0', paddingBottom: '20px' }}>
                    <div style={{ width: '120px', height: '120px', borderRadius: '50%', overflow: 'hidden', border: '4px solid #FFFFFF', boxShadow: '0 8px 24px rgba(0,0,0,0.08)', backgroundColor: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img 
                        src={selectedParentProfile.avatar} 
                        alt={selectedParentProfile.name}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedParentProfile.name || 'Parent')}&background=E0E7FF&color=4338CA&bold=true`;
                        }}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                    </div>
                    <div>
                      {isEditingParent ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', textAlign: 'left', width: '100%', marginTop: '8px' }}>
                          <div>
                            <label style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)' }}>Full Name *</label>
                            <input
                              type="text"
                              value={editParentDisplayName}
                              onChange={(e) => setEditParentDisplayName(e.target.value)}
                              style={{ width: '100%', padding: '4px 8px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border-color)', outline: 'none' }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)' }}>Occupation</label>
                            <input
                              type="text"
                              value={editParentOccupation}
                              onChange={(e) => setEditParentOccupation(e.target.value)}
                              style={{ width: '100%', padding: '4px 8px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border-color)', outline: 'none' }}
                            />
                          </div>
                        </div>
                      ) : (
                        <>
                          <div style={{ fontSize: '20px', fontWeight: '800', color: 'var(--text-primary)' }}>{selectedParentProfile.name}</div>
                          <div style={{ fontSize: '13px', fontWeight: '700', color: '#4F46E5', marginTop: '4px' }}>{selectedParentProfile.occupation || 'Parent / Guardian'}</div>
                        </>
                      )}
                    </div>
                    
                    {isEditingParent ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', textAlign: 'left', width: '100%' }}>
                        <div>
                          <label style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)' }}>Username *</label>
                          <input
                            type="text"
                            value={editParentUsername}
                            onChange={(e) => setEditParentUsername(e.target.value)}
                            style={{ width: '100%', padding: '4px 8px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border-color)', outline: 'none' }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)' }}>New Password (leave empty to keep current)</label>
                          <input
                            type="password"
                            value={editParentPassword}
                            onChange={(e) => setEditParentPassword(e.target.value)}
                            placeholder="••••••••"
                            style={{ width: '100%', padding: '4px 8px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border-color)', outline: 'none' }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)' }}>Relationship</label>
                          <select
                            value={editParentRelationship}
                            onChange={(e) => setEditParentRelationship(e.target.value)}
                            style={{ width: '100%', padding: '4px 8px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border-color)', outline: 'none' }}
                          >
                            <option value="Father">Father</option>
                            <option value="Mother">Mother</option>
                            <option value="Guardian">Guardian / Relative</option>
                            <option value="Spouse">Spouse</option>
                          </select>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'center', marginTop: '6px' }}>
                        <span className="meta-pill pill-purple">ID: {selectedParentProfile.parentId}</span>
                        <span className="meta-pill pill-yellow">{selectedParentProfile.relationship || 'Guardian'}</span>
                      </div>
                    )}
                  </div>

                  {/* Fast Facts */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', borderBottom: '1.5px solid #E2E8F0', paddingBottom: '20px' }}>
                    <h3 style={{ fontSize: '12px', fontWeight: '800', color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0 }}>📋 Fast Facts</h3>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                      <span style={{ color: 'var(--text-secondary)', fontWeight: '600' }}>Primary Phone:</span>
                      {isEditingParent ? (
                        <input
                          type="text"
                          value={editParentPhone}
                          onChange={(e) => setEditParentPhone(e.target.value)}
                          style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '150px', outline: 'none' }}
                        />
                      ) : (
                        <a href={`tel:${selectedParentProfile.phone}`} style={{ color: '#2563EB', fontWeight: '700', textDecoration: 'none' }}>{selectedParentProfile.phone}</a>
                      )}
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                      <span style={{ color: 'var(--text-secondary)', fontWeight: '600' }}>Email Address:</span>
                      {isEditingParent ? (
                        <input
                          type="email"
                          value={editParentEmail}
                          onChange={(e) => setEditParentEmail(e.target.value)}
                          style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border-color)', width: '150px', outline: 'none' }}
                        />
                      ) : (
                        <a href={`mailto:${selectedParentProfile.email}`} style={{ color: 'var(--text-primary)', fontWeight: '700', textDecoration: 'none', maxWidth: '170px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedParentProfile.email}</a>
                      )}
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                      <span style={{ color: 'var(--text-secondary)', fontWeight: '600' }}>Language:</span>
                      {isEditingParent ? (
                        <select
                          value={editParentPreferredLanguage}
                          onChange={(e) => setEditParentPreferredLanguage(e.target.value)}
                          style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border-color)', outline: 'none' }}
                        >
                          <option value="English">English</option>
                          <option value="Hindi">Hindi</option>
                          <option value="Kannada">Kannada</option>
                          <option value="Tamil">Tamil</option>
                          <option value="Telugu">Telugu</option>
                          <option value="Spanish">Spanish</option>
                        </select>
                      ) : (
                        <span style={{ color: 'var(--text-primary)', fontWeight: '700' }}>{selectedParentProfile.preferredLanguage}</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: 'var(--text-secondary)', fontWeight: '600' }}>Linked Wards:</span>
                      <span style={{ fontWeight: '700', color: 'var(--text-primary)' }}>{selectedParentProfile.linkedStudents ? selectedParentProfile.linkedStudents.length : 0} student(s)</span>
                    </div>
                  </div>

                  {/* Emergency & Spouse */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <h3 style={{ fontSize: '12px', fontWeight: '800', color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0 }}>🚨 Emergency & Spouse</h3>
                    
                    {editParentError && <div style={{ color: 'red', fontSize: '12px', fontWeight: 'bold' }}>{editParentError}</div>}

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'left' }}>
                      <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '600' }}>Spouse / Secondary Guardian:</span>
                      {isEditingParent ? (
                        <input
                          type="text"
                          value={editParentSpouseName}
                          onChange={(e) => setEditParentSpouseName(e.target.value)}
                          style={{ width: '100%', padding: '4px 8px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border-color)', outline: 'none' }}
                        />
                      ) : (
                        <span style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: '700' }}>{selectedParentProfile.spouseName || 'N/A'}</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px', textAlign: 'left' }}>
                      <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '600' }}>Emergency Contact:</span>
                      {isEditingParent ? (
                        <input
                          type="text"
                          value={editParentEmergencyContact}
                          onChange={(e) => setEditParentEmergencyContact(e.target.value)}
                          style={{ width: '100%', padding: '4px 8px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border-color)', outline: 'none' }}
                        />
                      ) : (
                        <span style={{ fontSize: '13px', color: '#FF7B72', fontWeight: '700' }}>{selectedParentProfile.emergencyContact}</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px', textAlign: 'left' }}>
                      <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '600' }}>Pickup Authorization:</span>
                      {isEditingParent ? (
                        <select
                          value={editParentPickupAuth}
                          onChange={(e) => setEditParentPickupAuth(e.target.value)}
                          style={{ width: '100%', padding: '4px 8px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border-color)', outline: 'none' }}
                        >
                          <option value="Authorized">Authorized</option>
                          <option value="Authorized for registered guardians">Authorized for registered guardians</option>
                          <option value="Restricted">Restricted / Suspended</option>
                          <option value="Pending Verification">Pending Verification</option>
                        </select>
                      ) : (
                        <span style={{ fontSize: '12px', color: '#059669', fontWeight: '600' }}>✓ {selectedParentProfile.pickupAuth}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* RIGHT COLUMN: Tabs and Details */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {/* Tabs Nav */}
                  <div className="profile-tabs-nav" style={{ display: 'flex', gap: '8px', borderBottom: '2px solid #F1F3F5', paddingBottom: '16px', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    {[
                      { id: 'wards', label: `👨‍👩‍👧 Linked Wards (${selectedParentProfile.linkedStudents ? selectedParentProfile.linkedStudents.length : 0})` },
                      { id: 'contact', label: '📍 Address & Contact Info' },
                      { id: 'notifications', label: '🔔 Alerts & Notifications' },
                      { id: 'activity', label: '💬 Activity & Support' }
                    ].map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => setParentProfileActiveTab(tab.id)}
                        style={{
                          padding: '10px 18px',
                          borderRadius: '12px',
                          fontSize: '13px',
                          fontWeight: '700',
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          border: 'none',
                          backgroundColor: parentProfileActiveTab === tab.id ? 'var(--text-primary)' : 'var(--bg-hover)',
                          color: parentProfileActiveTab === tab.id ? 'var(--surface-color)' : 'var(--text-secondary)',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Tabs Content */}
                  <div className="profile-tabs-content" style={{ animation: 'fadeIn 0.25s ease-out' }}>
                    {/* TAB 1: LINKED WARDS */}
                    {parentProfileActiveTab === 'wards' && (
                      <div className="profile-section-card" style={{ width: '100%' }}>
                        <div className="profile-section-title">👨‍👩‍👧 Linked Students / Wards</div>
                        {isEditingParent ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '12px' }}>
                            <div style={{ padding: '12px', backgroundColor: '#F8FAFC', borderRadius: '12px', border: '1.5px dashed #CBD5E1', display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-secondary)' }}>Link a new student:</span>
                              <select
                                id="linkStudentSelector"
                                defaultValue=""
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (val) {
                                    const sId = parseInt(val);
                                    const student = allStudents.find(s => s.id === sId);
                                    if (student) {
                                      if (student.parent_id && student.parent_id !== selectedParentProfile.id) {
                                        const otherParent = allUsers.find(u => u.id === student.parent_id);
                                        const otherParentName = otherParent ? (otherParent.display_name || otherParent.name) : `Parent ID ${student.parent_id}`;
                                        
                                        triggerConfirm(
                                          'Confirm Reassignment',
                                          `Warning: "${student.first_name} ${student.last_name}" is already linked to parent "${otherParentName}". Are you sure you want to reassign this student to the current parent?`,
                                          () => {
                                            if (!editParentStudentIds.includes(sId)) {
                                              setEditParentStudentIds(prev => [...prev, sId]);
                                            }
                                          }
                                        );
                                      } else {
                                        if (!editParentStudentIds.includes(sId)) {
                                          setEditParentStudentIds(prev => [...prev, sId]);
                                        }
                                      }
                                    }
                                    e.target.value = "";
                                  }
                                }}
                                style={{ flex: 1, padding: '6px 12px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none' }}
                              >
                                <option value="">Select student to link...</option>
                                {allStudents
                                  .filter(s => !editParentStudentIds.includes(s.id))
                                  .map(s => (
                                    <option key={s.id} value={s.id}>
                                      {s.first_name} {s.last_name} (RFID: {s.rfid_tag_id || 'None'})
                                    </option>
                                  ))}
                              </select>
                            </div>

                            {editParentStudentIds.length > 0 ? (
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                                {editParentStudentIds.map(sId => {
                                  const student = allStudents.find(s => s.id === sId);
                                  if (!student) return null;
                                  const sFirstName = (student.first_name || '').trim();
                                  const sProfile = studentProfilesData[sFirstName] || {};
                                  const sAvatar = getStudentAvatar(student);
                                  const sAdm = sProfile.admissionNo || ('ADM-00' + student.id);
                                  const sGrade = student.grade || sProfile.grade || 'N/A';
                                  
                                  return (
                                    <div
                                      key={student.id}
                                      style={{
                                        border: '1.5px solid var(--border-color)',
                                        borderRadius: '16px',
                                        padding: '16px',
                                        backgroundColor: 'var(--surface-color)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '12px'
                                      }}
                                    >
                                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                          <div style={{ width: '40px', height: '40px', borderRadius: '50%', overflow: 'hidden', background: '#F1F5F9', border: '2px solid #E2E8F0', flexShrink: 0 }}>
                                            <img src={sAvatar} alt={student.first_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                          </div>
                                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <strong style={{ fontSize: '14px', color: 'var(--text-primary)' }}>{student.first_name} {student.last_name}</strong>
                                            <span style={{ fontSize: '11px', fontWeight: '700', color: '#2563EB' }}>{sAdm} ({sGrade})</span>
                                          </div>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            triggerConfirm(
                                              'Confirm Removal',
                                              `Are you sure you want to remove the link to student "${student.first_name} ${student.last_name}"?`,
                                              () => {
                                                setEditParentStudentIds(prev => prev.filter(id => id !== student.id));
                                              },
                                              () => {},
                                              true
                                            );
                                          }}
                                          style={{
                                            padding: '4px 8px',
                                            backgroundColor: '#FEE2E2',
                                            color: '#EF4444',
                                            border: 'none',
                                            borderRadius: '6px',
                                            fontSize: '11px',
                                            fontWeight: '700',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          Remove Link
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div style={{ padding: '24px', textAlign: 'center', color: '#94A3B8', fontSize: '13px' }}>
                                No students currently linked to this parent profile. Use the selector above to add.
                              </div>
                            )}
                          </div>
                        ) : (
                          <>
                            {selectedParentProfile.linkedStudents && selectedParentProfile.linkedStudents.length > 0 ? (
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px', marginTop: '12px' }}>
                                {selectedParentProfile.linkedStudents.map(student => {
                                  const sFirstName = (student.first_name || '').trim();
                                  const sProfile = studentProfilesData[sFirstName] || 
                                                  studentProfilesData[sFirstName.charAt(0).toUpperCase() + sFirstName.slice(1).toLowerCase()] || 
                                                  Object.entries(studentProfilesData).find(([k]) => k.toLowerCase() === sFirstName.toLowerCase())?.[1] || {};
                                  const sAvatar = getStudentAvatar(student);
                                  const sAdm = sProfile.admissionNo || ('ADM-00' + student.id);
                                  const sGrade = student.grade || sProfile.grade || 'N/A';
                                  const sSec = student.section || sProfile.section || '';

                                  return (
                                    <div
                                      key={student.id}
                                      style={{
                                        border: '1.5px solid var(--border-color)',
                                        borderRadius: '16px',
                                        padding: '16px',
                                        backgroundColor: 'var(--surface-color)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '12px',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
                                      }}
                                    >
                                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                        <div style={{ width: '40px',
                                      height: '40px', borderRadius: '50%', overflow: 'hidden', background: '#F1F5F9', border: '2px solid #E2E8F0', flexShrink: 0 }}>
                                          <img src={sAvatar} alt={student.first_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                          <strong style={{ fontSize: '14px', color: 'var(--text-primary)' }}>{student.first_name} {student.last_name}</strong>
                                          <div style={{ display: 'flex', gap: '4px', marginTop: '2px', flexWrap: 'wrap' }}>
                                            <span style={{ fontSize: '11px', fontWeight: '700', color: '#2563EB', background: '#EFF6FF', padding: '1px 6px', borderRadius: '4px' }}>{sAdm}</span>
                                            <span style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', background: '#F1F5F9', padding: '1px 6px', borderRadius: '4px' }}>{sGrade} {sSec ? `(${sSec})` : ''}</span>
                                          </div>
                                        </div>
                                      </div>

                                      <div style={{ background: '#F8FAFC', borderRadius: '8px', padding: '8px 10px', fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                          <span style={{ color: 'var(--text-secondary)' }}>RFID Tag:</span>
                                          <strong style={{ color: student.rfid_tag_id ? '#15803D' : '#DC2626' }}>{student.rfid_tag_id || 'Unassigned'}</strong>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                          <span style={{ color: 'var(--text-secondary)' }}>Bus Route:</span>
                                          <strong style={{ color: 'var(--text-primary)' }}>{sProfile.busNo || 'Bus 10A'}</strong>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                                          <span style={{ color: '#10B981' }}>●</span> <span>{student.pickup_stop_name || 'Pickup Stop'}</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                                          <span style={{ color: '#EF4444' }}>●</span> <span>{student.dropoff_stop_name || 'Drop Stop'}</span>
                                        </div>
                                      </div>

                                      <button
                                        onClick={() => {
                                          setSelectedParentProfile(null);
                                          handleStudentClick(student);
                                        }}
                                        style={{
                                          padding: '7px 12px',
                                          borderRadius: '8px',
                                          border: '1px solid #CBD5E1',
                                          backgroundColor: 'var(--bg-alt)',
                                          color: 'var(--text-primary)',
                                          fontSize: '12px',
                                          fontWeight: '700',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          gap: '6px'
                                        }}
                                      >
                                        👤 View Student Profile
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div style={{ padding: '24px', textAlign: 'center', color: '#94A3B8', fontSize: '13px' }}>
                                No student profiles currently mapped to this parent account.
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {/* TAB 2: ADDRESS & CONTACT */}
                    {parentProfileActiveTab === 'contact' && (
                      <div className="profile-section-card" style={{ width: '100%' }}>
                        <div className="profile-section-title">📍 Address & Communication Details</div>
                        <div className="profile-fields-list">
                          <div className="profile-field-row">
                            <span className="field-label">Residential Address:</span>
                            {isEditingParent ? (
                              <textarea
                                value={editParentAddress}
                                onChange={(e) => setEditParentAddress(e.target.value)}
                                style={{ flex: 1, padding: '6px 10px', fontSize: '13px', borderRadius: '8px', border: '1px solid var(--border-color)', minHeight: '60px', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value">{selectedParentProfile.address}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Primary Mobile:</span>
                            {isEditingParent ? (
                              <input
                                type="text"
                                value={editParentPhone}
                                onChange={(e) => setEditParentPhone(e.target.value)}
                                style={{ flex: 1, padding: '6px 10px', fontSize: '13px', borderRadius: '8px', border: '1px solid var(--border-color)', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value">{selectedParentProfile.phone}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Alternate / Emergency:</span>
                            {isEditingParent ? (
                              <input
                                type="text"
                                value={editParentAltPhone}
                                onChange={(e) => setEditParentAltPhone(e.target.value)}
                                style={{ flex: 1, padding: '6px 10px', fontSize: '13px', borderRadius: '8px', border: '1px solid var(--border-color)', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value">{selectedParentProfile.altPhone}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Email Address:</span>
                            {isEditingParent ? (
                              <input
                                type="email"
                                value={editParentEmail}
                                onChange={(e) => setEditParentEmail(e.target.value)}
                                style={{ flex: 1, padding: '6px 10px', fontSize: '13px', borderRadius: '8px', border: '1px solid var(--border-color)', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value">{selectedParentProfile.email}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Pickup Authorized Persons:</span>
                            {isEditingParent ? (
                              <select
                                value={editParentPickupAuth}
                                onChange={(e) => setEditParentPickupAuth(e.target.value)}
                                style={{ flex: 1, padding: '6px 10px', fontSize: '13px', borderRadius: '8px', border: '1px solid var(--border-color)', outline: 'none' }}
                              >
                                <option value="Authorized">Authorized</option>
                                <option value="Authorized for registered guardians">Authorized for registered guardians</option>
                                <option value="Restricted">Restricted / Suspended</option>
                                <option value="Pending Verification">Pending Verification</option>
                              </select>
                            ) : (
                              <span className="field-value" style={{ color: '#059669', fontWeight: '700' }}>{selectedParentProfile.pickupAuth}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Preferred Contact Language:</span>
                            {isEditingParent ? (
                              <select
                                value={editParentPreferredLanguage}
                                onChange={(e) => setEditParentPreferredLanguage(e.target.value)}
                                style={{ flex: 1, padding: '6px 10px', fontSize: '13px', borderRadius: '8px', border: '1px solid var(--border-color)', outline: 'none' }}
                              >
                                <option value="English">English</option>
                                <option value="Hindi">Hindi</option>
                                <option value="Kannada">Kannada</option>
                                <option value="Tamil">Tamil</option>
                                <option value="Telugu">Telugu</option>
                                <option value="Spanish">Spanish</option>
                              </select>
                            ) : (
                              <span className="field-value">{selectedParentProfile.preferredLanguage}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* TAB 3: NOTIFICATIONS */}
                    {parentProfileActiveTab === 'notifications' && (
                      <div className="profile-section-card" style={{ width: '100%' }}>
                        <div className="profile-section-title">🔔 Alert Channels & Notification Settings</div>
                        <div className="profile-fields-list">
                          <div className="profile-field-row">
                            <span className="field-label">SMS Alerts:</span>
                            {isEditingParent ? (
                              <input
                                type="checkbox"
                                checked={editParentSmsEnabled}
                                onChange={(e) => setEditParentSmsEnabled(e.target.checked)}
                                style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                              />
                            ) : (
                              <span className="field-value" style={{ color: '#16A34A', fontWeight: '700' }}>{selectedParentProfile.smsEnabled ? '✓ Enabled (Real-time dispatch)' : 'Disabled'}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Mobile App Push:</span>
                            {isEditingParent ? (
                              <input
                                type="checkbox"
                                checked={editParentPushEnabled}
                                onChange={(e) => setEditParentPushEnabled(e.target.checked)}
                                style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                              />
                            ) : (
                              <span className="field-value" style={{ color: '#16A34A', fontWeight: '700' }}>{selectedParentProfile.pushEnabled ? '✓ Enabled (FCM Token Active)' : 'Disabled'}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">ETA Proximity Trigger:</span>
                            {isEditingParent ? (
                              <select
                                value={editParentEtaAlertSetting}
                                onChange={(e) => setEditParentEtaAlertSetting(e.target.value)}
                                style={{ flex: 1, padding: '6px 10px', fontSize: '13px', borderRadius: '8px', border: '1px solid var(--border-color)', outline: 'none' }}
                              >
                                <option value="2 mins before arrival">2 mins before arrival</option>
                                <option value="5 mins before arrival">5 mins before arrival</option>
                                <option value="10 mins before arrival">10 mins before arrival</option>
                                <option value="15 mins before arrival">15 mins before arrival</option>
                              </select>
                            ) : (
                              <span className="field-value" style={{ fontWeight: '700', color: '#2563EB' }}>{selectedParentProfile.etaAlertSetting}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Speed / Delay Warnings:</span>
                            <span className="field-value" style={{ color: '#16A34A', fontWeight: '700' }}>✓ Active for all boarded trips</span>
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Daily Attendance Digest:</span>
                            {isEditingParent ? (
                              <input
                                type="checkbox"
                                checked={editParentEmailAlerts}
                                onChange={(e) => setEditParentEmailAlerts(e.target.checked)}
                                style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                              />
                            ) : (
                              <span className="field-value">{selectedParentProfile.emailAlerts ? '✓ Enabled (Sent at 5:00 PM)' : 'Disabled'}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* TAB 4: ACTIVITY & SUPPORT */}
                    {parentProfileActiveTab === 'activity' && (
                      <div className="profile-section-card" style={{ width: '100%' }}>
                        <div className="profile-section-title">💬 Activity & Communication History</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px' }}>
                          <div style={{ padding: '12px', borderRadius: '10px', background: '#F8FAFC', border: '1px solid var(--border-color)', fontSize: '13px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                              <strong style={{ color: 'var(--text-primary)' }}>🟢 Morning Trip Check-In Notification</strong>
                              <span style={{ fontSize: '11px', color: '#94A3B8' }}>Today, 07:56 AM</span>
                            </div>
                            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '12px' }}>
                              Automated RFID boarding alert dispatched via Push & SMS to {selectedParentProfile.phone}.
                            </p>
                          </div>

                          <div style={{ padding: '12px', borderRadius: '10px', background: '#F8FAFC', border: '1px solid var(--border-color)', fontSize: '13px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                              <strong style={{ color: 'var(--text-primary)' }}>📢 School Announcement Broadcast</strong>
                              <span style={{ fontSize: '11px', color: '#94A3B8' }}>Yesterday, 03:30 PM</span>
                            </div>
                            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '12px' }}>
                              Received: "Driver assignment update for afternoon drop routes."
                            </p>
                          </div>

                          <div style={{ padding: '12px', borderRadius: '10px', background: '#F8FAFC', border: '1px solid var(--border-color)', fontSize: '13px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                              <strong style={{ color: 'var(--text-primary)' }}>🎫 Helpdesk Inquiry #101</strong>
                              <span style={{ fontSize: '11px', color: '#94A3B8' }}>Aug 25, 2026</span>
                            </div>
                            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '12px' }}>
                              Status: <span style={{ color: '#16A34A', fontWeight: '700' }}>Resolved</span> • Inquiry regarding stop arrival timing.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : selectedBusProfile ? (
            <div className="student-profile-fullpage" style={{ animation: 'fadeIn 0.3s ease-out' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <button 
                    onClick={() => setSelectedBusProfile(null)}
                    style={{
                      backgroundColor: 'var(--surface-color)',
                      border: '1.5px solid var(--border-color)',
                      borderRadius: '12px',
                      padding: '6px 12px',
                            fontSize: '11px',
                      fontWeight: '700',
                      color: 'var(--text-secondary)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    ← Back to Fleet
                  </button>
                  <div>
                    <h1 className="page-title" style={{ fontSize: '24px', margin: 0 }}>Vehicle Profile & Fleet Telemetry</h1>
                    <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>Complete vehicle specifications, route assignments, hardware telemetry & service history</p>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  {isEditingBusProfile ? (
                    <>
                      <button
                        onClick={() => setIsEditingBusProfile(false)}
                        disabled={savingBusProfile}
                        style={{
                          backgroundColor: '#FFFFFF',
                          color: 'var(--text-secondary)',
                          border: '1.5px solid var(--border-color)',
                          borderRadius: '12px',
                          padding: '8px 16px',
                          fontSize: '12.5px',
                          fontWeight: '700',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveBusProfile}
                        disabled={savingBusProfile}
                        style={{
                          backgroundColor: '#2563EB',
                          color: '#FFFFFF',
                          border: 'none',
                          borderRadius: '12px',
                          padding: '8px 18px',
                          fontSize: '12.5px',
                          fontWeight: '700',
                          cursor: savingBusProfile ? 'not-allowed' : 'pointer',
                          boxShadow: '0 2px 8px rgba(37,99,235,0.25)'
                        }}
                      >
                        {savingBusProfile ? 'Saving...' : 'Save Changes'}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => startEditingBusProfile(selectedBusProfile)}
                        style={{
                          backgroundColor: '#2563EB',
                          color: '#FFFFFF',
                          border: 'none',
                          borderRadius: '12px',
                          padding: '8px 16px',
                          fontSize: '12.5px',
                          fontWeight: '700',
                          cursor: 'pointer',
                          boxShadow: '0 2px 8px rgba(37,99,235,0.25)'
                        }}
                      >
                        ✏️ Edit Vehicle
                      </button>
                      <button
                        onClick={() => handleDeleteBus(selectedBusProfile.id)}
                        style={{
                          backgroundColor: '#FEF2F2',
                          color: '#DC2626',
                          border: '1px solid #FECACA',
                          borderRadius: '12px',
                          padding: '8px 14px',
                          fontSize: '12.5px',
                          fontWeight: '700',
                          cursor: 'pointer'
                        }}
                      >
                        Delete Vehicle
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="profile-layout-grid" style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '28px', alignItems: 'start' }}>
                {/* LEFT COLUMN: Sidebar Card (Vehicle Badge, Quick Specs, Driver Callout) */}
                <div className="profile-sidebar-card" style={{ background: '#F8FAFC', borderRadius: '24px', border: '1.5px solid var(--border-color)', padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {/* Big Center Bus Icon Section */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '12px', borderBottom: '1.5px solid #E2E8F0', paddingBottom: '20px' }}>
                    <div style={{
                      width: '110px',
                      height: '110px',
                      borderRadius: '24px',
                      backgroundColor: '#EFF6FF',
                      border: '4px solid #FFFFFF',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '48px',
                      color: '#2563EB',
                      position: 'relative'
                    }}>
                      🚌
                      <span style={{
                        position: 'absolute',
                        bottom: '-6px',
                        right: '-6px',
                        backgroundColor: (selectedBusProfile.status || '').toLowerCase() === 'active' ? '#10B981' : '#F59E0B',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        border: '3px solid #FFFFFF'
                      }} />
                    </div>

                    <div>
                      <div style={{ fontSize: '22px', fontWeight: '800', color: 'var(--text-primary)' }}>{selectedBusProfile.busNumber}</div>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: '#2563EB', marginTop: '2px' }}>{selectedBusProfile.makeModel}</div>
                    </div>

                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'center', marginTop: '4px' }}>
                      <span className="meta-pill pill-purple">{selectedBusProfile.registration}</span>
                      <span className="meta-pill pill-yellow">{selectedBusProfile.capacity} Passenger Seats</span>
                    </div>
                  </div>

                  {/* Quick Vehicle Specs */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: 'var(--text-secondary)', fontWeight: '500' }}>Status:</span>
                      <span style={{
                        fontWeight: '700',
                        fontSize: '11px',
                        padding: '2px 8px',
                        borderRadius: '6px',
                        backgroundColor: (selectedBusProfile.status || '').toLowerCase() === 'active' ? '#DCFCE7' : '#FEF3C7',
                        color: (selectedBusProfile.status || '').toLowerCase() === 'active' ? '#15803D' : '#D97706',
                        textTransform: 'uppercase'
                      }}>
                        {selectedBusProfile.status}
                      </span>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: 'var(--text-secondary)', fontWeight: '500' }}>Model Year:</span>
                      <span style={{ color: 'var(--text-primary)', fontWeight: '700' }}>{selectedBusProfile.modelYear}</span>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: 'var(--text-secondary)', fontWeight: '500' }}>Fuel Type:</span>
                      <span style={{ color: 'var(--text-primary)', fontWeight: '700' }}>{selectedBusProfile.fuelType}</span>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: 'var(--text-secondary)', fontWeight: '500' }}>Odometer:</span>
                      <span style={{ color: 'var(--text-primary)', fontWeight: '700' }}>{selectedBusProfile.odometer}</span>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: 'var(--text-secondary)', fontWeight: '500' }}>GPS Hardware:</span>
                      <span style={{ color: '#059669', fontWeight: '700', fontSize: '12px' }}>● Online</span>
                    </div>
                  </div>

                  {/* Assigned Driver Callout Box */}
                  <div style={{ background: '#FFFFFF', borderRadius: '12px',
                                border: '1.5px solid var(--border-color)',
                                padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Assigned Driver
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '48px',
                                  height: '48px', borderRadius: '50%', background: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
                        👮‍♂️
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: '800', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {selectedBusProfile.assignedDriver}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{selectedBusProfile.driverPhone}</div>
                      </div>
                    </div>
                    <a
                      href={`tel:${selectedBusProfile.driverPhone}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        padding: '8px',
                        borderRadius: '8px',
                        backgroundcolor: 'var(--text-primary)',
                        color: '#FFFFFF',
                        fontSize: '12px',
                        fontWeight: '700',
                        textDecoration: 'none',
                        marginTop: '4px'
                      }}
                    >
                      <Phone size={13} /> Call Driver
                    </a>
                  </div>
                </div>

                {/* RIGHT COLUMN: Tabbed Detail Content */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {/* Tab Navigation Pill Bar */}
                  <div style={{ display: 'flex', gap: '8px', borderBottom: '1.5px solid #E2E8F0', paddingBottom: '12px', flexWrap: 'wrap' }}>
                    {[
                      { id: 'overview', label: '📋 Specifications & Registration' },
                      { id: 'assignment', label: '🗺️ Route & Driver Assignment' },
                      { id: 'documents', label: `📁 Documents (${selectedBusProfile.documents ? selectedBusProfile.documents.length : 0})` },
                      { id: 'telemetry', label: '⚙️ Telemetry & Safety' },
                      { id: 'maintenance', label: '🔧 Maintenance History' }
                    ].map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => setBusProfileActiveTab(tab.id)}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '10px',
                          border: 'none',
                          fontSize: '13px',
                          fontWeight: '700',
                          backgroundColor: busProfileActiveTab === tab.id ? 'var(--text-primary)' : 'var(--bg-hover)',
                          color: busProfileActiveTab === tab.id ? '#FFFFFF' : '#64748B',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* TAB 1: SPECIFICATIONS & REGISTRATION */}
                  {busProfileActiveTab === 'overview' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <div className="profile-section-card" style={{ width: '100%' }}>
                        <div className="profile-section-title">🚌 Technical Specifications</div>
                        <div className="profile-fields-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                          <div className="profile-field-row">
                            <span className="field-label">Make & Model:</span>
                            {isEditingBusProfile ? (
                              <input
                                type="text"
                                value={editBusMakeModel}
                                onChange={(e) => setEditBusMakeModel(e.target.value)}
                                placeholder="e.g. Tata Starbus Ultra"
                                style={{ flex: 1, padding: '6px 10px', fontSize: '13px', borderRadius: '8px', border: '1px solid var(--border-color)', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value" style={{ fontWeight: '700', color: 'var(--text-primary)' }}>{selectedBusProfile.makeModel}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Manufacturing Year:</span>
                            {isEditingBusProfile ? (
                              <input
                                type="text"
                                value={editBusModelYear}
                                onChange={(e) => setEditBusModelYear(e.target.value)}
                                placeholder="e.g. 2024"
                                style={{ flex: 1, padding: '6px 10px', fontSize: '13px', borderRadius: '8px', border: '1px solid var(--border-color)', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value">{selectedBusProfile.modelYear}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Seating Layout:</span>
                            {isEditingBusProfile ? (
                              <input
                                type="number"
                                value={editBusCapacity}
                                onChange={(e) => setEditBusCapacity(e.target.value)}
                                min="5"
                                max="100"
                                placeholder="Capacity"
                                style={{ flex: 1, padding: '6px 10px', fontSize: '13px', borderRadius: '8px', border: '1px solid var(--border-color)', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value" style={{ fontWeight: '700', color: '#2563EB' }}>{selectedBusProfile.capacity} Seats (2x2 High-Back School Layout)</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Fuel & Powertrain:</span>
                            {isEditingBusProfile ? (
                              <input
                                type="text"
                                list="master_bus_fuel_types_list"
                                value={editBusFuelType}
                                onChange={(e) => setEditBusFuelType(e.target.value)}
                                placeholder="Select Fuel Type"
                                style={{ flex: 1, padding: '6px 10px', fontSize: '13px', borderRadius: '8px', border: '1px solid var(--border-color)', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value">{selectedBusProfile.fuelType}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Ownership Type:</span>
                            {isEditingBusProfile ? (
                              <input
                                type="text"
                                list="master_bus_ownership_types_list"
                                value={editBusOwnershipType}
                                onChange={(e) => setEditBusOwnershipType(e.target.value)}
                                placeholder="Select Ownership"
                                style={{ flex: 1, padding: '6px 10px', fontSize: '13px', borderRadius: '8px', border: '1px solid var(--border-color)', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value">{selectedBusProfile.ownershipType || 'School Owned'}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Fleet Status:</span>
                            {isEditingBusProfile ? (
                              <select
                                value={editBusStatus}
                                onChange={(e) => setEditBusStatus(e.target.value)}
                                style={{ flex: 1, padding: '6px 10px', fontSize: '13px', borderRadius: '8px', border: '1px solid var(--border-color)', outline: 'none' }}
                              >
                                <option value="active">Active</option>
                                <option value="maintenance">Maintenance</option>
                                <option value="inactive">Inactive</option>
                              </select>
                            ) : (
                              <span className="field-value" style={{ textTransform: 'capitalize', fontWeight: '700' }}>{selectedBusProfile.status}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Engine Number:</span>
                            {isEditingBusProfile ? (
                              <input
                                type="text"
                                value={editBusEngineNo}
                                onChange={(e) => setEditBusEngineNo(e.target.value)}
                                placeholder="Engine Number"
                                style={{ flex: 1, padding: '6px 10px', fontSize: '13px', borderRadius: '8px', border: '1px solid var(--border-color)', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value" style={{ fontFamily: 'monospace', fontWeight: '700' }}>{selectedBusProfile.engineNo}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Chassis / VIN:</span>
                            {isEditingBusProfile ? (
                              <input
                                type="text"
                                value={editBusChassisNo}
                                onChange={(e) => setEditBusChassisNo(e.target.value)}
                                placeholder="Chassis / VIN"
                                style={{ flex: 1, padding: '6px 10px', fontSize: '13px', borderRadius: '8px', border: '1px solid var(--border-color)', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value" style={{ fontFamily: 'monospace', fontWeight: '700' }}>{selectedBusProfile.chassisNo}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Odometer Reading:</span>
                            <span className="field-value">{selectedBusProfile.odometer}</span>
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Average Fuel Mileage:</span>
                            <span className="field-value">{selectedBusProfile.fuelMileage}</span>
                          </div>
                        </div>
                      </div>

                      <div className="profile-section-card" style={{ width: '100%' }}>
                        <div className="profile-section-title">📜 Regulatory Compliance & Expirations</div>
                        <div className="profile-fields-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                          <div className="profile-field-row">
                            <span className="field-label">Registration Number:</span>
                            {isEditingBusProfile ? (
                              <input
                                type="text"
                                value={editBusRegistration}
                                onChange={(e) => setEditBusRegistration(e.target.value)}
                                placeholder="e.g. DL-01-EA-5566"
                                style={{ flex: 1, padding: '6px 10px', fontSize: '13px', borderRadius: '8px', border: '1px solid var(--border-color)', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value" style={{ fontWeight: '800', color: '#7C3AED' }}>{selectedBusProfile.registration}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Commercial Insurance:</span>
                            {isEditingBusProfile ? (
                              <div style={{ display: 'flex', gap: '6px', flex: 1 }}>
                                <input
                                  type="text"
                                  value={editBusInsuranceProvider}
                                  onChange={(e) => setEditBusInsuranceProvider(e.target.value)}
                                  placeholder="Provider"
                                  style={{ flex: 1, padding: '6px 10px', fontSize: '13px', borderRadius: '8px', border: '1px solid var(--border-color)', outline: 'none' }}
                                />
                                <input
                                  type="text"
                                  value={editBusInsurancePolicyNo}
                                  onChange={(e) => setEditBusInsurancePolicyNo(e.target.value)}
                                  placeholder="Policy #"
                                  style={{ flex: 1, padding: '6px 10px', fontSize: '13px', borderRadius: '8px', border: '1px solid var(--border-color)', outline: 'none' }}
                                />
                              </div>
                            ) : (
                              <span className="field-value">{selectedBusProfile.insuranceProvider} ({selectedBusProfile.insurancePolicyNo})</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Insurance Valid Till:</span>
                            {isEditingBusProfile ? (
                              <input
                                type="text"
                                value={editBusInsuranceExpiry}
                                onChange={(e) => setEditBusInsuranceExpiry(e.target.value)}
                                placeholder="e.g. Nov 14, 2027"
                                style={{ flex: 1, padding: '6px 10px', fontSize: '13px', borderRadius: '8px', border: '1px solid var(--border-color)', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value" style={{ color: '#059669', fontWeight: '700' }}>{selectedBusProfile.insuranceExpiry}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">RTO Fitness Expiry:</span>
                            {isEditingBusProfile ? (
                              <input
                                type="text"
                                value={editBusFitnessExpiry}
                                onChange={(e) => setEditBusFitnessExpiry(e.target.value)}
                                placeholder="e.g. Sep 20, 2027"
                                style={{ flex: 1, padding: '6px 10px', fontSize: '13px', borderRadius: '8px', border: '1px solid var(--border-color)', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value" style={{ color: '#059669', fontWeight: '700' }}>{selectedBusProfile.fitnessExpiry}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Pollution (PUC) Valid Till:</span>
                            {isEditingBusProfile ? (
                              <input
                                type="text"
                                value={editBusPucExpiry}
                                onChange={(e) => setEditBusPucExpiry(e.target.value)}
                                placeholder="e.g. Oct 15, 2027"
                                style={{ flex: 1, padding: '6px 10px', fontSize: '13px', borderRadius: '8px', border: '1px solid var(--border-color)', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value" style={{ color: '#059669', fontWeight: '700' }}>{selectedBusProfile.pucExpiry}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">All-State Road Permit:</span>
                            {isEditingBusProfile ? (
                              <input
                                type="text"
                                value={editBusRoadPermitExpiry}
                                onChange={(e) => setEditBusRoadPermitExpiry(e.target.value)}
                                placeholder="e.g. Dec 31, 2028"
                                style={{ flex: 1, padding: '6px 10px', fontSize: '13px', borderRadius: '8px', border: '1px solid var(--border-color)', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value">{selectedBusProfile.roadPermitExpiry}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Road Tax / Doc Status:</span>
                            {isEditingBusProfile ? (
                              <input
                                type="text"
                                list="master_bus_doc_statuses_list"
                                value={editBusRoadTaxStatus}
                                onChange={(e) => setEditBusRoadTaxStatus(e.target.value)}
                                placeholder="Select or type status"
                                style={{ flex: 1, padding: '6px 10px', fontSize: '13px', borderRadius: '8px', border: '1px solid var(--border-color)', outline: 'none' }}
                              />
                            ) : (
                              <span className="field-value" style={{ color: '#059669', fontWeight: '700' }}>{selectedBusProfile.roadTaxStatus}</span>
                            )}
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Speed Governor Limit:</span>
                            <span className="field-value" style={{ color: '#DC2626', fontWeight: '700' }}>Calibrated (Max 40 km/h)</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* TAB 2: ROUTE & DRIVER ASSIGNMENT */}
                  {busProfileActiveTab === 'assignment' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <div className="profile-section-card" style={{ width: '100%' }}>
                        <div className="profile-section-title">🗺️ Active Route Assignment</div>
                        <div style={{ background: '#F8FAFC', borderRadius: '12px', padding: '16px', border: '1px solid var(--border-color)', marginBottom: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <strong style={{ fontSize: '15px', color: 'var(--text-primary)' }}>{selectedBusProfile.assignedRoute}</strong>
                            <span style={{ fontSize: '11px', fontWeight: '700', padding: '3px 8px', borderRadius: '6px', backgroundColor: '#EFF6FF', color: '#2563EB' }}>
                              {selectedBusProfile.routeDirection}
                            </span>
                          </div>
                          <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>
                            Morning Pickup schedule commences at <strong>07:30 AM</strong> • Afternoon Drop departs campus at <strong>03:30 PM</strong>.
                          </p>
                        </div>

                        <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '8px', textTransform: 'uppercase' }}>
                          Sequential Stops Along Route
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {[
                            { seq: 1, name: "Kalyan Nagar Crossing", time: "07:35 AM / 03:45 PM", students: 14 },
                            { seq: 2, name: "HRBR Layout Playground", time: "07:45 AM / 03:55 PM", students: 12 },
                            { seq: 3, name: "Banaswadi Apartments", time: "07:55 AM / 04:05 PM", students: 14 }
                          ].map((st) => (
                            <div key={st.seq} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#FFFFFF', borderRadius: '10px', border: '1px solid var(--border-color)' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundcolor: 'var(--text-primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '800' }}>
                                  {st.seq}
                                </div>
                                <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>{st.name}</span>
                              </div>
                              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', fontSize: '12px' }}>
                                <span style={{ color: 'var(--text-secondary)' }}>🕒 {st.time}</span>
                                <span style={{ padding: '2px 8px', borderRadius: '4px', backgroundColor: 'var(--bg-hover)', color: 'var(--text-secondary)', fontWeight: '600' }}>
                                  {st.students} Students
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="profile-section-card" style={{ width: '100%' }}>
                        <div className="profile-section-title">👮‍♂️ Assigned Primary Driver</div>
                        <div className="profile-fields-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                          <div className="profile-field-row">
                            <span className="field-label">Driver Name:</span>
                            <span className="field-value" style={{ fontWeight: '700', color: 'var(--text-primary)' }}>{selectedBusProfile.assignedDriver}</span>
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Driver ID:</span>
                            <span className="field-value">{selectedBusProfile.driverId}</span>
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Phone Number:</span>
                            <span className="field-value">
                              <a href={`tel:${selectedBusProfile.driverPhone}`} style={{ color: '#2563EB', fontWeight: '700', textDecoration: 'none' }}>
                                {selectedBusProfile.driverPhone}
                              </a>
                            </span>
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Commercial License:</span>
                            <span className="field-value" style={{ color: '#059669', fontWeight: '700' }}>VALID (Expires Oct 2028)</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* TAB 3: DOCUMENTS */}
                  {busProfileActiveTab === 'documents' && (
                    <div className="profile-section-card" style={{ width: '100%' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <div className="profile-section-title" style={{ margin: 0 }}>📁 Vehicle Documents Vault</div>
                        <button
                          onClick={() => {
                            setUploadEntityType('bus');
                            setUploadEntityId(selectedBusProfile.id || selectedBusProfile.busNumber);
                            setUploadDocType((masterLookups?.bus_doc_types || DEFAULT_LOOKUPS.bus_doc_types)[0] || 'Registration Certificate (RC)');
                            setUploadExpiryDate('');
                            setUploadFileObj(null);
                            setShowUploadModal(true);
                          }}
                          style={{
                            padding: '6px 14px',
                            borderRadius: '8px',
                            backgroundcolor: 'var(--text-primary)',
                            color: '#FFFFFF',
                            border: 'none',
                            fontSize: '12px',
                            fontWeight: '700',
                            cursor: 'pointer'
                          }}
                        >
                          + Upload Document
                        </button>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '14px' }}>
                        {selectedBusProfile.documents && selectedBusProfile.documents.map((doc, idx) => (
                          <div key={idx} style={{ padding: '14px', borderRadius: '12px', border: '1.5px solid var(--border-color)', background: '#F8FAFC', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <img src="/icons/pdf.svg" alt="PDF" style={{ width: '32px', height: '32px' }} />
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {doc.name}
                                </div>
                                <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{doc.format} • {doc.size}</div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', borderTop: '1px solid var(--border-color)', paddingTop: '8px', marginTop: 'auto' }}>
                              <span style={{ color: '#059669', fontWeight: '700' }}>✓ Valid ({doc.expiry})</span>
                              <button
                                onClick={() => alert(`Downloading ${doc.name}...`)}
                                style={{ background: 'none', border: 'none', color: '#2563EB', fontWeight: '700', cursor: 'pointer', fontSize: '11px', padding: 0 }}
                              >
                                View / Download
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* TAB 4: TELEMETRY & SAFETY */}
                  {busProfileActiveTab === 'telemetry' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <div className="profile-section-card" style={{ width: '100%' }}>
                        <div className="profile-section-title">🛰️ GPS & Telematics Hardware</div>
                        <div className="profile-fields-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                          <div className="profile-field-row">
                            <span className="field-label">Tracker Hardware:</span>
                            <span className="field-value" style={{ fontWeight: '700' }}>{selectedBusProfile.gpsDeviceModel}</span>
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Hardware IMEI:</span>
                            <span className="field-value" style={{ fontFamily: 'monospace', fontWeight: '700', color: 'var(--text-primary)' }}>{selectedBusProfile.gpsImei}</span>
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">IoT SIM Connection:</span>
                            <span className="field-value">{selectedBusProfile.gpsSimNumber}</span>
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Live Signal Telemetry:</span>
                            <span className="field-value" style={{ color: '#059669', fontWeight: '700' }}>{selectedBusProfile.gpsStatus}</span>
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">Speed Governor:</span>
                            <span className="field-value" style={{ color: '#2563EB', fontWeight: '700' }}>{selectedBusProfile.speedGovernor}</span>
                          </div>
                          <div className="profile-field-row">
                            <span className="field-label">CCTV Surveillance:</span>
                            <span className="field-value">{selectedBusProfile.cctvSystem}</span>
                          </div>
                        </div>
                      </div>

                      <div className="profile-section-card" style={{ width: '100%' }}>
                        <div className="profile-section-title">🛡️ Onboard Child Safety Equipment</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                          {selectedBusProfile.safetyFeatures && selectedBusProfile.safetyFeatures.map((feat, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: '#F8FAFC', borderRadius: '8px', border: '1px solid var(--border-color)', fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>
                              <span style={{ color: '#10B981', fontSize: '16px' }}>✓</span>
                              {feat}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* TAB 5: MAINTENANCE & SERVICE HISTORY */}
                  {busProfileActiveTab === 'maintenance' && (
                    <div className="profile-section-card" style={{ width: '100%' }}>
                      <div className="profile-section-title">🔧 Periodic Maintenance & Service Log</div>
                      <div style={{ display: 'flex', gap: '16px', marginBottom: '8px', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: '200px', padding: '12px 16px', background: '#F8FAFC', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
                          <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '700', textTransform: 'uppercase' }}>Last Serviced</span>
                          <div style={{ fontSize: '16px', fontWeight: '800', color: 'var(--text-primary)', marginTop: '4px' }}>{selectedBusProfile.lastServiceDate}</div>
                        </div>
                        <div style={{ flex: 1, minWidth: '200px', padding: '12px 16px', background: '#EFF6FF', borderRadius: '12px', border: '1px solid #BFDBFE' }}>
                          <span style={{ fontSize: '11px', color: '#1E40AF', fontWeight: '700', textTransform: 'uppercase' }}>Next Service Due</span>
                          <div style={{ fontSize: '16px', fontWeight: '800', color: '#1E40AF', marginTop: '4px' }}>{selectedBusProfile.nextServiceDue}</div>
                        </div>
                      </div>

                      <table className="manage-table" style={{ width: '100%' }}>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Odometer</th>
                            <th>Service Category</th>
                            <th>Work Carried Out & Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedBusProfile.serviceHistory && selectedBusProfile.serviceHistory.map((s, idx) => (
                            <tr key={idx}>
                              <td><strong>{s.date}</strong></td>
                              <td>{s.odometer}</td>
                              <td>
                                <span style={{ padding: '2px 8px', borderRadius: '4px', backgroundColor: 'var(--bg-hover)', color: 'var(--text-secondary)', fontSize: '11px', fontWeight: '700' }}>
                                  {s.type}
                                </span>
                              </td>
                              <td style={{ fontSize: '12px', color: '#475569' }}>{s.notes}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="page-title-section">
                <h1 className="page-title">
                  {activeTab === 'monitor' && 'Live Monitor'}
                  {activeTab === 'trips' && "Today's Trips & Route Planning"}
                  {activeTab === 'reports' && 'Reports Center'}
                  {activeTab === 'students' && 'Students Directory'}
                  {activeTab === 'parents' && 'Parents & Guardians Directory'}
                  {activeTab === 'drivers' && 'Bus Drivers Directory'}
                  {activeTab === 'buses' && 'School Buses Fleet'}
                  {activeTab === 'documents' && 'Documents Vault'}
                  {activeTab === 'announcements' && 'Send Announcements'}
                  {activeTab === 'tickets' && 'Support Helpdesk'}
                  {activeTab === 'general_settings' && 'General System Settings'}
                  {activeTab === 'connections' && 'Service Connections & APIs'}
                  {activeTab === 'access_control' && 'Access Control Settings'}
                  {activeTab === 'database_ops' && 'Database Health & Operations'}
                  {activeTab === 'system_logs' && 'Audit & System Logs'}
                  {activeTab === 'changelog' && 'System Changelog & History'}
                </h1>
                <p className="page-subtitle">
                  {activeTab === 'monitor' && 'Watch school buses moving live and track active trips.'}
                  {activeTab === 'trips' && "Assign drivers to buses, schedule routes, and manage stops."}
                  {activeTab === 'reports' && 'View trip histories, attendance logs, and configure scheduled alerts.'}
                  {activeTab === 'students' && 'Manage student profiles, RFID card tags, and bus routes.'}
                  {activeTab === 'parents' && 'Manage parent profiles, emergency contacts, and notification channels.'}
                  {activeTab === 'drivers' && 'Manage driver licenses, assignments, and contact details.'}
                  {activeTab === 'buses' && 'Manage vehicle inventory, maintenance records, and GPS tracking hardware.'}
                  {activeTab === 'documents' && 'Access driver licenses, bus registrations, and other vital documents.'}
                  {activeTab === 'announcements' && 'Broadcast alert banners and push messages to parents and drivers.'}
                  {activeTab === 'tickets' && 'View and respond to inquiries from parent and driver mobile apps.'}
                  {activeTab === 'general_settings' && 'Customize system datetime formats and regional timezones.'}
                  {activeTab === 'connections' && 'Configure external Map API keys, SMS gateways, and SMTP mail alerts.'}
                  {activeTab === 'access_control' && 'Manage administrator accounts, user groups, and console access permissions.'}
                  {activeTab === 'database_ops' && 'Perform indexes rebuilds, table defragmentation, and background cleanup schedules.'}
                  {activeTab === 'system_logs' && 'Monitor database audit trails, system performance logs, and error traces.'}
                  {activeTab === 'changelog' && 'Track application release history, component updates, and recent code milestones.'}
                </p>
              </div>

          {activeTab === 'monitor' && (
            <div className="monitor-dashboard-wrapper">
              {/* METRICS ROW - REAL-TIME CURRENT DATE DATA */}
              {(() => {
                const now = new Date();
                const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

                // Aggregate current date's trips across active trips and full historical deployments
                const todayMap = new Map();
                (allTrips || []).forEach(t => {
                  if (t.scheduled_date === todayStr || (!t.scheduled_date && t.status === 'EN_ROUTE')) {
                    todayMap.set(t.id, t);
                  }
                });
                (trips || []).forEach(t => {
                  if (t.scheduled_date === todayStr || (!t.scheduled_date && t.status === 'EN_ROUTE')) {
                    todayMap.set(t.id, { ...(todayMap.get(t.id) || {}), ...t });
                  }
                });
                const todayTripsList = Array.from(todayMap.values());

                const activeCount = todayTripsList.filter(t => t.status === 'EN_ROUTE').length;
                const schedCount = todayTripsList.filter(t => t.status === 'SCHEDULED').length;
                const completedCount = todayTripsList.filter(t => t.status === 'COMPLETED').length;
                const totalTodayCount = todayTripsList.length;

                const todayDelays = todayTripsList.map(t => t.delay_minutes || 0);
                const avgDelayToday = todayDelays.length > 0 ? Math.round(todayDelays.reduce((acc, d) => acc + d, 0) / todayDelays.length) : 0;
                const maxDelayToday = todayDelays.length > 0 ? Math.max(...todayDelays) : 0;

                const onTimeTripsCount = todayTripsList.filter(t => (t.delay_minutes || 0) <= 5).length;
                const onTimeRate = totalTodayCount > 0 ? Math.round((onTimeTripsCount / totalTodayCount) * 100) : 100;

                // Comparison with previous date for dynamic trend
                const yesterday = new Date(Date.now() - 86400000);
                const yesterdayStr = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`;
                const yesterdayTrips = (allTrips || []).filter(t => t.scheduled_date === yesterdayStr);
                const yesterdayOnTime = yesterdayTrips.filter(t => (t.delay_minutes || 0) <= 5).length;
                const yesterdayRate = yesterdayTrips.length > 0 ? Math.round((yesterdayOnTime / yesterdayTrips.length) * 100) : 95;
                const rateDiff = onTimeRate - yesterdayRate;
                const trendText = rateDiff >= 0 ? `+${rateDiff}%` : `${rateDiff}%`;

                // 35-dot wellness grid representation
                const activeDots = Math.round((onTimeRate / 100) * 35);

                // Dynamically build 5 trailing days ending on current day
                const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                const weekDaysList = [];
                for (let i = 4; i >= 0; i--) {
                  const d = new Date(Date.now() - i * 86400000);
                  const dStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                  const isToday = dStr === todayStr;
                  const dTrips = (allTrips || []).filter(t => t.scheduled_date === dStr);
                  const dayTripsCount = isToday ? totalTodayCount : dTrips.length;
                  const dayOnTime = isToday ? onTimeTripsCount : dTrips.filter(t => (t.delay_minutes || 0) <= 5).length;
                  const dayRate = dayTripsCount > 0 ? Math.round((dayOnTime / dayTripsCount) * 100) : (isToday ? onTimeRate : 80);

                  const h1 = Math.max(20, Math.min(100, Math.round(dayRate * 0.8)));
                  const h2 = Math.max(25, Math.min(100, Math.round(dayTripsCount > 0 ? Math.min(dayTripsCount * 25, 80) : 40)));

                  weekDaysList.push({
                    label: dayLabels[d.getDay()],
                    isToday,
                    h1: `${h1}%`,
                    h2: `${h2}%`
                  });
                }

                const circleTotal = totalTodayCount || 1;
                const getCircleStyle = (count, positionStyle) => {
                  const ratio = count / circleTotal;
                  const size = Math.round(55 + ratio * 40);
                  const fontVal = Math.round(size * 0.26);
                  return {
                    width: `${size}px`,
                    height: `${size}px`,
                    fontSize: `${fontVal}px`,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    position: 'absolute',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
                    ...positionStyle
                  };
                };

                const getLabelStyle = (count) => {
                  const ratio = count / circleTotal;
                  const size = Math.round(55 + ratio * 40);
                  const fontLbl = Math.round(size * 0.11);
                  return {
                    fontSize: `${fontLbl}px`,
                    textTransform: 'uppercase',
                    fontWeight: '750',
                    opacity: 0.85
                  };
                };

                return (

                  
                  <>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
                    <button onClick={() => setShowWidgetEditModal(true)} className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Settings size={16} /> Edit Widgets
                    </button>
                  </div>
                  {showWidgetEditModal && (
                    <WidgetEditModal
                      visibleWidgets={visibleWidgets}
                      widgetOrder={widgetOrder}
                      onClose={() => setShowWidgetEditModal(false)}
                      onSave={(newOrder, newVisible) => {
                        setWidgetOrder(newOrder);
                        setVisibleWidgets(newVisible);
                        localStorage.setItem('adminDashboardWidgetOrder', JSON.stringify(newOrder));
                        localStorage.setItem('adminDashboardWidgets', JSON.stringify(newVisible));
                        setShowWidgetEditModal(false);
                      }}
                    />
                  )}
                  <div ref={gridContainerRef} style={{ width: '100%' }}>
                      {gridMounted && (
                      <ResponsiveGridLayout
                        width={gridWidth}
                    className="layout"
                    layouts={{ lg: dashboardLayout }}
                    breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                    cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                    rowHeight={80}
                    onLayoutChange={(currentLayout, allLayouts) => {
                      const layoutToSave = allLayouts.lg || currentLayout;
                      setDashboardLayout(layoutToSave);
                      localStorage.setItem('dashboardLayout', JSON.stringify(layoutToSave));
                    }}
                    draggableHandle=".metric-card-header"
                    isResizable={true}
                  >
                    {visibleWidgets.tripsBreakdown && (
                      <div className="metric-card card-large" key="tripsBreakdown">
                        <div className="metric-card-header">
                          <span className="metric-card-title">⚡ Trips Breakdown</span>
                          <span className="metric-card-options">⋮</span>
                        </div>
                        <div className="metric-card-big-value">
                          {totalTodayCount} <span className="metric-card-value-sub">trips today</span>
                        </div>

                        {/* Overlapping Circles Chart */}
                        <div className="overlapping-circles-container" style={{ height: '90px', margin: '4px 0' }}>
                          <div className="circle circle-purple" style={getCircleStyle(activeCount, { left: '16%' })}>
                            <span style={{ fontWeight: '800' }}>{activeCount}</span>
                            <span style={getLabelStyle(activeCount)}>Active</span>
                          </div>
                          <div className="circle circle-dark" style={getCircleStyle(schedCount, { right: '16%', color: '#FFFFFF' })}>
                            <span style={{ fontWeight: '800' }}>{schedCount}</span>
                            <span style={getLabelStyle(schedCount)}>Sched</span>
                          </div>
                          <div className="circle circle-lime" style={getCircleStyle(completedCount, { left: '50%', transform: 'translateX(-50%)', bottom: '-10px' })}>
                            <span style={{ fontWeight: '800', color: 'var(--text-primary)' }}>{completedCount}</span>
                            <span style={{ ...getLabelStyle(completedCount), color: 'var(--text-primary)' }}>Done</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {visibleWidgets.averageDelay && (
                      <div className="metric-card card-compact" key="averageDelay">
                        <div className="metric-card-header">
                          <span className="metric-card-title">⏱️ Average Delay</span>
                          <span className="metric-card-options">⋮</span>
                        </div>
                        <div className="metric-card-flex-body">
                          <div className="flex-main-value">
                            {avgDelayToday} <span className="flex-main-unit">mins</span>
                          </div>
                          <div className="flex-side-value">
                            <span className="side-lbl">Max Delay</span>
                            <span className="side-val">{maxDelayToday} mins</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {visibleWidgets.fleetActive && (
                      <div className="metric-card card-compact" key="fleetActive">
                        <div className="metric-card-header">
                          <span className="metric-card-title">🚌 Fleet Active</span>
                          <span className="metric-card-options">⋮</span>
                        </div>
                        <div className="metric-card-flex-body">
                          <div className="flex-main-value">
                            {allBuses.length} <span className="flex-main-unit">buses</span>
                          </div>
                          <div className="flex-side-value">
                            <span className="side-lbl">Active Now</span>
                            <span className="side-val">{activeCount} active</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {visibleWidgets.activeStudents && (
                      <div className="metric-card card-compact" key="activeStudents">
                        <div className="metric-card-header">
                          <span className="metric-card-title">👨‍🎓 Total Students</span>
                          <span className="metric-card-options">⋮</span>
                        </div>
                        <div className="metric-card-flex-body">
                          <div className="flex-main-value">
                            {allStudents ? allStudents.length : 0} <span className="flex-main-unit">students</span>
                          </div>
                          <div className="flex-side-value">
                            <span className="side-lbl">Active</span>
                            <span className="side-val">{allStudents ? allStudents.filter(s => s.status === 'Active').length : 0}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {visibleWidgets.systemAlerts && (
                      <div className="metric-card card-compact" key="systemAlerts">
                        <div className="metric-card-header">
                          <span className="metric-card-title">🔔 Announcements</span>
                          <span className="metric-card-options">⋮</span>
                        </div>
                        <div className="metric-card-flex-body">
                          <div className="flex-main-value">
                            {announcementsList ? announcementsList.length : 0} <span className="flex-main-unit">alerts</span>
                          </div>
                          <div className="flex-side-value">
                            <span className="side-lbl">Active</span>
                            <span className="side-val">{announcementsList ? announcementsList.filter(a => a.status === 'active').length : 0}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {visibleWidgets.onTimeRating && (
                      <div className="metric-card card-medium" key="onTimeRating">
                        <div className="metric-card-header">
                          <span className="metric-card-title">📈 On-Time Rating</span>
                          <span className="metric-card-options">⋮</span>
                        </div>
                        <div className="wellness-index-body">
                          <div className="wellness-value-row">
                            <span className="wellness-big-val">{onTimeRate}%</span>
                            <span className="badge-trend" style={rateDiff < 0 ? { backgroundColor: '#FEE2E2', color: '#DC2626' } : {}}>
                              {trendText}
                            </span>
                          </div>
                          {/* Dynamic Dots Grid Chart */}
                          <div className="dots-grid-chart">
                            {Array.from({ length: 35 }).map((_, i) => (
                              <span key={i} className={`dot-point ${i < activeDots ? 'active' : 'inactive'}`}></span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {visibleWidgets.recentLogs && (
                      <div className="metric-card card-medium" key="recentLogs">
                        <div className="metric-card-header">
                          <span className="metric-card-title">📝 Recent System Logs</span>
                          <span className="metric-card-options">⋮</span>
                        </div>
                        <div className="logs-widget-content" style={{ padding: '10px 0', overflowY: 'auto', height: 'calc(100% - 40px)' }}>
                          {logsList.length === 0 ? (
                            <div className="empty-message-text" style={{ padding: '20px' }}>No recent activity.</div>
                          ) : (
                            logsList.slice(0, 4).map((log, idx) => (
                              <div key={idx} style={{ display: 'flex', gap: '10px', padding: '8px 16px', borderBottom: '1px solid var(--border-color)', fontSize: '12px' }}>
                                <div style={{ color: log.type === 'ERROR' ? 'var(--coral-red)' : 'var(--slate-blue)', fontWeight: 'bold' }}>
                                  {log.type === 'ERROR' ? '⚠️' : 'ℹ️'}
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div style={{ color: 'var(--text-primary)', fontWeight: '600' }}>{log.message}</div>
                                  <div style={{ color: 'var(--text-secondary)', fontSize: '10px', marginTop: '2px' }}>{new Date(log.timestamp).toLocaleTimeString()}</div>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    )}

                    {visibleWidgets.busHealth && (
                      <div className="metric-card card-medium" key="busHealth">
                        <div className="metric-card-header">
                          <span className="metric-card-title">🔧 Fleet Maintenance</span>
                          <span className="metric-card-options">⋮</span>
                        </div>
                        <div className="bus-health-content" style={{ display: 'flex', flexDirection: 'column', padding: '16px', gap: '12px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Active & Healthy</span>
                            <span style={{ fontSize: '16px', fontWeight: 'bold', color: 'var(--lime-green)' }}>{allBuses.filter(b => b.status === 'ACTIVE').length || 12}</span>
                          </div>
                          <div style={{ width: '100%', height: '6px', backgroundColor: 'var(--bg-alt)', borderRadius: '3px', overflow: 'hidden' }}>
                            <div style={{ width: '85%', height: '100%', backgroundColor: 'var(--lime-green)' }}></div>
                          </div>

                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Needs Maintenance</span>
                            <span style={{ fontSize: '16px', fontWeight: 'bold', color: 'var(--coral-red)' }}>{allBuses.filter(b => b.status === 'MAINTENANCE').length || 2}</span>
                          </div>
                          <div style={{ width: '100%', height: '6px', backgroundColor: 'var(--bg-alt)', borderRadius: '3px', overflow: 'hidden' }}>
                            <div style={{ width: '15%', height: '100%', backgroundColor: 'var(--coral-red)' }}></div>
                          </div>
                          
                          <div style={{ marginTop: 'auto', fontSize: '11px', color: 'var(--slate-blue)', textAlign: 'center', cursor: 'pointer' }}>
                            View Maintenance Schedule →
                          </div>
                        </div>
                      </div>
                    )}

                    {visibleWidgets.weeklyPerformance && (
                      <div className="metric-card card-dark-theme" key="weeklyPerformance">
                        <div className="metric-card-header">
                          <span className="metric-card-title text-white">📊 Weekly Performance</span>
                          <div className="dark-theme-dropdown">Today ▾</div>
                        </div>
                        <div className="dark-theme-stats">
                          <div className="stat-item">
                            <span className="stat-big-val text-white">{onTimeRate}%</span>
                            <span className="stat-lbl">Today Rating</span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-big-val text-white">{avgDelayToday}m</span>
                            <span className="stat-lbl">Today Delay</span>
                          </div>
                        </div>
                        {/* Custom chart bars */}
                        <div className="dark-theme-chart-container">
                          {weekDaysList.map((col, idx) => (
                            <div key={idx} className={`bar-column ${col.isToday ? 'active' : ''}`}>
                              <div className="bar-pair">
                                <div className={`bar-sub-col ${col.isToday ? 'bg-lime' : 'bg-grey'}`} style={{ height: col.h1 }}></div>
                                <div className="bar-sub-col bg-purple" style={{ height: col.h2 }}></div>
                              </div>
                              <span className="bar-col-lbl">{col.label}{col.isToday ? ' ↗' : ''}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  </ResponsiveGridLayout>
                      )}
                  </div>
                  </>
                );
              })()}


              {/* TRACKING AND MAP WORKSPACE */}
              <div className="tracking-workspace-grid">
                {/* LEFT PANEL: TRIPS LIST */}
                <div className="workspace-panel list-panel">
                  <div className="workspace-panel-header">Active Route Trips <Clock size={16} /></div>
                  <div className="workspace-trip-list">
                    {trips.length === 0 ? (
                      <div className="empty-message-text">No active or scheduled trips for today.</div>
                    ) : (
                      [...trips].sort((a,b)=> (a.status==='EN_ROUTE'?-1:b.status==='EN_ROUTE'?1:0)).map(trip => {
                        const isSelected = selectedTrip && selectedTrip.id === trip.id;
                        const gps = busPositions[trip.id];
                        return (
                          <div
                            key={trip.id}
                            className={`workspace-trip-card ${isSelected ? 'active' : ''}`}
                            onClick={() => setSelectedTrip(trip)}
                          >
                            <div className="trip-header">
                              <span className="trip-name">{trip.route_name}</span>
                              <span className={`badge-status status-${trip.status.toLowerCase()}`}>{trip.status}</span>
                            </div>
                            <div className="trip-info-row"><Bus size={12} /> Bus: {trip.bus_number}</div>
                            <div className="trip-info-row"><User size={12} /> Driver: {trip.driver_name}</div>
                            {trip.scheduled_date && (
                              <div className="trip-info-row"><Calendar size={12} /> Date: {trip.scheduled_date}</div>
                            )}
                            {trip.delay_minutes > 0 && (
                              <div className="trip-info-row delay-highlight">
                                <Clock size={12} /> Delayed: {trip.delay_minutes} mins
                              </div>
                            )}
                            {gps && (
                              <div className="trip-info-row gps-highlight">
                                <Navigation size={12} /> Live ({gps.speed} km/h)
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* MIDDLE PANEL: LEAFLET MAP */}
                <div className="workspace-panel map-panel" style={{ padding: 0 }}>
                  <div className="map-container-wrapper">
                    <MapContainer center={getMapCenter()} zoom={14} scrollWheelZoom={true}>
                      <TileLayer
                        attribution={mapProvider === 'ola' ? '&copy; Ola Maps' : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'}
                        url={mapProvider === 'ola' && mapApiKey ? `https://api.olamaps.io/tiles/vector/v1/styles/default-light/{z}/{x}/{y}.png?api_key=${mapApiKey}` : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"}
                      />
                      {selectedTrip && (
                        <>
                          <ChangeMapView center={getMapCenter()} />
                          {selectedTripStops.length > 0 && (
                            <Polyline positions={getRoutePolyline()} color="#2EAEA0" weight={5} opacity={0.8} />
                          )}
                          {selectedTripStops.map((stop) => {
                            const isSchool = stop.stop_name.toLowerCase().includes('school');
                            return (
                              <Marker
                                key={stop.id}
                                position={[stop.lat, stop.lng]}
                                icon={isSchool ? getSchoolIcon() : getStopIcon(stop.sequence_number)}
                              >
                                <Popup>
                                  <strong>{stop.stop_name}</strong><br />
                                  Scheduled Arrival: {stop.scheduled_time}
                                </Popup>
                              </Marker>
                            );
                          })}
                          {busPositions[selectedTrip.id] && (
                            <Marker
                              position={[busPositions[selectedTrip.id].lat, busPositions[selectedTrip.id].lng]}
                              icon={getBusIcon()}
                            >
                              <Popup>
                                <strong>{selectedTrip.bus_number}</strong><br />
                                Speed: {busPositions[selectedTrip.id].speed} km/h<br />
                                Last updated: {new Date(busPositions[selectedTrip.id].timestamp).toLocaleTimeString()}
                              </Popup>
                            </Marker>
                          )}
                        </>
                      )}
                    </MapContainer>
                  </div>
                </div>

                {/* RIGHT PANEL: TRIP INSPECTOR */}
                <div className="workspace-panel inspector-panel">
                  <div className="workspace-panel-header">Trip Inspector <FileText size={16} /></div>
                  {selectedTrip ? (
                    <div className="details-panel-content" style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0 }}>
                      <div className="detail-section">
                        <div className="detail-title">Selected Trip</div>
                        <h3 className="inspector-trip-name">{selectedTrip.route_name}</h3>
                        <span className="inspector-trip-status">Status: <strong>{selectedTrip.status}</strong></span>
                        {selectedTrip.scheduled_date && (
                          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Calendar size={12} /> Scheduled Date: <strong>{selectedTrip.scheduled_date}</strong>
                          </div>
                        )}
                      </div>

                      <div className="detail-section">
                        <div className="detail-title">Driver & Vehicle</div>
                        <div className="driver-detail-card">
                          <img 
                            src={getDriverAvatar(selectedTrip.driver_name ? { name: selectedTrip.driver_name } : null)} 
                            alt="Driver" 
                            className="avatar-circle-sm" 
                            style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }}
                          />
                          <div style={{ flex: 1, marginLeft: '8px' }}>
                            <div className="driver-name-text">{selectedTrip.driver_name}</div>
                            <div className="driver-phone-text"><Phone size={10} style={{ display: 'inline', marginRight: '4px' }} />{selectedTrip.driver_phone}</div>
                          </div>
                        </div>
                        <div className="vehicle-meta-text">
                          <strong>Bus ID:</strong> {selectedTrip.bus_number} ({selectedTrip.registration})
                        </div>
                      </div>

                      <div className="detail-section">
                        <div className="detail-title">Delay Controller</div>
                        <div className="delay-box">
                          <input
                            type="number"
                            min="0"
                            max="120"
                            className="delay-input"
                            placeholder="Mins"
                            defaultValue={selectedTrip.delay_minutes}
                            id={`delay-input-${selectedTrip.id}`}
                          />
                          <button
                            className="save-delay-btn"
                            onClick={() => {
                              const input = document.getElementById(`delay-input-${selectedTrip.id}`);
                              if (input) updateDelay(selectedTrip.id, input.value);
                            }}
                          >
                            Update Delay
                          </button>
                        </div>
                      </div>

                      <div className="detail-section" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        <div className="detail-title">Student Boarding Roster ({selectedTripRoster.length})</div>
                        <div className="roster-list" style={{ overflowY: 'auto', flex: 1, paddingRight: '4px' }}>
                          {selectedTripRoster.length === 0 ? (
                            <div className="empty-roster-text">No students assigned to this route stops.</div>
                          ) : (
                            selectedTripRoster.map(student => {
                                const isAbsent = !!student.absence_reason;
                                const status = isAbsent ? 'ON_LEAVE' : (student.attendance_status || 'NOT_BOARDED');
                                const displayStatus = isAbsent ? 'ON LEAVE' : status.replace('_', ' ');
                                return (
                                  <div key={student.id} className="student-roster-row" onClick={() => handleStudentClick(student)} style={{ cursor: 'pointer', opacity: isAbsent ? 0.6 : 1 }}>
                                    <div>
                                      <div className="student-name-text">{student.first_name} {student.last_name} {isAbsent && <span style={{ color: '#EF4444', fontSize: '11px', marginLeft: '6px' }}>({student.absence_reason})</span>}</div>
                                      <div className="student-stop-text">Stop: {student.assigned_stop_name || student.pickup_stop_name || student.dropoff_stop_name}</div>
                                    </div>
                                    <span className={!isAbsent ? `student-status-badge status-${status.toLowerCase()}` : 'student-status-badge'} style={isAbsent ? { backgroundColor: '#FEE2E2', color: '#B91C1C', border: '1px solid #FECACA' } : {}}>
                                      {displayStatus}
                                    </span>
                                  </div>
                                );
                              })
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="empty-inspector-wrapper">
                      <HelpCircle size={32} color="#7E8794" />
                      <span className="empty-inspector-text">Select an active trip from the left panel to begin monitoring.</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab !== 'monitor' && (
            <div className="management-tab-container">
              {/* LAYMAN MENU 1: TODAY'S TRIPS & ROUTE PLANNING */}
              {activeTab === 'trips' && (
                <div className="manage-panel">
                  {/* Layman Sub-navigation tabs */}
                  <div style={{ display: 'flex', gap: '8px', borderBottom: '2px solid #E2E8F0', paddingBottom: '12px', marginBottom: '20px' }}>
                    <button 
                      onClick={() => setTripsSubTab('schedules')}
                      style={{
                        padding: '10px 18px',
                        borderRadius: '12px',
                        fontSize: '13px',
                        fontWeight: '700',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        border: 'none',
                        backgroundColor: tripsSubTab === 'schedules' ? 'var(--text-primary)' : 'var(--bg-hover)',
                        color: tripsSubTab === 'schedules' ? 'var(--surface-color)' : 'var(--text-secondary)',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      🗺️ 1. Bus Stops & Routes Planner
                    </button>
                    <button 
                      onClick={() => setTripsSubTab('assign')}
                      style={{
                        padding: '10px 18px',
                        borderRadius: '12px',
                        fontSize: '13px',
                        fontWeight: '700',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        border: 'none',
                        backgroundColor: tripsSubTab === 'assign' ? 'var(--text-primary)' : 'var(--bg-hover)',
                        color: tripsSubTab === 'assign' ? 'var(--surface-color)' : 'var(--text-secondary)',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      👤 2. Assign Drivers & Buses
                    </button>
                    <button 
                      onClick={() => setTripsSubTab('roster')}
                      style={{
                        padding: '10px 18px',
                        borderRadius: '12px',
                        fontSize: '13px',
                        fontWeight: '700',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        border: 'none',
                        backgroundColor: tripsSubTab === 'roster' ? 'var(--text-primary)' : 'var(--bg-hover)',
                        color: tripsSubTab === 'roster' ? 'var(--surface-color)' : 'var(--text-secondary)',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      👥 3. Student Route Assignments
                    </button>
                  </div>

                  {tripsSubTab === 'schedules' && (
                    /* Existing Routes schedules tab rendering */
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <h2 style={{ fontSize: '18px', color: 'var(--deep-navy)', margin: 0 }}>Route Stops Plan</h2>
                        {!showAddRouteForm && (
                          <button
                            className="save-delay-btn"
                            style={{ padding: '8px 16px', fontSize: '13px' }}
                            onClick={() => setShowAddRouteForm(true)}
                          >
                            + Add Route Schedule
                          </button>
                        )}
                      </div>

                      {showAddRouteForm && (
                        <form onSubmit={handleSaveRoute} style={{ background: '#F8FAFC', border: '1.5px solid var(--border-color)', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
                          <h3 style={{ fontSize: '16px', color: 'var(--deep-navy)', marginBottom: '8px' }}>{editingRouteId ? 'Edit Route Schedule' : 'Create New Route Schedule'}</h3>
                          
                          <div className="add-route-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                            <div className="route-form-group">
                              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: 'var(--text-secondary)', marginBottom: '6px' }}>Route Name</label>
                              <input 
                                type="text" 
                                className="route-form-input" 
                                placeholder="e.g. Route A - Morning Pickup" 
                                value={newRouteName}
                                onChange={(e) => setNewRouteName(e.target.value)}
                              />
                            </div>
                            <div className="route-form-group">
                              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: 'var(--text-secondary)', marginBottom: '6px' }}>Direction</label>
                              <select 
                                className="route-form-select"
                                value={newRouteDirection}
                                onChange={(e) => setNewRouteDirection(e.target.value)}
                              >
                                <option value="PICKUP">PICKUP (Morning Run)</option>
                                <option value="DROP">DROP (Afternoon Run)</option>
                              </select>
                            </div>
                          </div>

                          <div className="stops-builder-layout" style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '20px', marginBottom: '20px' }}>
                            <div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                <h4 style={{ fontSize: '14px', color: 'var(--deep-navy)', margin: 0 }}>Planned Stops sequence</h4>
                                <button type="button" className="add-stop-btn" onClick={addStopRow} style={{ backgroundColor: 'var(--text-primary)', color: 'white', border: 'none', borderRadius: '8px', padding: '6px 12px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }}>+ Add Stop</button>
                              </div>

                              <div className="stops-form-scroll" style={{ maxHeight: '320px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px', paddingRight: '4px' }}>
                                {newRouteStops.map((stop, index) => (
                                  <div key={index} className="stop-input-row" style={{ display: 'grid', gridTemplateColumns: '26px 1.5fr 1fr 1fr 1.2fr 24px', gap: '6px', alignItems: 'center', background: activeStopIndex === index ? '#EFF6FF' : 'transparent', padding: '6px', borderRadius: '8px', border: activeStopIndex === index ? '1px solid #BFDBFE' : '1px solid transparent' }}>
                                    <div 
                                      style={{ width: '22px', height: '22px', borderRadius: '50%', backgroundColor: activeStopIndex === index ? '#2563EB' : '#64748B', color: 'white', display: 'flex', alignItems: 'center', justifyText: 'center', fontSize: '10px', fontWeight: 'bold', justifyContent: 'center', cursor: 'pointer' }}
                                      onClick={() => setActiveStopIndex(index)}
                                    >
                                      {stop.sequenceNumber}
                                    </div>
                                    <input 
                                      type="text" 
                                      placeholder="Stop Name" 
                                      value={stop.stopName}
                                      onChange={(e) => updateStopRow(index, 'stopName', e.target.value)}
                                      style={{ padding: '6px 8px', borderRadius: '6px', border: '1px solid var(--border-color)', fontSize: '11px', fontWeight: 'bold' }}
                                    />
                                    <input 
                                      type="text" 
                                      placeholder="Latitude" 
                                      value={stop.lat}
                                      onChange={(e) => updateStopRow(index, 'lat', e.target.value)}
                                      style={{ padding: '6px 8px', borderRadius: '6px', border: '1px solid var(--border-color)', fontSize: '11px' }}
                                    />
                                    <input 
                                      type="text" 
                                      placeholder="Longitude" 
                                      value={stop.lng}
                                      onChange={(e) => updateStopRow(index, 'lng', e.target.value)}
                                      style={{ padding: '6px 8px', borderRadius: '6px', border: '1px solid var(--border-color)', fontSize: '11px' }}
                                    />
                                    <input 
                                      type="text" 
                                      placeholder="Time (e.g. 07:30 AM)" 
                                      value={stop.scheduledTime}
                                      onChange={(e) => updateStopRow(index, 'scheduledTime', e.target.value)}
                                      style={{ padding: '6px 8px', borderRadius: '6px', border: '1px solid var(--border-color)', fontSize: '11px', fontWeight: 'bold' }}
                                    />
                                    <button 
                                      type="button" 
                                      onClick={() => removeStopRow(index)}
                                      style={{ border: 'none', background: 'transparent', color: '#FF7B72', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>

                            <div>
                              <h4 style={{ fontSize: '14px', color: 'var(--deep-navy)', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>Pin on Interactive Map</span>
                                <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Stop {activeStopIndex + 1} Selected</span>
                              </h4>
                              
                              <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
                                <input 
                                  type="text" 
                                  placeholder="Search address (e.g. Banaswadi, Bangalore)..."
                                  value={mapSearchQuery}
                                  onChange={(e) => setMapSearchQuery(e.target.value)}
                                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleMapSearch())}
                                  style={{ flex: 1, padding: '8px 12px', borderRadius: '8px', border: '1.5px solid var(--border-color)', fontSize: '12px' }}
                                />
                                <button type="button" onClick={handleMapSearch} disabled={isSearching} style={{ padding: '8px 16px', background: '#131417', color: 'white', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}>
                                  {isSearching ? '...' : 'Find'}
                                </button>
                              </div>

                              <div style={{ height: '240px', borderRadius: '12px', overflow: 'hidden', border: '1.5px solid var(--border-color)' }}>
                                <MapContainer center={mapCenter} zoom={13} style={{ height: '100%', width: '100%' }}>
                                  <TileLayer
                                    attribution={mapProvider === 'ola' ? '&copy; Ola Maps' : '&copy; OpenStreetMap'}
                                    url={mapProvider === 'ola' && mapApiKey ? `https://api.olamaps.io/tiles/vector/v1/styles/default-light/{z}/{x}/{y}.png?api_key=${mapApiKey}` : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"}
                                  />
                                  <ChangeMapView center={mapCenter} />
                                  <InteractiveMapEvents 
                                    activeStopIndex={activeStopIndex} 
                                    newRouteStops={newRouteStops} 
                                    updateStopRow={updateStopRow} 
                                  />

                                  {newRouteStops.map((stop, index) => {
                                    const parsedLat = parseFloat(stop.lat);
                                    const parsedLng = parseFloat(stop.lng);
                                    if (isNaN(parsedLat) || isNaN(parsedLng)) return null;

                                    return (
                                      <Marker 
                                        key={index} 
                                        position={[parsedLat, parsedLng]}
                                        icon={getPlanningStopIcon(stop.sequenceNumber, activeStopIndex === index)}
                                        eventHandlers={{
                                          click: () => {
                                            setActiveStopIndex(index);
                                          }
                                        }}
                                      >
                                        <Popup>
                                          <strong>Stop {stop.sequenceNumber}:</strong> {stop.stopName || 'No Name'}
                                        </Popup>
                                      </Marker>
                                    );
                                  })}

                                  {newRouteStops.length > 1 && (
                                    <Polyline 
                                      positions={newRouteStops
                                        .filter(s => !isNaN(parseFloat(s.lat)) && !isNaN(parseFloat(s.lng)))
                                        .map(s => [parseFloat(s.lat), parseFloat(s.lng)])}
                                      color="#2563EB"
                                      weight={3}
                                      opacity={0.8}
                                      dashArray="5, 8"
                                    />
                                  )}
                                </MapContainer>
                              </div>
                            </div>
                          </div>

                          <div style={{ display: 'flex', gap: '12px', borderTop: '1.5px solid #E2E8F0', paddingTop: '16px' }}>
                            <button type="submit" style={{ background: '#131417', color: 'white', fontWeight: 'bold', padding: '10px 24px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '13px' }}>Save Route Plan</button>
                            <button type="button" onClick={() => { setShowAddRouteForm(false); setEditingRouteId(null); setNewRouteName(''); setNewRouteStops([{ stopName: '', lat: '', lng: '', scheduledTime: '', sequenceNumber: 1 }]); }} style={{ background: 'transparent', color: 'var(--text-secondary)', padding: '10px 16px', border: 'none', cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
                          </div>
                        </form>
                      )}

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        {allRoutes.map(route => (
                          <div key={route.id} style={{ border: '1.5px solid var(--border-color)', borderRadius: '12px', padding: '16px', background: 'white' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                              <h3 style={{ fontSize: '16px', color: 'var(--deep-navy)', margin: 0 }}>{route.route_name} ({route.direction})</h3>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  type="button"
                                  onClick={() => handleEditRoute(route)}
                                  style={{
                                    background: 'white',
                                    color: '#2563EB',
                                    border: '1.5px solid var(--border-color)',
                                    borderRadius: '8px',
                                    padding: '6px 12px',
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Modify
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteRoute(route.id)}
                                  style={{
                                    background: 'white',
                                    color: '#FF7B72',
                                    border: '1.5px solid var(--border-color)',
                                    borderRadius: '8px',
                                    padding: '6px 12px',
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            </div>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                              {route.stops && route.stops.map((stop, i) => (
                                <div key={stop.id} style={{ background: '#F1F5F9', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <div style={{ width: '18px', height: '18px', borderRadius: '50%', backgroundcolor: 'var(--text-secondary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 'bold' }}>
                                    {stop.sequence_number}
                                  </div>
                                  <div>
                                    <div style={{ fontSize: '11px', fontWeight: 'bold', color: 'var(--text-primary)' }}>{stop.stop_name}</div>
                                    <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>{stop.scheduled_time}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {tripsSubTab === 'assign' && (
                    /* Existing Assign Drivers tab rendering */
                    <div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 2fr', gap: '24px', alignItems: 'start' }}>
                        <div style={{ border: '1.5px solid var(--border-color)', borderRadius: '12px', padding: '20px', background: 'white' }}>
                          <h3 style={{ fontSize: '16px', color: 'var(--deep-navy)', marginBottom: '8px', margin: 0 }}>Configure Daily Route Deployment</h3>
                          <form onSubmit={handleAssignDriver} style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
                            <div className="route-form-group">
                              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: 'var(--text-secondary)', marginBottom: '6px' }}>Select Route Plan</label>
                              <select 
                                className="route-form-select"
                                value={selectedRouteId}
                                onChange={(e) => setSelectedRouteId(e.target.value)}
                              >
                                <option value="">-- Choose Route --</option>
                                {allRoutes.map(r => (
                                  <option key={r.id} value={r.id}>{r.route_name} ({r.direction})</option>
                                ))}
                              </select>
                            </div>

                            <div className="route-form-group">
                              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: 'var(--text-secondary)', marginBottom: '6px' }}>Assign School Bus</label>
                              <select 
                                className="route-form-select"
                                value={selectedBusId}
                                onChange={(e) => setSelectedBusId(e.target.value)}
                              >
                                <option value="">-- Choose Bus --</option>
                                {allBuses.map(b => (
                                  <option key={b.id} value={b.id}>{b.bus_number} ({b.registration})</option>
                                ))}
                              </select>
                            </div>

                            <div className="route-form-group">
                              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: 'var(--text-secondary)', marginBottom: '6px' }}>Assign Bus Driver</label>
                              <select 
                                className="route-form-select"
                                value={selectedDriverId}
                                onChange={(e) => setSelectedDriverId(e.target.value)}
                              >
                                <option value="">-- Choose Driver --</option>
                                {allUsers.filter(u => u.role === 'driver').map(d => (
                                  <option key={d.id} value={d.id}>{d.display_name} ({d.phone})</option>
                                ))}
                              </select>
                            </div>

                            <div className="route-form-group">
                              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: 'var(--text-secondary)', marginBottom: '6px' }}>Schedule Date</label>
                              <input 
                                type="date" 
                                className="route-form-input"
                                value={scheduledDate}
                                onChange={(e) => setScheduledDate(e.target.value)}
                                style={{ width: '100%' }}
                              />
                            </div>

                            <button type="submit" style={{ background: '#131417', color: 'white', fontWeight: 'bold', padding: '12px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px', marginTop: '10px' }}>Deploy Driver & Bus</button>
                          </form>
                        </div>

                        <div>
                          <h3 style={{ fontSize: '16px', color: 'var(--deep-navy)', marginBottom: '8px', margin: 0 }}>Active / Scheduled Deployments</h3>

                          {/* Search & Filter Bar */}
                          <div style={{ 
                            background: '#F8FAFC', 
                            border: '1.5px solid var(--border-color)', 
                            borderRadius: '12px', 
                            padding: '12px', 
                            marginTop: '16px',
                            marginBottom: '8px',
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', 
                            gap: '8px' 
                          }}>
                            <div>
                              <label style={{ display: 'block', fontSize: '10px', fontWeight: 'bold', color: 'var(--text-secondary)', marginBottom: '4px' }}>Filter Route</label>
                              <select 
                                value={filterRouteId} 
                                onChange={(e) => setFilterRouteId(e.target.value)}
                                style={{ width: '100%', padding: '6px 8px', fontSize: '12px', borderRadius: '8px', border: '1.5px solid var(--border-color)', outline: 'none' }}
                              >
                                <option value="">All Routes</option>
                                {allRoutes.map(r => (
                                  <option key={r.id} value={r.id}>{r.route_name}</option>
                                ))}
                              </select>
                            </div>
                            
                            <div>
                              <label style={{ display: 'block', fontSize: '10px', fontWeight: 'bold', color: 'var(--text-secondary)', marginBottom: '4px' }}>Filter Bus</label>
                              <select 
                                value={filterBusId} 
                                onChange={(e) => setFilterBusId(e.target.value)}
                                style={{ width: '100%', padding: '6px 8px', fontSize: '12px', borderRadius: '8px', border: '1.5px solid var(--border-color)', outline: 'none' }}
                              >
                                <option value="">All Buses</option>
                                {allBuses.map(b => (
                                  <option key={b.id} value={b.id}>{b.bus_number}</option>
                                ))}
                              </select>
                            </div>
                            
                            <div>
                              <label style={{ display: 'block', fontSize: '10px', fontWeight: 'bold', color: 'var(--text-secondary)', marginBottom: '4px' }}>Filter Driver</label>
                              <select 
                                value={filterDriverId} 
                                onChange={(e) => setFilterDriverId(e.target.value)}
                                style={{ width: '100%', padding: '6px 8px', fontSize: '12px', borderRadius: '8px', border: '1.5px solid var(--border-color)', outline: 'none' }}
                              >
                                <option value="">All Drivers</option>
                                {allUsers.filter(u => u.role === 'driver').map(d => (
                                  <option key={d.id} value={d.id}>{d.display_name}</option>
                                ))}
                              </select>
                            </div>
                            
                            <div>
                              <label style={{ display: 'block', fontSize: '10px', fontWeight: 'bold', color: 'var(--text-secondary)', marginBottom: '4px' }}>Filter Date</label>
                              <input 
                                type="date" 
                                value={filterDate} 
                                onChange={(e) => setFilterDate(e.target.value)}
                                style={{ width: '100%', padding: '5px 8px', fontSize: '12px', borderRadius: '8px', border: '1.5px solid var(--border-color)', outline: 'none' }}
                              />
                            </div>
                            
                            <div>
                              <label style={{ display: 'block', fontSize: '10px', fontWeight: 'bold', color: 'var(--text-secondary)', marginBottom: '4px' }}>Filter Status</label>
                              <select 
                                value={filterStatus} 
                                onChange={(e) => setFilterStatus(e.target.value)}
                                style={{ width: '100%', padding: '6px 8px', fontSize: '12px', borderRadius: '8px', border: '1.5px solid var(--border-color)', outline: 'none' }}
                              >
                                <option value="">All Statuses</option>
                                <option value="SCHEDULED">Scheduled</option>
                                <option value="EN_ROUTE">Active / En Route</option>
                                <option value="COMPLETED">Completed</option>
                              </select>
                            </div>
                            
                            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                              <button 
                                type="button" 
                                onClick={() => {
                                  setFilterRouteId('');
                                  setFilterBusId('');
                                  setFilterDriverId('');
                                  setFilterDate('');
                                  setFilterStatus('');
                                }}
                                style={{ 
                                  width: '100%', 
                                  padding: '8px 12px', 
                                  fontSize: '11px', 
                                  fontWeight: 'bold', 
                                  backgroundColor: '#E2E8F0', 
                                  color: '#475569', 
                                  borderRadius: '8px',
                                  cursor: 'pointer' 
                                }}
                              >
                                Clear
                              </button>
                            </div>
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {paginatedTrips.length === 0 ? (
                              <div style={{ padding: '24px', textAlign: 'center', background: '#F8FAFC', border: '1.5px solid var(--border-color)', borderRadius: '12px', color: 'var(--text-secondary)' }}>
                                No bus runs matching search criteria.
                              </div>
                            ) : (
                              paginatedTrips.map(trip => {
                                const assignedBus = allBuses.find(b => b.id === trip.bus_id || b.bus_number === trip.bus_number);
                                const busCapacity = assignedBus?.capacity || 40;
                                const isExpanded = expandedTripId === trip.id;
                                const tripStudents = tripStudentsData[trip.id] || [];
                                const isLoadingStudents = loadingTripStudents[trip.id];
                                const routeObj = allRoutes.find(r => r.id === trip.route_id);
                                const routeStops = routeObj ? (routeObj.stops || []) : [];

                                const capacityPercent = Math.min(100, Math.round((tripStudents.length / busCapacity) * 100));
                                const isOverCapacity = tripStudents.length > busCapacity;

                                return (
                                  <div key={trip.id} style={{ display: 'flex', flexDirection: 'column', background: 'white', border: isExpanded ? '2px solid #3B82F6' : '1.5px solid var(--border-color)', borderRadius: '12px', padding: '16px', transition: 'all 0.2s ease', boxShadow: isExpanded ? '0 4px 14px rgba(59,130,246,0.1)' : 'none' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                                      <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                          <div style={{ fontSize: '15px', fontWeight: 'bold', color: 'var(--text-primary)' }}>{trip.route_name}</div>
                                          <span style={{
                                            fontSize: '11px',
                                            fontWeight: '700',
                                            padding: '2px 8px',
                                            borderRadius: '6px',
                                            backgroundColor: trip.direction === 'PICKUP' ? '#ECFDF5' : '#FFF7ED',
                                            color: trip.direction === 'PICKUP' ? '#047857' : '#C2410C',
                                            border: '1px solid ' + (trip.direction === 'PICKUP' ? '#A7F3D0' : '#FFEDD5')
                                          }}>
                                            {trip.direction === 'PICKUP' ? '🌅 Morning Pickup Route' : '🌆 Afternoon Drop-off Route'}
                                          </span>
                                        </div>

                                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '6px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                          <span>🚌 Bus: <strong>{trip.bus_number}</strong> ({trip.registration || 'Active'})</span>
                                          <span>🧑 Driver: <strong>{trip.driver_name}</strong></span>
                                          <span>📅 Date: <strong>{trip.scheduled_date}</strong></span>
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
                                          <span className={`student-status-badge ${trip.status === 'EN_ROUTE' ? 'status-boarded' : 'status-not_boarded'}`}>
                                            {trip.status}
                                          </span>
                                          {trip.delay_minutes > 0 && (
                                            <span className="student-status-badge status-deboarded">
                                              +{trip.delay_minutes} min delay
                                            </span>
                                          )}
                                        </div>
                                      </div>

                                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <button
                                          type="button"
                                          onClick={() => toggleExpandTrip(trip.id)}
                                          style={{
                                            background: isExpanded ? '#EFF6FF' : '#F8FAFC',
                                            color: isExpanded ? '#2563EB' : '#475569',
                                            border: '1.5px solid',
                                            borderColor: isExpanded ? '#93C5FD' : 'var(--border-color)',
                                            borderRadius: '8px',
                                            padding: '8px 14px',
                                            fontSize: '12px',
                                            fontWeight: '700',
                                            cursor: 'pointer',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                          }}
                                        >
                                          <span>👥</span>
                                          <span>{isExpanded ? 'Hide Route Students' : 'View Route Students'}</span>
                                          <span style={{ fontSize: '10px' }}>{isExpanded ? '▲' : '▼'}</span>
                                        </button>

                                        {trip.status !== 'COMPLETED' && (
                                          <button
                                            onClick={() => handleCancelAssignment(trip.id)}
                                            style={{
                                              background: 'white',
                                              color: '#FF7B72',
                                              border: '1.5px solid var(--border-color)',
                                              borderRadius: '8px',
                                              padding: '8px 12px',
                                              fontSize: '12px',
                                              fontWeight: 'bold',
                                              cursor: 'pointer'
                                            }}
                                          >
                                            Cancel Run
                                          </button>
                                        )}
                                      </div>
                                    </div>

                                    {/* Expandable Students & Stops Panel */}
                                    {isExpanded && (
                                      <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1.5px solid #F1F5F9' }}>
                                        {isLoadingStudents ? (
                                          <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>
                                            Loading student route stops...
                                          </div>
                                        ) : (
                                          <div>
                                            {/* Capacity and Overview Bar */}
                                            <div style={{
                                              display: 'flex',
                                              justifyContent: 'space-between',
                                              alignItems: 'center',
                                              backgroundColor: '#F8FAFC',
                                              border: '1px solid #E2E8F0',
                                              borderRadius: '10px',
                                              padding: '12px 16px',
                                              marginBottom: '16px',
                                              flexWrap: 'wrap',
                                              gap: '12px'
                                            }}>
                                              <div>
                                                <div style={{ fontSize: '13px', fontWeight: '800', color: 'var(--text-primary)' }}>
                                                  Route Passenger Load: {tripStudents.length} / {busCapacity} Students Assigned
                                                </div>
                                                <div style={{ fontSize: '11px', color: isOverCapacity ? '#EF4444' : 'var(--text-secondary)', marginTop: '2px', fontWeight: isOverCapacity ? '700' : 'normal' }}>
                                                  {isOverCapacity ? `⚠️ Over seating capacity by ${tripStudents.length - busCapacity} student(s)!` : `${busCapacity - tripStudents.length} empty seats remaining`}
                                                </div>
                                              </div>

                                              <div style={{ width: '180px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', fontWeight: 'bold', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                                                  <span>Bus Capacity</span>
                                                  <span>{capacityPercent}%</span>
                                                </div>
                                                <div style={{ width: '100%', height: '8px', backgroundColor: '#E2E8F0', borderRadius: '4px', overflow: 'hidden' }}>
                                                  <div style={{
                                                    width: `${capacityPercent}%`,
                                                    height: '100%',
                                                    backgroundColor: isOverCapacity ? '#EF4444' : capacityPercent > 80 ? '#F59E0B' : '#10B981',
                                                    borderRadius: '4px'
                                                  }} />
                                                </div>
                                              </div>
                                            </div>

                                            {/* Sequenced Stops and Assigned Students */}
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                              {routeStops.length === 0 ? (
                                                <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                                                  No stops configured on this route.
                                                </div>
                                              ) : (
                                                routeStops.map((stop, sIdx) => {
                                                  const stopStudents = tripStudents.filter(st => {
                                                    if (trip.direction === 'PICKUP') return st.pickup_stop_id === stop.id || st.pickup_stop_name === stop.stop_name;
                                                    return st.drop_stop_id === stop.id || st.dropoff_stop_name === stop.stop_name;
                                                  });

                                                  return (
                                                    <div key={stop.id || sIdx} style={{ border: '1px solid #E2E8F0', borderRadius: '10px', padding: '12px', backgroundColor: stopStudents.length > 0 ? '#FFFFFF' : '#FAFAFA' }}>
                                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: stopStudents.length > 0 ? '10px' : '0' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                          <div style={{
                                                            width: '24px',
                                                            height: '24px',
                                                            borderRadius: '50%',
                                                            backgroundColor: trip.direction === 'PICKUP' ? '#047857' : '#C2410C',
                                                            color: 'white',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '11px',
                                                            fontWeight: '800'
                                                          }}>
                                                            {stop.sequence_number || sIdx + 1}
                                                          </div>
                                                          <div>
                                                            <strong style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{stop.stop_name}</strong>
                                                            <span style={{ fontSize: '11px', color: 'var(--text-secondary)', marginLeft: '8px' }}>⏰ {stop.scheduled_time}</span>
                                                          </div>
                                                        </div>

                                                        <span style={{
                                                          fontSize: '11px',
                                                          fontWeight: '700',
                                                          padding: '2px 8px',
                                                          borderRadius: '12px',
                                                          backgroundColor: stopStudents.length > 0 ? '#EFF6FF' : '#F1F5F9',
                                                          color: stopStudents.length > 0 ? '#1D4ED8' : '#94A3B8'
                                                        }}>
                                                          {stopStudents.length} student{stopStudents.length === 1 ? '' : 's'} {trip.direction === 'PICKUP' ? 'boarding' : 'deboarding'}
                                                        </span>
                                                      </div>

                                                      {stopStudents.length > 0 && (
                                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '8px', marginTop: '8px' }}>
                                                          {stopStudents.map(st => {
                                                            const stStatus = st.attendance_status || 'NOT_BOARDED';
                                                            return (
                                                              <div
                                                                key={st.id}
                                                                onClick={() => handleStudentClick(st)}
                                                                style={{
                                                                  display: 'flex',
                                                                  alignItems: 'center',
                                                                  gap: '10px',
                                                                  padding: '8px 10px',
                                                                  borderRadius: '8px',
                                                                  border: '1px solid #E2E8F0',
                                                                  backgroundColor: '#F8FAFC',
                                                                  cursor: 'pointer',
                                                                  transition: 'all 0.15s ease'
                                                                }}
                                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#EFF6FF'}
                                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#F8FAFC'}
                                                              >
                                                                <div style={{ width: '32px', height: '32px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }}>
                                                                  <img
                                                                    src={getStudentAvatar(st)}
                                                                    alt={st.first_name}
                                                                    onError={(e) => {
                                                                      e.currentTarget.onerror = null;
                                                                      e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(st.first_name + ' ' + (st.last_name || ''))}&background=E2E8F0&color=475569&bold=true`;
                                                                    }}
                                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                  />
                                                                </div>
                                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                                  <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                    {st.first_name} {st.last_name}
                                                                  </div>
                                                                  <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>
                                                                    {st.admission_no || ('ADM-00' + st.id)} • 📞 {st.parent_phone || 'N/A'}
                                                                  </div>
                                                                </div>
                                                                <span className={`student-status-badge status-${stStatus.toLowerCase()}`} style={{ fontSize: '9px', padding: '2px 6px' }}>
                                                                  {stStatus}
                                                                </span>
                                                              </div>
                                                            );
                                                          })}
                                                        </div>
                                                      )}
                                                    </div>
                                                  );
                                                })
                                              )}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })
                            )}
                          </div>

                          {/* Pagination controls */}
                          {totalPages > 1 && (
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center', 
                              marginTop: '16px', 
                              background: 'white', 
                              border: '1.5px solid var(--border-color)', 
                              borderRadius: '12px', 
                              padding: '10px 16px' 
                            }}>
                              <button 
                                type="button"
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                style={{ 
                                  padding: '6px 12px', 
                                  fontSize: '12px', 
                                  fontWeight: 'bold', 
                                  backgroundColor: currentPage === 1 ? 'var(--bg-hover)' : 'var(--text-primary)', 
                                  color: currentPage === 1 ? '#94A3B8' : '#FFFFFF', 
                                  borderRadius: '8px',
                                  cursor: currentPage === 1 ? 'default' : 'pointer'
                                }}
                              >
                                Previous
                              </button>
                              <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#475569' }}>
                                Page {currentPage} of {totalPages}
                              </span>
                              <button 
                                type="button"
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                style={{ 
                                  padding: '6px 12px', 
                                  fontSize: '12px', 
                                  fontWeight: 'bold', 
                                  backgroundColor: currentPage === totalPages ? 'var(--bg-hover)' : 'var(--text-primary)', 
                                  color: currentPage === totalPages ? '#94A3B8' : '#FFFFFF', 
                                  borderRadius: '8px',
                                  cursor: currentPage === totalPages ? 'default' : 'pointer'
                                }}
                              >
                                Next
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* SUB-TAB 3: STUDENT ROUTE & STOP ASSIGNMENTS */}
                  {tripsSubTab === 'roster' && (() => {
                    const busRidersCount = allStudents.filter(s => (s.transport_mode || 'School Bus') === 'School Bus' && (s.pickup_stop_id || s.drop_stop_id)).length;
                    const noTransportCount = allStudents.filter(s => s.transport_mode === 'No Transport' || (!s.pickup_stop_id && !s.drop_stop_id)).length;

                    const q = rosterSearchQuery.trim().toLowerCase();

                    // Filter routes based on selected filter
                    let displayedRoutes = allRoutes.filter(route => {
                      if (rosterTransportFilter === 'pickup' && route.direction !== 'PICKUP') return false;
                      if (rosterTransportFilter === 'drop' && route.direction !== 'DROP') return false;
                      if (rosterSelectedRouteId && rosterSelectedRouteId !== 'all' && rosterSelectedRouteId !== 'no_bus') {
                        return String(route.id) === String(rosterSelectedRouteId);
                      }
                      return true;
                    });

                    // Students with no school transport
                    const noTransportStudents = allStudents.filter(s => {
                      const isNoTransport = s.transport_mode === 'No Transport' || (!s.pickup_stop_id && !s.drop_stop_id);
                      if (!isNoTransport) return false;
                      if (!q) return true;
                      const fullName = `${s.first_name || ''} ${s.last_name || ''}`.toLowerCase();
                      const adm = (s.admission_no || `adm-00${s.id}`).toLowerCase();
                      const parent = (s.parent_name || '').toLowerCase();
                      const phone = (s.parent_phone || '').toLowerCase();
                      return fullName.includes(q) || adm.includes(q) || parent.includes(q) || phone.includes(q);
                    });

                    const showNoTransportSection = rosterSelectedRouteId === 'no_bus' || rosterTransportFilter === 'no_bus' || (!rosterSelectedRouteId && rosterTransportFilter === 'all');

                    return (
                      <div>
                        {/* Header & Stats Cards */}
                        <div style={{ marginBottom: '20px' }}>
                          <h2 style={{ fontSize: '18px', color: 'var(--deep-navy)', margin: '0 0 4px 0' }}>
                            Students Pickup & Drop-Off Route Assignments
                          </h2>
                          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0 }}>
                            Review student route rosters, sequenced bus stops, and manage students opting out of school transportation.
                          </p>
                        </div>

                        {/* Top Metric Cards */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                          <div style={{ background: '#FFFFFF', border: '1.5px solid var(--border-color)', borderRadius: '12px', padding: '16px' }}>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)' }}>Total Registered Students</div>
                            <div style={{ fontSize: '24px', fontWeight: '800', color: 'var(--text-primary)', marginTop: '4px' }}>{allStudents.length}</div>
                            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>Enrolled across all grades</div>
                          </div>

                          <div style={{ background: '#FFFFFF', border: '1.5px solid #93C5FD', borderRadius: '12px', padding: '16px' }}>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#1D4ED8' }}>🚌 School Bus Riders</div>
                            <div style={{ fontSize: '24px', fontWeight: '800', color: '#1D4ED8', marginTop: '4px' }}>{busRidersCount}</div>
                            <div style={{ fontSize: '11px', color: '#3B82F6', marginTop: '2px' }}>
                              {allStudents.length > 0 ? `${Math.round((busRidersCount / allStudents.length) * 100)}% of student body` : '0%'}
                            </div>
                          </div>

                          <div style={{ background: '#FFFFFF', border: '1.5px solid #FCA5A5', borderRadius: '12px', padding: '16px' }}>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#B91C1C' }}>🚫 No School Transport</div>
                            <div style={{ fontSize: '24px', fontWeight: '800', color: '#B91C1C', marginTop: '4px' }}>{noTransportCount}</div>
                            <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>
                              Private transport / self / walk
                            </div>
                          </div>

                          <div style={{ background: '#FFFFFF', border: '1.5px solid var(--border-color)', borderRadius: '12px', padding: '16px' }}>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)' }}>Active Bus Routes</div>
                            <div style={{ fontSize: '24px', fontWeight: '800', color: 'var(--text-primary)', marginTop: '4px' }}>{allRoutes.length}</div>
                            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>Morning & Afternoon corridors</div>
                          </div>
                        </div>

                        {/* Search & Filter Toolbar */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: '12px',
                          background: 'white',
                          border: '1.5px solid var(--border-color)',
                          borderRadius: '12px',
                          padding: '12px 16px',
                          marginBottom: '20px',
                          flexWrap: 'wrap'
                        }}>
                          {/* Filter Pills */}
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {[
                              { id: 'all', label: 'All Corridors' },
                              { id: 'pickup', label: '🌅 Morning Pickup' },
                              { id: 'drop', label: '🌆 Afternoon Drop' },
                              { id: 'no_bus', label: `🚫 Opted Out (${noTransportCount})` }
                            ].map(filter => (
                              <button
                                key={filter.id}
                                type="button"
                                onClick={() => {
                                  setRosterTransportFilter(filter.id);
                                  if (filter.id === 'no_bus') setRosterSelectedRouteId('no_bus');
                                  else if (rosterSelectedRouteId === 'no_bus') setRosterSelectedRouteId('all');
                                }}
                                style={{
                                  padding: '6px 12px',
                                  borderRadius: '8px',
                                  fontSize: '12px',
                                  fontWeight: '700',
                                  border: '1px solid',
                                  borderColor: rosterTransportFilter === filter.id ? 'var(--text-primary)' : 'var(--border-color)',
                                  backgroundColor: rosterTransportFilter === filter.id ? 'var(--text-primary)' : 'var(--surface-color)',
                                  color: rosterTransportFilter === filter.id ? 'var(--surface-color)' : 'var(--text-secondary)',
                                  cursor: 'pointer',
                                  transition: 'all 0.15s ease'
                                }}
                              >
                                {filter.label}
                              </button>
                            ))}
                          </div>

                          {/* Route Dropdown and Search */}
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <select
                              value={rosterSelectedRouteId}
                              onChange={(e) => {
                                const val = e.target.value;
                                setRosterSelectedRouteId(val);
                                if (val === 'no_bus') setRosterTransportFilter('no_bus');
                                else if (rosterTransportFilter === 'no_bus') setRosterTransportFilter('all');
                              }}
                              style={{
                                padding: '8px 12px',
                                borderRadius: '8px',
                                border: '1px solid var(--border-color)',
                                fontSize: '13px',
                                backgroundColor: 'var(--bg-alt)',
                                outline: 'none'
                              }}
                            >
                              <option value="all">-- All Bus Routes --</option>
                              <option value="no_bus">🚫 No School Transport ({noTransportCount} students)</option>
                              {allRoutes.map(r => (
                                <option key={r.id} value={r.id}>
                                  {r.route_name} ({r.direction === 'PICKUP' ? '🌅 Pickup' : '🌆 Drop'})
                                </option>
                              ))}
                            </select>

                            <input
                              type="text"
                              value={rosterSearchQuery}
                              onChange={(e) => setRosterSearchQuery(e.target.value)}
                              placeholder="Search student, admission #, parent..."
                              style={{
                                padding: '8px 12px',
                                borderRadius: '8px',
                                border: '1px solid var(--border-color)',
                                fontSize: '13px',
                                minWidth: '220px',
                                outline: 'none'
                              }}
                            />
                            {rosterSearchQuery && (
                              <button
                                type="button"
                                onClick={() => setRosterSearchQuery('')}
                                style={{
                                  padding: '8px 12px',
                                  borderRadius: '8px',
                                  border: '1px solid var(--border-color)',
                                  background: 'var(--bg-hover)',
                                  color: 'var(--text-secondary)',
                                  fontSize: '12px',
                                  fontWeight: 'bold',
                                  cursor: 'pointer'
                                }}
                              >
                                Clear
                              </button>
                            )}
                          </div>
                        </div>

                        {/* SECTION 1: Students Opted Out of School Transport */}
                        {showNoTransportSection && (
                          <div style={{
                            background: '#FFFFFF',
                            border: '1.5px solid #FCA5A5',
                            borderRadius: '12px',
                            padding: '20px',
                            marginBottom: '24px'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                              <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{ fontSize: '18px' }}>🚫</span>
                                  <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#B91C1C', margin: 0 }}>
                                    Students with No School Transport (Private / Self-Transit)
                                  </h3>
                                  <span style={{
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    backgroundColor: '#FEF2F2',
                                    color: '#B91C1C',
                                    border: '1px solid #FECACA'
                                  }}>
                                    {noTransportStudents.length} Student{noTransportStudents.length === 1 ? '' : 's'}
                                  </span>
                                </div>
                                <div style={{ fontSize: '12px', color: '#7F1D1D', marginTop: '4px' }}>
                                  These students do not utilize school buses and are excluded from trip manifests, route rosters, and RFID bus boarding.
                                </div>
                              </div>
                            </div>

                            {noTransportStudents.length === 0 ? (
                              <div style={{ padding: '24px', textAlign: 'center', background: '#FEF2F2', borderRadius: '8px', color: '#991B1B', fontSize: '13px' }}>
                                {q ? 'No opted-out students match your search.' : 'All registered students are currently utilizing school bus transportation.'}
                              </div>
                            ) : (
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
                                {noTransportStudents.map(student => (
                                  <div
                                    key={student.id}
                                    style={{
                                      background: '#FAFAFA',
                                      border: '1px solid #E2E8F0',
                                      borderRadius: '10px',
                                      padding: '12px',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '10px'
                                    }}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                      <div style={{ width: '36px', height: '36px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }}>
                                        <img
                                          src={getStudentAvatar(student)}
                                          alt={student.first_name}
                                          onError={(e) => {
                                            e.currentTarget.onerror = null;
                                            e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(student.first_name + ' ' + (student.last_name || ''))}&background=FEE2E2&color=B91C1C&bold=true`;
                                          }}
                                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                      </div>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                          {student.first_name} {student.last_name}
                                        </div>
                                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                                          {student.admission_no || ('ADM-00' + student.id)} • Grade: {student.grade || 'N/A'}
                                        </div>
                                      </div>
                                    </div>

                                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                      <div>🧑 <strong>Parent:</strong> {student.parent_name || 'N/A'}</div>
                                      <div>📞 <strong>Phone:</strong> {student.parent_phone || 'N/A'}</div>
                                      <div style={{ color: '#B91C1C', fontWeight: '600' }}>
                                        🚗 <strong>Transit:</strong> {student.transport_mode || 'Independent / Private'}
                                      </div>
                                    </div>

                                    <button
                                      type="button"
                                      onClick={() => {
                                        handleStudentClick(student);
                                        setIsEditingStudent(true);
                                      }}
                                      style={{
                                        marginTop: 'auto',
                                        padding: '6px 10px',
                                        borderRadius: '6px',
                                        backgroundColor: '#EFF6FF',
                                        color: '#1D4ED8',
                                        border: '1px solid #BFDBFE',
                                        fontSize: '11px',
                                        fontWeight: '700',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '4px'
                                      }}
                                    >
                                      🚌 Assign School Bus Transport
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* SECTION 2: Bus Routes & Sequenced Stops Roster */}
                        {rosterSelectedRouteId !== 'no_bus' && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            {displayedRoutes.length === 0 ? (
                              <div style={{ padding: '32px', textAlign: 'center', background: '#FFFFFF', border: '1.5px solid var(--border-color)', borderRadius: '12px', color: 'var(--text-secondary)' }}>
                                No bus routes matching filter.
                              </div>
                            ) : (
                              displayedRoutes.map(route => {
                                const isPickup = route.direction === 'PICKUP';
                                const stops = route.stops || [];

                                // Collect students assigned to any stop in this route
                                const assignedStudents = allStudents.filter(s => {
                                  if (s.transport_mode === 'No Transport') return false;
                                  const stopId = isPickup ? s.pickup_stop_id : s.drop_stop_id;
                                  if (!stopId) return false;
                                  const matchesStop = stops.some(st => st.id === stopId);
                                  if (!matchesStop) return false;

                                  if (!q) return true;
                                  const fullName = `${s.first_name || ''} ${s.last_name || ''}`.toLowerCase();
                                  const adm = (s.admission_no || `adm-00${s.id}`).toLowerCase();
                                  const parent = (s.parent_name || '').toLowerCase();
                                  return fullName.includes(q) || adm.includes(q) || parent.includes(q);
                                });

                                return (
                                  <div
                                    key={route.id}
                                    style={{
                                      background: '#FFFFFF',
                                      border: '1.5px solid var(--border-color)',
                                      borderRadius: '12px',
                                      padding: '20px'
                                    }}
                                  >
                                    {/* Route Header */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                                      <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                          <h3 style={{ fontSize: '16px', fontWeight: '800', color: 'var(--text-primary)', margin: 0 }}>
                                            {route.route_name}
                                          </h3>
                                          <span style={{
                                            fontSize: '11px',
                                            fontWeight: '700',
                                            padding: '2px 8px',
                                            borderRadius: '6px',
                                            backgroundColor: isPickup ? '#ECFDF5' : '#FFF7ED',
                                            color: isPickup ? '#047857' : '#C2410C',
                                            border: '1px solid ' + (isPickup ? '#A7F3D0' : '#FFEDD5')
                                          }}>
                                            {isPickup ? '🌅 Morning Pickup Route' : '🌆 Afternoon Drop-off Route'}
                                          </span>
                                        </div>
                                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                                          {stops.length} sequenced stop{stops.length === 1 ? '' : 's'} • {assignedStudents.length} student{assignedStudents.length === 1 ? '' : 's'} scheduled
                                        </div>
                                      </div>

                                      <div style={{ display: 'flex', gap: '8px' }}>
                                        <span style={{
                                          fontSize: '12px',
                                          fontWeight: '700',
                                          padding: '6px 12px',
                                          borderRadius: '8px',
                                          backgroundColor: assignedStudents.length > 0 ? '#EFF6FF' : '#F8FAFC',
                                          color: assignedStudents.length > 0 ? '#1D4ED8' : 'var(--text-secondary)',
                                          border: '1px solid var(--border-color)'
                                        }}>
                                          👥 {assignedStudents.length} Bus Riders
                                        </span>
                                      </div>
                                    </div>

                                    {/* Sequenced Stops List */}
                                    {stops.length === 0 ? (
                                      <div style={{ padding: '16px', textAlign: 'center', background: '#F8FAFC', borderRadius: '8px', color: 'var(--text-secondary)', fontSize: '12px' }}>
                                        No stops have been added to this route yet.
                                      </div>
                                    ) : (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        {stops.map((stop, sIdx) => {
                                          const stopStudents = assignedStudents.filter(s => {
                                            const stopId = isPickup ? s.pickup_stop_id : s.drop_stop_id;
                                            return stopId === stop.id;
                                          });

                                          return (
                                            <div
                                              key={stop.id || sIdx}
                                              style={{
                                                border: '1px solid #E2E8F0',
                                                borderRadius: '10px',
                                                padding: '14px',
                                                backgroundColor: stopStudents.length > 0 ? '#FFFFFF' : '#FAFAFA'
                                              }}
                                            >
                                              {/* Stop title bar */}
                                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: stopStudents.length > 0 ? '12px' : 0 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                  <div style={{
                                                    width: '26px',
                                                    height: '26px',
                                                    borderRadius: '50%',
                                                    backgroundColor: isPickup ? '#047857' : '#C2410C',
                                                    color: 'white',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '11px',
                                                    fontWeight: '800'
                                                  }}>
                                                    {stop.sequence_number || sIdx + 1}
                                                  </div>
                                                  <div>
                                                    <strong style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{stop.stop_name}</strong>
                                                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)', marginLeft: '8px' }}>⏰ Scheduled: {stop.scheduled_time}</span>
                                                  </div>
                                                </div>

                                                <span style={{
                                                  fontSize: '11px',
                                                  fontWeight: '700',
                                                  padding: '2px 8px',
                                                  borderRadius: '12px',
                                                  backgroundColor: stopStudents.length > 0 ? '#EFF6FF' : '#F1F5F9',
                                                  color: stopStudents.length > 0 ? '#1D4ED8' : '#94A3B8'
                                                }}>
                                                  {stopStudents.length} student{stopStudents.length === 1 ? '' : 's'} {isPickup ? 'boarding' : 'deboarding'}
                                                </span>
                                              </div>

                                              {/* Students Grid */}
                                              {stopStudents.length > 0 && (
                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '10px' }}>
                                                  {stopStudents.map(student => (
                                                    <div
                                                      key={student.id}
                                                      onClick={() => handleStudentClick(student)}
                                                      style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '10px',
                                                        padding: '8px 12px',
                                                        borderRadius: '8px',
                                                        border: '1px solid #E2E8F0',
                                                        backgroundColor: '#F8FAFC',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.15s ease'
                                                      }}
                                                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#EFF6FF'}
                                                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#F8FAFC'}
                                                    >
                                                      <div style={{ width: '34px', height: '34px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }}>
                                                        <img
                                                          src={getStudentAvatar(student)}
                                                          alt={student.first_name}
                                                          onError={(e) => {
                                                            e.currentTarget.onerror = null;
                                                            e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(student.first_name + ' ' + (student.last_name || ''))}&background=E2E8F0&color=475569&bold=true`;
                                                          }}
                                                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        />
                                                      </div>
                                                      <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                          {student.first_name} {student.last_name}
                                                        </div>
                                                        <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>
                                                          {student.admission_no || ('ADM-00' + student.id)} • 📞 {student.parent_phone || 'N/A'}
                                                        </div>
                                                      </div>
                                                      <span style={{ fontSize: '10px', color: '#3B82F6', fontWeight: 'bold' }}>
                                                        View →
                                                      </span>
                                                    </div>
                                                  ))}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* LAYMAN MENU 2: REPORTS & SCHEDULED ALERTS */}
              {activeTab === 'reports' && (
                <div className="manage-panel">
                  <div style={{ display: 'flex', gap: '8px', borderBottom: '2px solid #E2E8F0', paddingBottom: '12px', marginBottom: '20px' }}>
                    <button 
                      onClick={() => setReportsSubTab('attendance')}
                      style={{
                        padding: '10px 18px',
                        borderRadius: '12px',
                        fontSize: '13px',
                        fontWeight: '700',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        border: 'none',
                        backgroundColor: reportsSubTab === 'attendance' ? 'var(--text-primary)' : 'var(--bg-hover)',
                        color: reportsSubTab === 'attendance' ? 'var(--surface-color)' : 'var(--text-secondary)',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      📊 Attendance Swipe Logs
                    </button>
                    <button 
                      onClick={() => setReportsSubTab('schedule')}
                      style={{
                        padding: '10px 18px',
                        borderRadius: '12px',
                        fontSize: '13px',
                        fontWeight: '700',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        border: 'none',
                        backgroundColor: reportsSubTab === 'schedule' ? 'var(--text-primary)' : 'var(--bg-hover)',
                        color: reportsSubTab === 'schedule' ? 'var(--surface-color)' : 'var(--text-secondary)',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      📧 Automated Alerts & Reports
                    </button>
                  </div>

                  {reportsSubTab === 'attendance' ? (
                    <div>
                      <h3 style={{ fontSize: '15px', color: 'var(--text-secondary)', marginBottom: '6px', textAlign: 'left' }}>RFID Swipe Activity Stream</h3>
                      <table className="manage-table">
                        <thead>
                          <tr>
                            <th>Student ID</th>
                            <th>Student Name</th>
                            <th>Bus ID</th>
                            <th>Event Status</th>
                            <th>Recorded Time</th>
                            <th>Location Captured</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allStudents.map((st, i) => (
                            <tr key={st.id}>
                              <td><strong>ADM-00{st.id}</strong></td>
                              <td><strong>{st.first_name} {st.last_name}</strong></td>
                              <td>Bus 10A</td>
                              <td>
                                <span className={`student-status-badge ${i % 2 === 0 ? 'status-boarded' : 'status-deboarded'}`}>
                                  {i % 2 === 0 ? 'BOARDED' : 'DEBOARDED'}
                                </span>
                              </td>
                              <td>{new Date(Date.now() - i * 15 * 60000).toLocaleTimeString()}</td>
                              <td>12.9913, 77.6483 (Stop #{i+1})</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="settings-card" style={{ maxWidth: '600px' }}>
                      <div className="settings-title">📧 Configure Automated Alerts & Email Reports</div>
                      <form className="settings-form" onSubmit={(e) => {
                        e.preventDefault();
                        localStorage.setItem('alert_email', alertConfigEmail);
                        localStorage.setItem('alert_freq', alertConfigFreq);
                        localStorage.setItem('alert_type', alertConfigType);
                        alert('Automated report schedule saved successfully!');
                      }}>
                        <div className="form-group">
                          <label>Send Reports To (Email Address)</label>
                          <input type="email" className="form-control" value={alertConfigEmail} onChange={(e) => setAlertConfigEmail(e.target.value)} required />
                        </div>
                        <div className="form-group">
                          <label>Schedule Frequency</label>
                          <select className="form-control" value={alertConfigFreq} onChange={(e) => setAlertConfigFreq(e.target.value)}>
                            <option value="daily">Daily at 5:00 PM</option>
                            <option value="weekly">Weekly on Fridays</option>
                            <option value="immediate">Immediate delay alert (&gt;15 mins delay)</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Report Details Include</label>
                          <select className="form-control" value={alertConfigType} onChange={(e) => setAlertConfigType(e.target.value)}>
                            <option value="attendance">Student Swipe & Attendance Summary</option>
                            <option value="delay">Bus Delay & Speed Limit Incidents logs</option>
                            <option value="comprehensive">Comprehensive Fleet Log</option>
                          </select>
                        </div>
                        <button type="submit" className="settings-btn">Save Report Schedule</button>
                      </form>
                    </div>
                  )}
                </div>
              )}

              {/* LAYMAN MENU 3: STUDENTS DIRECTORY */}
              {activeTab === 'students' && (() => {
                // Extract unique dynamic values for dropdown filters
                const uniqueGrades = Array.from(new Set(allStudents.map(s => {
                  const firstName = (s.first_name || '').trim();
                  const p = studentProfilesData[firstName] || 
                            studentProfilesData[firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()] || 
                            Object.entries(studentProfilesData).find(([k]) => k.toLowerCase() === firstName.toLowerCase())?.[1] || {};
                  return s.grade || p.grade;
                }).filter(Boolean))).sort((a, b) => {
                  const numA = parseInt(a.replace(/\D/g, '')) || 0;
                  const numB = parseInt(b.replace(/\D/g, '')) || 0;
                  return numA - numB;
                });

                const uniqueStops = Array.from(new Set(allStudents.flatMap(s => [s.pickup_stop_name, s.dropoff_stop_name]).filter(Boolean))).sort();

                // 1. Filtering & Searching
                const filteredStudents = allStudents.filter(student => {
                  const firstName = (student.first_name || '').trim();
                  const profile = studentProfilesData[firstName] || 
                                  studentProfilesData[firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()] || 
                                  Object.entries(studentProfilesData).find(([k]) => k.toLowerCase() === firstName.toLowerCase())?.[1] || {};

                  const fullName = `${student.first_name || ''} ${student.last_name || ''}`.trim();
                  const admissionNo = profile.admissionNo || ('ADM-00' + student.id);
                  const grade = student.grade || profile.grade || '';
                  const rfid = student.rfid_tag_id || '';
                  const parentName = student.parent_name || profile.fatherName || '';
                  const parentPhone = student.parent_phone || profile.primaryContact || '';
                  const pickupStop = student.pickup_stop_name || '';
                  const dropStop = student.dropoff_stop_name || '';

                  // Search query match
                  if (studentSearchQuery) {
                    const q = studentSearchQuery.toLowerCase().trim();
                    const matchesSearch =
                      fullName.toLowerCase().includes(q) ||
                      admissionNo.toLowerCase().includes(q) ||
                      rfid.toLowerCase().includes(q) ||
                      parentName.toLowerCase().includes(q) ||
                      parentPhone.toLowerCase().includes(q) ||
                      pickupStop.toLowerCase().includes(q) ||
                      dropStop.toLowerCase().includes(q) ||
                      grade.toLowerCase().includes(q);
                    if (!matchesSearch) return false;
                  }

                  // Category pill filter
                  if (studentCategoryFilter === 'bus_transport') {
                    const usesBus = (student.transport_mode || profile.modeOfTransport || 'School Bus') === 'School Bus' && (student.pickup_stop_id || student.drop_stop_id);
                    if (!usesBus) return false;
                  }
                  if (studentCategoryFilter === 'no_transport') {
                    const noBus = (student.transport_mode === 'No Transport') || (profile.modeOfTransport && profile.modeOfTransport !== 'School Bus') || (!student.pickup_stop_id && !student.drop_stop_id);
                    if (!noBus) return false;
                  }
                  if (studentCategoryFilter === 'rfid_assigned' && !rfid) return false;
                  if (studentCategoryFilter === 'no_rfid' && rfid) return false;
                  if (studentCategoryFilter === 'high_school') {
                    const isHigh = grade.includes('9') || grade.includes('10') || grade.includes('11') || grade.includes('12');
                    if (!isHigh) return false;
                  }
                  if (studentCategoryFilter === 'middle_school') {
                    const isMid = grade.includes('6') || grade.includes('7') || grade.includes('8') || grade.includes('Primary') || grade.includes('5');
                    if (!isMid) return false;
                  }

                  // Grade dropdown filter
                  if (studentGradeFilter !== 'all' && grade !== studentGradeFilter) {
                    return false;
                  }

                  // Stop dropdown filter
                  if (studentStopFilter !== 'all' && pickupStop !== studentStopFilter && dropStop !== studentStopFilter) {
                    return false;
                  }

                  // RFID dropdown filter
                  if (studentRfidFilter === 'assigned' && !rfid) return false;
                  if (studentRfidFilter === 'unassigned' && rfid) return false;

                  return true;
                }).sort((a, b) => {
                  const profileA = studentProfilesData[a.first_name] || {};
                  const profileB = studentProfilesData[b.first_name] || {};
                  const nameA = `${a.first_name || ''} ${a.last_name || ''}`.trim().toLowerCase();
                  const nameB = `${b.first_name || ''} ${b.last_name || ''}`.trim().toLowerCase();
                  const admA = profileA.admissionNo || ('ADM-00' + a.id);
                  const admB = profileB.admissionNo || ('ADM-00' + b.id);
                  const grA = a.grade || profileA.grade || '';
                  const grB = b.grade || profileB.grade || '';
                  const stA = a.pickup_stop_name || '';
                  const stB = b.pickup_stop_name || '';

                  if (studentSortBy === 'name_asc') return nameA.localeCompare(nameB);
                  if (studentSortBy === 'name_desc') return nameB.localeCompare(nameA);
                  if (studentSortBy === 'admission_asc') return admA.localeCompare(admB);
                  if (studentSortBy === 'admission_desc') return admB.localeCompare(admA);
                  if (studentSortBy === 'grade_asc') return grA.localeCompare(grB);
                  if (studentSortBy === 'stop_asc') return stA.localeCompare(stB);
                  return 0;
                });

                // 2. Pagination computation
                const totalFilteredStudents = filteredStudents.length;
                const totalStudentPages = Math.max(1, Math.ceil(totalFilteredStudents / studentPageSize));
                const validStudentPage = Math.min(Math.max(1, studentCurrentPage), totalStudentPages);
                const studentStartIndex = (validStudentPage - 1) * studentPageSize;
                const studentEndIndex = Math.min(studentStartIndex + studentPageSize, totalFilteredStudents);
                const paginatedStudents = filteredStudents.slice(studentStartIndex, studentEndIndex);

                const hasActiveFilters = Boolean(
                  studentSearchQuery || 
                  studentCategoryFilter !== 'all' || 
                  studentGradeFilter !== 'all' || 
                  studentStopFilter !== 'all' || 
                  studentRfidFilter !== 'all' || 
                  studentSortBy !== 'name_asc'
                );

                const resetAllStudentFilters = () => {
                  setStudentSearchQuery('');
                  setStudentCategoryFilter('all');
                  setStudentGradeFilter('all');
                  setStudentStopFilter('all');
                  setStudentRfidFilter('all');
                  setStudentSortBy('name_asc');
                  setStudentCurrentPage(1);
                };

                return (
                  <div className="manage-panel">
                    {/* Top Bar: Category Pills & Action Buttons */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center', width: '100%', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {[
                          { id: 'all', label: `All Students (${allStudents.length})` },
                          { id: 'bus_transport', label: `🚌 School Bus (${allStudents.filter(s => (s.transport_mode || 'School Bus') === 'School Bus' && (s.pickup_stop_id || s.drop_stop_id)).length})` },
                          { id: 'no_transport', label: `🚫 No School Transport (${allStudents.filter(s => s.transport_mode === 'No Transport' || (!s.pickup_stop_id && !s.drop_stop_id)).length})` },
                          { id: 'rfid_assigned', label: `RFID Linked (${allStudents.filter(s => !!s.rfid_tag_id).length})` },
                          { id: 'no_rfid', label: `No RFID (${allStudents.filter(s => !s.rfid_tag_id).length})` },
                          { id: 'high_school', label: 'High School (9-12)' },
                          { id: 'middle_school', label: 'Middle & Primary (6-8)' }
                        ].map((cat) => (
                          <button
                            key={cat.id}
                            onClick={() => {
                              setStudentCategoryFilter(cat.id);
                              setStudentCurrentPage(1);
                            }}
                            style={{
                              height: '36px',
                              padding: '0 12px',
                              borderRadius: '10px',
                              fontSize: '12px',
                              fontWeight: '700',
                              backgroundColor: studentCategoryFilter === cat.id ? 'var(--text-primary)' : 'var(--surface-color)',
                              color: studentCategoryFilter === cat.id ? 'var(--surface-color)' : 'var(--text-secondary)',
                              border: '1px solid ' + (studentCategoryFilter === cat.id ? 'var(--text-primary)' : 'var(--border-color)'),
                              cursor: 'pointer',
                              transition: 'all 0.15s ease',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxSizing: 'border-box'
                            }}
                          >
                            {cat.label}
                          </button>
                        ))}
                      </div>

                      <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {/* Grid vs Detail View Mode Switcher */}
                        <div style={{ display: 'flex', backgroundColor: 'var(--bg-hover)', padding: '3px', borderRadius: '10px', border: '1px solid var(--border-color)', height: '36px', boxSizing: 'border-box', alignItems: 'center' }}>
                          <button
                            onClick={() => setStudentViewMode('grid')}
                            title="Grid View"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              height: '28px',
                              padding: '0 12px',
                              borderRadius: '8px',
                              border: 'none',
                              fontSize: '12px',
                              fontWeight: '700',
                              backgroundColor: studentViewMode === 'grid' ? '#FFFFFF' : 'transparent',
                              color: studentViewMode === 'grid' ? '#0F172A' : '#64748B',
                              boxShadow: studentViewMode === 'grid' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                              cursor: 'pointer',
                              boxSizing: 'border-box'
                            }}
                          >
                            <LayoutGrid size={15} />
                            Grid
                          </button>
                          <button
                            onClick={() => setStudentViewMode('detail')}
                            title="Detail / Table View"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              height: '28px',
                              padding: '0 12px',
                              borderRadius: '8px',
                              border: 'none',
                              fontSize: '12px',
                              fontWeight: '700',
                              backgroundColor: studentViewMode === 'detail' ? '#FFFFFF' : 'transparent',
                              color: studentViewMode === 'detail' ? '#0F172A' : '#64748B',
                              boxShadow: studentViewMode === 'detail' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                              cursor: 'pointer',
                              boxSizing: 'border-box'
                            }}
                          >
                            <List size={15} />
                            Detail
                          </button>
                        </div>

                        <button 
                          onClick={() => { clearStudentFormFields(); setShowAddStudentModal(true); }}
                          style={{
                            backgroundColor: '#10B981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '10px',
                            height: '36px',
                            padding: '0 14px',
                            fontSize: '12px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            boxSizing: 'border-box',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          <span>➕</span> Add Student
                        </button>

                        <button 
                          onClick={() => setShowCSVModal(true)}
                          style={{
                            backgroundColor: 'var(--text-primary)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '10px',
                            height: '36px',
                            padding: '0 14px',
                            fontSize: '12px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            boxSizing: 'border-box',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          <span>📥</span> Import CSV
                        </button>
                      </div>
                    </div>

                    {/* Search & Filter Bar */}
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      marginBottom: '10px',
                      padding: '8px 12px',
                      backgroundColor: 'var(--surface-color)',
                      borderRadius: '12px',
                      border: '1px solid var(--border-color)',
                      alignItems: 'center',
                      flexWrap: 'wrap'
                    }}>
                      {/* Search Input */}
                      <div style={{ position: 'relative', flex: '1 1 240px', minWidth: '220px' }}>
                        <Search size={16} color="#94A3B8" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                        <input
                          type="text"
                          placeholder="Search student, admission ID, parent, phone, RFID, stop..."
                          value={studentSearchQuery}
                          onChange={(e) => {
                            setStudentSearchQuery(e.target.value);
                            setStudentCurrentPage(1);
                          }}
                          style={{
                            width: '100%',
                            padding: '4px 28px 4px 32px',
                            borderRadius: '8px',
                            border: '1px solid #CBD5E1',
                            fontSize: '13px',
                            outline: 'none'
                          }}
                        />
                        {studentSearchQuery && (
                          <button
                            onClick={() => {
                              setStudentSearchQuery('');
                              setStudentCurrentPage(1);
                            }}
                            style={{
                              position: 'absolute',
                              right: '10px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#94A3B8',
                              display: 'flex',
                              padding: 0
                            }}
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>

                      {/* Grade / Class Filter */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>Grade:</span>
                        <select
                          value={studentGradeFilter}
                          onChange={(e) => {
                            setStudentGradeFilter(e.target.value);
                            setStudentCurrentPage(1);
                          }}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            border: '1px solid #CBD5E1',
                            fontSize: '11px',
                            backgroundColor: 'var(--bg-alt)',
                            color: 'var(--text-primary)',
                            fontWeight: '600',
                            outline: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="all">All Grades</option>
                          {uniqueGrades.map(g => (
                            <option key={g} value={g}>{g}</option>
                          ))}
                        </select>
                      </div>

                      {/* Stop Filter */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>Stop:</span>
                        <select
                          value={studentStopFilter}
                          onChange={(e) => {
                            setStudentStopFilter(e.target.value);
                            setStudentCurrentPage(1);
                          }}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            border: '1px solid #CBD5E1',
                            fontSize: '11px',
                            backgroundColor: 'var(--bg-alt)',
                            color: 'var(--text-primary)',
                            fontWeight: '600',
                            outline: 'none',
                            cursor: 'pointer',
                            maxWidth: '160px'
                          }}
                        >
                          <option value="all">All Stops</option>
                          {uniqueStops.map(st => (
                            <option key={st} value={st}>{st}</option>
                          ))}
                        </select>
                      </div>

                      {/* RFID Status Filter */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>RFID:</span>
                        <select
                          value={studentRfidFilter}
                          onChange={(e) => {
                            setStudentRfidFilter(e.target.value);
                            setStudentCurrentPage(1);
                          }}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            border: '1px solid #CBD5E1',
                            fontSize: '11px',
                            backgroundColor: 'var(--bg-alt)',
                            color: 'var(--text-primary)',
                            fontWeight: '600',
                            outline: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="all">All RFID</option>
                          <option value="assigned">RFID Linked</option>
                          <option value="unassigned">Missing RFID</option>
                        </select>
                      </div>

                      {/* Sort By */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>Sort:</span>
                        <select
                          value={studentSortBy}
                          onChange={(e) => {
                            setStudentSortBy(e.target.value);
                            setStudentCurrentPage(1);
                          }}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            border: '1px solid #CBD5E1',
                            fontSize: '11px',
                            backgroundColor: 'var(--bg-alt)',
                            color: 'var(--text-primary)',
                            fontWeight: '600',
                            outline: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="name_asc">Name (A–Z)</option>
                          <option value="name_desc">Name (Z–A)</option>
                          <option value="admission_asc">Admission ID (Asc)</option>
                          <option value="admission_desc">Admission ID (Desc)</option>
                          <option value="grade_asc">Grade / Class</option>
                          <option value="stop_asc">Pickup Stop</option>
                        </select>
                      </div>

                      {/* Reset Filters button */}
                      {hasActiveFilters && (
                        <button
                          onClick={resetAllStudentFilters}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '7px 12px',
                            borderRadius: '8px',
                            border: '1px solid #CBD5E1',
                            fontSize: '12px',
                            backgroundColor: 'var(--surface-color)',
                            color: '#EF4444',
                            fontWeight: '700',
                            cursor: 'pointer'
                          }}
                        >
                          <RotateCcw size={13} />
                          Reset
                        </button>
                      )}

                      <div style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>
                        Showing {totalFilteredStudents === 0 ? 0 : studentStartIndex + 1}–{studentEndIndex} of {totalFilteredStudents}
                      </div>
                    </div>

                    {/* Table vs Grid View */}
                    {totalFilteredStudents === 0 ? (
                      <div style={{
                        padding: '48px 24px',
                        textAlign: 'center',
                        backgroundColor: 'var(--surface-color)',
                        borderRadius: '16px',
                        border: '1.5px dashed #E2E8F0',
                        marginTop: '12px'
                      }}>
                        <div style={{ fontSize: '36px', marginBottom: '6px' }}>🔍</div>
                        <h4 style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)', margin: '0 0 6px 0' }}>No Students Found</h4>
                        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 16px 0' }}>
                          No student transit profiles match your current search terms or filter criteria.
                        </p>
                        {hasActiveFilters && (
                          <button
                            onClick={resetAllStudentFilters}
                            style={{
                              padding: '8px 16px',
                              borderRadius: '8px',
                              border: 'none',
                              backgroundcolor: 'var(--text-primary)',
                              color: '#FFFFFF',
                              fontSize: '13px',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            Clear All Filters
                          </button>
                        )}
                      </div>
                    ) : studentViewMode === 'grid' ? (
                      /* Grid View */
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '12px',
                        marginTop: '12px'
                      }}>
                        {paginatedStudents.map(student => {
                          const firstName = (student.first_name || '').trim();
                          const profile = studentProfilesData[firstName] || 
                                          studentProfilesData[firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()] || 
                                          Object.entries(studentProfilesData).find(([k]) => k.toLowerCase() === firstName.toLowerCase())?.[1] || {};
                          const avatarUrl = getStudentAvatar(student);
                          const admissionNo = profile.admissionNo || ('ADM-00' + student.id);
                          const gradeDisplay = student.grade || profile.grade || 'N/A';
                          const sectionDisplay = student.section || profile.section || '';
                          const linkedParent = getLinkedParentForStudent(student);
                          const parentDisplayName = linkedParent ? (linkedParent.display_name || linkedParent.name || student.parent_name || 'Parent') : (student.parent_name || profile.fatherName || 'N/A');
                          const parentAvatar = linkedParent ? getParentAvatar(linkedParent) : `https://ui-avatars.com/api/?name=${encodeURIComponent(parentDisplayName)}&background=E0E7FF&color=4338CA&bold=true`;
                          const parentProfile = parentProfilesData[parentDisplayName] || Object.entries(parentProfilesData).find(([k]) => k.toLowerCase() === (parentDisplayName || '').toLowerCase())?.[1] || {};
                          const parentRelationship = parentProfile.relationship || 'Guardian';

                          return (
                            <div
                              key={student.id}
                              onClick={() => handleStudentClick(student)}
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                backgroundColor: 'var(--surface-color)',
                                borderRadius: '12px',
                                border: '1.5px solid var(--border-color)',
                                padding: '14px',
                                position: 'relative',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.03)'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#CBD5E1';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.06)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = '#E2E8F0';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.03)';
                              }}
                            >
                              {/* Header with Avatar & Basic Info */}
                              <div style={{ display: 'flex', gap: '14px', alignItems: 'center', marginBottom: '8px' }}>
                                <div style={{
                                  width: '44px',
                                  height: '44px',
                                  borderRadius: '50%',
                                  overflow: 'hidden',
                                  flexShrink: 0,
                                  border: '2px solid #FFFFFF',
                                  boxShadow: '0 3px 10px rgba(0,0,0,0.1)',
                                  background: '#F1F5F9'
                                }}>
                                  <img
                                    src={avatarUrl}
                                    alt={`${student.first_name} ${student.last_name}`}
                                    onError={(e) => {
                                      e.currentTarget.onerror = null;
                                      e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(student.first_name + ' ' + (student.last_name || ''))}&background=E2E8F0&color=475569&bold=true`;
                                    }}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                  />
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, textAlign: 'left' }}>
                                  <span style={{
                                    fontWeight: '800',
                                    fontSize: '15px',
                                    color: 'var(--text-primary)',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}>
                                    {student.first_name} {student.last_name}
                                  </span>
                                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '4px', flexWrap: 'wrap' }}>
                                    <span style={{
                                      fontSize: '11px',
                                      fontWeight: '700',
                                      color: '#3B82F6',
                                      backgroundColor: '#EFF6FF',
                                      padding: '2px 6px',
                                      borderRadius: '4px'
                                    }}>
                                      {admissionNo}
                                    </span>
                                    <span style={{
                                      fontSize: '11px',
                                      fontWeight: '600',
                                      color: 'var(--text-secondary)',
                                      backgroundColor: 'var(--bg-hover)',
                                      padding: '2px 6px',
                                      borderRadius: '4px'
                                    }}>
                                      {gradeDisplay} {sectionDisplay ? `(${sectionDisplay})` : ''}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Details Box */}
                              <div style={{
                                backgroundColor: 'var(--bg-alt)',
                                borderRadius: '10px',
                                padding: '4px 6px',
                                fontSize: '10px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '2px',
                                marginBottom: '4px',
                                textAlign: 'left'
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ color: 'var(--text-secondary)', fontWeight: '500' }}>RFID Tag:</span>
                                  {student.rfid_tag_id ? (
                                    <span style={{
                                      padding: '2px 6px',
                                      borderRadius: '4px',
                                      backgroundColor: '#DCFCE7',
                                      color: '#15803D',
                                      fontSize: '11px',
                                      fontWeight: '700'
                                    }}>
                                      {student.rfid_tag_id}
                                    </span>
                                  ) : (
                                    <span style={{
                                      padding: '2px 6px',
                                      borderRadius: '4px',
                                      backgroundColor: '#FEE2E2',
                                      color: '#DC2626',
                                      fontSize: '11px',
                                      fontWeight: '700'
                                    }}>
                                      Unassigned
                                    </span>
                                  )}
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ color: 'var(--text-secondary)', fontWeight: '500' }}>Linked Parent:</span>
                                  {linkedParent ? (
                                    <div
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleParentClick(linkedParent);
                                      }}
                                      title="Click to view parent profile"
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        backgroundColor: 'var(--surface-color)',
                                        border: '1px solid #CBD5E1',
                                        borderRadius: '16px',
                                        padding: '2px 8px 2px 3px',
                                        fontSize: '11px',
                                        fontWeight: '700',
                                        color: 'var(--text-primary)',
                                        cursor: 'pointer',
                                        maxWidth: '180px',
                                        transition: 'all 0.15s ease'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.borderColor = '#4F46E5';
                                        e.currentTarget.style.backgroundColor = '#EEF2FF';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.borderColor = '#CBD5E1';
                                        e.currentTarget.style.backgroundColor = '#FFFFFF';
                                      }}
                                    >
                                      <div style={{ width: '18px', height: '18px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }}>
                                        <img 
                                          src={parentAvatar} 
                                          alt={parentDisplayName} 
                                          onError={(e) => {
                                            e.currentTarget.onerror = null;
                                            e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(parentDisplayName)}&background=E0E7FF&color=4338CA&bold=true`;
                                          }}
                                          style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                                        />
                                      </div>
                                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {parentDisplayName}
                                      </span>
                                      {parentRelationship && (
                                        <span style={{ color: 'var(--text-secondary)', fontWeight: '500', fontSize: '10px' }}>({parentRelationship})</span>
                                      )}
                                    </div>
                                  ) : (
                                    <span style={{ color: 'var(--text-primary)', fontWeight: '600', maxWidth: '170px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {student.parent_name || profile.fatherName || 'N/A'}
                                    </span>
                                  )}
                                </div>

                                {student.transport_mode === 'No Transport' || (!student.pickup_stop_id && !student.drop_stop_id) ? (
                                  <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '6px', marginTop: '2px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#B91C1C', backgroundColor: '#FEF2F2', padding: '4px 8px', borderRadius: '6px', fontWeight: 'bold' }}>
                                      <span>🚫</span>
                                      <span>No School Transport (Private/Self)</span>
                                    </div>
                                  </div>
                                ) : (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderTop: '1px solid var(--border-color)', paddingTop: '6px', marginTop: '2px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                                      <span style={{ color: '#10B981', fontSize: '10px' }}>●</span>
                                      <strong style={{ color: 'var(--text-secondary)' }}>Pickup:</strong>
                                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {student.pickup_stop_name || 'N/A'}
                                      </span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                                      <span style={{ color: '#EF4444', fontSize: '10px' }}>●</span>
                                      <strong style={{ color: 'var(--text-secondary)' }}>Drop:</strong>
                                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {student.dropoff_stop_name || 'N/A'}
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Card Footer Button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStudentClick(student);
                                }}
                                style={{
                                  marginTop: 'auto',
                                  width: '100%',
                                  padding: '8px',
                                  borderRadius: '8px',
                                  border: '1px solid var(--border-color)',
                                  backgroundColor: 'var(--surface-color)',
                                  color: 'var(--text-primary)',
                                  fontSize: '12px',
                                  fontWeight: '700',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '6px'
                                }}
                              >
                                👤 View Full Profile
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      /* Detail / Table View */
                      <div style={{ overflowX: 'auto', backgroundColor: 'var(--surface-color)', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
                        <table className="manage-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th>Admission ID</th>
                              <th>Student Photo</th>
                              <th>Full Name</th>
                              <th>Grade & Section</th>
                              <th>Transport</th>
                              <th>RFID Tag ID</th>
                              <th>Linked Parent / Guardian</th>
                              <th>Parent Phone</th>
                              <th>Pickup Stop</th>
                              <th>Dropoff Stop</th>
                              <th style={{ textAlign: 'right' }}>Profile</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedStudents.map(student => {
                              const firstName = (student.first_name || '').trim();
                              const profile = studentProfilesData[firstName] || 
                                              studentProfilesData[firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()] || 
                                              Object.entries(studentProfilesData).find(([k]) => k.toLowerCase() === firstName.toLowerCase())?.[1] || {};
                              const avatarUrl = getStudentAvatar(student);
                              const admissionNo = profile.admissionNo || ('ADM-00' + student.id);
                              const gradeDisplay = student.grade || profile.grade || 'N/A';
                              const sectionDisplay = student.section || profile.section || '';
                              const linkedParent = getLinkedParentForStudent(student);
                              const parentDisplayName = linkedParent ? (linkedParent.display_name || linkedParent.name || student.parent_name || 'Parent') : (student.parent_name || profile.fatherName || 'N/A');
                              const parentAvatar = linkedParent ? getParentAvatar(linkedParent) : `https://ui-avatars.com/api/?name=${encodeURIComponent(parentDisplayName)}&background=E0E7FF&color=4338CA&bold=true`;
                              const parentProfile = parentProfilesData[parentDisplayName] || Object.entries(parentProfilesData).find(([k]) => k.toLowerCase() === (parentDisplayName || '').toLowerCase())?.[1] || {};
                              const parentRelationship = parentProfile.relationship || 'Guardian';

                              return (
                                <tr 
                                  key={student.id} 
                                  onClick={() => handleStudentClick(student)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <td>
                                    <span style={{ fontWeight: '700', fontSize: '13px', color: 'var(--text-primary)' }}>
                                      {admissionNo}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="avatar-circle-sm">
                                      <img 
                                        src={avatarUrl} 
                                        alt={`${student.first_name} ${student.last_name}`} 
                                        onError={(e) => {
                                          e.currentTarget.onerror = null;
                                          e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(student.first_name + ' ' + (student.last_name || ''))}&background=E2E8F0&color=475569&bold=true`;
                                        }}
                                      />
                                    </div>
                                  </td>
                                  <td>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                      <strong style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
                                        {student.first_name} {student.last_name}
                                      </strong>
                                      {profile.house && (
                                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{profile.house}</span>
                                      )}
                                    </div>
                                  </td>
                                  <td>
                                    <span style={{
                                      display: 'inline-block',
                                      padding: '2px 8px',
                                      borderRadius: '6px',
                                      backgroundColor: 'var(--bg-hover)',
                                      color: 'var(--text-secondary)',
                                      fontSize: '11px',
                                      fontWeight: '700'
                                    }}>
                                      {gradeDisplay} {sectionDisplay ? `(${sectionDisplay})` : ''}
                                    </span>
                                  </td>
                                  <td>
                                    {(student.transport_mode === 'No Transport' || (!student.pickup_stop_id && !student.drop_stop_id)) ? (
                                      <span style={{
                                        display: 'inline-block',
                                        padding: '2px 8px',
                                        borderRadius: '6px',
                                        backgroundColor: '#FEF2F2',
                                        color: '#B91C1C',
                                        fontSize: '11px',
                                        fontWeight: '700'
                                      }}>
                                        🚫 No Bus
                                      </span>
                                    ) : (
                                      <span style={{
                                        display: 'inline-block',
                                        padding: '2px 8px',
                                        borderRadius: '6px',
                                        backgroundColor: '#EFF6FF',
                                        color: '#1D4ED8',
                                        fontSize: '11px',
                                        fontWeight: '700'
                                      }}>
                                        🚌 School Bus
                                      </span>
                                    )}
                                  </td>
                                  <td>
                                    {student.rfid_tag_id ? (
                                      <span style={{
                                        display: 'inline-block',
                                        padding: '2px 8px',
                                        borderRadius: '6px',
                                        backgroundColor: '#DCFCE7',
                                        color: '#15803D',
                                        fontSize: '11px',
                                        fontWeight: '700'
                                      }}>
                                        {student.rfid_tag_id}
                                      </span>
                                    ) : (
                                      <span style={{
                                        display: 'inline-block',
                                        padding: '2px 8px',
                                        borderRadius: '6px',
                                        backgroundColor: '#FEE2E2',
                                        color: '#DC2626',
                                        fontSize: '11px',
                                        fontWeight: '700'
                                      }}>
                                        Unassigned
                                      </span>
                                    )}
                                  </td>
                                  <td>
                                    {linkedParent ? (
                                      <div
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleParentClick(linkedParent);
                                        }}
                                        title="Click to view parent profile"
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '6px',
                                          backgroundColor: '#EEF2FF',
                                          border: '1px solid #C7D2FE',
                                          borderRadius: '16px',
                                          padding: '2px 8px 2px 3px',
                                          fontSize: '12px',
                                          fontWeight: '700',
                                          color: '#4338CA',
                                          cursor: 'pointer',
                                          transition: 'all 0.15s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = '#E0E7FF';
                                          e.currentTarget.style.borderColor = '#A5B4FC';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = '#EEF2FF';
                                          e.currentTarget.style.borderColor = '#C7D2FE';
                                        }}
                                      >
                                        <div style={{ width: '18px', height: '18px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }}>
                                          <img 
                                            src={parentAvatar} 
                                            alt={parentDisplayName} 
                                            onError={(e) => {
                                              e.currentTarget.onerror = null;
                                              e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(parentDisplayName)}&background=E0E7FF&color=4338CA&bold=true`;
                                            }}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                                          />
                                        </div>
                                        <span>{parentDisplayName}</span>
                                        {parentRelationship && (
                                          <span style={{ color: '#6366F1', fontWeight: '600', fontSize: '10px' }}>• {parentRelationship}</span>
                                        )}
                                      </div>
                                    ) : (
                                      <span style={{ color: 'var(--text-secondary)' }}>{student.parent_name || profile.fatherName || 'N/A'}</span>
                                    )}
                                  </td>
                                  <td>{student.parent_phone || profile.primaryContact || 'N/A'}</td>
                                  <td>
                                    {student.transport_mode === 'No Transport' || (!student.pickup_stop_id && !student.drop_stop_id) ? (
                                      <span style={{ color: '#94A3B8', fontStyle: 'italic', fontSize: '11px' }}>Independent / Self</span>
                                    ) : (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <span style={{ color: '#10B981', fontSize: '10px' }}>●</span>
                                        <span>{student.pickup_stop_name || 'N/A'}</span>
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    {student.transport_mode === 'No Transport' || (!student.pickup_stop_id && !student.drop_stop_id) ? (
                                      <span style={{ color: '#94A3B8', fontStyle: 'italic', fontSize: '11px' }}>Independent / Self</span>
                                    ) : (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <span style={{ color: '#EF4444', fontSize: '10px' }}>●</span>
                                        <span>{student.dropoff_stop_name || 'N/A'}</span>
                                      </div>
                                    )}
                                  </td>
                                  <td style={{ textAlign: 'right' }}>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleStudentClick(student);
                                      }}
                                      style={{
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        fontSize: '10px',
                                        fontWeight: '700',
                                        backgroundColor: 'var(--bg-hover)',
                                        color: 'var(--text-primary)',
                                        border: '1px solid var(--border-color)',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      View
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Pagination Controls Bar */}
                    {totalFilteredStudents > 0 && (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '10px',
                        padding: '8px 12px',
                        backgroundColor: 'var(--surface-color)',
                        borderRadius: '12px',
                        border: '1px solid var(--border-color)',
                        flexWrap: 'wrap',
                        gap: '12px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: '500' }}>
                            Showing <strong>{studentStartIndex + 1}</strong> to <strong>{studentEndIndex}</strong> of <strong>{totalFilteredStudents}</strong> entries
                          </span>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Per page:</span>
                            <select
                              value={studentPageSize}
                              onChange={(e) => {
                                setStudentPageSize(Number(e.target.value));
                                setStudentCurrentPage(1);
                              }}
                              style={{
                                padding: '4px 8px',
                                borderRadius: '6px',
                                border: '1px solid #CBD5E1',
                                fontSize: '12px',
                                backgroundColor: 'var(--bg-alt)',
                                color: 'var(--text-primary)',
                                fontWeight: '600',
                                cursor: 'pointer'
                              }}
                            >
                              <option value={5}>5</option>
                              <option value={10}>10</option>
                              <option value={20}>20</option>
                              <option value={50}>50</option>
                            </select>
                          </div>
                        </div>

                        {/* Page Navigation Controls */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <button
                            onClick={() => setStudentCurrentPage(1)}
                            disabled={validStudentPage <= 1}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid var(--border-color)',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: validStudentPage <= 1 ? '#F8FAFC' : '#FFFFFF',
                              color: validStudentPage <= 1 ? '#CBD5E1' : '#0F172A',
                              cursor: validStudentPage <= 1 ? 'not-allowed' : 'pointer'
                            }}
                          >
                            First
                          </button>
                          <button
                            onClick={() => setStudentCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={validStudentPage <= 1}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid var(--border-color)',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: validStudentPage <= 1 ? '#F8FAFC' : '#FFFFFF',
                              color: validStudentPage <= 1 ? '#CBD5E1' : '#0F172A',
                              cursor: validStudentPage <= 1 ? 'not-allowed' : 'pointer'
                            }}
                          >
                            <ChevronLeft size={14} />
                            Prev
                          </button>

                          {/* Page Number Pills */}
                          {Array.from({ length: totalStudentPages }, (_, i) => i + 1)
                            .filter(p => p === 1 || p === totalStudentPages || Math.abs(p - validStudentPage) <= 1)
                            .reduce((acc, p, idx, arr) => {
                              if (idx > 0 && p - arr[idx - 1] > 1) {
                                acc.push('...');
                              }
                              acc.push(p);
                              return acc;
                            }, [])
                            .map((p, idx) => (
                              p === '...' ? (
                                <span key={`ellipsis-${idx}`} style={{ padding: '0 4px', color: '#94A3B8' }}>...</span>
                              ) : (
                                <button
                                  key={`page-${p}`}
                                  onClick={() => setStudentCurrentPage(p)}
                                  style={{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '4px',
                                    border: validStudentPage === p ? 'none' : '1px solid #E2E8F0',
                                    fontSize: '12px',
                                    fontWeight: '700',
                                    backgroundColor: validStudentPage === p ? 'var(--text-primary)' : 'var(--surface-color)',
                                    color: validStudentPage === p ? '#FFFFFF' : '#64748B',
                                    cursor: 'pointer'
                                  }}
                                >
                                  {p}
                                </button>
                              )
                            ))}

                          <button
                            onClick={() => setStudentCurrentPage(prev => Math.min(totalStudentPages, prev + 1))}
                            disabled={validStudentPage >= totalStudentPages}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid var(--border-color)',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: validStudentPage >= totalStudentPages ? '#F8FAFC' : '#FFFFFF',
                              color: validStudentPage >= totalStudentPages ? '#CBD5E1' : '#0F172A',
                              cursor: validStudentPage >= totalStudentPages ? 'not-allowed' : 'pointer'
                            }}
                          >
                            Next
                            <ChevronRight size={14} />
                          </button>
                          <button
                            onClick={() => setStudentCurrentPage(totalStudentPages)}
                            disabled={validStudentPage >= totalStudentPages}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid var(--border-color)',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: validStudentPage >= totalStudentPages ? '#F8FAFC' : '#FFFFFF',
                              color: validStudentPage >= totalStudentPages ? '#CBD5E1' : '#0F172A',
                              cursor: validStudentPage >= totalStudentPages ? 'not-allowed' : 'pointer'
                            }}
                          >
                            Last
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* LAYMAN MENU 4: PARENTS DIRECTORY */}
              {activeTab === 'parents' && (() => {
                const allParents = allUsers.filter(u => u.role === 'parent' || u.role === 'PARENT');

                // Precompute linked wards and profiles for each parent
                const parentsWithWards = allParents.map(parent => {
                  const parentName = (parent.display_name || parent.name || '').trim();
                  const profile = parentProfilesData[parentName] || 
                                  Object.entries(parentProfilesData).find(([k]) => k.toLowerCase() === parentName.toLowerCase())?.[1] || {};
                  const linkedWards = allStudents.filter(s => 
                    s.parent_id === parent.id || 
                    (s.parent_name && s.parent_name.toLowerCase() === parentName.toLowerCase()) ||
                    (s.parent_phone && parent.phone && s.parent_phone.replace(/\D/g, '') === parent.phone.replace(/\D/g, ''))
                  );
                  const avatar = getParentAvatar(parent);
                  const parentIdDisplay = profile.parentId || (`#PRT-00${parent.id}`);
                  const email = parent.email || profile.email || `${parent.username || parentName.toLowerCase().replace(/\s+/g, '.')}@school.com`;
                  const phone = parent.phone || profile.phone || 'N/A';
                  const occupation = profile.occupation || 'Parent / Guardian';
                  const relationship = profile.relationship || 'Guardian';
                  const address = profile.address || 'Banaswadi, Bangalore - 560043';

                  return {
                    ...parent,
                    parentName,
                    profile,
                    linkedWards,
                    avatar,
                    parentIdDisplay,
                    email,
                    phone,
                    occupation,
                    relationship,
                    address
                  };
                });

                // Extract unique dynamic values for dropdown filters
                const uniqueParentGrades = Array.from(new Set(
                  parentsWithWards.flatMap(p => p.linkedWards.map(w => {
                    const firstName = (w.first_name || '').trim();
                    const sProfile = studentProfilesData[firstName] || 
                                    studentProfilesData[firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()] || 
                                    Object.entries(studentProfilesData).find(([k]) => k.toLowerCase() === firstName.toLowerCase())?.[1] || {};
                    return w.grade || sProfile.grade;
                  })).filter(Boolean)
                )).sort((a, b) => {
                  const numA = parseInt(a.replace(/\D/g, '')) || 0;
                  const numB = parseInt(b.replace(/\D/g, '')) || 0;
                  return numA - numB;
                });

                const uniqueParentStops = Array.from(new Set(
                  parentsWithWards.flatMap(p => p.linkedWards.flatMap(w => [w.pickup_stop_name, w.dropoff_stop_name])).filter(Boolean)
                )).sort();

                // 1. Filtering & Searching
                const filteredParents = parentsWithWards.filter(parent => {
                  const { parentName, parentIdDisplay, email, phone, occupation, relationship, address, linkedWards } = parent;

                  // Search query match
                  if (parentSearchQuery) {
                    const q = parentSearchQuery.toLowerCase().trim();
                    const matchesParentInfo = 
                      parentName.toLowerCase().includes(q) ||
                      parentIdDisplay.toLowerCase().includes(q) ||
                      email.toLowerCase().includes(q) ||
                      phone.toLowerCase().includes(q) ||
                      occupation.toLowerCase().includes(q) ||
                      relationship.toLowerCase().includes(q) ||
                      address.toLowerCase().includes(q);

                    const matchesWards = linkedWards.some(w => {
                      const wName = `${w.first_name || ''} ${w.last_name || ''}`.toLowerCase();
                      const wGrade = (w.grade || '').toLowerCase();
                      const wRfid = (w.rfid_tag_id || '').toLowerCase();
                      const wPickup = (w.pickup_stop_name || '').toLowerCase();
                      const wDrop = (w.dropoff_stop_name || '').toLowerCase();
                      return wName.includes(q) || wGrade.includes(q) || wRfid.includes(q) || wPickup.includes(q) || wDrop.includes(q);
                    });

                    if (!matchesParentInfo && !matchesWards) return false;
                  }

                  // Category pill filter
                  if (parentCategoryFilter === 'multiple_wards' && linkedWards.length <= 1) return false;
                  if (parentCategoryFilter === 'single_ward' && linkedWards.length !== 1) return false;
                  if (parentCategoryFilter === 'active_alerts' && parent.profile.notificationStatus === 'INACTIVE') return false;
                  if (parentCategoryFilter === 'high_school') {
                    const hasHighSchool = linkedWards.some(w => {
                      const gr = w.grade || (studentProfilesData[w.first_name] || {}).grade || '';
                      return gr.includes('9') || gr.includes('10') || gr.includes('11') || gr.includes('12');
                    });
                    if (!hasHighSchool) return false;
                  }
                  if (parentCategoryFilter === 'middle_school') {
                    const hasMidSchool = linkedWards.some(w => {
                      const gr = w.grade || (studentProfilesData[w.first_name] || {}).grade || '';
                      return gr.includes('6') || gr.includes('7') || gr.includes('8') || gr.includes('Primary') || gr.includes('5');
                    });
                    if (!hasMidSchool) return false;
                  }

                  // Grade dropdown filter
                  if (parentGradeFilter !== 'all') {
                    const hasGrade = linkedWards.some(w => {
                      const gr = w.grade || (studentProfilesData[w.first_name] || {}).grade || '';
                      return gr === parentGradeFilter;
                    });
                    if (!hasGrade) return false;
                  }

                  // Stop dropdown filter
                  if (parentStopFilter !== 'all') {
                    const hasStop = linkedWards.some(w => w.pickup_stop_name === parentStopFilter || w.dropoff_stop_name === parentStopFilter);
                    if (!hasStop) return false;
                  }

                  // Wards count filter
                  if (parentWardsFilter === 'single' && linkedWards.length !== 1) return false;
                  if (parentWardsFilter === 'multiple' && linkedWards.length < 2) return false;
                  if (parentWardsFilter === 'none' && linkedWards.length > 0) return false;

                  return true;
                }).sort((a, b) => {
                  if (parentSortBy === 'name_asc') return a.parentName.localeCompare(b.parentName);
                  if (parentSortBy === 'name_desc') return b.parentName.localeCompare(a.parentName);
                  if (parentSortBy === 'id_asc') return a.id - b.id;
                  if (parentSortBy === 'id_desc') return b.id - a.id;
                  if (parentSortBy === 'wards_desc') return b.linkedWards.length - a.linkedWards.length;
                  if (parentSortBy === 'phone_asc') return a.phone.localeCompare(b.phone);
                  return 0;
                });

                // 2. Pagination computation
                const totalFilteredParents = filteredParents.length;
                const totalParentPages = Math.max(1, Math.ceil(totalFilteredParents / parentPageSize));
                const validParentPage = Math.min(Math.max(1, parentCurrentPage), totalParentPages);
                const parentStartIndex = (validParentPage - 1) * parentPageSize;
                const parentEndIndex = Math.min(parentStartIndex + parentPageSize, totalFilteredParents);
                const paginatedParents = filteredParents.slice(parentStartIndex, parentEndIndex);

                const hasActiveFilters = Boolean(
                  parentSearchQuery ||
                  parentCategoryFilter !== 'all' ||
                  parentGradeFilter !== 'all' ||
                  parentStopFilter !== 'all' ||
                  parentWardsFilter !== 'all' ||
                  parentSortBy !== 'name_asc'
                );

                const resetAllParentFilters = () => {
                  setParentSearchQuery('');
                  setParentCategoryFilter('all');
                  setParentGradeFilter('all');
                  setParentStopFilter('all');
                  setParentWardsFilter('all');
                  setParentSortBy('name_asc');
                  setParentCurrentPage(1);
                };

                return (
                  <div className="manage-panel">
                    {/* Top Bar: Category Pills & View Mode Switcher */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center', width: '100%', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {[
                          { id: 'all', label: `All Parents (${parentsWithWards.length})` },
                          { id: 'multiple_wards', label: `Multiple Wards (${parentsWithWards.filter(p => p.linkedWards.length > 1).length})` },
                          { id: 'single_ward', label: `Single Ward (${parentsWithWards.filter(p => p.linkedWards.length === 1).length})` },
                          { id: 'active_alerts', label: `Active Alerts (${parentsWithWards.length})` },
                          { id: 'high_school', label: 'High School Wards' },
                          { id: 'middle_school', label: 'Middle & Primary Wards' }
                        ].map((cat) => (
                          <button
                            key={cat.id}
                            onClick={() => {
                              setParentCategoryFilter(cat.id);
                              setParentCurrentPage(1);
                            }}
                            style={{
                              height: '36px',
                              padding: '0 12px',
                              borderRadius: '10px',
                              fontSize: '12px',
                              fontWeight: '700',
                              backgroundColor: parentCategoryFilter === cat.id ? 'var(--text-primary)' : 'var(--surface-color)',
                              color: parentCategoryFilter === cat.id ? 'var(--surface-color)' : 'var(--text-secondary)',
                              border: '1px solid ' + (parentCategoryFilter === cat.id ? 'var(--text-primary)' : 'var(--border-color)'),
                              cursor: 'pointer',
                              transition: 'all 0.15s ease',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxSizing: 'border-box'
                            }}
                          >
                            {cat.label}
                          </button>
                        ))}
                      </div>

                      <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {/* Grid vs Detail View Mode Switcher */}
                        <div style={{ display: 'flex', backgroundColor: 'var(--bg-hover)', padding: '3px', borderRadius: '10px', border: '1px solid var(--border-color)', height: '36px', boxSizing: 'border-box', alignItems: 'center' }}>
                          <button
                            onClick={() => setParentViewMode('grid')}
                            title="Grid View"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              height: '28px',
                              padding: '0 12px',
                              borderRadius: '8px',
                              border: 'none',
                              fontSize: '12px',
                              fontWeight: '700',
                              backgroundColor: parentViewMode === 'grid' ? '#FFFFFF' : 'transparent',
                              color: parentViewMode === 'grid' ? '#0F172A' : '#64748B',
                              boxShadow: parentViewMode === 'grid' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                              cursor: 'pointer',
                              boxSizing: 'border-box'
                            }}
                          >
                            <LayoutGrid size={15} />
                            Grid
                          </button>
                          <button
                            onClick={() => setParentViewMode('detail')}
                            title="Detail / Table View"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              height: '28px',
                              padding: '0 12px',
                              borderRadius: '8px',
                              border: 'none',
                              fontSize: '12px',
                              fontWeight: '700',
                              backgroundColor: parentViewMode === 'detail' ? '#FFFFFF' : 'transparent',
                              color: parentViewMode === 'detail' ? '#0F172A' : '#64748B',
                              boxShadow: parentViewMode === 'detail' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                              cursor: 'pointer',
                              boxSizing: 'border-box'
                            }}
                          >
                            <List size={15} />
                            Detail
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Search & Filter Bar */}
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      marginBottom: '10px',
                      padding: '8px 12px',
                      backgroundColor: 'var(--surface-color)',
                      borderRadius: '12px',
                      border: '1px solid var(--border-color)',
                      alignItems: 'center',
                      flexWrap: 'wrap'
                    }}>
                      {/* Search Input */}
                      <div style={{ position: 'relative', flex: '1 1 240px', minWidth: '220px' }}>
                        <Search size={16} color="#94A3B8" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                        <input
                          type="text"
                          placeholder="Search parent, ID, email, phone, ward name, grade, stop..."
                          value={parentSearchQuery}
                          onChange={(e) => {
                            setParentSearchQuery(e.target.value);
                            setParentCurrentPage(1);
                          }}
                          style={{
                            width: '100%',
                            padding: '4px 28px 4px 32px',
                            borderRadius: '8px',
                            border: '1px solid #CBD5E1',
                            fontSize: '13px',
                            outline: 'none'
                          }}
                        />
                        {parentSearchQuery && (
                          <button
                            onClick={() => {
                              setParentSearchQuery('');
                              setParentCurrentPage(1);
                            }}
                            style={{
                              position: 'absolute',
                              right: '10px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#94A3B8',
                              display: 'flex',
                              padding: 0
                            }}
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>

                      {/* Wards Grade Filter */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>Ward Grade:</span>
                        <select
                          value={parentGradeFilter}
                          onChange={(e) => {
                            setParentGradeFilter(e.target.value);
                            setParentCurrentPage(1);
                          }}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            border: '1px solid #CBD5E1',
                            fontSize: '11px',
                            backgroundColor: 'var(--bg-alt)',
                            color: 'var(--text-primary)',
                            fontWeight: '600',
                            outline: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="all">All Grades</option>
                          {uniqueParentGrades.map(g => (
                            <option key={g} value={g}>{g}</option>
                          ))}
                        </select>
                      </div>

                      {/* Wards Stop Filter */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>Stop:</span>
                        <select
                          value={parentStopFilter}
                          onChange={(e) => {
                            setParentStopFilter(e.target.value);
                            setParentCurrentPage(1);
                          }}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            border: '1px solid #CBD5E1',
                            fontSize: '11px',
                            backgroundColor: 'var(--bg-alt)',
                            color: 'var(--text-primary)',
                            fontWeight: '600',
                            outline: 'none',
                            cursor: 'pointer',
                            maxWidth: '160px'
                          }}
                        >
                          <option value="all">All Stops</option>
                          {uniqueParentStops.map(st => (
                            <option key={st} value={st}>{st}</option>
                          ))}
                        </select>
                      </div>

                      {/* Wards Count Filter */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>Wards:</span>
                        <select
                          value={parentWardsFilter}
                          onChange={(e) => {
                            setParentWardsFilter(e.target.value);
                            setParentCurrentPage(1);
                          }}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            border: '1px solid #CBD5E1',
                            fontSize: '11px',
                            backgroundColor: 'var(--bg-alt)',
                            color: 'var(--text-primary)',
                            fontWeight: '600',
                            outline: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="all">All Counts</option>
                          <option value="single">Single Ward (1)</option>
                          <option value="multiple">Multiple Wards (2+)</option>
                        </select>
                      </div>

                      {/* Sort By */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>Sort:</span>
                        <select
                          value={parentSortBy}
                          onChange={(e) => {
                            setParentSortBy(e.target.value);
                            setParentCurrentPage(1);
                          }}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            border: '1px solid #CBD5E1',
                            fontSize: '11px',
                            backgroundColor: 'var(--bg-alt)',
                            color: 'var(--text-primary)',
                            fontWeight: '600',
                            outline: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="name_asc">Name (A–Z)</option>
                          <option value="name_desc">Name (Z–A)</option>
                          <option value="id_asc">Parent ID (Asc)</option>
                          <option value="id_desc">Parent ID (Desc)</option>
                          <option value="wards_desc">Most Wards First</option>
                          <option value="phone_asc">Phone Number</option>
                        </select>
                      </div>

                      {/* Reset Filters button */}
                      {hasActiveFilters && (
                        <button
                          onClick={resetAllParentFilters}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '7px 12px',
                            borderRadius: '8px',
                            border: '1px solid #CBD5E1',
                            fontSize: '12px',
                            backgroundColor: 'var(--surface-color)',
                            color: '#EF4444',
                            fontWeight: '700',
                            cursor: 'pointer'
                          }}
                        >
                          <RotateCcw size={13} />
                          Reset
                        </button>
                      )}

                      <button
                        onClick={() => { clearParentFormFields(); setShowAddParentModal(true); }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '8px 14px',
                          backgroundColor: '#22C55E',
                          color: '#FFFFFF',
                          borderRadius: '10px',
                          border: 'none',
                          fontSize: '12px',
                          fontWeight: '700',
                          cursor: 'pointer',
                          boxShadow: '0 2px 5px rgba(34, 197, 94, 0.2)'
                        }}
                      >
                        <span>➕</span> Add Parent
                      </button>

                      <div style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>
                        Showing {totalFilteredParents === 0 ? 0 : parentStartIndex + 1}–{parentEndIndex} of {totalFilteredParents}
                      </div>
                    </div>

                    {/* Table vs Grid View */}
                    {totalFilteredParents === 0 ? (
                      <div style={{
                        padding: '48px 24px',
                        textAlign: 'center',
                        backgroundColor: 'var(--surface-color)',
                        borderRadius: '16px',
                        border: '1.5px dashed #E2E8F0',
                        marginTop: '12px'
                      }}>
                        <div style={{ fontSize: '36px', marginBottom: '6px' }}>🔍</div>
                        <h4 style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)', margin: '0 0 6px 0' }}>No Parents Found</h4>
                        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 16px 0' }}>
                          No parent or guardian profiles match your current search terms or filter criteria.
                        </p>
                        {hasActiveFilters && (
                          <button
                            onClick={resetAllParentFilters}
                            style={{
                              padding: '8px 16px',
                              borderRadius: '8px',
                              border: 'none',
                              backgroundcolor: 'var(--text-primary)',
                              color: '#FFFFFF',
                              fontSize: '13px',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            Clear All Filters
                          </button>
                        )}
                      </div>
                    ) : parentViewMode === 'grid' ? (
                      /* Grid View */
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '12px',
                        marginTop: '12px'
                      }}>
                        {paginatedParents.map(parent => {
                          const { parentName, parentIdDisplay, email, phone, occupation, relationship, address, linkedWards, avatar } = parent;

                          return (
                            <div
                              key={parent.id}
                              onClick={() => handleParentClick(parent)}
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                backgroundColor: 'var(--surface-color)',
                                borderRadius: '12px',
                                border: '1.5px solid var(--border-color)',
                                padding: '14px',
                                position: 'relative',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.03)'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#CBD5E1';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.06)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = '#E2E8F0';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.03)';
                              }}
                            >
                              {/* Header with Avatar & Basic Info */}
                              <div style={{ display: 'flex', gap: '14px', alignItems: 'center', marginBottom: '8px' }}>
                                <div style={{
                                  width: '44px',
                                  height: '44px',
                                  borderRadius: '50%',
                                  overflow: 'hidden',
                                  flexShrink: 0,
                                  border: '2px solid #FFFFFF',
                                  boxShadow: '0 3px 10px rgba(0,0,0,0.1)',
                                  background: '#EEF2FF'
                                }}>
                                  <img
                                    src={avatar}
                                    alt={parentName}
                                    onError={(e) => {
                                      e.currentTarget.onerror = null;
                                      e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(parentName || 'Parent')}&background=E0E7FF&color=4338CA&bold=true`;
                                    }}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                  />
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, textAlign: 'left' }}>
                                  <span style={{
                                    fontWeight: '800',
                                    fontSize: '15px',
                                    color: 'var(--text-primary)',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}>
                                    {parentName}
                                  </span>
                                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '4px', flexWrap: 'wrap' }}>
                                    <span style={{
                                      fontSize: '11px',
                                      fontWeight: '700',
                                      color: '#4F46E5',
                                      backgroundColor: '#EEF2FF',
                                      padding: '2px 6px',
                                      borderRadius: '4px'
                                    }}>
                                      {parentIdDisplay}
                                    </span>
                                    <span style={{
                                      fontSize: '11px',
                                      fontWeight: '600',
                                      color: 'var(--text-secondary)',
                                      backgroundColor: 'var(--bg-hover)',
                                      padding: '2px 6px',
                                      borderRadius: '4px'
                                    }}>
                                      {relationship}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Details Box */}
                              <div style={{
                                backgroundColor: 'var(--bg-alt)',
                                borderRadius: '10px',
                                padding: '4px 6px',
                                fontSize: '10px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '2px',
                                marginBottom: '4px',
                                textAlign: 'left'
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ color: 'var(--text-secondary)', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Phone size={12} color="#94A3B8" /> Phone:
                                  </span>
                                  <a 
                                    href={`tel:${phone}`}
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ color: '#2563EB', fontWeight: '700', textDecoration: 'none' }}
                                  >
                                    {phone}
                                  </a>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ color: 'var(--text-secondary)', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Mail size={12} color="#94A3B8" /> Email:
                                  </span>
                                  <a 
                                    href={`mailto:${email}`}
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ color: 'var(--text-primary)', fontWeight: '600', maxWidth: '170px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: 'none' }}
                                  >
                                    {email}
                                  </a>
                                </div>

                                {/* Linked Students Chips */}
                                <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '8px', marginTop: '2px' }}>
                                  <span style={{ color: 'var(--text-secondary)', fontWeight: '600', fontSize: '11px', display: 'block', marginBottom: '6px' }}>
                                    Linked Student(s) ({linkedWards.length}):
                                  </span>
                                  {linkedWards.length > 0 ? (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                      {linkedWards.map(w => {
                                        const wFirstName = (w.first_name || '').trim();
                                        const wProfile = studentProfilesData[wFirstName] || 
                                                        studentProfilesData[wFirstName.charAt(0).toUpperCase() + wFirstName.slice(1).toLowerCase()] || 
                                                        Object.entries(studentProfilesData).find(([k]) => k.toLowerCase() === wFirstName.toLowerCase())?.[1] || {};
                                        const wAvatar = getStudentAvatar(w);
                                        const wGrade = w.grade || wProfile.grade || '';

                                        return (
                                          <div
                                            key={w.id}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleStudentClick(w);
                                            }}
                                            title="Click to view student profile"
                                            style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '6px',
                                              backgroundColor: 'var(--surface-color)',
                                              border: '1px solid #CBD5E1',
                                              borderRadius: '16px',
                                              padding: '2px 8px 2px 3px',
                                              fontSize: '11px',
                                              fontWeight: '700',
                                              color: 'var(--text-primary)',
                                              cursor: 'pointer'
                                            }}
                                          >
                                            <div style={{ width: '18px', height: '18px', borderRadius: '50%', overflow: 'hidden' }}>
                                              <img src={wAvatar} alt={w.first_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            </div>
                                            <span>{w.first_name} {w.last_name}</span>
                                            {wGrade && <span style={{ color: 'var(--text-secondary)', fontWeight: '500', fontSize: '10px' }}>({wGrade})</span>}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <span style={{ fontSize: '11px', color: '#94A3B8', fontStyle: 'italic' }}>No students linked</span>
                                  )}
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border-color)', paddingTop: '6px', marginTop: '2px' }}>
                                  <span style={{ color: 'var(--text-secondary)', fontWeight: '500' }}>Alert Channels:</span>
                                  <span style={{
                                    padding: '2px 8px',
                                    borderRadius: '6px',
                                    backgroundColor: '#DCFCE7',
                                    color: '#15803D',
                                    fontSize: '11px',
                                    fontWeight: '700'
                                  }}>
                                    SMS & PUSH ACTIVE
                                  </span>
                                </div>
                              </div>

                              {/* Card Footer Button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleParentClick(parent);
                                }}
                                style={{
                                  marginTop: 'auto',
                                  width: '100%',
                                  padding: '8px',
                                  borderRadius: '8px',
                                  border: '1px solid var(--border-color)',
                                  backgroundColor: 'var(--surface-color)',
                                  color: 'var(--text-primary)',
                                  fontSize: '12px',
                                  fontWeight: '700',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '6px'
                                }}
                              >
                                👤 View Full Profile
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      /* Detail / Table View */
                      <div style={{ overflowX: 'auto', backgroundColor: 'var(--surface-color)', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
                        <table className="manage-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th>Parent ID</th>
                              <th>Parent Photo</th>
                              <th>Parent Name</th>
                              <th>Relationship</th>
                              <th>Email Address</th>
                              <th>Mobile Number</th>
                              <th>Linked Wards</th>
                              <th>Notification Channel</th>
                              <th style={{ textAlign: 'right' }}>Profile</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedParents.map(parent => {
                              const { parentName, parentIdDisplay, email, phone, occupation, relationship, address, linkedWards, avatar } = parent;

                              return (
                                <tr
                                  key={parent.id}
                                  onClick={() => handleParentClick(parent)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <td>
                                    <span style={{ fontWeight: '700', fontSize: '13px', color: 'var(--text-primary)' }}>
                                      {parentIdDisplay}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="avatar-circle-sm" style={{ background: '#EEF2FF' }}>
                                      <img
                                        src={avatar}
                                        alt={parentName}
                                        onError={(e) => {
                                          e.currentTarget.onerror = null;
                                          e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(parentName || 'Parent')}&background=E0E7FF&color=4338CA&bold=true`;
                                        }}
                                      />
                                    </div>
                                  </td>
                                  <td>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                      <strong style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
                                        {parentName}
                                      </strong>
                                      <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{occupation}</span>
                                    </div>
                                  </td>
                                  <td>
                                    <span style={{
                                      display: 'inline-block',
                                      padding: '2px 8px',
                                      borderRadius: '6px',
                                      backgroundColor: 'var(--bg-hover)',
                                      color: 'var(--text-secondary)',
                                      fontSize: '11px',
                                      fontWeight: '700'
                                    }}>
                                      {relationship}
                                    </span>
                                  </td>
                                  <td>
                                    <a
                                      href={`mailto:${email}`}
                                      onClick={(e) => e.stopPropagation()}
                                      style={{ color: 'var(--text-secondary)', textDecoration: 'none', fontSize: '13px' }}
                                    >
                                      {email}
                                    </a>
                                  </td>
                                  <td>
                                    <a
                                      href={`tel:${phone}`}
                                      onClick={(e) => e.stopPropagation()}
                                      style={{ color: '#2563EB', fontWeight: '700', textDecoration: 'none', fontSize: '13px' }}
                                    >
                                      {phone}
                                    </a>
                                  </td>
                                  <td>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                      {linkedWards.length > 0 ? (
                                        linkedWards.map(w => {
                                          const wFirstName = (w.first_name || '').trim();
                                          const wProfile = studentProfilesData[wFirstName] || 
                                                          studentProfilesData[wFirstName.charAt(0).toUpperCase() + wFirstName.slice(1).toLowerCase()] || 
                                                          Object.entries(studentProfilesData).find(([k]) => k.toLowerCase() === wFirstName.toLowerCase())?.[1] || {};
                                          const wGrade = w.grade || wProfile.grade || '';

                                          return (
                                            <span
                                              key={w.id}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleStudentClick(w);
                                              }}
                                              style={{
                                                display: 'inline-block',
                                                padding: '2px 8px',
                                                borderRadius: '6px',
                                                backgroundColor: '#EFF6FF',
                                                color: '#1D4ED8',
                                                fontSize: '11px',
                                                fontWeight: '700',
                                                cursor: 'pointer'
                                              }}
                                            >
                                              {w.first_name} {wGrade ? `(${wGrade})` : ''}
                                            </span>
                                          );
                                        })
                                      ) : (
                                        <span style={{ color: '#94A3B8', fontSize: '12px' }}>N/A</span>
                                      )}
                                    </div>
                                  </td>
                                  <td>
                                    <span style={{
                                      display: 'inline-block',
                                      padding: '2px 8px',
                                      borderRadius: '6px',
                                      backgroundColor: '#DCFCE7',
                                      color: '#15803D',
                                      fontSize: '11px',
                                      fontWeight: '700'
                                    }}>
                                      SMS & PUSH ACTIVE
                                    </span>
                                  </td>
                                  <td style={{ textAlign: 'right' }}>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleParentClick(parent);
                                      }}
                                      style={{
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        fontSize: '10px',
                                        fontWeight: '700',
                                        backgroundColor: 'var(--bg-hover)',
                                        color: 'var(--text-primary)',
                                        border: '1px solid var(--border-color)',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      View
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Pagination Controls Bar */}
                    {totalFilteredParents > 0 && (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '10px',
                        padding: '8px 12px',
                        backgroundColor: 'var(--surface-color)',
                        borderRadius: '12px',
                        border: '1px solid var(--border-color)',
                        flexWrap: 'wrap',
                        gap: '12px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: '500' }}>
                            Showing <strong>{parentStartIndex + 1}</strong> to <strong>{parentEndIndex}</strong> of <strong>{totalFilteredParents}</strong> entries
                          </span>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Per page:</span>
                            <select
                              value={parentPageSize}
                              onChange={(e) => {
                                setParentPageSize(Number(e.target.value));
                                setParentCurrentPage(1);
                              }}
                              style={{
                                padding: '4px 8px',
                                borderRadius: '6px',
                                border: '1px solid #CBD5E1',
                                fontSize: '12px',
                                backgroundColor: 'var(--bg-alt)',
                                color: 'var(--text-primary)',
                                fontWeight: '600',
                                cursor: 'pointer'
                              }}
                            >
                              <option value={5}>5</option>
                              <option value={10}>10</option>
                              <option value={20}>20</option>
                              <option value={50}>50</option>
                            </select>
                          </div>
                        </div>

                        {/* Page Navigation Controls */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <button
                            onClick={() => setParentCurrentPage(1)}
                            disabled={validParentPage <= 1}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid var(--border-color)',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: validParentPage <= 1 ? '#F8FAFC' : '#FFFFFF',
                              color: validParentPage <= 1 ? '#CBD5E1' : '#0F172A',
                              cursor: validParentPage <= 1 ? 'not-allowed' : 'pointer'
                            }}
                          >
                            First
                          </button>
                          <button
                            onClick={() => setParentCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={validParentPage <= 1}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid var(--border-color)',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: validParentPage <= 1 ? '#F8FAFC' : '#FFFFFF',
                              color: validParentPage <= 1 ? '#CBD5E1' : '#0F172A',
                              cursor: validParentPage <= 1 ? 'not-allowed' : 'pointer'
                            }}
                          >
                            <ChevronLeft size={14} />
                            Prev
                          </button>

                          {/* Page Number Pills */}
                          {Array.from({ length: totalParentPages }, (_, i) => i + 1)
                            .filter(p => p === 1 || p === totalParentPages || Math.abs(p - validParentPage) <= 1)
                            .reduce((acc, p, idx, arr) => {
                              if (idx > 0 && p - arr[idx - 1] > 1) {
                                acc.push('...');
                              }
                              acc.push(p);
                              return acc;
                            }, [])
                            .map((p, idx) => (
                              p === '...' ? (
                                <span key={`ellipsis-${idx}`} style={{ padding: '0 4px', color: '#94A3B8' }}>...</span>
                              ) : (
                                <button
                                  key={`page-${p}`}
                                  onClick={() => setParentCurrentPage(p)}
                                  style={{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '4px',
                                    border: validParentPage === p ? 'none' : '1px solid #E2E8F0',
                                    fontSize: '12px',
                                    fontWeight: '700',
                                    backgroundColor: validParentPage === p ? 'var(--text-primary)' : 'var(--surface-color)',
                                    color: validParentPage === p ? '#FFFFFF' : '#64748B',
                                    cursor: 'pointer'
                                  }}
                                >
                                  {p}
                                </button>
                              )
                            ))}

                          <button
                            onClick={() => setParentCurrentPage(prev => Math.min(totalParentPages, prev + 1))}
                            disabled={validParentPage >= totalParentPages}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid var(--border-color)',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: validParentPage >= totalParentPages ? '#F8FAFC' : '#FFFFFF',
                              color: validParentPage >= totalParentPages ? '#CBD5E1' : '#0F172A',
                              cursor: validParentPage >= totalParentPages ? 'not-allowed' : 'pointer'
                            }}
                          >
                            Next
                            <ChevronRight size={14} />
                          </button>
                          <button
                            onClick={() => setParentCurrentPage(totalParentPages)}
                            disabled={validParentPage >= totalParentPages}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid var(--border-color)',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: validParentPage >= totalParentPages ? '#F8FAFC' : '#FFFFFF',
                              color: validParentPage >= totalParentPages ? '#CBD5E1' : '#0F172A',
                              cursor: validParentPage >= totalParentPages ? 'not-allowed' : 'pointer'
                            }}
                          >
                            Last
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* LAYMAN MENU 5: DRIVERS DIRECTORY */}
              {activeTab === 'drivers' && (() => {
                const allDrivers = allUsers.filter(u => u.role === 'driver' || u.role === 'DRIVER');

                // Enrich driver items with profile, vehicle assignment, routes, etc.
                const driversWithDetails = allDrivers.map(driver => {
                  const driverName = (driver.display_name || driver.name || '').trim();
                  const profile = driverProfiles[driverName] || driverProfilesData[driverName] ||
                                  Object.entries(driverProfiles).find(([k]) => k.toLowerCase() === driverName.toLowerCase())?.[1] ||
                                  Object.entries(driverProfilesData).find(([k]) => k.toLowerCase() === driverName.toLowerCase())?.[1] || {};
                  
                  const driverIdDisplay = profile.driverId || (`#DVR-00${driver.id}`);
                  const phone = driver.phone || profile.phone || 'N/A';
                  const licensingStatus = profile.licensingStatus || 'VALID LICENSE';
                  const licenseExpiry = profile.licenseExpiry || 'Oct 15, 2028';
                  const status = profile.status || 'ACTIVE';
                  const address = profile.address || 'Banaswadi, Bangalore - 560043';
                  const avatar = profile.avatar || getDriverAvatar(driver);
                  const documents = profile.documents || [];

                  // Match assigned trips / routes / buses
                  const assignedTrips = allTrips.filter(t => 
                    t.driver_id === driver.id || 
                    (t.driver_name && t.driver_name.toLowerCase() === driverName.toLowerCase())
                  );
                  const assignedBusObj = allBuses.find(b => b.driver_id === driver.id || (assignedTrips[0] && assignedTrips[0].bus_number === b.bus_number));
                  const assignedBus = assignedTrips[0]?.bus_number || assignedBusObj?.bus_number || 'Unassigned';
                  const assignedRoute = assignedTrips[0]?.route_name || (allRoutes.find(r => r.driver_id === driver.id)?.route_name) || 'Standby Route';

                  return {
                    ...driver,
                    driverName,
                    profile,
                    driverIdDisplay,
                    phone,
                    licensingStatus,
                    licenseExpiry,
                    status,
                    address,
                    avatar,
                    documents,
                    assignedTrips,
                    assignedBus,
                    assignedRoute
                  };
                });

                // Unique dynamic values for filters
                const uniqueDriverStatuses = Array.from(new Set(driversWithDetails.map(d => d.status).filter(Boolean))).sort();
                const uniqueLicenseStatuses = Array.from(new Set(driversWithDetails.map(d => d.licensingStatus).filter(Boolean))).sort();
                const uniqueDriverBuses = Array.from(new Set(
                  driversWithDetails.map(d => d.assignedBus).filter(b => b && b !== 'Unassigned')
                )).sort();

                // 1. Filtering & Searching
                const filteredDrivers = driversWithDetails.filter(driver => {
                  const { driverName, driverIdDisplay, phone, licensingStatus, licenseExpiry, status, address, assignedBus, assignedRoute } = driver;

                  // Search query match
                  if (driverSearchQuery) {
                    const q = driverSearchQuery.toLowerCase().trim();
                    const matchesSearch =
                      driverName.toLowerCase().includes(q) ||
                      driverIdDisplay.toLowerCase().includes(q) ||
                      phone.toLowerCase().includes(q) ||
                      licensingStatus.toLowerCase().includes(q) ||
                      licenseExpiry.toLowerCase().includes(q) ||
                      status.toLowerCase().includes(q) ||
                      address.toLowerCase().includes(q) ||
                      assignedBus.toLowerCase().includes(q) ||
                      assignedRoute.toLowerCase().includes(q);

                    if (!matchesSearch) return false;
                  }

                  // Category pill filter
                  if (driverCategoryFilter === 'active' && status.toUpperCase() !== 'ACTIVE') return false;
                  if (driverCategoryFilter === 'valid_license' && licensingStatus.toUpperCase() !== 'VALID LICENSE') return false;
                  if (driverCategoryFilter === 'assigned_bus' && assignedBus === 'Unassigned') return false;
                  if (driverCategoryFilter === 'standby' && assignedBus !== 'Unassigned') return false;

                  // Status dropdown filter
                  if (driverStatusFilter !== 'all' && status !== driverStatusFilter) return false;

                  // License dropdown filter
                  if (driverLicenseFilter !== 'all' && licensingStatus !== driverLicenseFilter) return false;

                  // Bus dropdown filter
                  if (driverBusFilter !== 'all' && assignedBus !== driverBusFilter) return false;

                  return true;
                }).sort((a, b) => {
                  if (driverSortBy === 'name_asc') return a.driverName.localeCompare(b.driverName);
                  if (driverSortBy === 'name_desc') return b.driverName.localeCompare(a.driverName);
                  if (driverSortBy === 'id_asc') return (a.id || 0) - (b.id || 0);
                  if (driverSortBy === 'id_desc') return (b.id || 0) - (a.id || 0);
                  if (driverSortBy === 'expiry_asc') return (a.licenseExpiry || '').localeCompare(b.licenseExpiry || '');
                  if (driverSortBy === 'bus_asc') return a.assignedBus.localeCompare(b.assignedBus);
                  if (driverSortBy === 'status_asc') return a.status.localeCompare(b.status);
                  return 0;
                });

                // 2. Pagination computation
                const totalFilteredDrivers = filteredDrivers.length;
                const totalDriverPages = Math.max(1, Math.ceil(totalFilteredDrivers / driverPageSize));
                const validDriverPage = Math.min(Math.max(1, driverCurrentPage), totalDriverPages);
                const driverStartIndex = (validDriverPage - 1) * driverPageSize;
                const driverEndIndex = Math.min(driverStartIndex + driverPageSize, totalFilteredDrivers);
                const paginatedDrivers = filteredDrivers.slice(driverStartIndex, driverEndIndex);

                const hasActiveFilters = Boolean(
                  driverSearchQuery ||
                  driverCategoryFilter !== 'all' ||
                  driverStatusFilter !== 'all' ||
                  driverLicenseFilter !== 'all' ||
                  driverBusFilter !== 'all' ||
                  driverSortBy !== 'name_asc'
                );

                const resetAllDriverFilters = () => {
                  setDriverSearchQuery('');
                  setDriverCategoryFilter('all');
                  setDriverStatusFilter('all');
                  setDriverLicenseFilter('all');
                  setDriverBusFilter('all');
                  setDriverSortBy('name_asc');
                  setDriverCurrentPage(1);
                };

                return (
                  <div className="manage-panel">
                    {/* Top Bar: Category Pills & Action Buttons */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center', width: '100%', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {[
                          { id: 'all', label: `All Drivers (${driversWithDetails.length})` },
                          { id: 'active', label: `Active Duty (${driversWithDetails.filter(d => d.status.toUpperCase() === 'ACTIVE').length})` },
                          { id: 'valid_license', label: `Valid License (${driversWithDetails.filter(d => d.licensingStatus.toUpperCase() === 'VALID LICENSE').length})` },
                          { id: 'assigned_bus', label: `Assigned to Bus (${driversWithDetails.filter(d => d.assignedBus !== 'Unassigned').length})` },
                          { id: 'standby', label: `Standby (${driversWithDetails.filter(d => d.assignedBus === 'Unassigned').length})` }
                        ].map((cat) => (
                          <button
                            key={cat.id}
                            onClick={() => {
                              setDriverCategoryFilter(cat.id);
                              setDriverCurrentPage(1);
                            }}
                            style={{
                              height: '36px',
                              padding: '0 12px',
                              borderRadius: '10px',
                              fontSize: '12px',
                              fontWeight: '700',
                              backgroundColor: driverCategoryFilter === cat.id ? 'var(--text-primary)' : 'var(--surface-color)',
                              color: driverCategoryFilter === cat.id ? 'var(--surface-color)' : 'var(--text-secondary)',
                              border: '1px solid ' + (driverCategoryFilter === cat.id ? 'var(--text-primary)' : 'var(--border-color)'),
                              cursor: 'pointer',
                              transition: 'all 0.15s ease',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxSizing: 'border-box'
                            }}
                          >
                            {cat.label}
                          </button>
                        ))}
                      </div>

                      <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {/* Grid vs Detail View Mode Switcher */}
                        <div style={{ display: 'flex', backgroundColor: 'var(--bg-hover)', padding: '3px', borderRadius: '10px', border: '1px solid var(--border-color)', height: '36px', boxSizing: 'border-box', alignItems: 'center' }}>
                          <button
                            onClick={() => setDriverViewMode('grid')}
                            title="Grid View"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              height: '28px',
                              padding: '0 12px',
                              borderRadius: '8px',
                              border: 'none',
                              fontSize: '12px',
                              fontWeight: '700',
                              backgroundColor: driverViewMode === 'grid' ? '#FFFFFF' : 'transparent',
                              color: driverViewMode === 'grid' ? '#0F172A' : '#64748B',
                              boxShadow: driverViewMode === 'grid' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                              cursor: 'pointer',
                              boxSizing: 'border-box'
                            }}
                          >
                            <LayoutGrid size={15} />
                            Grid
                          </button>
                          <button
                            onClick={() => setDriverViewMode('detail')}
                            title="Detail / Table View"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              height: '28px',
                              padding: '0 12px',
                              borderRadius: '8px',
                              border: 'none',
                              fontSize: '12px',
                              fontWeight: '700',
                              backgroundColor: driverViewMode === 'detail' ? '#FFFFFF' : 'transparent',
                              color: driverViewMode === 'detail' ? '#0F172A' : '#64748B',
                              boxShadow: driverViewMode === 'detail' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                              cursor: 'pointer',
                              boxSizing: 'border-box'
                            }}
                          >
                            <List size={15} />
                            Detail
                          </button>
                        </div>

                        <button
                          onClick={handleAddNewDriverClick}
                          style={{
                            backgroundColor: 'var(--text-primary)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '10px',
                            height: '36px',
                            padding: '0 14px',
                            fontSize: '12px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            boxSizing: 'border-box',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          <span>+</span> Add New Driver
                        </button>
                      </div>
                    </div>

                    {/* Search & Filter Bar */}
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      marginBottom: '10px',
                      padding: '8px 12px',
                      backgroundColor: 'var(--surface-color)',
                      borderRadius: '12px',
                      border: '1px solid var(--border-color)',
                      alignItems: 'center',
                      flexWrap: 'wrap'
                    }}>
                      {/* Search Input */}
                      <div style={{ position: 'relative', flex: '1 1 240px', minWidth: '220px' }}>
                        <Search size={16} color="#94A3B8" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                        <input
                          type="text"
                          placeholder="Search driver name, ID, phone, license, bus, route, address..."
                          value={driverSearchQuery}
                          onChange={(e) => {
                            setDriverSearchQuery(e.target.value);
                            setDriverCurrentPage(1);
                          }}
                          style={{
                            width: '100%',
                            padding: '4px 28px 4px 32px',
                            borderRadius: '8px',
                            border: '1px solid #CBD5E1',
                            fontSize: '13px',
                            outline: 'none'
                          }}
                        />
                        {driverSearchQuery && (
                          <button
                            onClick={() => {
                              setDriverSearchQuery('');
                              setDriverCurrentPage(1);
                            }}
                            style={{
                              position: 'absolute',
                              right: '10px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#94A3B8',
                              display: 'flex',
                              padding: 0
                            }}
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>

                      {/* Operational Status Filter */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>Status:</span>
                        <select
                          value={driverStatusFilter}
                          onChange={(e) => {
                            setDriverStatusFilter(e.target.value);
                            setDriverCurrentPage(1);
                          }}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            border: '1px solid #CBD5E1',
                            fontSize: '11px',
                            backgroundColor: 'var(--bg-alt)',
                            color: 'var(--text-primary)',
                            fontWeight: '600',
                            outline: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="all">All Statuses</option>
                          {uniqueDriverStatuses.map(st => (
                            <option key={st} value={st}>{st}</option>
                          ))}
                        </select>
                      </div>

                      {/* Licensing Filter */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>License:</span>
                        <select
                          value={driverLicenseFilter}
                          onChange={(e) => {
                            setDriverLicenseFilter(e.target.value);
                            setDriverCurrentPage(1);
                          }}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            border: '1px solid #CBD5E1',
                            fontSize: '11px',
                            backgroundColor: 'var(--bg-alt)',
                            color: 'var(--text-primary)',
                            fontWeight: '600',
                            outline: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="all">All Licenses</option>
                          {uniqueLicenseStatuses.map(lic => (
                            <option key={lic} value={lic}>{lic}</option>
                          ))}
                        </select>
                      </div>

                      {/* Bus / Vehicle Filter */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>Vehicle:</span>
                        <select
                          value={driverBusFilter}
                          onChange={(e) => {
                            setDriverBusFilter(e.target.value);
                            setDriverCurrentPage(1);
                          }}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            border: '1px solid #CBD5E1',
                            fontSize: '11px',
                            backgroundColor: 'var(--bg-alt)',
                            color: 'var(--text-primary)',
                            fontWeight: '600',
                            outline: 'none',
                            cursor: 'pointer',
                            maxWidth: '160px'
                          }}
                        >
                          <option value="all">All Vehicles</option>
                          <option value="Unassigned">Unassigned / Standby</option>
                          {uniqueDriverBuses.map(bus => (
                            <option key={bus} value={bus}>{bus}</option>
                          ))}
                        </select>
                      </div>

                      {/* Sort By */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>Sort:</span>
                        <select
                          value={driverSortBy}
                          onChange={(e) => {
                            setDriverSortBy(e.target.value);
                            setDriverCurrentPage(1);
                          }}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            border: '1px solid #CBD5E1',
                            fontSize: '11px',
                            backgroundColor: 'var(--bg-alt)',
                            color: 'var(--text-primary)',
                            fontWeight: '600',
                            outline: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="name_asc">Name (A–Z)</option>
                          <option value="name_desc">Name (Z–A)</option>
                          <option value="id_asc">Driver ID (Asc)</option>
                          <option value="id_desc">Driver ID (Desc)</option>
                          <option value="expiry_asc">License Expiry (Soonest)</option>
                          <option value="bus_asc">Assigned Bus</option>
                          <option value="status_asc">Operational Status</option>
                        </select>
                      </div>

                      {/* Reset Filters button */}
                      {hasActiveFilters && (
                        <button
                          onClick={resetAllDriverFilters}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '7px 12px',
                            borderRadius: '8px',
                            border: '1px solid #CBD5E1',
                            fontSize: '12px',
                            backgroundColor: 'var(--surface-color)',
                            color: '#EF4444',
                            fontWeight: '700',
                            cursor: 'pointer'
                          }}
                        >
                          <RotateCcw size={13} />
                          Reset
                        </button>
                      )}

                      <div style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>
                        Showing {totalFilteredDrivers === 0 ? 0 : driverStartIndex + 1}–{driverEndIndex} of {totalFilteredDrivers}
                      </div>
                    </div>

                    {/* Table vs Grid View */}
                    {totalFilteredDrivers === 0 ? (
                      <div style={{
                        padding: '48px 24px',
                        textAlign: 'center',
                        backgroundColor: 'var(--surface-color)',
                        borderRadius: '16px',
                        border: '1.5px dashed #E2E8F0',
                        marginTop: '12px'
                      }}>
                        <div style={{ fontSize: '36px', marginBottom: '6px' }}>🔍</div>
                        <h4 style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)', margin: '0 0 6px 0' }}>No Drivers Found</h4>
                        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 16px 0' }}>
                          No school bus driver profiles match your current search terms or filter criteria.
                        </p>
                        {hasActiveFilters && (
                          <button
                            onClick={resetAllDriverFilters}
                            style={{
                              padding: '8px 16px',
                              borderRadius: '8px',
                              border: 'none',
                              backgroundcolor: 'var(--text-primary)',
                              color: '#FFFFFF',
                              fontSize: '13px',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            Clear All Filters
                          </button>
                        )}
                      </div>
                    ) : driverViewMode === 'grid' ? (
                      /* Grid View */
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '12px',
                        marginTop: '12px'
                      }}>
                        {paginatedDrivers.map(driver => {
                          const isLicenseValid = (driver.licensingStatus || '').toUpperCase() === 'VALID LICENSE';
                          const isActive = (driver.status || '').toUpperCase() === 'ACTIVE';

                          return (
                            <div
                              key={driver.id}
                              onClick={() => handleDriverClick(driver)}
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                backgroundColor: 'var(--surface-color)',
                                borderRadius: '12px',
                                border: '1.5px solid var(--border-color)',
                                padding: '14px',
                                position: 'relative',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.03)'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#CBD5E1';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.06)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = '#E2E8F0';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.03)';
                              }}
                            >
                              {/* Header with Avatar & Basic Info */}
                              <div style={{ display: 'flex', gap: '14px', alignItems: 'center', marginBottom: '8px' }}>
                                <div style={{
                                  width: '44px',
                                  height: '44px',
                                  borderRadius: '50%',
                                  overflow: 'hidden',
                                  flexShrink: 0,
                                  border: '2px solid #FFFFFF',
                                  boxShadow: '0 3px 10px rgba(0,0,0,0.1)',
                                  background: '#0F172A'
                                }}>
                                  <img
                                    src={driver.avatar}
                                    alt={driver.driverName}
                                    onError={(e) => {
                                      e.currentTarget.onerror = null;
                                      e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(driver.driverName)}&background=0F172A&color=FFFFFF&bold=true`;
                                    }}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                  />
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, textAlign: 'left' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                                    <span style={{
                                      fontWeight: '800',
                                      fontSize: '15px',
                                      color: 'var(--text-primary)',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis'
                                    }}>
                                      {driver.driverName}
                                    </span>
                                  </div>
                                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '4px', flexWrap: 'wrap' }}>
                                    <span style={{
                                      fontSize: '11px',
                                      fontWeight: '700',
                                      color: '#8B5CF6',
                                      backgroundColor: '#F5F3FF',
                                      padding: '2px 6px',
                                      borderRadius: '4px'
                                    }}>
                                      {driver.driverIdDisplay}
                                    </span>
                                    <span style={{
                                      fontSize: '11px',
                                      fontWeight: '700',
                                      color: isActive ? '#15803D' : '#B45309',
                                      backgroundColor: isActive ? '#DCFCE7' : '#FEF3C7',
                                      padding: '2px 6px',
                                      borderRadius: '4px',
                                      textTransform: 'uppercase'
                                    }}>
                                      {driver.status}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Details Box */}
                              <div style={{
                                backgroundColor: 'var(--bg-alt)',
                                borderRadius: '10px',
                                padding: '4px 6px',
                                fontSize: '10px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '2px',
                                marginBottom: '4px',
                                textAlign: 'left'
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ color: 'var(--text-secondary)', fontWeight: '500' }}>Licensing:</span>
                                  <span style={{
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    backgroundColor: isLicenseValid ? '#DCFCE7' : '#FEE2E2',
                                    color: isLicenseValid ? '#15803D' : '#DC2626',
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    textTransform: 'uppercase'
                                  }}>
                                    {driver.licensingStatus}
                                  </span>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ color: 'var(--text-secondary)', fontWeight: '500' }}>License Expiry:</span>
                                  <span style={{ color: 'var(--text-primary)', fontWeight: '600' }}>
                                    {driver.licenseExpiry}
                                  </span>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ color: 'var(--text-secondary)', fontWeight: '500' }}>Mobile:</span>
                                  <span style={{ color: 'var(--text-primary)', fontWeight: '600' }}>
                                    {driver.phone}
                                  </span>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderTop: '1px solid var(--border-color)', paddingTop: '6px', marginTop: '2px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                                    <span style={{ color: '#3B82F6', fontSize: '12px' }}>🚌</span>
                                    <strong style={{ color: 'var(--text-secondary)' }}>Vehicle:</strong>
                                    <span style={{
                                      fontWeight: '600',
                                      color: driver.assignedBus === 'Unassigned' ? '#94A3B8' : '#0F172A',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis'
                                    }}>
                                      {driver.assignedBus}
                                    </span>
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                                    <span style={{ color: '#10B981', fontSize: '10px' }}>●</span>
                                    <strong style={{ color: 'var(--text-secondary)' }}>Route:</strong>
                                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {driver.assignedRoute}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Card Footer Button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDriverClick(driver);
                                }}
                                style={{
                                  marginTop: 'auto',
                                  width: '100%',
                                  padding: '8px',
                                  borderRadius: '8px',
                                  border: '1px solid var(--border-color)',
                                  backgroundColor: 'var(--surface-color)',
                                  color: 'var(--text-primary)',
                                  fontSize: '12px',
                                  fontWeight: '700',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '6px'
                                }}
                              >
                                👤 View Driver Profile
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      /* Detail / Table View */
                      <div style={{ overflowX: 'auto', backgroundColor: 'var(--surface-color)', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
                        <table className="manage-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th>Driver ID</th>
                              <th>Driver Photo</th>
                              <th>Driver Name</th>
                              <th>Mobile Number</th>
                              <th>Assigned Vehicle</th>
                              <th>Assigned Route</th>
                              <th>Licensing Status</th>
                              <th>License Expiry</th>
                              <th>Operational Status</th>
                              <th style={{ textAlign: 'right' }}>Profile</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedDrivers.map(driver => {
                              const isLicenseValid = (driver.licensingStatus || '').toUpperCase() === 'VALID LICENSE';
                              const isActive = (driver.status || '').toUpperCase() === 'ACTIVE';

                              return (
                                <tr
                                  key={driver.id}
                                  onClick={() => handleDriverClick(driver)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <td>
                                    <span style={{
                                      fontWeight: '700',
                                      fontSize: '12px',
                                      color: '#8B5CF6',
                                      backgroundColor: '#F5F3FF',
                                      padding: '3px 8px',
                                      borderRadius: '6px'
                                    }}>
                                      {driver.driverIdDisplay}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="avatar-circle-sm" style={{ backgroundcolor: 'var(--text-primary)' }}>
                                      <img
                                        src={driver.avatar}
                                        alt={driver.driverName}
                                        onError={(e) => {
                                          e.currentTarget.onerror = null;
                                          e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(driver.driverName)}&background=0F172A&color=FFFFFF&bold=true`;
                                        }}
                                      />
                                    </div>
                                  </td>
                                  <td>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                      <strong style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
                                        {driver.driverName}
                                      </strong>
                                      {driver.address && (
                                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {driver.address}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td>
                                    <span style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: '500' }}>
                                      {driver.phone}
                                    </span>
                                  </td>
                                  <td>
                                    {driver.assignedBus !== 'Unassigned' ? (
                                      <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        padding: '2px 8px',
                                        borderRadius: '6px',
                                        backgroundColor: '#EFF6FF',
                                        color: '#1D4ED8',
                                        fontSize: '11px',
                                        fontWeight: '700'
                                      }}>
                                        🚌 {driver.assignedBus}
                                      </span>
                                    ) : (
                                      <span style={{
                                        display: 'inline-block',
                                        padding: '2px 8px',
                                        borderRadius: '6px',
                                        backgroundColor: 'var(--bg-hover)',
                                        color: '#94A3B8',
                                        fontSize: '11px',
                                        fontWeight: '600'
                                      }}>
                                        Standby
                                      </span>
                                    )}
                                  </td>
                                  <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                                      <span style={{ color: '#10B981', fontSize: '10px' }}>●</span>
                                      <span>{driver.assignedRoute}</span>
                                    </div>
                                  </td>
                                  <td>
                                    <span style={{
                                      display: 'inline-block',
                                      padding: '2px 8px',
                                      borderRadius: '6px',
                                      backgroundColor: isLicenseValid ? '#DCFCE7' : '#FEE2E2',
                                      color: isLicenseValid ? '#15803D' : '#DC2626',
                                      fontSize: '11px',
                                      fontWeight: '700',
                                      textTransform: 'uppercase'
                                    }}>
                                      {driver.licensingStatus}
                                    </span>
                                  </td>
                                  <td>
                                    <span style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>
                                      {driver.licenseExpiry}
                                    </span>
                                  </td>
                                  <td>
                                    <span style={{
                                      display: 'inline-block',
                                      padding: '2px 8px',
                                      borderRadius: '6px',
                                      backgroundColor: isActive ? '#DCFCE7' : '#FEF3C7',
                                      color: isActive ? '#15803D' : '#B45309',
                                      fontSize: '11px',
                                      fontWeight: '700',
                                      textTransform: 'uppercase'
                                    }}>
                                      {driver.status}
                                    </span>
                                  </td>
                                  <td style={{ textAlign: 'right' }}>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDriverClick(driver);
                                        }}
                                        style={{
                                          padding: '4px 8px',
                                          borderRadius: '4px',
                                          fontSize: '10px',
                                          fontWeight: '700',
                                          backgroundColor: 'var(--bg-hover)',
                                          color: 'var(--text-primary)',
                                          border: '1px solid var(--border-color)',
                                          cursor: 'pointer'
                                        }}
                                      >
                                        View
                                      </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Pagination Controls Bar */}
                    {totalFilteredDrivers > 0 && (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '10px',
                        padding: '8px 12px',
                        backgroundColor: 'var(--surface-color)',
                        borderRadius: '12px',
                        border: '1px solid var(--border-color)',
                        flexWrap: 'wrap',
                        gap: '12px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: '500' }}>
                            Showing <strong>{driverStartIndex + 1}</strong> to <strong>{driverEndIndex}</strong> of <strong>{totalFilteredDrivers}</strong> entries
                          </span>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Per page:</span>
                            <select
                              value={driverPageSize}
                              onChange={(e) => {
                                setDriverPageSize(Number(e.target.value));
                                setDriverCurrentPage(1);
                              }}
                              style={{
                                padding: '4px 8px',
                                borderRadius: '6px',
                                border: '1px solid #CBD5E1',
                                fontSize: '12px',
                                backgroundColor: 'var(--bg-alt)',
                                color: 'var(--text-primary)',
                                fontWeight: '600',
                                cursor: 'pointer'
                              }}
                            >
                              <option value={5}>5</option>
                              <option value={10}>10</option>
                              <option value={20}>20</option>
                              <option value={50}>50</option>
                            </select>
                          </div>
                        </div>

                        {/* Page Navigation Controls */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <button
                            onClick={() => setDriverCurrentPage(1)}
                            disabled={validDriverPage <= 1}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid var(--border-color)',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: validDriverPage <= 1 ? '#F8FAFC' : '#FFFFFF',
                              color: validDriverPage <= 1 ? '#CBD5E1' : '#0F172A',
                              cursor: validDriverPage <= 1 ? 'not-allowed' : 'pointer'
                            }}
                          >
                            First
                          </button>
                          <button
                            onClick={() => setDriverCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={validDriverPage <= 1}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid var(--border-color)',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: validDriverPage <= 1 ? '#F8FAFC' : '#FFFFFF',
                              color: validDriverPage <= 1 ? '#CBD5E1' : '#0F172A',
                              cursor: validDriverPage <= 1 ? 'not-allowed' : 'pointer'
                            }}
                          >
                            Prev
                          </button>

                          {Array.from({ length: totalDriverPages }, (_, idx) => idx + 1)
                            .filter(page => Math.abs(page - validDriverPage) <= 2 || page === 1 || page === totalDriverPages)
                            .map((page, idx, arr) => {
                              const showEllipsis = idx > 0 && page - arr[idx - 1] > 1;
                              return (
                                <React.Fragment key={page}>
                                  {showEllipsis && <span style={{ padding: '0 4px', color: '#94A3B8' }}>...</span>}
                                  <button
                                    onClick={() => setDriverCurrentPage(page)}
                                    style={{
                                      padding: '6px 12px',
                                      borderRadius: '6px',
                                      border: validDriverPage === page ? '1px solid var(--text-primary)' : '1px solid var(--border-color)',
                                      fontSize: '12px',
                                      fontWeight: '700',
                                      backgroundColor: validDriverPage === page ? 'var(--text-primary)' : 'var(--surface-color)',
                                      color: validDriverPage === page ? 'var(--surface-color)' : 'var(--text-primary)',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    {page}
                                  </button>
                                </React.Fragment>
                              );
                            })}

                          <button
                            onClick={() => setDriverCurrentPage(prev => Math.min(totalDriverPages, prev + 1))}
                            disabled={validDriverPage >= totalDriverPages}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid var(--border-color)',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: validDriverPage >= totalDriverPages ? '#F8FAFC' : '#FFFFFF',
                              color: validDriverPage >= totalDriverPages ? '#CBD5E1' : '#0F172A',
                              cursor: validDriverPage >= totalDriverPages ? 'not-allowed' : 'pointer'
                            }}
                          >
                            Next
                          </button>
                          <button
                            onClick={() => setDriverCurrentPage(totalDriverPages)}
                            disabled={validDriverPage >= totalDriverPages}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid var(--border-color)',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: validDriverPage >= totalDriverPages ? '#F8FAFC' : '#FFFFFF',
                              color: validDriverPage >= totalDriverPages ? '#CBD5E1' : '#0F172A',
                              cursor: validDriverPage >= totalDriverPages ? 'not-allowed' : 'pointer'
                            }}
                          >
                            Last
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* LAYMAN MENU 6: SCHOOL BUSES */}
              {activeTab === 'buses' && (() => {
                // Enrich bus list with matching trips, drivers, routes, and profile info
                const busesWithDetails = allBuses.map(bus => {
                  const busNum = (bus.bus_number || '').trim();
                  const profile = busProfilesData[busNum] ||
                                  Object.entries(busProfilesData).find(([k]) => k.toLowerCase() === busNum.toLowerCase())?.[1] || {};

                  const assignedTrip = allTrips.find(t => t.bus_id === bus.id || (t.bus_number && t.bus_number.toLowerCase() === busNum.toLowerCase()));
                  const assignedDriverObj = allUsers.find(u => (u.role === 'driver' || u.role === 'DRIVER') && (u.id === assignedTrip?.driver_id || u.display_name === assignedTrip?.driver_name));
                  const assignedRouteObj = allRoutes.find(r => r.id === assignedTrip?.route_id || r.route_name === assignedTrip?.route_name);

                  const makeModel = profile.makeModel || `${(bus.capacity || 40) > 40 ? 'Tata Starbus Ultra' : 'Ashok Leyland Sunshine'} ${bus.capacity || 40}L`;
                  const assignedRoute = assignedTrip?.route_name || assignedRouteObj?.route_name || profile.assignedRoute || 'Standby Fleet';
                  const assignedDriver = assignedTrip?.driver_name || assignedDriverObj?.display_name || profile.assignedDriver || 'Standby Driver';
                  const driverPhone = assignedDriverObj?.phone || profile.driverPhone || 'N/A';
                  const gpsImei = profile.gpsImei || `86229104033${bus.id}91`;
                  const registration = bus.registration || profile.registration || `KA-03-XX-${1000 + bus.id}`;
                  const capacity = parseInt(bus.capacity) || profile.capacity || 40;
                  const status = (bus.status || profile.status || 'active').toLowerCase();

                  return {
                    ...bus,
                    busNum,
                    profile,
                    makeModel,
                    assignedRoute,
                    assignedDriver,
                    driverPhone,
                    gpsImei,
                    registration,
                    capacity,
                    status
                  };
                });

                // Extract unique dynamic values for dropdown filters
                const uniqueBusRoutes = Array.from(new Set(busesWithDetails.map(b => b.assignedRoute).filter(r => r && r !== 'Standby Fleet'))).sort();
                const uniqueBusDrivers = Array.from(new Set(busesWithDetails.map(b => b.assignedDriver).filter(d => d && d !== 'Standby Driver'))).sort();

                // 1. Filtering & Searching
                const filteredBuses = busesWithDetails.filter(bus => {
                  const { busNum, registration, makeModel, assignedDriver, assignedRoute, gpsImei, status, capacity } = bus;

                  // Search query match
                  if (busSearchQuery) {
                    const q = busSearchQuery.toLowerCase().trim();
                    const matchesSearch =
                      busNum.toLowerCase().includes(q) ||
                      registration.toLowerCase().includes(q) ||
                      makeModel.toLowerCase().includes(q) ||
                      assignedDriver.toLowerCase().includes(q) ||
                      assignedRoute.toLowerCase().includes(q) ||
                      gpsImei.toLowerCase().includes(q) ||
                      String(capacity).includes(q) ||
                      status.toLowerCase().includes(q);
                    if (!matchesSearch) return false;
                  }

                  // Category pill filter
                  if (busCategoryFilter === 'active' && status !== 'active') return false;
                  if (busCategoryFilter === 'maintenance' && status !== 'maintenance') return false;
                  if (busCategoryFilter === 'large' && capacity < 40) return false;
                  if (busCategoryFilter === 'medium' && capacity >= 40) return false;

                  // Capacity dropdown filter
                  if (busCapacityFilter === 'mini' && capacity >= 35) return false;
                  if (busCapacityFilter === 'medium' && (capacity < 35 || capacity > 40)) return false;
                  if (busCapacityFilter === 'large' && capacity <= 40) return false;

                  // Status dropdown filter
                  if (busStatusFilter !== 'all' && status !== busStatusFilter.toLowerCase()) {
                    return false;
                  }

                  // Route dropdown filter
                  if (busRouteFilter !== 'all' && assignedRoute !== busRouteFilter) {
                    return false;
                  }

                  // Driver dropdown filter
                  if (busDriverFilter !== 'all' && assignedDriver !== busDriverFilter) {
                    return false;
                  }

                  return true;
                }).sort((a, b) => {
                  if (busSortBy === 'number_asc') return (a.bus_number || '').localeCompare(b.bus_number || '', undefined, { numeric: true });
                  if (busSortBy === 'number_desc') return (b.bus_number || '').localeCompare(a.bus_number || '', undefined, { numeric: true });
                  if (busSortBy === 'id_asc') return a.id - b.id;
                  if (busSortBy === 'id_desc') return b.id - a.id;
                  if (busSortBy === 'capacity_desc') return b.capacity - a.capacity;
                  if (busSortBy === 'capacity_asc') return a.capacity - b.capacity;
                  if (busSortBy === 'registration_asc') return (a.registration || '').localeCompare(b.registration || '');
                  return 0;
                });

                // 2. Pagination computation
                const totalFilteredBuses = filteredBuses.length;
                const totalBusPages = Math.max(1, Math.ceil(totalFilteredBuses / busPageSize));
                const validBusPage = Math.min(Math.max(1, busCurrentPage), totalBusPages);
                const busStartIndex = (validBusPage - 1) * busPageSize;
                const busEndIndex = Math.min(busStartIndex + busPageSize, totalFilteredBuses);
                const paginatedBuses = filteredBuses.slice(busStartIndex, busEndIndex);

                const hasActiveFilters = Boolean(
                  busSearchQuery ||
                  busCategoryFilter !== 'all' ||
                  busCapacityFilter !== 'all' ||
                  busStatusFilter !== 'all' ||
                  busRouteFilter !== 'all' ||
                  busDriverFilter !== 'all' ||
                  busSortBy !== 'number_asc'
                );

                const resetAllBusFilters = () => {
                  setBusSearchQuery('');
                  setBusCategoryFilter('all');
                  setBusCapacityFilter('all');
                  setBusStatusFilter('all');
                  setBusRouteFilter('all');
                  setBusDriverFilter('all');
                  setBusSortBy('number_asc');
                  setBusCurrentPage(1);
                };

                return (
                  <div className="manage-panel">
                    {/* Top Bar: Category Pills & Action Buttons */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center', width: '100%', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {[
                          { id: 'all', label: `All Fleet (${allBuses.length})` },
                          { id: 'active', label: `Active Fleet (${allBuses.filter(b => (b.status || 'active').toLowerCase() === 'active').length})` },
                          { id: 'maintenance', label: `In Maintenance (${allBuses.filter(b => (b.status || '').toLowerCase() === 'maintenance').length})` },
                          { id: 'large', label: `Large (40+ Seats) (${allBuses.filter(b => (b.capacity || 0) >= 40).length})` },
                          { id: 'medium', label: `Mini / Medium (${allBuses.filter(b => (b.capacity || 0) < 40).length})` }
                        ].map((cat) => (
                          <button
                            key={cat.id}
                            onClick={() => {
                              setBusCategoryFilter(cat.id);
                              setBusCurrentPage(1);
                            }}
                            style={{
                              height: '36px',
                              padding: '0 12px',
                              borderRadius: '10px',
                              fontSize: '12px',
                              fontWeight: '700',
                              backgroundColor: busCategoryFilter === cat.id ? 'var(--text-primary)' : 'var(--surface-color)',
                              color: busCategoryFilter === cat.id ? 'var(--surface-color)' : 'var(--text-secondary)',
                              border: '1px solid ' + (busCategoryFilter === cat.id ? 'var(--text-primary)' : 'var(--border-color)'),
                              cursor: 'pointer',
                              transition: 'all 0.15s ease',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxSizing: 'border-box'
                            }}
                          >
                            {cat.label}
                          </button>
                        ))}
                      </div>

                      <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {/* Grid vs Detail View Mode Switcher */}
                        <div style={{ display: 'flex', backgroundColor: 'var(--bg-hover)', padding: '3px', borderRadius: '10px', border: '1px solid var(--border-color)', height: '36px', boxSizing: 'border-box', alignItems: 'center' }}>
                          <button
                            onClick={() => setBusViewMode('grid')}
                            title="Grid View"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              height: '28px',
                              padding: '0 12px',
                              borderRadius: '8px',
                              border: 'none',
                              fontSize: '12px',
                              fontWeight: '700',
                              backgroundColor: busViewMode === 'grid' ? '#FFFFFF' : 'transparent',
                              color: busViewMode === 'grid' ? '#0F172A' : '#64748B',
                              boxShadow: busViewMode === 'grid' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                              cursor: 'pointer',
                              boxSizing: 'border-box'
                            }}
                          >
                            <LayoutGrid size={15} />
                            Grid
                          </button>
                          <button
                            onClick={() => setBusViewMode('detail')}
                            title="Detail / Table View"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              height: '28px',
                              padding: '0 12px',
                              borderRadius: '8px',
                              border: 'none',
                              fontSize: '12px',
                              fontWeight: '700',
                              backgroundColor: busViewMode === 'detail' ? '#FFFFFF' : 'transparent',
                              color: busViewMode === 'detail' ? '#0F172A' : '#64748B',
                              boxShadow: busViewMode === 'detail' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                              cursor: 'pointer',
                              boxSizing: 'border-box'
                            }}
                          >
                            <List size={15} />
                            Detail
                          </button>
                        </div>

                        <button 
                          onClick={() => setShowAddBusModal(true)}
                          style={{
                            backgroundColor: 'var(--text-primary)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '10px',
                            height: '36px',
                            padding: '0 14px',
                            fontSize: '12px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            boxSizing: 'border-box',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          <span>+</span> Add Vehicle
                        </button>
                      </div>
                    </div>

                    {/* Search & Filter Bar */}
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      marginBottom: '10px',
                      padding: '8px 12px',
                      backgroundColor: 'var(--surface-color)',
                      borderRadius: '12px',
                      border: '1px solid var(--border-color)',
                      alignItems: 'center',
                      flexWrap: 'wrap'
                    }}>
                      {/* Search Input */}
                      <div style={{ position: 'relative', flex: '1 1 240px', minWidth: '220px' }}>
                        <Search size={16} color="#94A3B8" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                        <input
                          type="text"
                          placeholder="Search bus number, registration, make/model, driver, route, IMEI..."
                          value={busSearchQuery}
                          onChange={(e) => {
                            setBusSearchQuery(e.target.value);
                            setBusCurrentPage(1);
                          }}
                          style={{
                            width: '100%',
                            padding: '4px 28px 4px 32px',
                            borderRadius: '8px',
                            border: '1px solid #CBD5E1',
                            fontSize: '13px',
                            outline: 'none'
                          }}
                        />
                        {busSearchQuery && (
                          <button
                            onClick={() => {
                              setBusSearchQuery('');
                              setBusCurrentPage(1);
                            }}
                            style={{
                              position: 'absolute',
                              right: '10px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#94A3B8',
                              display: 'flex',
                              padding: 0
                            }}
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>

                      {/* Capacity Filter */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>Capacity:</span>
                        <select
                          value={busCapacityFilter}
                          onChange={(e) => {
                            setBusCapacityFilter(e.target.value);
                            setBusCurrentPage(1);
                          }}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            border: '1px solid #CBD5E1',
                            fontSize: '11px',
                            backgroundColor: 'var(--bg-alt)',
                            color: 'var(--text-primary)',
                            fontWeight: '600',
                            outline: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="all">All Capacities</option>
                          <option value="mini">Mini (&lt; 35 Seats)</option>
                          <option value="medium">Medium (35–40 Seats)</option>
                          <option value="large">Large (40+ Seats)</option>
                        </select>
                      </div>

                      {/* Status Filter */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>Status:</span>
                        <select
                          value={busStatusFilter}
                          onChange={(e) => {
                            setBusStatusFilter(e.target.value);
                            setBusCurrentPage(1);
                          }}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            border: '1px solid #CBD5E1',
                            fontSize: '11px',
                            backgroundColor: 'var(--bg-alt)',
                            color: 'var(--text-primary)',
                            fontWeight: '600',
                            outline: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="all">All Statuses</option>
                          <option value="active">Active Fleet</option>
                          <option value="maintenance">In Maintenance</option>
                        </select>
                      </div>

                      {/* Route Filter */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>Route:</span>
                        <select
                          value={busRouteFilter}
                          onChange={(e) => {
                            setBusRouteFilter(e.target.value);
                            setBusCurrentPage(1);
                          }}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            border: '1px solid #CBD5E1',
                            fontSize: '11px',
                            backgroundColor: 'var(--bg-alt)',
                            color: 'var(--text-primary)',
                            fontWeight: '600',
                            outline: 'none',
                            cursor: 'pointer',
                            maxWidth: '160px'
                          }}
                        >
                          <option value="all">All Routes</option>
                          {uniqueBusRoutes.map(rt => (
                            <option key={rt} value={rt}>{rt}</option>
                          ))}
                        </select>
                      </div>

                      {/* Driver Filter */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>Driver:</span>
                        <select
                          value={busDriverFilter}
                          onChange={(e) => {
                            setBusDriverFilter(e.target.value);
                            setBusCurrentPage(1);
                          }}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            border: '1px solid #CBD5E1',
                            fontSize: '11px',
                            backgroundColor: 'var(--bg-alt)',
                            color: 'var(--text-primary)',
                            fontWeight: '600',
                            outline: 'none',
                            cursor: 'pointer',
                            maxWidth: '160px'
                          }}
                        >
                          <option value="all">All Drivers</option>
                          {uniqueBusDrivers.map(drv => (
                            <option key={drv} value={drv}>{drv}</option>
                          ))}
                        </select>
                      </div>

                      {/* Sort By */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>Sort:</span>
                        <select
                          value={busSortBy}
                          onChange={(e) => {
                            setBusSortBy(e.target.value);
                            setBusCurrentPage(1);
                          }}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            border: '1px solid #CBD5E1',
                            fontSize: '11px',
                            backgroundColor: 'var(--bg-alt)',
                            color: 'var(--text-primary)',
                            fontWeight: '600',
                            outline: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="number_asc">Bus Number (A–Z)</option>
                          <option value="number_desc">Bus Number (Z–A)</option>
                          <option value="id_asc">Vehicle ID (Asc)</option>
                          <option value="id_desc">Vehicle ID (Desc)</option>
                          <option value="capacity_desc">Capacity (High to Low)</option>
                          <option value="capacity_asc">Capacity (Low to High)</option>
                          <option value="registration_asc">Registration Plate</option>
                        </select>
                      </div>

                      {/* Reset Filters button */}
                      {hasActiveFilters && (
                        <button
                          onClick={resetAllBusFilters}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '7px 12px',
                            borderRadius: '8px',
                            border: '1px solid #CBD5E1',
                            fontSize: '12px',
                            backgroundColor: 'var(--surface-color)',
                            color: '#EF4444',
                            fontWeight: '700',
                            cursor: 'pointer'
                          }}
                        >
                          <RotateCcw size={13} />
                          Reset
                        </button>
                      )}

                      <div style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>
                        Showing {totalFilteredBuses === 0 ? 0 : busStartIndex + 1}–{busEndIndex} of {totalFilteredBuses}
                      </div>
                    </div>

                    {/* Table vs Grid View */}
                    {totalFilteredBuses === 0 ? (
                      <div style={{
                        padding: '48px 24px',
                        textAlign: 'center',
                        backgroundColor: 'var(--surface-color)',
                        borderRadius: '16px',
                        border: '1.5px dashed #E2E8F0',
                        marginTop: '12px'
                      }}>
                        <div style={{ fontSize: '36px', marginBottom: '6px' }}>🚌</div>
                        <h4 style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)', margin: '0 0 6px 0' }}>No Fleet Vehicles Found</h4>
                        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 16px 0' }}>
                          No buses in the fleet match your current search terms or selected filter parameters.
                        </p>
                        {hasActiveFilters && (
                          <button
                            onClick={resetAllBusFilters}
                            style={{
                              padding: '8px 16px',
                              borderRadius: '8px',
                              border: 'none',
                              backgroundcolor: 'var(--text-primary)',
                              color: '#FFFFFF',
                              fontSize: '13px',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            Clear All Filters
                          </button>
                        )}
                      </div>
                    ) : busViewMode === 'grid' ? (
                      /* Grid View */
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '12px',
                        marginTop: '12px'
                      }}>
                        {paginatedBuses.map(bus => {
                          const isBusActive = bus.status === 'active';
                          return (
                            <div
                              key={bus.id}
                              onClick={() => handleBusClick(bus)}
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                backgroundColor: 'var(--surface-color)',
                                borderRadius: '12px',
                                border: '1.5px solid var(--border-color)',
                                padding: '14px',
                                position: 'relative',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.03)'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#CBD5E1';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.06)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = '#E2E8F0';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.03)';
                              }}
                            >
                              {/* Header with Bus Icon & Basic Info */}
                              <div style={{ display: 'flex', gap: '14px', alignItems: 'center', marginBottom: '8px' }}>
                                <div style={{
                                  width: '44px',
                                  height: '44px',
                                  borderRadius: '14px',
                                  backgroundColor: '#EFF6FF',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '26px',
                                  flexShrink: 0,
                                  border: '2px solid #FFFFFF',
                                  boxShadow: '0 3px 10px rgba(0,0,0,0.08)',
                                  position: 'relative'
                                }}>
                                  🚌
                                  <span style={{
                                    position: 'absolute',
                                    bottom: '-2px',
                                    right: '-2px',
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    backgroundColor: isBusActive ? '#10B981' : '#F59E0B',
                                    border: '2px solid #FFFFFF'
                                  }} />
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, textAlign: 'left' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{
                                      fontWeight: '800',
                                      fontSize: '16px',
                                      color: 'var(--text-primary)',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis'
                                    }}>
                                      {bus.bus_number}
                                    </span>
                                    <span style={{
                                      fontSize: '11px',
                                      fontWeight: '700',
                                      padding: '2px 6px',
                                      borderRadius: '4px',
                                      backgroundColor: isBusActive ? '#DCFCE7' : '#FEF3C7',
                                      color: isBusActive ? '#15803D' : '#D97706',
                                      textTransform: 'uppercase'
                                    }}>
                                      {bus.status}
                                    </span>
                                  </div>

                                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '4px', flexWrap: 'wrap' }}>
                                    <span style={{
                                      fontSize: '11px',
                                      fontWeight: '700',
                                      color: '#7C3AED',
                                      backgroundColor: '#F5F3FF',
                                      padding: '2px 6px',
                                      borderRadius: '4px'
                                    }}>
                                      {bus.registration}
                                    </span>
                                    <span style={{
                                      fontSize: '11px',
                                      fontWeight: '600',
                                      color: 'var(--text-secondary)',
                                      backgroundColor: 'var(--bg-hover)',
                                      padding: '2px 6px',
                                      borderRadius: '4px'
                                    }}>
                                      #{bus.id} • {bus.capacity} Seats
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Details Box */}
                              <div style={{
                                backgroundColor: 'var(--bg-alt)',
                                borderRadius: '10px',
                                padding: '4px 6px',
                                fontSize: '10px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '2px',
                                marginBottom: '4px',
                                textAlign: 'left'
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ color: 'var(--text-secondary)', fontWeight: '500' }}>Make & Model:</span>
                                  <span style={{ color: 'var(--text-primary)', fontWeight: '700', maxWidth: '170px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {bus.makeModel}
                                  </span>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ color: 'var(--text-secondary)', fontWeight: '500' }}>Assigned Driver:</span>
                                  <span style={{ color: '#2563EB', fontWeight: '600', maxWidth: '170px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    👮‍♂️ {bus.assignedDriver}
                                  </span>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ color: 'var(--text-secondary)', fontWeight: '500' }}>Assigned Route:</span>
                                  <span style={{ color: 'var(--text-primary)', fontWeight: '600', maxWidth: '170px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    🗺️ {bus.assignedRoute}
                                  </span>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border-color)', paddingTop: '6px', marginTop: '2px' }}>
                                  <span style={{ color: 'var(--text-secondary)', fontWeight: '500' }}>GPS Telemetry:</span>
                                  <span style={{ color: '#059669', fontSize: '11px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#10B981', display: 'inline-block' }} />
                                    {bus.gpsImei}
                                  </span>
                                </div>
                              </div>

                              {/* Card Footer Button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleBusClick(bus);
                                }}
                                style={{
                                  marginTop: 'auto',
                                  width: '100%',
                                  padding: '8px',
                                  borderRadius: '8px',
                                  border: '1px solid var(--border-color)',
                                  backgroundColor: 'var(--surface-color)',
                                  color: 'var(--text-primary)',
                                  fontSize: '12px',
                                  fontWeight: '700',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '6px'
                                }}
                              >
                                🚌 View Vehicle Profile
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      /* Detail / Table View */
                      <div style={{ overflowX: 'auto', backgroundColor: 'var(--surface-color)', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
                        <table className="manage-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th>Vehicle ID</th>
                              <th>Bus Number</th>
                              <th>Registration Plate</th>
                              <th>Make & Model</th>
                              <th>Seating Capacity</th>
                              <th>Assigned Route</th>
                              <th>Assigned Driver</th>
                              <th>GPS Tracker IMEI</th>
                              <th>Operational Status</th>
                              <th style={{ textAlign: 'right' }}>Profile</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedBuses.map(bus => {
                              const isBusActive = bus.status === 'active';
                              return (
                                <tr 
                                  key={bus.id} 
                                  onClick={() => handleBusClick(bus)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <td>
                                    <span style={{ fontWeight: '700', fontSize: '13px', color: 'var(--text-primary)' }}>
                                      #{bus.id}
                                    </span>
                                  </td>
                                  <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>
                                        🚌
                                      </div>
                                      <strong style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
                                        {bus.bus_number}
                                      </strong>
                                    </div>
                                  </td>
                                  <td>
                                    <span style={{
                                      display: 'inline-block',
                                      padding: '2px 8px',
                                      borderRadius: '6px',
                                      backgroundColor: '#F5F3FF',
                                      color: '#7C3AED',
                                      fontSize: '11px',
                                      fontWeight: '700'
                                    }}>
                                      {bus.registration}
                                    </span>
                                  </td>
                                  <td>{bus.makeModel}</td>
                                  <td>
                                    <span style={{
                                      display: 'inline-block',
                                      padding: '2px 8px',
                                      borderRadius: '6px',
                                      backgroundColor: 'var(--bg-hover)',
                                      color: 'var(--text-secondary)',
                                      fontSize: '11px',
                                      fontWeight: '700'
                                    }}>
                                      {bus.capacity} seats
                                    </span>
                                  </td>
                                  <td>
                                    <span style={{ color: 'var(--text-primary)', fontWeight: '600' }}>
                                      {bus.assignedRoute}
                                    </span>
                                  </td>
                                  <td>
                                    <span style={{ color: '#2563EB', fontWeight: '600' }}>
                                      {bus.assignedDriver}
                                    </span>
                                  </td>
                                  <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontFamily: 'monospace' }}>
                                      <span style={{ color: '#10B981', fontSize: '10px' }}>●</span>
                                      <span>{bus.gpsImei}</span>
                                    </div>
                                  </td>
                                  <td>
                                    <span style={{
                                      display: 'inline-block',
                                      padding: '2px 8px',
                                      borderRadius: '6px',
                                      backgroundColor: isBusActive ? '#DCFCE7' : '#FEF3C7',
                                      color: isBusActive ? '#15803D' : '#D97706',
                                      fontSize: '11px',
                                      fontWeight: '700',
                                      textTransform: 'uppercase'
                                    }}>
                                      {bus.status}
                                    </span>
                                  </td>
                                  <td style={{ textAlign: 'right' }}>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleBusClick(bus);
                                      }}
                                      style={{
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        fontSize: '10px',
                                        fontWeight: '700',
                                        backgroundColor: 'var(--bg-hover)',
                                        color: 'var(--text-primary)',
                                        border: '1px solid var(--border-color)',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      View
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Pagination Controls Bar */}
                    {totalFilteredBuses > 0 && (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '10px',
                        padding: '8px 12px',
                        backgroundColor: 'var(--surface-color)',
                        borderRadius: '12px',
                        border: '1px solid var(--border-color)',
                        flexWrap: 'wrap',
                        gap: '12px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: '500' }}>
                            Showing <strong>{busStartIndex + 1}</strong> to <strong>{busEndIndex}</strong> of <strong>{totalFilteredBuses}</strong> entries
                          </span>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Per page:</span>
                            <select
                              value={busPageSize}
                              onChange={(e) => {
                                setBusPageSize(Number(e.target.value));
                                setBusCurrentPage(1);
                              }}
                              style={{
                                padding: '4px 8px',
                                borderRadius: '6px',
                                border: '1px solid #CBD5E1',
                                fontSize: '12px',
                                backgroundColor: 'var(--bg-alt)',
                                color: 'var(--text-primary)',
                                fontWeight: '600',
                                cursor: 'pointer'
                              }}
                            >
                              <option value={5}>5</option>
                              <option value={10}>10</option>
                              <option value={20}>20</option>
                              <option value={50}>50</option>
                            </select>
                          </div>
                        </div>

                        {/* Page Navigation Controls */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <button
                            onClick={() => setBusCurrentPage(1)}
                            disabled={validBusPage <= 1}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid var(--border-color)',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: validBusPage <= 1 ? '#F8FAFC' : '#FFFFFF',
                              color: validBusPage <= 1 ? '#CBD5E1' : '#0F172A',
                              cursor: validBusPage <= 1 ? 'not-allowed' : 'pointer'
                            }}
                          >
                            First
                          </button>
                          <button
                            onClick={() => setBusCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={validBusPage <= 1}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid var(--border-color)',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: validBusPage <= 1 ? '#F8FAFC' : '#FFFFFF',
                              color: validBusPage <= 1 ? '#CBD5E1' : '#0F172A',
                              cursor: validBusPage <= 1 ? 'not-allowed' : 'pointer'
                            }}
                          >
                            <ChevronLeft size={14} />
                            Prev
                          </button>

                          {/* Page Number Pills */}
                          {Array.from({ length: totalBusPages }, (_, i) => i + 1)
                            .filter(p => p === 1 || p === totalBusPages || Math.abs(p - validBusPage) <= 1)
                            .reduce((acc, p, idx, arr) => {
                              if (idx > 0 && p - arr[idx - 1] > 1) {
                                acc.push('...');
                              }
                              acc.push(p);
                              return acc;
                            }, [])
                            .map((p, idx) => (
                              p === '...' ? (
                                <span key={`bus-ellipsis-${idx}`} style={{ padding: '0 4px', color: '#94A3B8' }}>...</span>
                              ) : (
                                <button
                                  key={`bus-page-${p}`}
                                  onClick={() => setBusCurrentPage(p)}
                                  style={{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '4px',
                                    border: validBusPage === p ? 'none' : '1px solid #E2E8F0',
                                    fontSize: '12px',
                                    fontWeight: '700',
                                    backgroundColor: validBusPage === p ? 'var(--text-primary)' : 'var(--surface-color)',
                                    color: validBusPage === p ? '#FFFFFF' : '#64748B',
                                    cursor: 'pointer'
                                  }}
                                >
                                  {p}
                                </button>
                              )
                            ))}

                          <button
                            onClick={() => setBusCurrentPage(prev => Math.min(totalBusPages, prev + 1))}
                            disabled={validBusPage >= totalBusPages}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid var(--border-color)',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: validBusPage >= totalBusPages ? '#F8FAFC' : '#FFFFFF',
                              color: validBusPage >= totalBusPages ? '#CBD5E1' : '#0F172A',
                              cursor: validBusPage >= totalBusPages ? 'not-allowed' : 'pointer'
                            }}
                          >
                            Next
                            <ChevronRight size={14} />
                          </button>
                          <button
                            onClick={() => setBusCurrentPage(totalBusPages)}
                            disabled={validBusPage >= totalBusPages}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid var(--border-color)',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: validBusPage >= totalBusPages ? '#F8FAFC' : '#FFFFFF',
                              color: validBusPage >= totalBusPages ? '#CBD5E1' : '#0F172A',
                              cursor: validBusPage >= totalBusPages ? 'not-allowed' : 'pointer'
                            }}
                          >
                            Last
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* LAYMAN MENU 7: DOCUMENTS VAULT */}
              {activeTab === 'documents' && (() => {
                // Filter & Search computation for Documents Vault
                const filteredDocuments = documents.filter((doc) => {
                  // 1. Search Query (File Name, Entity Name, Document Type)
                  if (docSearchQuery) {
                    const q = docSearchQuery.toLowerCase().trim();
                    const fileName = (doc.file_name || '').toLowerCase();
                    const entityName = (doc.entity_name || '').toLowerCase();
                    const docType = (doc.document_type || '').toLowerCase();
                    if (!fileName.includes(q) && !entityName.includes(q) && !docType.includes(q)) {
                      return false;
                    }
                  }

                  // 2. Status Filter
                  if (docStatusFilter !== 'all') {
                    let isExpired = doc.status === 'expired';
                    let isExpiringSoon = false;
                    if (doc.expiry_date) {
                      const expiryTime = new Date(doc.expiry_date).getTime();
                      const todayTime = new Date().setHours(0, 0, 0, 0);
                      const diffDays = Math.ceil((expiryTime - todayTime) / (1000 * 60 * 60 * 24));
                      if (diffDays <= 30 && diffDays >= 0) {
                        isExpiringSoon = true;
                      }
                      if (diffDays < 0) {
                        isExpired = true;
                      }
                    }
                    if (docStatusFilter === 'expired' && !isExpired) return false;
                    if (docStatusFilter === 'active' && (isExpired || isExpiringSoon)) return false;
                    if (docStatusFilter === 'expiring_soon' && (!isExpiringSoon || isExpired)) return false;
                  }

                  // 3. File Format Filter
                  if (docFormatFilter !== 'all') {
                    const name = (doc.file_name || '').toLowerCase();
                    const mime = (doc.mime_type || '').toLowerCase();
                    if (docFormatFilter === 'pdf') {
                      if (!mime.includes('pdf') && !name.endsWith('.pdf')) return false;
                    } else if (docFormatFilter === 'word') {
                      if (!mime.includes('word') && !/\.(docx?|rtf|odt)$/i.test(name)) return false;
                    } else if (docFormatFilter === 'excel') {
                      if (!mime.includes('excel') && !mime.includes('spreadsheet') && !/\.(xlsx?|csv|tsv)$/i.test(name)) return false;
                    } else if (docFormatFilter === 'image') {
                      if (!mime.includes('image') && !/\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(name)) return false;
                    } else if (docFormatFilter === 'text') {
                      if (!mime.includes('text') && !/\.(txt|json|xml|log|md)$/i.test(name)) return false;
                    }
                  }

                  return true;
                }).sort((a, b) => {
                  if (docSortBy === 'newest') {
                    return new Date(b.uploaded_at) - new Date(a.uploaded_at);
                  } else if (docSortBy === 'oldest') {
                    return new Date(a.uploaded_at) - new Date(b.uploaded_at);
                  } else if (docSortBy === 'expiry_soon') {
                    if (!a.expiry_date) return 1;
                    if (!b.expiry_date) return -1;
                    return new Date(a.expiry_date) - new Date(b.expiry_date);
                  } else if (docSortBy === 'name_asc') {
                    return (a.entity_name || '').localeCompare(b.entity_name || '');
                  }
                  return 0;
                });

                // Pagination computations (Default 10 per page)
                const totalFilteredDocs = filteredDocuments.length;
                const totalPages = Math.max(1, Math.ceil(totalFilteredDocs / docPageSize));
                const validPage = Math.min(Math.max(1, docCurrentPage), totalPages);
                const startIndex = (validPage - 1) * docPageSize;
                const endIndex = Math.min(startIndex + docPageSize, totalFilteredDocs);
                const paginatedDocuments = filteredDocuments.slice(startIndex, endIndex);

                return (
                  <div className="manage-panel">
                    {/* Top Bar: Categories & Mode Switcher & Upload Button */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center', width: '100%', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {['all', 'driver', 'bus', 'student'].map((filt) => (
                          <button
                            key={filt}
                            onClick={() => {
                              setDocFilter(filt);
                              setDocCurrentPage(1);
                              const apiFilter = filt === 'bus' ? 'vehicle' : filt;
                              fetchDocuments(apiFilter);
                            }}
                            style={{
                              height: '36px',
                              padding: '0 12px',
                              borderRadius: '10px',
                              fontSize: '12px',
                              fontWeight: '700',
                              backgroundColor: docFilter === filt ? 'var(--text-primary)' : 'var(--surface-color)',
                              color: docFilter === filt ? 'var(--surface-color)' : 'var(--text-secondary)',
                              border: '1px solid ' + (docFilter === filt ? 'var(--text-primary)' : 'var(--border-color)'),
                              textTransform: 'capitalize',
                              cursor: 'pointer',
                              transition: 'all 0.15s ease',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxSizing: 'border-box'
                            }}
                          >
                            {filt === 'bus' ? 'Bus' : filt} Documents
                          </button>
                        ))}
                      </div>

                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginLeft: 'auto' }}>
                        {/* Grid vs Detail View Mode Switcher */}
                        <div style={{ display: 'flex', backgroundColor: 'var(--bg-hover)', padding: '3px', borderRadius: '10px', border: '1px solid var(--border-color)', height: '36px', boxSizing: 'border-box', alignItems: 'center' }}>
                          <button
                            onClick={() => setDocViewMode('grid')}
                            title="Grid View"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              height: '28px',
                              padding: '0 12px',
                              borderRadius: '8px',
                              border: 'none',
                              fontSize: '12px',
                              fontWeight: '700',
                              backgroundColor: docViewMode === 'grid' ? '#FFFFFF' : 'transparent',
                              color: docViewMode === 'grid' ? '#0F172A' : '#64748B',
                              boxShadow: docViewMode === 'grid' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                              cursor: 'pointer',
                              boxSizing: 'border-box'
                            }}
                          >
                            <LayoutGrid size={15} />
                            Grid
                          </button>
                          <button
                            onClick={() => setDocViewMode('detail')}
                            title="Detail / Table View"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              height: '28px',
                              padding: '0 12px',
                              borderRadius: '8px',
                              border: 'none',
                              fontSize: '12px',
                              fontWeight: '700',
                              backgroundColor: docViewMode === 'detail' ? '#FFFFFF' : 'transparent',
                              color: docViewMode === 'detail' ? '#0F172A' : '#64748B',
                              boxShadow: docViewMode === 'detail' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                              cursor: 'pointer',
                              boxSizing: 'border-box'
                            }}
                          >
                            <List size={15} />
                            Detail
                          </button>
                        </div>

                        <button
                          onClick={() => {
                            setUploadEntityType('bus');
                            setUploadEntityId(allBuses[0] ? allBuses[0].id : '');
                            setUploadDocType((masterLookups?.bus_doc_types || DEFAULT_LOOKUPS.bus_doc_types)[0] || 'Registration Certificate (RC)');
                            setUploadExpiryDate('');
                            setUploadFileObj(null);
                            setShowUploadModal(true);
                          }}
                          style={{
                            backgroundColor: '#3B82F6',
                            color: '#FFFFFF',
                            border: 'none',
                            borderRadius: '10px',
                            height: '36px',
                            padding: '0 14px',
                            fontSize: '12px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            boxSizing: 'border-box',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          + Upload Document
                        </button>
                      </div>
                    </div>

                    {/* Search & Filter Bar */}
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      marginBottom: '10px',
                      padding: '8px 12px',
                      backgroundColor: 'var(--surface-color)',
                      borderRadius: '12px',
                      border: '1px solid var(--border-color)',
                      alignItems: 'center',
                      flexWrap: 'wrap'
                    }}>
                      {/* Search Input */}
                      <div style={{ position: 'relative', flex: '1 1 240px', minWidth: '220px' }}>
                        <Search size={16} color="#94A3B8" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                        <input
                          type="text"
                          placeholder="Search document, plate, driver, student..."
                          value={docSearchQuery}
                          onChange={(e) => {
                            setDocSearchQuery(e.target.value);
                            setDocCurrentPage(1);
                          }}
                          style={{
                            width: '100%',
                            padding: '4px 28px 4px 32px',
                            borderRadius: '8px',
                            border: '1px solid #CBD5E1',
                            fontSize: '13px',
                            outline: 'none'
                          }}
                        />
                        {docSearchQuery && (
                          <button
                            onClick={() => {
                              setDocSearchQuery('');
                              setDocCurrentPage(1);
                            }}
                            style={{
                              position: 'absolute',
                              right: '10px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#94A3B8',
                              display: 'flex',
                              padding: 0
                            }}
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>

                      {/* Expiry / Status Filter */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>Status:</span>
                        <select
                          value={docStatusFilter}
                          onChange={(e) => {
                            setDocStatusFilter(e.target.value);
                            setDocCurrentPage(1);
                          }}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            border: '1px solid #CBD5E1',
                            fontSize: '11px',
                            backgroundColor: 'var(--bg-alt)',
                            color: 'var(--text-primary)',
                            fontWeight: '600',
                            outline: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="all">All Statuses</option>
                          <option value="active">Active Only</option>
                          <option value="expiring_soon">Expiring Soon (≤30d)</option>
                          <option value="expired">Expired Only</option>
                        </select>
                      </div>

                      {/* Format Filter */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>Format:</span>
                        <select
                          value={docFormatFilter}
                          onChange={(e) => {
                            setDocFormatFilter(e.target.value);
                            setDocCurrentPage(1);
                          }}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            border: '1px solid #CBD5E1',
                            fontSize: '11px',
                            backgroundColor: 'var(--bg-alt)',
                            color: 'var(--text-primary)',
                            fontWeight: '600',
                            outline: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="all">All Formats</option>
                          <option value="pdf">PDF Documents</option>
                          <option value="word">Word Documents</option>
                          <option value="excel">Excel Spreadsheets</option>
                          <option value="image">Images</option>
                          <option value="text">Text / Code</option>
                        </select>
                      </div>

                      {/* Sort By */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>Sort:</span>
                        <select
                          value={docSortBy}
                          onChange={(e) => {
                            setDocSortBy(e.target.value);
                            setDocCurrentPage(1);
                          }}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            border: '1px solid #CBD5E1',
                            fontSize: '11px',
                            backgroundColor: 'var(--bg-alt)',
                            color: 'var(--text-primary)',
                            fontWeight: '600',
                            outline: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="newest">Newest First</option>
                          <option value="oldest">Oldest First</option>
                          <option value="expiry_soon">Expiring Soonest</option>
                          <option value="name_asc">Entity Name (A-Z)</option>
                        </select>
                      </div>

                      {/* Reset Filters button */}
                      {(docSearchQuery || docStatusFilter !== 'all' || docFormatFilter !== 'all' || docSortBy !== 'newest') && (
                        <button
                          onClick={() => {
                            setDocSearchQuery('');
                            setDocStatusFilter('all');
                            setDocFormatFilter('all');
                            setDocSortBy('newest');
                            setDocCurrentPage(1);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '7px 12px',
                            borderRadius: '8px',
                            border: '1px solid #CBD5E1',
                            fontSize: '12px',
                            backgroundColor: 'var(--surface-color)',
                            color: '#EF4444',
                            fontWeight: '700',
                            cursor: 'pointer'
                          }}
                        >
                          <RotateCcw size={13} />
                          Reset
                        </button>
                      )}

                      <div style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>
                        Showing {totalFilteredDocs === 0 ? 0 : startIndex + 1}–{endIndex} of {totalFilteredDocs}
                      </div>
                    </div>

                    {totalFilteredDocs === 0 ? (
                      <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '40px 0', backgroundColor: 'var(--surface-color)', borderRadius: '12px', border: '1.5px dashed #E2E8F0' }}>
                        No documents match your search or filter criteria.
                      </div>
                    ) : docViewMode === 'grid' ? (
                      <div className="doc-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '12px' }}>
                        {paginatedDocuments.map((doc) => {
                          // Determine expiry status and color
                          let statusColor = '#10B981'; // green
                          let statusLabel = 'Active';
                          if (doc.status === 'expired') {
                            statusColor = '#EF4444'; // red
                            statusLabel = 'Expired';
                          } else if (doc.expiry_date) {
                            const expiryTime = new Date(doc.expiry_date).getTime();
                            const todayTime = new Date().setHours(0, 0, 0, 0);
                            const diffDays = Math.ceil((expiryTime - todayTime) / (1000 * 60 * 60 * 24));
                            if (diffDays <= 30 && diffDays >= 0) {
                              statusColor = '#F59E0B'; // yellow/amber
                              statusLabel = `Expiring in ${diffDays} days`;
                            }
                          }

                          const iconData = getDocumentIcon(doc);

                          return (
                            <div key={doc.id} className="doc-card" style={{ display: 'flex', flexDirection: 'column', padding: '14px', border: '1px solid var(--border-color)', borderRadius: '10px', backgroundColor: 'var(--surface-color)', position: 'relative', minHeight: '140px' }}>
                              <div 
                                onClick={() => setPreviewDoc(doc)}
                                style={{ display: 'flex', gap: '12px', marginBottom: '6px', alignItems: 'center', cursor: 'pointer' }}
                                title="Click to preview document"
                              >
                                <div className="doc-icon-box" style={{ width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                  {iconData.icon}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
                                  <span style={{ fontWeight: '700', fontSize: '15px', color: 'var(--text-primary)' }}>{doc.entity_name}</span>
                                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px', fontWeight: '500' }}>{doc.document_type}</span>
                                </div>
                              </div>

                              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textAlign: 'left', marginBottom: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <span 
                                  onClick={() => setPreviewDoc(doc)} 
                                  style={{ cursor: 'pointer' }} 
                                  title="Click to preview"
                                >
                                  <strong>Original File:</strong> <span style={{ color: '#2563EB', textDecoration: 'underline' }}>{doc.file_name}</span>
                                </span>
                                <span><strong>Uploaded:</strong> {new Date(doc.uploaded_at).toLocaleString()}</span>
                                {doc.expiry_date ? (
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                    <strong>Expiry:</strong> {doc.expiry_date} 
                                    <span style={{ padding: '2px 6px', borderRadius: '4px', backgroundColor: statusColor + '20', color: statusColor, fontSize: '10px', fontWeight: '700' }}>
                                      {statusLabel}
                                    </span>
                                  </span>
                                ) : (
                                  <span><strong>Expiry:</strong> No Expiry Date</span>
                                )}
                              </div>

                              <div style={{ display: 'flex', gap: '6px', marginTop: 'auto', flexWrap: 'wrap' }}>
                                <button
                                  onClick={() => setPreviewDoc(doc)}
                                  style={{
                                    flex: 1,
                                    padding: '5px 8px',
                                    borderRadius: '6px',
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    backgroundColor: '#2563EB',
                                    color: '#FFFFFF',
                                    border: 'none',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '4px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  <Eye size={13} />
                                  Preview
                                </button>
                                {doc.url && (
                                  <a 
                                    href={doc.url.includes('?') ? `${doc.url}&download=1` : `${doc.url}?download=1`}
                                    download={doc.file_name}
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    title="Download file"
                                    style={{
                                      padding: '5px 8px',
                                      borderRadius: '6px',
                                      fontSize: '11px',
                                      fontWeight: '700',
                                      backgroundColor: 'var(--bg-hover)',
                                      color: 'var(--text-primary)',
                                      border: '1px solid var(--border-color)',
                                      textDecoration: 'none',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      gap: '4px',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <Download size={13} />
                                  </a>
                                )}
                                <button 
                                  onClick={() => handleDeleteDoc(doc.id)} 
                                  title="Delete document"
                                  style={{
                                    padding: '5px 8px',
                                    borderRadius: '6px',
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    backgroundColor: '#FEE2E2',
                                    color: '#DC2626',
                                    border: '1px solid #FECACA',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      /* Detail / Table View */
                      <div style={{ overflowX: 'auto', backgroundColor: 'var(--surface-color)', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
                        <table className="manage-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th>Document & File</th>
                              <th>Associated Entity</th>
                              <th>Document Type</th>
                              <th>Uploaded On</th>
                              <th>Expiry Status</th>
                              <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedDocuments.map((doc) => {
                              let statusColor = '#10B981';
                              let statusLabel = 'Active';
                              if (doc.status === 'expired') {
                                statusColor = '#EF4444';
                                statusLabel = 'Expired';
                              } else if (doc.expiry_date) {
                                const expiryTime = new Date(doc.expiry_date).getTime();
                                const todayTime = new Date().setHours(0, 0, 0, 0);
                                const diffDays = Math.ceil((expiryTime - todayTime) / (1000 * 60 * 60 * 24));
                                if (diffDays <= 30 && diffDays >= 0) {
                                  statusColor = '#F59E0B';
                                  statusLabel = `Expiring in ${diffDays}d`;
                                }
                              }

                              const iconData = getDocumentIcon(doc);

                              return (
                                <tr key={doc.id}>
                                  <td>
                                    <div 
                                      onClick={() => setPreviewDoc(doc)}
                                      style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
                                      title="Click to preview document"
                                    >
                                      <div style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        {iconData.icon}
                                      </div>
                                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span style={{ fontWeight: '600', fontSize: '13px', color: 'var(--text-primary)', textDecoration: 'underline' }}>{doc.file_name}</span>
                                        <span style={{ fontSize: '11px', color: '#94A3B8' }}>{iconData.label}</span>
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                      <strong style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{doc.entity_name}</strong>
                                      <span style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{doc.entity_type}</span>
                                    </div>
                                  </td>
                                  <td>
                                    <span style={{ fontWeight: '600', fontSize: '13px', color: 'var(--text-secondary)' }}>
                                      {doc.document_type}
                                    </span>
                                  </td>
                                  <td style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                    {new Date(doc.uploaded_at).toLocaleString()}
                                  </td>
                                  <td>
                                    {doc.expiry_date ? (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <span style={{ 
                                          display: 'inline-block',
                                          padding: '2px 8px', 
                                          borderRadius: '6px', 
                                          backgroundColor: statusColor + '18', 
                                          color: statusColor, 
                                          fontSize: '11px', 
                                          fontWeight: '700',
                                          width: 'fit-content'
                                        }}>
                                          {statusLabel}
                                        </span>
                                        <span style={{ fontSize: '11px', color: '#94A3B8' }}>{doc.expiry_date}</span>
                                      </div>
                                    ) : (
                                      <span style={{ fontSize: '12px', color: '#94A3B8' }}>No Expiry</span>
                                    )}
                                  </td>
                                  <td style={{ textAlign: 'right' }}>
                                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', alignItems: 'center' }}>
                                      <button
                                        onClick={() => setPreviewDoc(doc)}
                                        style={{
                                          padding: '5px 10px',
                                          borderRadius: '6px',
                                          fontSize: '11px',
                                          fontWeight: '700',
                                          backgroundColor: '#2563EB',
                                          color: '#FFFFFF',
                                          border: 'none',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '4px',
                                          cursor: 'pointer'
                                        }}
                                      >
                                        <Eye size={13} />
                                        Preview
                                      </button>
                                      {doc.url ? (
                                        <a
                                          href={doc.url.includes('?') ? `${doc.url}&download=1` : `${doc.url}?download=1`}
                                          download={doc.file_name}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          title="Download file"
                                          style={{
                                            padding: '5px 8px',
                                            borderRadius: '6px',
                                            fontSize: '11px',
                                            fontWeight: '700',
                                            backgroundColor: 'var(--bg-hover)',
                                            color: 'var(--text-primary)',
                                            border: '1px solid var(--border-color)',
                                            textDecoration: 'none',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          <Download size={13} />
                                        </a>
                                      ) : null}
                                      <button
                                        onClick={() => handleDeleteDoc(doc.id)}
                                        title="Delete document"
                                        style={{
                                          padding: '5px 8px',
                                          borderRadius: '6px',
                                          fontSize: '11px',
                                          fontWeight: '700',
                                          backgroundColor: '#FEE2E2',
                                          color: '#DC2626',
                                          border: '1px solid #FECACA',
                                          cursor: 'pointer'
                                        }}
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Pagination Controls Bar */}
                    {totalFilteredDocs > 0 && (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '10px',
                        padding: '8px 12px',
                        backgroundColor: 'var(--surface-color)',
                        borderRadius: '12px',
                        border: '1px solid var(--border-color)',
                        flexWrap: 'wrap',
                        gap: '12px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: '500' }}>
                            Showing <strong>{startIndex + 1}</strong> to <strong>{endIndex}</strong> of <strong>{totalFilteredDocs}</strong> entries
                          </span>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Per page:</span>
                            <select
                              value={docPageSize}
                              onChange={(e) => {
                                setDocPageSize(Number(e.target.value));
                                setDocCurrentPage(1);
                              }}
                              style={{
                                padding: '4px 8px',
                                borderRadius: '6px',
                                border: '1px solid #CBD5E1',
                                fontSize: '12px',
                                backgroundColor: 'var(--bg-alt)',
                                color: 'var(--text-primary)',
                                fontWeight: '600',
                                cursor: 'pointer'
                              }}
                            >
                              <option value={5}>5</option>
                              <option value={10}>10</option>
                              <option value={20}>20</option>
                              <option value={50}>50</option>
                            </select>
                          </div>
                        </div>

                        {/* Page Navigation Controls */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <button
                            onClick={() => setDocCurrentPage(1)}
                            disabled={validPage <= 1}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid var(--border-color)',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: validPage <= 1 ? '#F8FAFC' : '#FFFFFF',
                              color: validPage <= 1 ? '#CBD5E1' : '#0F172A',
                              cursor: validPage <= 1 ? 'not-allowed' : 'pointer'
                            }}
                          >
                            First
                          </button>
                          <button
                            onClick={() => setDocCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={validPage <= 1}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid var(--border-color)',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: validPage <= 1 ? '#F8FAFC' : '#FFFFFF',
                              color: validPage <= 1 ? '#CBD5E1' : '#0F172A',
                              cursor: validPage <= 1 ? 'not-allowed' : 'pointer'
                            }}
                          >
                            <ChevronLeft size={14} />
                            Prev
                          </button>

                          {/* Page Number Pills */}
                          {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter(p => p === 1 || p === totalPages || Math.abs(p - validPage) <= 1)
                            .reduce((acc, p, idx, arr) => {
                              if (idx > 0 && p - arr[idx - 1] > 1) {
                                acc.push('...');
                              }
                              acc.push(p);
                              return acc;
                            }, [])
                            .map((p, idx) => (
                              p === '...' ? (
                                <span key={`ellipsis-${idx}`} style={{ padding: '0 4px', color: '#94A3B8' }}>...</span>
                              ) : (
                                <button
                                  key={`page-${p}`}
                                  onClick={() => setDocCurrentPage(p)}
                                  style={{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '4px',
                                    border: validPage === p ? 'none' : '1px solid #E2E8F0',
                                    fontSize: '12px',
                                    fontWeight: '700',
                                    backgroundColor: validPage === p ? 'var(--text-primary)' : 'var(--surface-color)',
                                    color: validPage === p ? '#FFFFFF' : '#64748B',
                                    cursor: 'pointer'
                                  }}
                                >
                                  {p}
                                </button>
                              )
                            ))}

                          <button
                            onClick={() => setDocCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={validPage >= totalPages}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid var(--border-color)',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: validPage >= totalPages ? '#F8FAFC' : '#FFFFFF',
                              color: validPage >= totalPages ? '#CBD5E1' : '#0F172A',
                              cursor: validPage >= totalPages ? 'not-allowed' : 'pointer'
                            }}
                          >
                            Next
                            <ChevronRight size={14} />
                          </button>
                          <button
                            onClick={() => setDocCurrentPage(totalPages)}
                            disabled={validPage >= totalPages}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid var(--border-color)',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: validPage >= totalPages ? '#F8FAFC' : '#FFFFFF',
                              color: validPage >= totalPages ? '#CBD5E1' : '#0F172A',
                              cursor: validPage >= totalPages ? 'not-allowed' : 'pointer'
                            }}
                          >
                            Last
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* LAYMAN MENU 8: ANNOUNCEMENTS */}
              {activeTab === 'announcements' && (
                <div className="manage-panel">
                  <div className="settings-grid">
                    <div className="settings-card">
                      <div className="settings-title">📣 Broadcast Announcement</div>
                      <form className="settings-form" onSubmit={(e) => {
                        e.preventDefault();
                        if (!announceTitle || !announceBody) return;
                        const newAnn = {
                          id: announcementsList.length + 1,
                          title: announceTitle,
                          body: announceBody,
                          target: announceTarget,
                          timestamp: new Date().toLocaleString()
                        };
                        setAnnouncementsList([newAnn, ...announcementsList]);
                        setAnnounceTitle('');
                        setAnnounceBody('');
                        alert('Broadcast announcement published and sent as push notification!');
                      }}>
                        <div className="form-group">
                          <label>Announcement Title</label>
                          <input type="text" className="form-control" value={announceTitle} onChange={(e) => setAnnounceTitle(e.target.value)} placeholder="e.g. Traffic Delay Alert" required />
                        </div>
                        <div className="form-group">
                          <label>Send Target Audience</label>
                          <select className="form-control" value={announceTarget} onChange={(e) => setAnnounceTarget(e.target.value)}>
                            <option value="all">All (Parents & Drivers)</option>
                            <option value="parents">Parents Only</option>
                            <option value="drivers">Drivers Only</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Message Content</label>
                          <textarea className="form-control" style={{ height: '100px' }} value={announceBody} onChange={(e) => setAnnounceBody(e.target.value)} placeholder="Type announcement details here..." required></textarea>
                        </div>
                        <button type="submit" className="settings-btn">Publish & Broadcast</button>
                      </form>
                    </div>

                    <div className="settings-card">
                      <div className="settings-title">🕒 Announcement History</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', maxHeight: '350px', overflowY: 'auto' }}>
                        {announcementsList.map((ann) => (
                          <div key={ann.id} style={{ borderBottom: '1px dashed #E2E8F0', paddingBottom: '12px', textAlign: 'left' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                              <h4 style={{ fontSize: '14px', margin: 0, color: 'var(--text-primary)' }}>{ann.title}</h4>
                              <span className="student-status-badge status-deboarded" style={{ fontSize: '9px', textTransform: 'uppercase' }}>
                                {ann.target}
                              </span>
                            </div>
                            <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '4px 0 6px 0', lineHeight: '1.4' }}>{ann.body}</p>
                            <span style={{ fontSize: '10px', color: '#BFC3C9' }}>{ann.timestamp}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* LAYMAN MENU 9: SUPPORT TICKETS */}
              {activeTab === 'tickets' && (
                <div className="manage-panel">
                  <h3 style={{ fontSize: '15px', color: 'var(--text-secondary)', marginBottom: '6px', textAlign: 'left' }}>Inbound Support & Feedback Tickets</h3>
                  <table className="manage-table">
                    <thead>
                      <tr>
                        <th>Ticket ID</th>
                        <th>Parent / Sender</th>
                        <th>Sender Phone</th>
                        <th>Inquiry Details</th>
                        <th>Ticket Date</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ticketsList.map((t) => (
                        <tr key={t.id}>
                          <td><strong>#TCK-{t.id}</strong></td>
                          <td><strong>{t.parent}</strong></td>
                          <td>{t.phone}</td>
                          <td>{t.issue}</td>
                          <td>{t.date}</td>
                          <td>
                            <span className={`student-status-badge ${t.status === 'OPEN' ? 'status-deboarded' : 'status-boarded'}`}>
                              {t.status}
                            </span>
                          </td>
                          <td>
                            {t.status === 'OPEN' ? (
                              <button
                                onClick={() => {
                                  setTicketsList(prev => prev.map(tick => tick.id === t.id ? { ...tick, status: 'RESOLVED' } : tick));
                                  alert('Ticket marked as resolved and parent notified!');
                                }}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: '11px',
                                  fontWeight: 'bold',
                                  color: 'white',
                                  backgroundcolor: 'var(--text-primary)',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer'
                                }}
                              >
                                Resolve
                              </button>
                            ) : (
                              <span style={{ fontSize: '11px', color: '#A1A1A9', fontWeight: 'bold' }}>Resolved ✓</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* LAYMAN MENU 10: GENERAL SETTINGS */}
              {activeTab === 'general_settings' && (
                <GeneralSettingsTabs
                  backendUrl={BACKEND_URL}
                  timeFormat={timeFormat}
                  setTimeFormat={setTimeFormat}
                  dateFormat={dateFormat}
                  setDateFormat={setDateFormat}
                  systemTimezone={systemTimezone}
                  setSystemTimezone={setSystemTimezone}
                  onLookupsUpdate={handleLookupsUpdate}
                />
              )}

              {/* LAYMAN MENU 11: CONNECTIONS */}
              {activeTab === 'connections' && (
                <div className="manage-panel">
                  <div className="settings-grid">
                    <div className="settings-card">
                      <div className="settings-title">🗺️ Map & GPS API Settings</div>
                      <form className="settings-form" onSubmit={async (e) => {
                        e.preventDefault();
                        
                        let olaKey = '';
                        let googleKey = '';
                        if (mapProvider === 'ola') olaKey = mapApiKey;
                        if (mapProvider === 'google') googleKey = mapApiKey;

                        try {
                          const res = await fetch('http://localhost:3000/api/settings/map', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              map_provider: mapProvider,
                              ola_api_key: olaKey,
                              google_api_key: googleKey
                            })
                          });
                          const data = await res.json();
                          if (data.success) {
                            alert('Map Configuration saved to database!');
                          } else {
                            alert('Failed to save: ' + data.message);
                          }
                        } catch (err) {
                          alert('Failed to save map settings');
                        }
                      }}>
                        <div className="form-group">
                          <label>Map Provider</label>
                          <select className="form-control" value={mapProvider} onChange={(e) => setMapProvider(e.target.value)}>
                            <option value="ola">Ola Maps</option>
                            <option value="google">Google Maps</option>
                            <option value="osm">OpenStreetMap (Free/No Key)</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>API Key (For Ola or Google)</label>
                          <input type="password" className="form-control" value={mapApiKey} onChange={(e) => setMapApiKey(e.target.value)} />
                        </div>
                        <button type="submit" className="settings-btn">Save Configuration</button>
                      </form>

                      <div className="settings-title" style={{ marginTop: '24px' }}>💬 SMS Gateway Integration</div>
                      <form className="settings-form" onSubmit={(e) => {
                        e.preventDefault();
                        localStorage.setItem('sms_gateway_key', smsGatewayKey);
                        localStorage.setItem('sms_sender', smsSenderNumber);
                        alert('SMS Gateway credentials stored successfully!');
                      }}>
                        <div className="form-group">
                          <label>Twilio Auth Token / API Key</label>
                          <input type="password" className="form-control" value={smsGatewayKey} onChange={(e) => setSmsGatewayKey(e.target.value)} required />
                        </div>
                        <div className="form-group">
                          <label>Twilio Sender Number / WhatsApp Sender ID</label>
                          <input type="text" className="form-control" value={smsSenderNumber} onChange={(e) => setSmsSenderNumber(e.target.value)} required />
                        </div>
                        <button type="submit" className="settings-btn">Save Gateway Configuration</button>
                      </form>
                    </div>

                    <div className="settings-card">
                      <div className="settings-title">📧 Mail Server Setup (SMTP)</div>
                      <form className="settings-form" onSubmit={(e) => {
                        e.preventDefault();
                        localStorage.setItem('smtp_host', smtpHost);
                        localStorage.setItem('smtp_port', smtpPort);
                        localStorage.setItem('smtp_user', smtpUser);
                        localStorage.setItem('smtp_pass', smtpPass);
                        alert('SMTP Mail configurations successfully updated!');
                      }}>
                        <div className="form-group">
                          <label>SMTP Host</label>
                          <input type="text" className="form-control" value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} required />
                        </div>
                        <div className="form-group">
                          <label>Port</label>
                          <input type="text" className="form-control" value={smtpPort} onChange={(e) => setSmtpPort(e.target.value)} required />
                        </div>
                        <div className="form-group">
                          <label>SMTP Username</label>
                          <input type="text" className="form-control" value={smtpUser} onChange={(e) => setSmtpUser(e.target.value)} required />
                        </div>
                        <div className="form-group">
                          <label>SMTP Password</label>
                          <input type="password" className="form-control" value={smtpPass} onChange={(e) => setSmtpPass(e.target.value)} required />
                        </div>
                        <button type="submit" className="settings-btn">Save SMTP Details</button>
                      </form>

                      <div className="settings-title" style={{ marginTop: '24px' }}>🔔 Active Alert Channels</div>
                      <div className="settings-form">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #F1F3F5' }}>
                          <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>Mobile Push Notifications</span>
                          <input type="checkbox" checked={chanPush} onChange={(e) => { setChanPush(e.target.checked); localStorage.setItem('chan_push', e.target.checked); }} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #F1F3F5' }}>
                          <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>Transactional SMS / Text alerts</span>
                          <input type="checkbox" checked={chanSMS} onChange={(e) => { setChanSMS(e.target.checked); localStorage.setItem('chan_sms', e.target.checked); }} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0' }}>
                          <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>Scheduled PDF email reports</span>
                          <input type="checkbox" checked={chanEmail} onChange={(e) => { setChanEmail(e.target.checked); localStorage.setItem('chan_email', e.target.checked); }} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                        </div>
                      </div>
                    </div>

                    <div className="settings-card">
                      <div className="settings-title">📁 Document Storage Provider</div>
                      <form className="settings-form" onSubmit={async (e) => {
                        e.preventDefault();
                        try {
                          const response = await fetch(`${BACKEND_URL}/api/settings/storage`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              storage_provider: storageProvider,
                              s3_region: s3Region,
                              s3_bucket_name: s3BucketName,
                              s3_access_key_id: s3AccessKeyId,
                              s3_secret_access_key: s3SecretAccessKey,
                              gcs_project_id: gcsProjectId,
                              gcs_bucket_name: gcsBucketName,
                              gcs_key_json: gcsKeyJson,
                              azure_connection_string: azureConnectionString,
                              azure_container_name: azureContainerName
                            })
                          });
                          const data = await response.json();
                          if (data.success) {
                            alert('Storage settings updated successfully!');
                            fetchStorageSettings();
                          } else {
                            alert('Failed to update storage settings: ' + data.message);
                          }
                        } catch (err) {
                          alert('Error saving storage settings: ' + err.message);
                        }
                      }}>
                        <div className="form-group">
                          <label>Active Storage Provider</label>
                          <select className="form-control" value={storageProvider} onChange={(e) => setStorageProvider(e.target.value)}>
                            <option value="local">Local Server Disk Storage</option>
                            <option value="s3">Amazon S3 Cloud Storage</option>
                            <option value="gcs">Google Cloud Storage (GCS)</option>
                            <option value="azure">Azure Blob Storage</option>
                          </select>
                        </div>

                        {storageProvider === 's3' && (
                          <>
                            <div className="form-group">
                              <label>AWS Region</label>
                              <input type="text" className="form-control" value={s3Region} onChange={(e) => setS3Region(e.target.value)} required />
                            </div>
                            <div className="form-group">
                              <label>S3 Bucket Name</label>
                              <input type="text" className="form-control" value={s3BucketName} onChange={(e) => setS3BucketName(e.target.value)} required />
                            </div>
                            <div className="form-group">
                              <label>AWS Access Key ID</label>
                              <input type="text" className="form-control" value={s3AccessKeyId} onChange={(e) => setS3AccessKeyId(e.target.value)} required />
                            </div>
                            <div className="form-group">
                              <label>AWS Secret Access Key</label>
                              <input type="password" className="form-control" value={s3SecretAccessKey} onChange={(e) => setS3SecretAccessKey(e.target.value)} required />
                            </div>
                          </>
                        )}

                        {storageProvider === 'gcs' && (
                          <>
                            <div className="form-group">
                              <label>GCP Project ID</label>
                              <input type="text" className="form-control" value={gcsProjectId} onChange={(e) => setGcsProjectId(e.target.value)} required />
                            </div>
                            <div className="form-group">
                              <label>GCS Bucket Name</label>
                              <input type="text" className="form-control" value={gcsBucketName} onChange={(e) => setGcsBucketName(e.target.value)} required />
                            </div>
                            <div className="form-group">
                              <label>GCP Key File JSON</label>
                              <textarea className="form-control" style={{ height: '80px', fontFamily: 'monospace', fontSize: '11px' }} value={gcsKeyJson} onChange={(e) => setGcsKeyJson(e.target.value)} placeholder='{"type": "service_account", ...}' required></textarea>
                            </div>
                          </>
                        )}

                        {storageProvider === 'azure' && (
                          <>
                            <div className="form-group">
                              <label>Azure Connection String</label>
                              <input type="password" className="form-control" value={azureConnectionString} onChange={(e) => setAzureConnectionString(e.target.value)} required />
                            </div>
                            <div className="form-group">
                              <label>Azure Container Name</label>
                              <input type="text" className="form-control" value={azureContainerName} onChange={(e) => setAzureContainerName(e.target.value)} required />
                            </div>
                          </>
                        )}

                        <button type="submit" className="settings-btn">Save Storage Settings</button>
                      </form>
                    </div>
                  </div>
                </div>
              )}

              {/* LAYMAN MENU 12: ACCESS CONTROL */}
              {activeTab === 'access_control' && (
                <div className="manage-panel">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <div>
                      <h3 style={{ fontSize: '16px', fontWeight: '800', color: 'var(--text-primary)', margin: 0, textAlign: 'left' }}>Console Administrator Logins</h3>
                      <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>Manage administrative staff, route managers, and viewer/guard accounts</p>
                    </div>
                    <button 
                      className="btn-primary"
                      onClick={() => setShowAddConsoleUserModal(true)}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: '700' }}
                    >
                      <Plus size={16} /> Add Console User
                    </button>
                  </div>

                  <table className="manage-table" style={{ marginBottom: '32px' }}>
                    <thead>
                      <tr>
                        <th>Admin ID</th>
                        <th>Full Name</th>
                        <th>Username</th>
                        <th>Assigned Role</th>
                        <th>Contact / Phone</th>
                        <th style={{ textAlign: 'center' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allUsers
                        .filter(u => {
                          const r = u.role?.toLowerCase();
                          return ['admin', 'route_manager', 'viewer_guard', 'guard', 'viewer'].includes(r) || roles.some(role => role.role_key?.toLowerCase() === r);
                        })
                        .map((u) => {
                          const normalizedRole = u.role?.toLowerCase();
                          const matchedRole = roles.find(r => r.role_key?.toLowerCase() === normalizedRole);
                          
                          let roleBadge;
                          if (matchedRole) {
                            let bg = '#F1F5F9';
                            let color = '#475569';
                            let border = '#CBD5E1';
                            if (normalizedRole === 'admin') {
                              bg = '#FEE2E2';
                              color = '#991B1B';
                              border = '#FECACA';
                            } else if (normalizedRole === 'route_manager') {
                              bg = '#E0F2FE';
                              color = '#0369A1';
                              border = '#BAE6FD';
                            } else if (['viewer_guard', 'guard', 'viewer'].includes(normalizedRole)) {
                              bg = '#FEF3C7';
                              color = '#B45309';
                              border = '#FDE68A';
                            } else {
                              bg = '#EDE9FE';
                              color = '#6D28D9';
                              border = '#DDD6FE';
                            }
                            roleBadge = (
                              <span className="student-status-badge" style={{ backgroundColor: bg, color, border: `1px solid ${border}`, fontWeight: '700' }}>
                                {matchedRole.role_name.toUpperCase()}
                              </span>
                            );
                          } else {
                            roleBadge = <span className="student-status-badge status-deboarded">{(u.role || 'USER').toUpperCase()}</span>;
                          }

                          const isProtected = u.id === 1 || u.id === user?.id;

                          return (
                            <tr key={u.id}>
                              <td>#ADM-00{u.id}</td>
                              <td><strong>{u.display_name}</strong></td>
                              <td><code style={{ fontSize: '12px', padding: '2px 6px', background: '#F1F5F9', borderRadius: '4px' }}>{u.username}</code></td>
                              <td>{roleBadge}</td>
                              <td>{u.phone || `${u.username}@school.com`}</td>
                              <td style={{ textAlign: 'center' }}>
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                  <button
                                    onClick={() => handleOpenEditConsoleUser(u)}
                                    title="Edit User & Role"
                                    style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', color: '#2563EB', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: '600' }}
                                  >
                                    <Edit2 size={13} /> Edit
                                  </button>
                                  {isProtected ? (
                                    <span style={{ fontSize: '11px', color: '#94A3B8', fontStyle: 'italic', padding: '0 4px' }}>Protected Root</span>
                                  ) : (
                                    <button
                                      onClick={() => handleDeleteConsoleUser(u.id, u.display_name)}
                                      title="Delete Console User"
                                      style={{ background: '#FEE2E2', border: '1px solid #FECACA', color: '#DC2626', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: '600' }}
                                    >
                                      <Trash2 size={13} /> Delete
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>

                  {/* PARENT USERS SECTION */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '28px', marginBottom: '16px' }}>
                    <div>
                      <h3 style={{ fontSize: '16px', fontWeight: '800', color: 'var(--text-primary)', margin: 0, textAlign: 'left' }}>Parent Users</h3>
                      <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>Manage parent application logins</p>
                    </div>
                    <button 
                      className="btn-primary"
                      onClick={() => setShowAddParentModal(true)}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: '700' }}
                    >
                      <Plus size={16} /> Add Parent User
                    </button>
                  </div>

                  <table className="manage-table" style={{ marginBottom: '32px' }}>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Parent Name</th>
                        <th>Username</th>
                        <th>Contact</th>
                        <th style={{ textAlign: 'center' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allUsers
                        .filter(u => u.role?.toLowerCase() === 'parent')
                        .map((u) => {
                          return (
                            <tr key={u.id}>
                              <td>#PRT-00{u.id}</td>
                              <td>
                                <a 
                                  href="#" 
                                  onClick={(e) => { 
                                    e.preventDefault(); 
                                    navigateTab('parents'); 
                                    handleParentClick(u);
                                  }}
                                  style={{ color: '#2563EB', fontWeight: '700', textDecoration: 'none' }}
                                >
                                  {u.display_name}
                                </a>
                              </td>
                              <td><code style={{ fontSize: '12px', padding: '2px 6px', background: '#F1F5F9', borderRadius: '4px' }}>{u.username}</code></td>
                              <td>{u.phone || `${u.username}@school.com`}</td>
                              <td style={{ textAlign: 'center' }}>
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                  <button
                                    onClick={() => handleParentPasswordReset(u)}
                                    title="Reset Password"
                                    style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', color: '#2563EB', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: '600' }}
                                  >
                                    <Key size={13} /> Reset Password
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '28px', marginBottom: '16px' }}>
                    <div>
                      <h3 style={{ fontSize: '16px', fontWeight: '800', color: 'var(--text-primary)', margin: 0, textAlign: 'left' }}>Access Control List (ACL) Roles & Permissions Matrix</h3>
                      <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>Configure granular capability matrices and create custom organizational roles</p>
                    </div>
                    <button 
                      className="btn-primary"
                      onClick={() => {
                        setNewRoleKey('');
                        setNewRoleName('');
                        setNewRoleDesc('');
                        setNewRolePermissions({
                          edit_routes: false,
                          view_gps: true,
                          prune_logs: false,
                          manage_fleet: false,
                          manage_users: false,
                          access_documents: false
                        });
                        setAddRoleError('');
                        setShowAddRoleModal(true);
                      }}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: '700' }}
                    >
                      <Plus size={16} /> Add New Role
                    </button>
                  </div>

                  <table className="manage-table">
                    <thead>
                      <tr>
                        <th>Role Name</th>
                        <th>Role Key</th>
                        <th style={{ textAlign: 'center' }}>Edit Routes</th>
                        <th style={{ textAlign: 'center' }}>View GPS Map</th>
                        <th style={{ textAlign: 'center' }}>Prune Logs</th>
                        <th style={{ textAlign: 'center' }}>Manage Fleet</th>
                        <th style={{ textAlign: 'center' }}>Manage Users</th>
                        <th style={{ textAlign: 'center' }}>Access Documents</th>
                        <th style={{ textAlign: 'center' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roles.map((r) => (
                        <tr key={r.id}>
                          <td>
                            <strong>{r.role_name}</strong>
                            {r.is_system === 1 && (
                              <span style={{ marginLeft: '8px', fontSize: '10px', background: '#F1F5F9', color: '#64748B', padding: '2px 6px', borderRadius: '4px', fontWeight: '600' }}>SYSTEM</span>
                            )}
                            {r.description && (
                              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>{r.description}</div>
                            )}
                          </td>
                          <td>
                            <code style={{ fontSize: '11px', padding: '2px 6px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '4px' }}>{r.role_key}</code>
                          </td>
                          <td style={{ textAlign: 'center' }}>{r.edit_routes ? '✅ Yes' : '❌ No'}</td>
                          <td style={{ textAlign: 'center' }}>{r.view_gps ? '✅ Yes' : '❌ No'}</td>
                          <td style={{ textAlign: 'center' }}>{r.prune_logs ? '✅ Yes' : '❌ No'}</td>
                          <td style={{ textAlign: 'center' }}>{r.manage_fleet ? '✅ Yes' : '❌ No'}</td>
                          <td style={{ textAlign: 'center' }}>{r.manage_users ? '✅ Yes' : '❌ No'}</td>
                          <td style={{ textAlign: 'center' }}>{r.access_documents ? '✅ Yes' : '❌ No'}</td>
                          <td style={{ textAlign: 'center' }}>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                              <button
                                onClick={() => handleOpenEditRole(r)}
                                title="Edit Role & Permissions"
                                style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', color: '#2563EB', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: '600' }}
                              >
                                <Edit2 size={13} /> Edit
                              </button>
                              {r.is_system === 1 ? (
                                <span style={{ fontSize: '11px', color: '#94A3B8', fontStyle: 'italic', padding: '0 4px' }}>Locked</span>
                              ) : (
                                <button
                                  onClick={() => handleDeleteRole(r)}
                                  title="Delete Role"
                                  style={{ background: '#FEE2E2', border: '1px solid #FECACA', color: '#DC2626', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: '600' }}
                                >
                                  <Trash2 size={13} /> Delete
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* LAYMAN MENU 13: DATABASE OPERATIONS */}
              {activeTab === 'database_ops' && (
                <div className="manage-panel">
                  <div className="db-metrics-grid">
                    <div className="db-metric-card">
                      <div className="db-metric-label">DB Engine Size</div>
                      <div className="db-metric-value">52 KB</div>
                    </div>
                    <div className="db-metric-card">
                      <div className="db-metric-label">Index Fragmentation</div>
                      <div className="db-metric-value">0.82%</div>
                    </div>
                    <div className="db-metric-card">
                      <div className="db-metric-label">Total Tables</div>
                      <div className="db-metric-value">10 tables</div>
                    </div>
                    <div className="db-metric-card">
                      <div className="db-metric-label">Auto-vacuum</div>
                      <div className="db-metric-value">FULL</div>
                    </div>
                  </div>

                  <div className="settings-grid">
                    <div className="settings-card">
                      <div className="settings-title">🛠️ Database Maintenance Actions</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <button className="settings-btn" style={{ width: '100%' }} onClick={() => {
                          setDbLogs(prev => [
                            ...prev,
                            '[' + new Date().toLocaleTimeString() + '] [CMD] REBUILD INDEXES initiated...',
                            '[' + new Date().toLocaleTimeString() + '] [SUCCESS] SQLite Indexes rebuilt: trips_driver_id_index, stops_route_id_index, students_parent_id_index.'
                          ]);
                          alert('Database Indexes successfully rebuilt!');
                        }}>
                          🔄 Rebuild Database Indexes
                        </button>
                        <button className="settings-btn" style={{ width: '100%' }} onClick={() => {
                          setDbLogs(prev => [
                            ...prev,
                            '[' + new Date().toLocaleTimeString() + '] [CMD] REINDEX tables initiated...',
                            '[' + new Date().toLocaleTimeString() + '] [SUCCESS] Reindexed tables: users, routes, buses, students, attendance, trips.'
                          ]);
                          alert('Reindex completed successfully for all SQLite tables.');
                        }}>
                          ⚡ Reindex All Tables
                        </button>
                        <button className="settings-btn" style={{ width: '100%' }} onClick={() => {
                          setDbLogs(prev => [
                            ...prev,
                            '[' + new Date().toLocaleTimeString() + '] [CMD] VACUUM / OPTIMIZE initiated...',
                            '[' + new Date().toLocaleTimeString() + '] [SUCCESS] Database size reduced from 53248 bytes to 49152 bytes.',
                            '[' + new Date().toLocaleTimeString() + '] [SUCCESS] Database optimization finished successfully.'
                          ]);
                          alert('Database optimization and vacuuming completed!');
                        }}>
                          🧹 Vacuum & Optimize Database
                        </button>
                      </div>
                    </div>

                    <div className="settings-card">
                      <div className="settings-title">📅 Log Pruning & Cleanup Task</div>
                      <form className="settings-form" onSubmit={(e) => {
                        e.preventDefault();
                        localStorage.setItem('prune_interval', pruneInterval);
                        setDbLogs(prev => [
                          ...prev,
                          '[' + new Date().toLocaleTimeString() + '] [CONFIG] Pruning job rescheduled to delete data older than ' + pruneInterval + ' days.'
                        ]);
                        alert('Background log cleanup frequency saved!');
                      }}>
                        <div className="form-group">
                          <label>Auto-delete GPS Logs older than</label>
                          <select className="form-control" value={pruneInterval} onChange={(e) => setPruneInterval(e.target.value)}>
                            <option value="30">30 Days</option>
                              <option value="60">60 Days</option>
                            <option value="90">90 Days (Recommended)</option>
                            <option value="180">180 Days</option>
                          </select>
                        </div>
                        <button type="submit" className="settings-btn">Save Cleanup Schedule</button>
                      </form>
                    </div>
                  </div>

                  <h3 style={{ fontSize: '15px', color: 'var(--text-secondary)', marginTop: '24px', marginBottom: '8px', textAlign: 'left' }}>Live SQL Diagnostics console logs</h3>
                  <div className="db-logs-panel">
                    {dbLogs.map((log, i) => (
                      <div key={i} style={{ marginBottom: '4px', lineHeight: '1.4' }}>{log}</div>
                    ))}
                  </div>
                </div>
              )}

              {/* LAYMAN MENU: SYSTEM & AUDIT LOGS */}
              {activeTab === 'system_logs' && (
                <div className="manage-panel">
                  {/* Top Bar: Category Pills & Action Buttons */}
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center', width: '100%', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {[
                        { id: 'audit', label: 'Database Audit Logs' },
                        { id: 'system', label: 'Local System Logs (Pino)' }
                      ].map((cat) => (
                        <button
                          key={cat.id}
                          onClick={() => {
                            setLogType(cat.id);
                            setLogsList([]);
                          }}
                          style={{
                            padding: '4px 10px',
                            borderRadius: '8px',
                            fontSize: '11px',
                            fontWeight: '700',
                            backgroundColor: logType === cat.id ? 'var(--text-primary)' : 'var(--surface-color)',
                            color: logType === cat.id ? 'var(--surface-color)' : 'var(--text-secondary)',
                            border: '1.5px solid ' + (logType === cat.id ? 'var(--text-primary)' : 'var(--border-color)'),
                            cursor: 'pointer',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          {cat.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Search & Filter Bar */}
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    marginBottom: '10px',
                    padding: '8px 12px',
                    backgroundColor: 'var(--surface-color)',
                    borderRadius: '12px',
                    border: '1px solid var(--border-color)',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    width: '100%'
                  }}>
                    {/* Search Input */}
                    <div style={{ position: 'relative', flex: '1 1 200px', minWidth: '180px' }}>
                      <Search size={14} color="#94A3B8" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
                      <input 
                        type="text" 
                        placeholder="Search action, message, correlation ID..." 
                        value={logSearch} 
                        onChange={(e) => setLogSearch(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && fetchLogsData(logType, logSearch, logLevelFilter, auditActionFilter, logStartDate, logEndDate)}
                        style={{
                          width: '100%',
                          padding: '4px 28px 4px 28px',
                          borderRadius: '8px',
                          border: '1px solid #CBD5E1',
                          fontSize: '12px',
                          outline: 'none'
                        }}
                      />
                      {logSearch && (
                        <button
                          onClick={() => setLogSearch('')}
                          style={{
                            position: 'absolute',
                            right: '8px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#94A3B8',
                            display: 'flex',
                            padding: 0
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </div>

                    {logType === 'system' ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>Level:</span>
                        <select
                          value={logLevelFilter}
                          onChange={(e) => setLogLevelFilter(e.target.value)}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            border: '1px solid #CBD5E1',
                            fontSize: '11px',
                            backgroundColor: 'var(--bg-alt)',
                            color: 'var(--text-primary)',
                            fontWeight: '600',
                            outline: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="">All Levels</option>
                          <option value="30">Info (30)</option>
                          <option value="40">Warning (40)</option>
                          <option value="50">Error (50)</option>
                        </select>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>Action:</span>
                        <select
                          value={auditActionFilter}
                          onChange={(e) => setAuditActionFilter(e.target.value)}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            border: '1px solid #CBD5E1',
                            fontSize: '11px',
                            backgroundColor: 'var(--bg-alt)',
                            color: 'var(--text-primary)',
                            fontWeight: '600',
                            outline: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="">All Actions</option>
                          <option value="USER_LOGIN">USER_LOGIN</option>
                          <option value="FAILED_LOGIN_ATTEMPT">FAILED_LOGIN_ATTEMPT</option>
                        </select>
                      </div>
                    )}

                    {/* From Date picker */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>From:</span>
                      <input
                        type="date"
                        value={logStartDate}
                        onChange={(e) => setLogStartDate(e.target.value)}
                        style={{
                          padding: '3px 6px',
                          borderRadius: '6px',
                          border: '1px solid #CBD5E1',
                          fontSize: '11px',
                          backgroundColor: 'var(--bg-alt)',
                          color: 'var(--text-primary)',
                          fontWeight: '600',
                          outline: 'none',
                          cursor: 'pointer'
                        }}
                      />
                    </div>

                    {/* To Date picker */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>To:</span>
                      <input
                        type="date"
                        value={logEndDate}
                        onChange={(e) => setLogEndDate(e.target.value)}
                        style={{
                          padding: '3px 6px',
                          borderRadius: '6px',
                          border: '1px solid #CBD5E1',
                          fontSize: '11px',
                          backgroundColor: 'var(--bg-alt)',
                          color: 'var(--text-primary)',
                          fontWeight: '600',
                          outline: 'none',
                          cursor: 'pointer'
                        }}
                      />
                    </div>

                    <button 
                      className="settings-btn" 
                      onClick={() => fetchLogsData(logType, logSearch, logLevelFilter, auditActionFilter, logStartDate, logEndDate)} 
                      disabled={loadingLogs}
                      style={{
                        padding: '4px 10px',
                        borderRadius: '8px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        marginLeft: '8px',
                        border: '1px solid #CBD5E1',
                        backgroundColor: 'var(--surface-color)',
                        color: 'var(--text-primary)'
                      }}
                    >
                      {loadingLogs ? 'Loading...' : '🔄 Refresh'}
                    </button>

                    {/* Export CSV Button */}
                    <button 
                      onClick={exportLogsToCSV}
                      disabled={logsList.length === 0}
                      style={{
                        backgroundcolor: 'var(--text-primary)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '5px 12px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        cursor: logsList.length === 0 ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        marginLeft: '4px',
                        opacity: logsList.length === 0 ? 0.5 : 1
                      }}
                    >
                      <span>📤</span> Export CSV
                    </button>

                    <div style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>
                      Showing {logsList.length} log entries
                    </div>
                  </div>

                  {/* Logs Table (Styled similar to student directory table view) */}
                  <div style={{ overflowX: 'auto', backgroundColor: 'var(--surface-color)', borderRadius: '12px', border: '1px solid var(--border-color)', marginTop: '12px' }}>
                    <table className="manage-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        {logType === 'audit' ? (
                          <tr>
                            <th style={{ width: '60px' }}>ID</th>
                            <th style={{ width: '180px' }}>Timestamp</th>
                            <th style={{ width: '180px' }}>User</th>
                            <th style={{ width: '200px' }}>Action</th>
                            <th>Details</th>
                            <th style={{ width: '120px' }}>IP Address</th>
                          </tr>
                        ) : (
                          <tr>
                            <th style={{ width: '100px' }}>Level</th>
                            <th style={{ width: '180px' }}>Timestamp</th>
                            <th style={{ width: '280px' }}>Correlation ID</th>
                            <th>Log Message</th>
                            <th>Context Details</th>
                          </tr>
                        )}
                      </thead>
                      <tbody>
                        {loadingLogs ? (
                          <tr>
                            <td colSpan={logType === 'audit' ? 6 : 5} style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)' }}>
                              Loading logs...
                            </td>
                          </tr>
                        ) : logsList.length === 0 ? (
                          <tr>
                            <td colSpan={logType === 'audit' ? 6 : 5} style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)' }}>
                              No log entries found matching filters.
                            </td>
                          </tr>
                        ) : (
                          logsList.map((log, i) => {
                            const codeStyle = { fontSize: '11px', color: 'var(--text-primary)', fontFamily: 'Consolas, Monaco, monospace', padding: '4px 6px', background: '#F8FAFC', borderRadius: '4px', border: '1px solid var(--border-color)', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', display: 'block', maxWidth: '350px', cursor: 'help' };
                            
                            if (logType === 'audit') {
                              return (
                                <tr key={log.id || i}>
                                  <td>
                                    <span style={{ fontWeight: '700', fontSize: '12px', color: 'var(--text-primary)' }}>
                                      {log.id}
                                    </span>
                                  </td>
                                  <td>{new Date(log.timestamp).toLocaleString()}</td>
                                  <td>
                                    {log.display_name ? (
                                      <div>
                                        <div style={{ fontWeight: '700', color: 'var(--text-primary)' }}>{log.display_name}</div>
                                        <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>@{log.username} (ID: {log.user_id})</div>
                                      </div>
                                    ) : (
                                      <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>Anonymous / System</span>
                                    )}
                                  </td>
                                  <td>
                                    <span style={{ 
                                      padding: '4px 8px', 
                                      borderRadius: '4px', 
                                      fontSize: '10px', 
                                      fontWeight: '700', 
                                      background: log.action === 'FAILED_LOGIN_ATTEMPT' ? '#FEE2E2' : '#D1FAE5',
                                      color: log.action === 'FAILED_LOGIN_ATTEMPT' ? '#991B1B' : '#065F46',
                                      border: log.action === 'FAILED_LOGIN_ATTEMPT' ? '1px solid #FCA5A5' : '1px solid #6EE7B7'
                                    }}>
                                      {log.action}
                                    </span>
                                  </td>
                                  <td>
                                    <code 
                                      style={codeStyle} 
                                      title="Hover to view parsed details table"
                                      onMouseEnter={(e) => {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setActiveLogTooltip({
                                          x: rect.left + rect.width / 2,
                                          y: rect.top,
                                          data: log.details
                                        });
                                      }}
                                      onMouseLeave={() => setActiveLogTooltip(null)}
                                    >
                                      {log.details || '-'}
                                    </code>
                                  </td>
                                  <td><code style={{ color: 'var(--text-secondary)' }}>{log.ip_address || '-'}</code></td>
                                </tr>
                              );
                            } else {
                              // System log layout
                              let levelName = 'INFO';
                              let levelBg = '#E0F2FE';
                              let levelColor = '#0369A1';
                              let levelBorder = '1px solid #7DD3FC';
                              
                              if (log.level >= 50) {
                                levelName = 'ERROR';
                                levelBg = '#FEE2E2';
                                levelColor = '#991B1B';
                                levelBorder = '1px solid #FCA5A5';
                              } else if (log.level >= 40) {
                                levelName = 'WARN';
                                levelBg = '#FEF3C7';
                                levelColor = '#92400E';
                                levelBorder = '1px solid #FCD34D';
                              } else if (log.level < 30) {
                                levelName = 'DEBUG';
                                levelBg = '#F3F4F6';
                                levelColor = '#374151';
                                levelBorder = '1px solid #D1D5DB';
                              }

                              // Extract message and meta
                              const { level, time, correlationId, pid, hostname, msg, ...meta } = log;

                              const hasMeta = Object.keys(meta).length > 0;
                              const metaStr = hasMeta ? JSON.stringify(meta) : '';

                              return (
                                <tr key={i}>
                                  <td>
                                    <span style={{ 
                                      padding: '4px 8px', 
                                      borderRadius: '4px', 
                                      fontSize: '10px', 
                                      fontWeight: '700', 
                                      background: levelBg,
                                      color: levelColor,
                                      border: levelBorder
                                    }}>
                                      {levelName} ({level})
                                    </span>
                                  </td>
                                  <td>{time ? new Date(time).toLocaleString() : '-'}</td>
                                  <td>
                                    {correlationId ? (
                                      <code style={{ fontSize: '11px', color: '#2563EB', fontFamily: 'Consolas, Monaco, monospace' }}>{correlationId}</code>
                                    ) : (
                                      <span style={{ color: '#9CA3AF' }}>-</span>
                                    )}
                                  </td>
                                  <td style={{ fontWeight: log.level >= 50 ? '700' : 'normal', color: log.level >= 50 ? '#991B1B' : '#0F172A' }}>
                                    {msg}
                                  </td>
                                  <td>
                                    {hasMeta ? (
                                      <code 
                                        style={codeStyle} 
                                        title="Hover to view parsed details table"
                                        onMouseEnter={(e) => {
                                          const rect = e.currentTarget.getBoundingClientRect();
                                          setActiveLogTooltip({
                                            x: rect.left + rect.width / 2,
                                            y: rect.top,
                                            data: meta
                                          });
                                        }}
                                        onMouseLeave={() => setActiveLogTooltip(null)}
                                      >
                                        {metaStr}
                                      </code>
                                    ) : (
                                      <span style={{ color: '#9CA3AF' }}>-</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            }
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Tooltip Overlay */}
                  {activeLogTooltip && (
                    <div style={{
                      position: 'fixed',
                      left: `${activeLogTooltip.x}px`,
                      top: `${activeLogTooltip.y}px`,
                      transform: 'translate(-50%, -105%)',
                      backgroundColor: 'var(--surface-color)',
                      border: '1px solid #CBD5E1',
                      borderRadius: '8px',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                      padding: '10px',
                      zIndex: 9999,
                      minWidth: '260px',
                      maxWidth: '450px',
                      pointerEvents: 'none',
                      marginBottom: '8px'
                    }}>
                      {renderJsonAsTooltipTable(activeLogTooltip.data)}
                      {/* Callout Pointer Arrow */}
                      <div style={{
                        position: 'absolute',
                        bottom: '-5px',
                        left: '50%',
                        transform: 'translateX(-50%) rotate(45deg)',
                        width: '8px',
                        height: '8px',
                        backgroundColor: 'var(--surface-color)',
                        borderRight: '1px solid #CBD5E1',
                        borderBottom: '1px solid #CBD5E1'
                      }} />
                    </div>
                  )}
                </div>
              )}

              {/* LAYMAN MENU 14: CHANGELOG */}
              {activeTab === 'changelog' && (
                <div className="manage-panel">
                  <h3 style={{ fontSize: '15px', color: 'var(--text-secondary)', marginBottom: '20px', textAlign: 'left' }}>System Update History</h3>
                  
                  <div className="settings-card" style={{ padding: '24px 32px' }}>
                    <div className="changelog-timeline">
                                                                  <div className="changelog-item">
                        <div className="changelog-dot" style={{ backgroundColor: '#10B981' }}></div>
                        <div className="changelog-header">
                          <span className="changelog-version" style={{ backgroundColor: '#10B981' }}>v1.7.0</span>
                          <span className="changelog-date">September 09, 2026</span>
                          <span className="changelog-title">Parent Portal, Mobile Companion & Secure Device Pairing</span>
                        </div>
                        <p className="changelog-desc">Introduced the dedicated Parent Portal, interactive parent mobile app with real-time trip tracking, secure QR device pairing, student absence tracking, and parent account administration.</p>
                        <ul className="changelog-list">
                          <li>Built dedicated ParentPortal dashboard for parent role logins, featuring assigned student overviews, live bus tracking, and route details.</li>
                          <li>Developed ParentEnrollmentModal component for cryptographic QR code generation and instant parent mobile device pairing.</li>
                          <li>Created interactive mobile trip map (trip-map.tsx) in parent-app-mobile with live bus telemetry, waypoint tracking, and status chips.</li>
                          <li>Integrated student absence tracking with &apos;ON LEAVE&apos; badges and custom reasons directly into the active trip roster.</li>
                          <li>Added Parent Users management table in User Roles settings with password reset workflows and profile inspection.</li>
                        </ul>
                      </div>

                      <div className="changelog-item" style={{ marginTop: '24px' }}>
                        <div className="changelog-dot" style={{ backgroundColor: '#6366F1' }}></div>
                        <div className="changelog-header">
                          <span className="changelog-version" style={{ backgroundColor: '#6366F1' }}>v1.6.0</span>
                          <span className="changelog-date">September 04, 2026</span>
                          <span className="changelog-title">Trip Scheduling, Search & Driver Profile Management</span>
                        </div>
                        <p className="changelog-desc">Added trip scheduling date controls, trip search functionality, a dedicated driver profile view, new driver onboarding, and device enrollment security.</p>
                        <ul className="changelog-list">
                          <li>Added date field in trip configuration to schedule trips for future dates.</li>
                          <li>Implemented search trips capability to quickly filter and locate trips.</li>
                          <li>Created new Driver Profile screen displaying full driver details, routes, and performance.</li>
                          <li>Added option to onboard and create new drivers directly from the admin dashboard.</li>
                          <li>Implemented cryptographic QR-code device enrollment and secure mobile driver authorization.</li>
                        </ul>
                      </div>

                      <div className="changelog-item" style={{ marginTop: '24px' }}>
                        <div className="changelog-dot" style={{ backgroundColor: '#06B6D4' }}></div>
                        <div className="changelog-header">
                          <span className="changelog-version" style={{ backgroundColor: '#06B6D4' }}>v1.5.0</span>
                          <span className="changelog-date">September 01, 2026</span>
                          <span className="changelog-title">React Native Mobile Apps & Offline Telemetry Engine</span>
                        </div>
                        <p className="changelog-desc">Introduced cross-platform React Native / Expo companion apps for drivers and parents, along with offline SQLite caching and background location tracking in Flutter.</p>
                        <ul className="changelog-list">
                          <li>Developed driver-app-mobile using React Native / Expo with shift navigation, route checklists, and themes.</li>
                          <li>Developed parent-app-mobile using React Native / Expo with student live tracking and animated status indicators.</li>
                          <li>Added offline SQLite telemetry database helper and background location service to driver_app_flutter.</li>
                          <li>Engineered map service utility for polyline route calculation and waypoint rendering across Flutter apps.</li>
                          <li>Optimized driver companion web application styles and telemetry synchronization with the backend server.</li>
                        </ul>
                      </div>

                      <div className="changelog-item" style={{ marginTop: '24px' }}>
                        <div className="changelog-dot" style={{ backgroundColor: '#EF4444' }}></div>
                        <div className="changelog-header">
                          <span className="changelog-version" style={{ backgroundColor: '#EF4444' }}>v1.4.0</span>
                          <span className="changelog-date">August 29, 2026</span>
                          <span className="changelog-title">Live Tracking, Ingestions & Multi-App Scaffolds</span>
                        </div>
                        <p className="changelog-desc">Added bulk student data imports, self-healing database migrations, extended CRUD APIs, and scaffolding for driver and parent application suites.</p>
                        <ul className="changelog-list">
                          <li>Created a bulk student ingestion template and REST API handlers for batch importing.</li>
                          <li>Developed self-healing database schema migrations for users and students.</li>
                          <li>Added new PUT and DELETE API endpoints for students, users, and document tracking.</li>
                          <li>Scaffolded React map components and core UI frameworks for driver-app and parent-app companion portals.</li>
                          <li>Configured inline document viewing and attachments retrieval modes.</li>
                        </ul>
                      </div>

                      <div className="changelog-item" style={{ marginTop: '24px' }}>
                        <div className="changelog-dot" style={{ backgroundColor: '#8B5CF6' }}></div>
                        <div className="changelog-header">
                          <span className="changelog-version" style={{ backgroundColor: '#8B5CF6' }}>v1.3.1</span>
                          <span className="changelog-date">August 27, 2026</span>
                          <span className="changelog-title">Automated UI Compaction & Fleet CRUD APIs</span>
                        </div>
                        <p className="changelog-desc">Introduced automated UI compaction utilities to switch dashboard density, and integrated new backend and frontend CRUD management APIs for school buses.</p>
                        <ul className="changelog-list">
                          <li>Developed automated styling compression scripts to dynamically compress card paddings, avatar sizes, buttons, and grid gaps.</li>
                          <li>Created relax.js tool to expand padding, margins, and restore comfortable layout modes.</li>
                          <li>Implemented server-side API endpoints (POST, PUT, DELETE /api/buses) to support full CRUD operations on school buses.</li>
                          <li>Connected administrative Fleet tab interface with the new CRUD endpoints.</li>
                        </ul>
                      </div>

                      <div className="changelog-item" style={{ marginTop: '24px' }}>
                        <div className="changelog-dot" style={{ backgroundColor: '#10B981' }}></div>
                        <div className="changelog-header">
                          <span className="changelog-version" style={{ backgroundColor: '#10B981' }}>v1.3.0</span>
                          <span className="changelog-date">August 27, 2026</span>
                          <span className="changelog-title">Multi-Provider Storage & Enterprise Logging System</span>
                        </div>
                        <p className="changelog-desc">Introduced pluggable storage options for file management and a production-grade Pino logger with automated log rotation and correlation tracing.</p>
                        <ul className="changelog-list">
                          <li>Designed a multi-provider Storage Service abstraction supporting Local, AWS S3, Google Cloud Storage (GCS), and Azure Blob storage backends.</li>
                          <li>Implemented high-performance Pino logging on the server with correlation IDs via AsyncLocalStorage context.</li>
                          <li>Integrated automatic log rotation (rotating-file-stream) with daily limits and Gzip compression.</li>
                          <li>Added a new "Audit & System Logs" page in the Admin console to monitor live server logs and database diagnostics.</li>
                          <li>Refactored workspace to a unified Monorepo structure with shared dependencies.</li>
                        </ul>
                      </div>

                      <div className="changelog-item" style={{ marginTop: '24px' }}>
                        <div className="changelog-dot" style={{ backgroundColor: '#F59E0B' }}></div>
                        <div className="changelog-header">
                          <span className="changelog-version" style={{ backgroundColor: '#F59E0B' }}>v1.2.1</span>
                          <span className="changelog-date">August 24, 2026</span>
                          <span className="changelog-title">Changelog, Trip Scheduling & Driver Management</span>
                        </div>
                        <p className="changelog-desc">Added a dedicated System Changelog interface under Setup & Config, introduced future trip scheduling with date selectors, enabled trip searching, and rolled out driver profile management screens.</p>
                        <ul className="changelog-list">
                          <li>Added date field in trip configurations to enable scheduling future trips.</li>
                          <li>Implemented search filters to easily locate scheduled trips.</li>
                          <li>Designed a new Driver Profile screen with the ability to add new drivers.</li>
                          <li>Created the Changelog menu item and visual timeline under Setup & Config.</li>
                        </ul>
                      </div>

                      <div className="changelog-item" style={{ marginTop: '24px' }}>
                        <div className="changelog-dot" style={{ backgroundColor: '#4E73DF' }}></div>
                        <div className="changelog-header">
                          <span className="changelog-version" style={{ backgroundColor: '#4E73DF' }}>v1.2.0</span>
                          <span className="changelog-date">August 20, 2026</span>
                          <span className="changelog-title">BusBuddy Driver App & Live Trip Tracking</span>
                        </div>
                        <p className="changelog-desc">Launched the driver mobile application with active route tracking and GPS synchronization.</p>
                        <ul className="changelog-list">
                          <li>Added Flutter-based driver app implementation.</li>
                          <li>Implemented active trip dashboard with live route progress tracking.</li>
                          <li>Integrated GPS coordinates streaming via WebSockets.</li>
                          <li>Added offline coordinate buffering/caching mechanism for low-connectivity zones.</li>
                        </ul>
                      </div>

                      <div className="changelog-item" style={{ marginTop: '24px' }}>
                        <div className="changelog-dot" style={{ backgroundColor: '#1CC88A' }}></div>
                        <div className="changelog-header">
                          <span className="changelog-version" style={{ backgroundColor: '#1CC88A' }}>v1.1.0</span>
                          <span className="changelog-date">August 15, 2026</span>
                          <span className="changelog-title">Console Admin Portal & Student Profiles</span>
                        </div>
                        <p className="changelog-desc">Introduced the console admin management interface with database optimization tools.</p>
                        <ul className="changelog-list">
                          <li>Created React-based administrator web console interface.</li>
                          <li>Integrated Leaflet maps for live visual monitoring of routes and stops.</li>
                          <li>Added comprehensive directory management for students, parents, drivers, and buses.</li>
                          <li>Implemented Access Control Lists (ACL) and security roles.</li>
                          <li>Added database operations: index rebuilding, reindexing, and vacuuming diagnostics.</li>
                        </ul>
                      </div>

                      <div className="changelog-item" style={{ marginTop: '24px' }}>
                        <div className="changelog-dot" style={{ backgroundColor: '#36B9CC' }}></div>
                        <div className="changelog-header">
                          <span className="changelog-version" style={{ backgroundColor: '#36B9CC' }}>v1.0.0</span>
                          <span className="changelog-date">August 10, 2026</span>
                          <span className="changelog-title">Parent Tracking App & Core SQLite Backend</span>
                        </div>
                        <p className="changelog-desc">Initialized the school bus tracking system with node backend server and parent interface.</p>
                        <ul className="changelog-list">
                          <li>Designed and deployed parent tracking application with real-time ETA alerts.</li>
                          <li>Configured central SQLite database with relationships for routes, stops, and users.</li>
                          <li>Created Express.js REST APIs for attendance tracking and route planning.</li>
                          <li>Integrated multi-channel automated alerts (Mobile Push, SMS via Twilio, SMTP Email reports).</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
            </>
          )}
        </div>
      </div>
      {/* Global Alert / Confirmation Modal Dialog */}
      {modalConfig && (() => {
        const isDestructive = modalConfig.isDestructive;
        const isError = modalConfig.type === 'error';
        const isWarning = modalConfig.type === 'warning';
        const isSuccess = modalConfig.type === 'success';

        let primaryColor = '#10B981'; // default green
        let emoji = 'ℹ️';

        if (isSuccess) {
          primaryColor = '#10B981'; // emerald green
          emoji = '✅';
        } else if (isWarning) {
          primaryColor = '#F59E0B'; // amber/yellow
          emoji = '⚠️';
        } else if (isError || (modalConfig.type === 'confirm' && isDestructive)) {
          primaryColor = '#EF4444'; // red for errors & destructive confirmations
          emoji = isError ? '❌' : '🗑️';
        } else if (modalConfig.type === 'confirm') {
          primaryColor = '#3B82F6'; // blue for standard confirmation
          emoji = '❓';
        }

        return (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            animation: 'fadeIn 0.2s ease-out'
          }}>
            <div style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '440px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              overflow: 'hidden'
            }}>
              {/* Top Accent Color Bar */}
              <div style={{ height: '4px', backgroundColor: primaryColor }} />

              {/* Modal Header */}
              <div style={{
                padding: '20px 24px',
                borderBottom: '1px solid #F1F5F9',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span style={{ fontSize: '24px' }}>{emoji}</span>
                <h3 style={{
                  margin: 0,
                  fontSize: '16px',
                  fontWeight: '800',
                  color: '#0F172A'
                }}>
                  {modalConfig.title}
                </h3>
              </div>

              {/* Modal Body */}
              <div style={{
                padding: '24px',
                fontSize: '14px',
                color: '#475569',
                lineHeight: '1.6',
                fontWeight: '500'
              }}>
                {modalConfig.message}
              </div>

              {/* Modal Footer / Action Buttons */}
              <div style={{
                padding: '16px 24px',
                backgroundColor: '#F8FAFC',
                borderTop: '1px solid #F1F5F9',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px'
              }}>
                {modalConfig.type === 'confirm' && (
                  <button
                    type="button"
                    onClick={modalConfig.onCancel}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: '1.5px solid #E2E8F0',
                      backgroundColor: '#ffffff',
                      color: '#475569',
                      fontSize: '13px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      outline: 'none'
                    }}
                   className="btn-secondary">
                    Cancel
                  </button>
                )}
                <button
                  type="button"
                  onClick={modalConfig.onConfirm}
                  style={{
                    padding: '8px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: primaryColor,
                    color: '#ffffff',
                    fontSize: '13px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    outline: 'none',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}
                >
                  {modalConfig.type === 'confirm' ? 'Confirm' : 'OK'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Global Datalists for Master Lookups */}
      <datalist id="master_student_grades_list">
        {(masterLookups?.student_grades || DEFAULT_LOOKUPS.student_grades).map(g => <option key={g} value={g} />)}
      </datalist>
      <datalist id="master_student_sections_list">
        {(masterLookups?.student_sections || DEFAULT_LOOKUPS.student_sections).map(s => <option key={s} value={s} />)}
      </datalist>
      <datalist id="master_student_boards_list">
        {(masterLookups?.student_boards || DEFAULT_LOOKUPS.student_boards).map(b => <option key={b} value={b} />)}
      </datalist>
      <datalist id="master_student_streams_list">
        {(masterLookups?.student_streams || DEFAULT_LOOKUPS.student_streams).map(st => <option key={st} value={st} />)}
      </datalist>
      <datalist id="master_student_languages_list">
        {(masterLookups?.student_languages || DEFAULT_LOOKUPS.student_languages).map(l => <option key={l} value={l} />)}
      </datalist>
      <datalist id="master_student_houses_list">
        {(masterLookups?.student_houses || DEFAULT_LOOKUPS.student_houses).map(h => <option key={h} value={h} />)}
      </datalist>
      <datalist id="master_driver_medical_statuses_list">
        {(masterLookups?.driver_medical_statuses || DEFAULT_LOOKUPS.driver_medical_statuses).map(m => <option key={m} value={m} />)}
      </datalist>
      <datalist id="master_driver_police_statuses_list">
        {(masterLookups?.driver_police_statuses || DEFAULT_LOOKUPS.driver_police_statuses).map(p => <option key={p} value={p} />)}
      </datalist>
      <datalist id="master_bus_doc_statuses_list">
        {(masterLookups?.bus_doc_statuses || DEFAULT_LOOKUPS.bus_doc_statuses).map(d => <option key={d} value={d} />)}
      </datalist>
      <datalist id="master_bus_fuel_types_list">
        {(masterLookups?.bus_fuel_types || DEFAULT_LOOKUPS.bus_fuel_types).map(f => <option key={f} value={f} />)}
      </datalist>
      <datalist id="master_bus_ownership_types_list">
        {(masterLookups?.bus_ownership_types || DEFAULT_LOOKUPS.bus_ownership_types).map(o => <option key={o} value={o} />)}
      </datalist>
      <datalist id="master_bus_doc_types_list">
        {(masterLookups?.bus_doc_types || DEFAULT_LOOKUPS.bus_doc_types).map(t => <option key={t} value={t} />)}
      </datalist>
      <datalist id="master_student_doc_types_list">
        {(masterLookups?.student_doc_types || DEFAULT_LOOKUPS.student_doc_types).map(t => <option key={t} value={t} />)}
      </datalist>
      <datalist id="master_driver_doc_types_list">
        {(masterLookups?.driver_doc_types || DEFAULT_LOOKUPS.driver_doc_types).map(t => <option key={t} value={t} />)}
      </datalist>
    </div>
  );
}


















